export const translations = {
  en: {
    meta: {
      home: {
        title: 'SafeEat | AI Food Scanner for Allergies & Intolerances',
        description: 'Scan labels, barcodes, menus and dishes to flag allergens and diet risks for your profile: allergies, intolerances & chronic conditions.'
      },
      privacy: {
        title: 'Privacy Policy | SafeEat',
        description: 'Read how SafeEat collects, uses, and protects your data.'
      },
      terms: {
        title: 'Terms of Use | SafeEat',
        description: 'Review the terms that govern your use of SafeEat.'
      },
      manual: {
        title: 'User Manual | SafeEat',
        description: 'Learn how to use SafeEat to scan food safely.'
      },
      disclaimer: {
        title: 'Disclaimer | SafeEat',
        description: 'Understand SafeEat informational guidance and limitations.'
      }
    },
    nav: {
      about: 'How it works',
      features: 'Features',
      testimonials: 'Testimonials',
      pricing: 'Pricing',
      faq: 'FAQ',
      contacts: 'Contacts',
      getStarted: 'Get Started'
    },
    about: {
      description: 'AI-powered food scanner that reads labels, barcodes, dishes, and menus to highlight allergens, ingredients, and nutrition based on health profile.'
    },
    hero: {
      badge: 'AI-Powered Food Safety',
      title: 'Instantly Check',
      title2: 'Food Safety',
      titleHighlight: 'Personal For You',
      subtitle: 'Based on Your',
      description: 'Scan Groceries, Barcodes, Ingredients, and Menus to instantly check food safety for your body',
      typing: ['food intolerances', 'chronic diseases', 'allergies'],
      stat1: '500+',
      stat1Label: 'Chronic Diseases',
      stat2: '250+',
      stat2Label: 'Allergies',
      stat3: '100+',
      stat3Label: 'Food Intolerances',
      downloadButton: 'Download on App Store'
    },
    howItWorks: {
      mainTitle: 'How It Works',
      badge: 'How it works',
      subtitle: 'Our food scanning process is simple, fast, and produces personalized results in just a few easy steps.',
      setupBadge: 'One-time setup',
      setupTitle: 'Set Up Your Health Profile',
      setupDesc: 'During onboarding, personalize SafeEat by adding your health information. This is done once and enables personalized food analysis just for you.',
      cards: {
        diseases: {
          title: 'Chronic Diseases',
          desc: 'Add your chronic conditions from our database of 500+ diseases. From diabetes to heart conditions - we\'ve got you covered.',
          statLabel: 'conditions'
        },
        allergies: {
          title: 'Allergies',
          desc: 'Select your allergies from 250+ options. From common nuts and shellfish to rare allergens - stay protected.',
          statLabel: 'allergies'
        },
        intolerances: {
          title: 'Food Intolerances',
          desc: 'Specify your intolerances from 100+ options. Lactose, gluten, fructose and more - eat without discomfort.',
          statLabel: 'intolerances'
        }
      },
      connectorText: 'Then, every time you scan:',
      processTitle: 'See It In Action',
      processSubtitle: 'Watch how SafeEat analyzes your food in real-time',
      stepLabel: 'Step',
      step1Title: 'Scan Food',
      step1Desc: 'Scan any food - individual products, prepared dishes, nutrition labels, barcodes, or even restaurant menus. Just point your camera and let SafeEat do the rest.',
      step2Title: 'AI Analysis',
      step2Desc: 'The app analyzes the image and cross-references it with your profile of diseases, allergies, and food intolerances to determine compatibility.',
      step3Title: 'Get Your Report',
      step3Desc: 'SafeEat provides a detailed report on the analyzed products, showing how well they match your health profile and dietary needs.',
      step4Title: 'Make Your Decision',
      step4Desc: 'Armed with personalized insights, you choose which food is best for you. Eat confidently, knowing exactly what works for your body.'
    },
    features: {
      title: 'Powerful Features',
      subtitle: 'Everything you need to eat safely',
      items: [
        {
          title: 'Scan Any Product',
          subtitle: 'from common snacks to exotic fruits',
          description: 'Point your camera at any packaged or unpackaged food, from supermarket staples to rare tropical fruits, and get an instant safety check. SafeEat identifies the product and analyses its composition, helping you avoid risky or harmful ingredients.',
          highlights: ['Packaged & unpackaged food', 'Instant safety check', 'Composition analysis'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Scan Nutrition Labels & Barcodes',
          subtitle: 'decode confusing labels instantly',
          description: 'No more struggling to read tiny print or decode confusing labels. SafeEat can scan nutrition facts, ingredient lists, and barcodes in seconds. The app instantly highlights allergens, additives, or toxic substances so you know exactly what you’re eating.',
          highlights: ['Nutrition facts scanning', 'Allergen detection', 'Toxic substance alerts'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Scan Dishes – Detect Ingredients',
          subtitle: 'from a photo of your meal',
          description: 'Snap a photo of your meal, and SafeEat will break it down into its ingredients. Whether it’s homemade, from a food court, or a gourmet restaurant, our AI will identify potential allergens or ingredients that don’t fit your diet.',
          highlights: ['AI ingredient detection', 'Works with any dish', 'Diet compatibility check'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Scan Restaurant Menus',
          subtitle: 'find safe dishes before you order',
          description: 'Point your camera at a café or restaurant menu, and SafeEat will analyse every dish to show you which ones are safe for your health needs. Perfect for eating out without worrying about hidden allergens or unwanted ingredients.',
          highlights: ['Menu analysis', 'Safe dish finder', 'Hidden allergen detection'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Create Your Personal Safe Food List',
          subtitle: 'build your clean eating library',
          description: 'Save the foods that don’t fit your diet, build your own "clean eating" library, and access it anytime while shopping or dining out. Your personalized food database grows with every scan.',
          highlights: ['Personal food library', 'Quick access anywhere', 'Smart recommendations'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Trusted by Thousands',
      subtitle: 'Trusted by a global community of users.',
      items: [
        { name: 'Sarah M.', location: 'New York, USA', rating: 5, content: 'SafeEat completely changed how I shop for groceries! I have celiac disease and scanning products takes seconds. No more reading tiny labels in the store.' },
        { name: 'Marcus L.', location: 'London, UK', rating: 5, content: 'As someone with multiple food allergies, dining out was always stressful. Now I just scan the menu and know exactly what\'s safe. Game changer!' },
        { name: 'Yuki T.', location: 'Tokyo, Japan', rating: 5, content: 'I use SafeEat every day to manage my diabetes. The nutrition label scanner helps me track carbs and sugar instantly. Highly recommend!' },
        { name: 'Anna K.', location: 'Berlin, Germany', rating: 5, content: 'My son has severe nut allergies. SafeEat gives me peace of mind when buying snacks for him. The barcode scanner is incredibly accurate.' },
        { name: 'Carlos R.', location: 'Madrid, Spain', rating: 5, content: 'I\'m lactose intolerant and travel a lot. SafeEat works in any language - I just scan restaurant menus abroad and eat without worry!' },
        { name: 'Priya S.', location: 'Mumbai, India', rating: 5, content: 'Managing my husband\'s heart condition means watching sodium carefully. SafeEat\'s instant analysis makes meal planning so much easier.' },
        { name: 'Emma W.', location: 'Sydney, Australia', rating: 5, content: 'The dish scanner is amazing! I photographed my restaurant meal and it identified hidden gluten ingredients. Saved me from a bad reaction!' },
        { name: 'Ahmed H.', location: 'Dubai, UAE', rating: 5, content: 'I follow a strict halal diet. SafeEat helps me verify ingredients in packaged foods I\'m not familiar with. Essential app!' },
        { name: 'Lisa M.', location: 'Toronto, Canada', rating: 5, content: 'With three kids having different allergies, grocery shopping was a nightmare. Now I scan everything in seconds. This app is a lifesaver!' },
        { name: 'Johan V.', location: 'Stockholm, Sweden', rating: 5, content: 'I have Crohn\'s disease and need to avoid certain triggers. SafeEat\'s personalized profile catches ingredients I would have missed.' },
        { name: 'Maria G.', location: 'São Paulo, Brazil', rating: 5, content: 'The menu scanner feature is perfect for my shellfish allergy. I feel confident ordering at new restaurants now!' },
        { name: 'David C.', location: 'Paris, France', rating: 5, content: 'As a chef with food sensitivities, I use SafeEat to check ingredients before cooking. The AI detection is impressively accurate.' },
        { name: 'Fatima A.', location: 'Cairo, Egypt', rating: 5, content: 'Managing gestational diabetes was overwhelming until I found SafeEat. Scanning food labels makes tracking so simple!' },
        { name: 'Michael O.', location: 'Lagos, Nigeria', rating: 5, content: 'I have a rare food intolerance that most apps don\'t cover. SafeEat let me add it to my profile and now catches it every time!' },
        { name: 'Sophie L.', location: 'Amsterdam, Netherlands', rating: 5, content: 'Vegan with soy allergy here - finding safe food was so hard! SafeEat identifies hidden animal products AND soy. Love it!' },
        { name: 'Kenji N.', location: 'Seoul, South Korea', rating: 5, content: 'The barcode database is huge! Even imported products are recognized. Perfect for checking unfamiliar international foods.' },
        { name: 'Elena P.', location: 'Rome, Italy', rating: 5, content: 'My elderly mother has multiple conditions. I set up her profile and now she can shop independently again. Thank you SafeEat!' }
      ]
    },
    pricing: {
      title: 'Simple, Transparent Pricing',
      subtitle: 'Choose the plan that works for you',
      free: 'Free',
      freeDesc: '5 free scans to try',
      freeFeature1: '5 AI scans',
      freeFeature2: 'Basic analysis',
      freeFeature3: 'Limited history',
      weekly: 'Weekly',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Perfect for regular users',
      weeklyFeature1: 'Unlimited scans',
      weeklyFeature2: 'Advanced analysis',
      weeklyFeature3: 'Full history',
      weeklyFeature4: 'Priority support',
      annual: 'Annual',
      annualPrice: '$29.99',
      annualDesc: 'Best value - Save 60%',
      annualBadge: 'Most Popular',
      annualFeature1: 'Everything in Weekly',
      annualFeature2: 'Save $125/year',
      annualFeature3: 'Premium support',
      annualFeature4: 'Early access to features',
      button: 'Get Started'
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        { q: 'What can I scan with SafeEat?', a: 'SafeEat can analyze: ingredient lists & nutrition facts (photos), barcodes, packaged and unpackaged foods, dishes (photos), and restaurant menus.' },
        { q: 'How does the dish recognition feature work?', a: 'AI identifies a dish and estimates likely ingredients. Best with good lighting/angle; hidden ingredients or cross-contact may be missed.' },
        { q: 'Can I scan restaurant menus?', a: 'Yes. It flags potential allergens/diet conflicts. Menus can be incomplete; confirm with staff about ingredients, oils, shared fryers, sauces, cross-contact.' },
        { q: 'What health conditions does SafeEat support?', a: 'Food allergies, intolerances (e.g., lactose, gluten), and diet needs (low sodium, carb awareness, ingredient avoidance).' },
        { q: 'Does SafeEat work offline?', a: 'Usually internet is needed for scans/AI. Saved results/lists may be available offline depending on app/device.' },
        { q: 'How accurate is SafeEat’s analysis?', a: 'Depends on photo quality, label text, language/format, menu completeness. It is informational—double-check when risk is high.' },
        { q: 'What if SafeEat makes a mistake?', a: 'Don’t rely on a doubtful result; rescan better; verify with label/site/staff; choose the safer option if high risk.' },
        { q: 'Can I report incorrect analysis results?', a: 'Yes: support@safeeat.app with screenshots, photo of label/menu/dish, country/brand/product, and what seems wrong.' },
        { q: 'Why might dish recognition be less accurate?', a: 'Similar-looking foods, hidden ingredients, heavy sauces/garnish, low light/blur, highly customized/regional dishes.' },
        { q: 'Is SafeEat a medical device?', a: 'No. It is informational only. For medical advice, consult a professional.' },
        { q: 'What is SafeEat’s liability?', a: 'Info is “as is”; cannot guarantee completeness. You are responsible for decisions; undeclared ingredients and cross-contact are possible.' },
        { q: 'Should I rely solely on SafeEat for severe allergies?', a: 'No. Read labels, ask staff, follow your action plan, carry prescribed meds; when unsure, avoid the item.' },
        { q: 'How do you protect my health data?', a: 'Collect minimal data, protect with safeguards, limited access, privacy contact support@safeeat.app. See Privacy Policy.' },
        { q: 'Can I use SafeEat for my children?', a: 'Yes, with supervision. Be conservative for severe allergies/complex diets; verify with reliable sources.' },
        { q: 'What should I do if I have a reaction?', a: 'Seek medical help immediately, follow your emergency plan, call emergency for severe symptoms, then report to provider/restaurant/manufacturer.' }
      ]
    },
    cta: {
      title: 'Ready to Transform Your Food Life?',
      subtitle: 'Join thousands using SafeEat every day',
      description: 'Download now and get 5 free AI scans',
      button: 'Download on App Store',
      feature1: 'No credit card required',
      feature2: '100% Private & Secure',
      feature3: 'Instant access'
    },
    newsletter: {
      title: 'Stay Updated',
      subtitle: 'Get the latest updates and health tips',
      placeholder: 'Enter your email',
      button: 'Subscribe',
      success: 'Thank you for subscribing!',
      error: 'Please enter a valid email address'
    },
    seo: {
      title: 'Your Personal Food Safety Assistant',
      text: 'SafeEat is an AI-powered food scanner app for people with food allergies, intolerances, and dietary restrictions. Instantly scan labels, nutrition facts, barcodes, restaurant menus, and prepared dishes to check safety for your profile. Whether you have celiac disease, lactose intolerance, nut allergies, or follow gluten-free, vegan, or low-sodium diets, SafeEat helps you choose safely.',
      keywords: [
        'Food Allergy App',
        'Ingredient Scanner',
        'Allergen Detector',
        'Nutrition Label Reader',
        'Barcode Scanner',
        'Menu Scanner',
        'Gluten-Free Checker',
        'Lactose Intolerance App',
        'Diet Tracker',
        'Food Safety'
      ]
    },
    footer: {
      product: 'Product',
      features: 'Features',
      howItWorks: 'How It Works',
      testimonials: 'Testimonials',
      pricing: 'Pricing',
      faq: 'FAQ',
      contacts: 'Contacts',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      usePolicy: 'User Manual',
      disclaimer: 'Disclaimer',
      company: 'FJCode OU',
      register: 'Register number 16993109',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. All rights reserved.'
    },
    cookies: {
      bannerText: 'We use cookies: necessary cookies to run the site, and analytics cookies only with your consent.',
      decline: 'Decline',
      accept: 'Accept'
    },
    notFound: {
      title: 'Looks like this ingredient is missing.',
      body: 'The page you’re looking for isn’t on the menu. Let’s get you back to a safe place.',
      backHome: 'Back to Home',
      goFaq: 'Go to FAQ'
    },
    legal: {
      backHome: 'Back to Home'
    }
  },
  es: {
    meta: {
      home: {
        title: 'SafeEat | Escáner de alimentos con IA para alergias e intolerancias',
        description: 'Escanea etiquetas, códigos de barras, menús y platos para detectar alérgenos y riesgos dietéticos según tu perfil: alergias, intolerancias y enfermedades crónicas.'
      },
      privacy: {
        title: 'Política de privacidad | SafeEat',
        description: 'Descubre cómo SafeEat recopila, usa y protege tus datos.'
      },
      terms: {
        title: 'Términos de uso | SafeEat',
        description: 'Revisa los términos que rigen tu uso de SafeEat.'
      },
      manual: {
        title: 'Manual de usuario | SafeEat',
        description: 'Aprende a usar SafeEat para escanear alimentos con seguridad.'
      },
      disclaimer: {
        title: 'Descargo de responsabilidad | SafeEat',
        description: 'Conoce las pautas informativas y las limitaciones de SafeEat.'
      }
    },
    nav: {
      about: 'Cómo funciona',
      features: 'Funciones',
      testimonials: 'Testimonios',
      pricing: 'Precios',
      faq: 'Preguntas frecuentes',
      contacts: 'Contacto',
      getStarted: 'Empezar'
    },
    about: {
      description: 'Escáner de alimentos con IA que lee etiquetas, códigos de barras, platos y menús para destacar alérgenos, ingredientes e información nutricional según tu perfil de salud.'
    },
    hero: {
      badge: 'Seguridad alimentaria con IA',
      title: 'Comprueba al instante',
      title2: 'la seguridad de tus alimentos',
      titleHighlight: 'Personalizada para ti',
      subtitle: 'Basado en tus',
      description: 'Escanea compras, códigos de barras, ingredientes y menús para comprobar al instante si un alimento es seguro para tu cuerpo',
      typing: ['enfermedades crónicas', 'alergias', 'intolerancias alimentarias'],
      stat1: '500+',
      stat1Label: 'Enfermedades crónicas',
      stat2: '250+',
      stat2Label: 'Alergias',
      stat3: '100+',
      stat3Label: 'Intolerancias alimentarias',
      downloadButton: 'Descargar en el App Store'
    },
    howItWorks: {
      badge: 'Cómo funciona',
      mainTitle: 'Cómo funciona SafeEat',
      subtitle: 'Nuestro proceso de escaneo de alimentos es sencillo, rápido y ofrece resultados personalizados en solo unos pasos.',
      setupBadge: 'Configuración única',
      setupTitle: 'Configura tu perfil de salud',
      setupDesc: 'Durante el registro inicial, personaliza SafeEat añadiendo tu información de salud. Se hace una sola vez y permite un análisis de alimentos personalizado para ti.',
      cards: {
        diseases: {
          title: 'Enfermedades crónicas',
          desc: 'Añade tus afecciones crónicas desde nuestra base de datos de más de 500 enfermedades. Desde la diabetes hasta problemas cardíacos: lo tenemos cubierto.',
          statLabel: 'afecciones'
        },
        allergies: {
          title: 'Alergias',
          desc: 'Selecciona tus alergias entre más de 250 opciones. Desde frutos secos y marisco habituales hasta alérgenos poco comunes: mantente protegido.',
          statLabel: 'alergias'
        },
        intolerances: {
          title: 'Intolerancias alimentarias',
          desc: 'Indica tus intolerancias entre más de 100 opciones. Lactosa, gluten, fructosa y más: come sin molestias.',
          statLabel: 'intolerancias'
        }
      },
      connectorText: 'Luego, cada vez que escanees:',
      stepLabel: 'Paso',
      step1Title: 'Escanea alimentos',
      step1Desc: 'Escanea cualquier comida: productos individuales, platos preparados, etiquetas nutricionales, códigos de barras o incluso menús de restaurantes. Solo apunta con la cámara y deja que SafeEat haga el resto.',
      step2Title: 'Análisis con IA',
      step2Desc: 'La app analiza la imagen y la compara con tu perfil de enfermedades, alergias e intolerancias alimentarias para determinar la compatibilidad.',
      step3Title: 'Obtén tu informe',
      step3Desc: 'SafeEat genera un informe detallado de los productos analizados, mostrando hasta qué punto encajan con tu perfil de salud y tus necesidades dietéticas.',
      step4Title: 'Toma tu decisión',
      step4Desc: 'Con información personalizada en mano, tú eliges qué alimento es mejor para ti. Come con confianza, sabiendo exactamente qué le sienta bien a tu cuerpo.',
      processTitle: 'Míralo en acción',
      processSubtitle: 'Observa cómo SafeEat analiza tus alimentos en tiempo real'
    },
    features: {
      title: 'Funciones potentes',
      subtitle: 'Todo lo que necesitas para comer con seguridad',
      items: [
        {
          title: 'Escanea cualquier producto',
          subtitle: 'desde snacks habituales hasta frutas exóticas',
          description: 'Apunta con la cámara a cualquier alimento envasado o a granel, desde básicos del supermercado hasta frutas tropicales poco comunes, y obtén una comprobación de seguridad al instante. SafeEat identifica el producto y analiza su composición, ayudándote a evitar ingredientes de riesgo o perjudiciales.',
          highlights: ['Alimentos envasados y a granel', 'Comprobación al instante', 'Análisis de composición'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Escanea etiquetas nutricionales y códigos de barras',
          subtitle: 'descifra etiquetas confusas al instante',
          description: 'Se acabó luchar por leer la letra pequeña o descifrar etiquetas confusas. SafeEat puede escanear la información nutricional, la lista de ingredientes y los códigos de barras en segundos. La app resalta al instante alérgenos, aditivos o sustancias tóxicas para que sepas exactamente qué estás comiendo.',
          highlights: ['Escaneo de información nutricional', 'Detección de alérgenos', 'Alertas de sustancias tóxicas'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Escanea platos: detecta ingredientes',
          subtitle: 'a partir de una foto de tu comida',
          description: 'Haz una foto de tu comida y SafeEat la desglosará en sus ingredientes. Ya sea casera, de una zona de restauración o de un restaurante gourmet, nuestra IA identificará posibles alérgenos o ingredientes que no encajen con tu dieta.',
          highlights: ['Detección de ingredientes con IA', 'Funciona con cualquier plato', 'Comprobación de compatibilidad dietética'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Escanea menús de restaurantes',
          subtitle: 'encuentra platos seguros antes de pedir',
          description: 'Apunta con la cámara al menú de un café o restaurante y SafeEat analizará cada plato para mostrarte cuáles son seguros para tus necesidades de salud. Perfecto para comer fuera sin preocuparte por alérgenos ocultos o ingredientes no deseados.',
          highlights: ['Análisis de menús', 'Buscador de platos seguros', 'Detección de alérgenos ocultos'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Crea tu lista personal de alimentos seguros',
          subtitle: 'construye tu biblioteca de comida «limpia»',
          description: 'Guarda los alimentos aptos para ti, crea tu propia biblioteca de «comida limpia» y accede a ella en cualquier momento mientras compras o comes fuera. Tu base de datos personalizada crece con cada escaneo.',
          highlights: ['Biblioteca personal de alimentos', 'Acceso rápido en cualquier lugar', 'Recomendaciones inteligentes'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Con la confianza de miles',
      subtitle: 'Con la confianza de una comunidad global de usuarios.',
      items: [
        { name: 'Sarah M.', location: 'Nueva York, EE. UU.', rating: 5, content: '¡SafeEat ha cambiado por completo mi forma de comprar! Tengo celiaquía y escanear productos lleva segundos. Ya no tengo que leer etiquetas diminutas en la tienda.' },
        { name: 'Marcus L.', location: 'Londres, Reino Unido', rating: 5, content: 'Como tengo varias alergias alimentarias, comer fuera siempre era estresante. Ahora solo escaneo el menú y sé exactamente qué es seguro. ¡Un antes y un después!' },
        { name: 'Yuki T.', location: 'Tokio, Japón', rating: 5, content: 'Uso SafeEat a diario para controlar mi diabetes. El escáner de etiquetas nutricionales me ayuda a seguir los carbohidratos y el azúcar al instante. ¡Muy recomendable!' },
        { name: 'Anna K.', location: 'Berlín, Alemania', rating: 5, content: 'Mi hijo tiene alergia grave a los frutos secos. SafeEat me da tranquilidad al comprarle snacks. El escáner de códigos de barras es increíblemente preciso.' },
        { name: 'Carlos R.', location: 'Madrid, España', rating: 5, content: 'Soy intolerante a la lactosa y viajo mucho. SafeEat funciona en cualquier idioma: escaneo menús de restaurantes en el extranjero y como sin preocuparme.' },
        { name: 'Priya S.', location: 'Mumbai, India', rating: 5, content: 'Gestionar la afección cardíaca de mi marido significa vigilar el sodio con cuidado. El análisis instantáneo de SafeEat hace que planificar las comidas sea mucho más fácil.' },
        { name: 'Emma W.', location: 'Sídney, Australia', rating: 5, content: '¡El escáner de platos es increíble! Fotografié mi comida en un restaurante e identificó ingredientes con gluten oculto. Me evitó una mala reacción.' },
        { name: 'Ahmed H.', location: 'Dubái, EAU', rating: 5, content: 'Sigo una dieta halal estricta. SafeEat me ayuda a verificar ingredientes en alimentos envasados que no me son familiares. ¡Una app imprescindible!' },
        { name: 'Lisa M.', location: 'Toronto, Canadá', rating: 5, content: 'Con tres hijos con alergias diferentes, hacer la compra era una pesadilla. Ahora escaneo todo en segundos. Esta app es un salvavidas.' },
        { name: 'Johan V.', location: 'Estocolmo, Suecia', rating: 5, content: 'Tengo enfermedad de Crohn y necesito evitar ciertos desencadenantes. El perfil personalizado de SafeEat detecta ingredientes que se me habrían pasado por alto.' },
        { name: 'Maria G.', location: 'São Paulo, Brasil', rating: 5, content: 'La función de escaneo de menús es perfecta para mi alergia al marisco. ¡Ahora me siento segura al pedir en restaurantes nuevos!' },
        { name: 'David C.', location: 'París, Francia', rating: 5, content: 'Como chef con sensibilidades alimentarias, uso SafeEat para comprobar ingredientes antes de cocinar. La detección con IA es sorprendentemente precisa.' },
        { name: 'Fatima A.', location: 'El Cairo, Egipto', rating: 5, content: 'Controlar la diabetes gestacional fue abrumador hasta que encontré SafeEat. Escanear etiquetas de alimentos hace que el seguimiento sea muy sencillo.' },
        { name: 'Michael O.', location: 'Lagos, Nigeria', rating: 5, content: 'Tengo una intolerancia alimentaria poco común que la mayoría de apps no cubren. SafeEat me permitió añadirla a mi perfil y ahora la detecta cada vez.' },
        { name: 'Sophie L.', location: 'Ámsterdam, Países Bajos', rating: 5, content: 'Vegana con alergia a la soja por aquí: encontrar comida segura era muy difícil. SafeEat identifica productos de origen animal ocultos Y soja. ¡Me encanta!' },
        { name: 'Kenji N.', location: 'Seúl, Corea del Sur', rating: 5, content: '¡La base de datos de códigos de barras es enorme! Incluso reconoce productos importados. Perfecto para comprobar alimentos internacionales desconocidos.' },
        { name: 'Elena P.', location: 'Roma, Italia', rating: 5, content: 'Mi madre mayor tiene varias afecciones. Configuré su perfil y ahora puede hacer la compra de forma independiente otra vez. Gracias, SafeEat.' }
      ]
    },
    pricing: {
      title: 'Precios simples y transparentes',
      subtitle: 'Elige el plan que mejor se adapte a ti',
      free: 'Gratis',
      freeDesc: '5 escaneos gratis para probar',
      freeFeature1: '5 escaneos con IA',
      freeFeature2: 'Análisis básico',
      freeFeature3: 'Historial limitado',
      weekly: 'Semanal',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Perfecto para usuarios habituales',
      weeklyFeature1: 'Escaneos ilimitados',
      weeklyFeature2: 'Análisis avanzado',
      weeklyFeature3: 'Historial completo',
      weeklyFeature4: 'Soporte prioritario',
      annual: 'Anual',
      annualPrice: '$29.99',
      annualDesc: 'Mejor valor: ahorra un 60%',
      annualBadge: 'Más popular',
      annualFeature1: 'Todo lo incluido en Semanal',
      annualFeature2: 'Ahorra 125 $/año',
      annualFeature3: 'Soporte premium',
      annualFeature4: 'Acceso anticipado a funciones',
      button: 'Empezar'
    },
    faq: {
      title: 'Preguntas frecuentes',
      items: [
        { q: '¿Qué puedo escanear con SafeEat?', a: 'SafeEat puede analizar: listas de ingredientes e información nutricional (fotos), códigos de barras, alimentos envasados y a granel, platos (fotos) y menús de restaurantes.' },
        { q: '¿Cómo funciona el reconocimiento de platos?', a: 'La IA identifica el plato y estima ingredientes probables. Funciona mejor con buena luz y un buen ángulo; pueden pasarse por alto ingredientes ocultos o contaminaciones cruzadas.' },
        { q: '¿Puedo escanear menús de restaurantes?', a: 'Sí. Señala posibles alérgenos o conflictos con tu dieta. Los menús pueden estar incompletos; confirma con el personal ingredientes, aceites, freidoras compartidas, salsas y contaminación cruzada.' },
        { q: '¿Qué condiciones de salud admite SafeEat?', a: 'Alergias alimentarias, intolerancias (p. ej., lactosa, gluten) y necesidades dietéticas (bajo en sodio, control de carbohidratos, evitar ingredientes).' },
        { q: '¿SafeEat funciona sin conexión?', a: 'Normalmente se necesita internet para los escaneos y la IA. Los resultados/listas guardados pueden estar disponibles sin conexión según la app y el dispositivo.' },
        { q: '¿Qué precisión tiene el análisis de SafeEat?', a: 'Depende de la calidad de la foto, el texto de la etiqueta, el idioma/formato y lo completo del menú. Es información orientativa: verifica de nuevo cuando el riesgo sea alto.' },
        { q: '¿Y si SafeEat se equivoca?', a: 'No te fíes de un resultado dudoso; vuelve a escanear mejor; verifica en la etiqueta/sitio web/personal; y elige la opción más segura si el riesgo es alto.' },
        { q: '¿Puedo informar de resultados de análisis incorrectos?', a: 'Sí: support@safeeat.app con capturas de pantalla, foto de la etiqueta/menú/plato, país/marca/producto y qué parece estar mal.' },
        { q: '¿Por qué el reconocimiento de platos puede ser menos preciso?', a: 'Comidas de aspecto similar, ingredientes ocultos, salsas/adornos abundantes, poca luz/desenfoque y platos muy personalizados o regionales.' },
        { q: '¿SafeEat es un dispositivo médico?', a: 'No. Es solo informativo. Para consejo médico, consulta a un profesional.' },
        { q: '¿Cuál es la responsabilidad de SafeEat?', a: 'La información se ofrece «tal cual»; no podemos garantizar que sea completa. Tú eres responsable de tus decisiones; pueden existir ingredientes no declarados y contaminación cruzada.' },
        { q: '¿Debo confiar solo en SafeEat si tengo alergias graves?', a: 'No. Lee etiquetas, pregunta al personal, sigue tu plan de acción y lleva los medicamentos prescritos; si dudas, evita el producto.' },
        { q: '¿Cómo protegéis mis datos de salud?', a: 'Recogemos los datos mínimos, los protegemos con medidas de seguridad y acceso limitado. Para privacidad, contacta con support@safeeat.app. Consulta la Política de privacidad.' },
        { q: '¿Puedo usar SafeEat con mis hijos?', a: 'Sí, con supervisión. Sé conservador en casos de alergias graves o dietas complejas; verifica con fuentes fiables.' },
        { q: '¿Qué debo hacer si tengo una reacción?', a: 'Busca ayuda médica inmediatamente, sigue tu plan de emergencia, llama a emergencias si hay síntomas graves y después informa al proveedor/restaurante/fabricante.' }
      ]
    },
    cta: {
      title: '¿Listo para transformar tu relación con la comida?',
      subtitle: 'Únete a miles de personas que usan SafeEat cada día',
      description: 'Descarga ahora y consigue 5 escaneos con IA gratis',
      button: 'Descargar en el App Store',
      feature1: 'No se requiere tarjeta',
      feature2: '100% privado y seguro',
      feature3: 'Acceso instantáneo'
    },
    newsletter: {
      title: 'Mantente al día',
      subtitle: 'Recibe las últimas novedades y consejos de salud',
      placeholder: 'Introduce tu correo electrónico',
      button: 'Suscribirse',
      success: '¡Gracias por suscribirte!',
      error: 'Introduce una dirección de correo válida'
    },
    seo: {
      title: 'Tu asistente personal de seguridad alimentaria',
      text: 'SafeEat es una app de escaneo de alimentos con IA para personas con alergias alimentarias, intolerancias y restricciones dietéticas. Escanea al instante etiquetas, información nutricional, códigos de barras, menús de restaurantes y platos preparados para comprobar si son adecuados para tu perfil. Tanto si tienes celiaquía, intolerancia a la lactosa, alergia a los frutos secos o sigues una dieta sin gluten, vegana o baja en sodio, SafeEat te ayuda a elegir con seguridad.',
      keywords: [
        'App de alergias alimentarias',
        'Escáner de ingredientes',
        'Detector de alérgenos',
        'Lector de etiquetas nutricionales',
        'Escáner de códigos de barras',
        'Escáner de menús',
        'Comprobador sin gluten',
        'App para intolerancia a la lactosa',
        'Seguimiento de dieta',
        'Seguridad alimentaria'
      ]
    },
    cookies: {
      bannerText: 'Usamos cookies: cookies necesarias para que el sitio funcione y cookies de analítica solo con tu consentimiento.',
      decline: 'Rechazar',
      accept: 'Aceptar'
    },
    legal: {
      backHome: 'Volver al inicio'
    },
    footer: {
      product: 'Producto',
      features: 'Funciones',
      howItWorks: 'Cómo funciona',
      testimonials: 'Testimonios',
      pricing: 'Precios',
      faq: 'Preguntas frecuentes',
      contacts: 'Contacto',
      legal: 'Legal',
      privacy: 'Política de privacidad',
      terms: 'Términos del servicio',
      usePolicy: 'Manual de usuario',
      disclaimer: 'Descargo de responsabilidad',
      company: 'FJCode OU',
      register: 'Número de registro 16993109',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. Todos los derechos reservados.'
    }
  },
  ru: {
    meta: {
      home: {
        title: 'SafeEat | AI‑сканер еды для аллергий и непереносимости',
        description: 'Сканируйте этикетки, штрихкоды, меню и блюда, чтобы находить аллергены и диетические риски на основе вашего профиля: аллергии, непереносимость и хронические заболевания.'
      },
      privacy: {
        title: 'Политика конфиденциальности | SafeEat',
        description: 'Узнайте, как SafeEat собирает, использует и защищает ваши данные.'
      },
      terms: {
        title: 'Условия использования | SafeEat',
        description: 'Ознакомьтесь с условиями, применимыми к использованию SafeEat.'
      },
      manual: {
        title: 'Руководство пользователя | SafeEat',
        description: 'Узнайте, как безопасно сканировать еду с помощью SafeEat.'
      },
      disclaimer: {
        title: 'Отказ от ответственности | SafeEat',
        description: 'Поймите, какие сведения предоставляет SafeEat и каковы его ограничения.'
      }
    },
    nav: {
      about: 'Как это работает',
      features: 'Функции',
      testimonials: 'Отзывы',
      pricing: 'Цены',
      faq: 'FAQ',
      contacts: 'Контакты',
      getStarted: 'Начать'
    },
    about: {
      description: 'AI‑сканер еды, который считывает этикетки, штрихкоды, блюда и меню и подсвечивает аллергены, состав и пищевую ценность на основе вашего профиля здоровья.'
    },
    hero: {
      badge: 'Безопасность питания с ИИ',
      title: 'Проверьте',
      title2: 'безопасность еды',
      titleHighlight: 'под вас',
      subtitle: 'На основе',
      description: 'Сканируйте продукты, штрихкоды, состав и меню — и сразу узнавайте, безопасна ли еда для вашего организма',
      typing: ['хронические заболевания', 'аллергии', 'пищевая непереносимость'],
      stat1: '500+',
      stat1Label: 'Хронические заболевания',
      stat2: '250+',
      stat2Label: 'Аллергии',
      stat3: '100+',
      stat3Label: 'Пищевая непереносимость',
      downloadButton: 'Скачать в App Store'
    },
    howItWorks: {
      badge: 'Как это работает',
      mainTitle: 'Как работает SafeEat',
      subtitle: 'Сканирование простое и быстрое — персональные результаты за несколько шагов.',
      setupBadge: 'Настройка один раз',
      setupTitle: 'Создайте профиль здоровья',
      setupDesc: 'Во время онбординга вы настраиваете SafeEat, добавляя информацию о здоровье. Это делается один раз и позволяет получать персональный анализ еды именно для вас.',
      cards: {
        diseases: {
          title: 'Хронические заболевания',
          desc: 'Добавьте свои хронические состояния из нашей базы с 500+ диагнозами. От диабета до сердечно‑сосудистых заболеваний — мы учитываем это.',
          statLabel: 'состояний'
        },
        allergies: {
          title: 'Аллергии',
          desc: 'Выберите свои аллергии из 250+ вариантов. От распространённых орехов и морепродуктов до редких аллергенов — будьте в безопасности.',
          statLabel: 'аллергий'
        },
        intolerances: {
          title: 'Пищевая непереносимость',
          desc: 'Укажите непереносимость из 100+ вариантов. Лактоза, глютен, фруктоза и многое другое — ешьте без дискомфорта.',
          statLabel: 'непереносимостей'
        }
      },
      connectorText: 'А дальше — каждый раз, когда вы сканируете:',
      stepLabel: 'Шаг',
      step1Title: 'Сканируйте еду',
      step1Desc: 'Сканируйте что угодно: продукты, готовые блюда, этикетки с пищевой ценностью, штрихкоды и даже меню ресторанов. Просто наведите камеру — SafeEat сделает остальное.',
      step2Title: 'AI‑анализ',
      step2Desc: 'Приложение анализирует изображение и сопоставляет его с вашим профилем (заболевания, аллергии, непереносимость), оценивая совместимость и возможные риски.',
      step3Title: 'Получите отчёт',
      step3Desc: 'SafeEat формирует подробный отчёт и показывает, насколько продукт или блюдо соответствует вашему профилю здоровья и диетическим требованиям.',
      step4Title: 'Принимайте решение',
      step4Desc: 'С персональными подсказками вы выбираете лучшее для себя. Ешьте спокойно — и точно знайте, что подходит вашему организму.',
      processTitle: 'Посмотрите в действии',
      processSubtitle: 'Увидьте, как SafeEat анализирует еду в реальном времени'
    },
    features: {
      title: 'Мощные функции',
      subtitle: 'Всё, что нужно, чтобы питаться безопасно',
      items: [
        {
          title: 'Сканируйте любой продукт',
          subtitle: 'от обычных перекусов до экзотических фруктов',
          description: 'Наведите камеру на любой продукт — упакованный или без упаковки — от привычных товаров в супермаркете до редких тропических фруктов — и получите мгновенную оценку безопасности. SafeEat распознаёт продукт и анализирует состав, помогая избегать рискованных или нежелательных ингредиентов.',
          highlights: ['Упакованные и неупакованные продукты', 'Мгновенная проверка безопасности', 'Анализ состава'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Сканируйте этикетки и штрихкоды',
          subtitle: 'понимайте сложные маркировки за секунды',
          description: 'Больше не нужно разбирать мелкий шрифт и запутанные этикетки. SafeEat за считанные секунды сканирует пищевую ценность, список ингредиентов и штрихкод. Приложение сразу подсвечивает аллергены, добавки или потенциально нежелательные вещества, чтобы вы точно знали, что едите.',
          highlights: ['Сканирование пищевой ценности', 'Обнаружение аллергенов', 'Предупреждения о нежелательных веществах'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Сканируйте блюда — распознавайте ингредиенты',
          subtitle: 'по фото вашей еды',
          description: 'Сделайте фото блюда — SafeEat разложит его на предполагаемые ингредиенты. Домашняя еда, фудкорт или ресторан — наша AI‑модель выявляет возможные аллергены и компоненты, которые могут не подходить вашей диете.',
          highlights: ['AI‑распознавание ингредиентов', 'Подходит для любых блюд', 'Проверка совместимости с диетой'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Сканируйте меню ресторанов',
          subtitle: 'находите безопасные варианты до заказа',
          description: 'Наведите камеру на меню кафе или ресторана — SafeEat проанализирует позиции и покажет, что безопасно с учётом ваших ограничений. Идеально для питания вне дома без тревоги из‑за скрытых аллергенов или нежелательных ингредиентов.',
          highlights: ['Анализ меню', 'Поиск безопасных блюд', 'Выявление скрытых аллергенов'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Создайте личный список безопасной еды',
          subtitle: 'соберите свою «чистую» библиотеку питания',
          description: 'Сохраняйте продукты и блюда, которые вам подходят, формируйте личную библиотеку «чистого питания» и открывайте её в любой момент — в магазине или в ресторане. Ваша персональная база будет расти с каждым сканом.',
          highlights: ['Личная библиотека еды', 'Быстрый доступ где угодно', 'Умные рекомендации'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Нам доверяют тысячи',
      subtitle: 'Глобальное сообщество пользователей доверяет SafeEat.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat полностью изменил мой подход к покупкам! У меня целиакия, и сканирование продуктов занимает секунды. Больше не нужно разбирать мелкий шрифт на этикетках.'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: 'С несколькими пищевыми аллергиями поход в кафе всегда был стрессом. Теперь я просто сканирую меню и сразу понимаю, что мне безопасно. Это реально меняет всё!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'Я использую SafeEat каждый день, чтобы контролировать диабет. Сканер пищевой ценности помогает мгновенно отслеживать углеводы и сахар. Очень рекомендую!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: 'У моего сына сильная аллергия на орехи. SafeEat даёт спокойствие, когда я покупаю ему перекусы. Сканирование штрихкодов очень точное.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: 'У меня непереносимость лактозы, и я много путешествую. SafeEat работает на разных языках — я сканирую меню за границей и ем без переживаний!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'Из‑за сердечного заболевания мужа нам нужно следить за натрием. Мгновенный анализ SafeEat значительно упрощает планирование питания.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Сканер блюд — просто супер! Я сфотографировала блюдо в ресторане, и приложение нашло скрытые источники глютена. Это спасло меня от плохой реакции!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'Я строго соблюдаю халяль‑диету. SafeEat помогает проверять состав незнакомых упакованных продуктов. Незаменимое приложение!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'С тремя детьми и разными аллергиями покупки были кошмаром. Теперь я сканирую всё за секунды. Это приложение — настоящий спасатель!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'У меня болезнь Крона, и мне нужно избегать определённых триггеров. Персональный профиль SafeEat замечает ингредиенты, которые я часто пропускаю.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'Функция сканирования меню идеально подходит при моей аллергии на морепродукты. Теперь я увереннее заказываю в новых ресторанах!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'Я повар и у меня есть чувствительность к некоторым продуктам — использую SafeEat, чтобы проверять ингредиенты перед готовкой. Точность распознавания впечатляет.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: 'Контролировать гестационный диабет было трудно, пока я не нашла SafeEat. Сканирование этикеток делает отслеживание гораздо проще!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'У меня редкая непереносимость, которую большинство приложений не поддерживает. В SafeEat я добавила её в профиль — и теперь она всегда учитывается!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: 'Я веган и у меня аллергия на сою — найти безопасную еду было очень сложно! SafeEat находит и скрытые животные ингредиенты, и сою. Обожаю!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'База штрихкодов огромная! Даже импортные продукты распознаются. Отлично подходит для проверки незнакомой международной еды.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: 'У моей пожилой мамы несколько заболеваний. Я настроил её профиль, и теперь она снова может ходить за покупками самостоятельно. Спасибо, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Простые и прозрачные цены',
      subtitle: 'Выберите подходящий план',
      free: 'Бесплатно',
      freeDesc: '5 бесплатных сканов для знакомства',
      freeFeature1: '5 AI‑сканов',
      freeFeature2: 'Базовый анализ',
      freeFeature3: 'Ограниченная история',
      weekly: 'Еженедельно',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Для регулярного использования',
      weeklyFeature1: 'Сканирования без ограничений',
      weeklyFeature2: 'Расширенный анализ',
      weeklyFeature3: 'Полная история',
      weeklyFeature4: 'Приоритетная поддержка',
      annual: 'Ежегодно',
      annualPrice: '$29.99',
      annualDesc: 'Лучшая выгода — экономия 60%',
      annualBadge: 'Самый популярный',
      annualFeature1: 'Все функции недельного плана',
      annualFeature2: 'Экономия $125 в год',
      annualFeature3: 'Премиум‑поддержка',
      annualFeature4: 'Ранний доступ к новым функциям',
      button: 'Начать'
    },
    faq: {
      title: 'Частые вопросы',
      items: [
        {
          q: 'Что можно сканировать в SafeEat?',
          a: 'SafeEat может анализировать: список ингредиентов и пищевую ценность (по фото), штрихкоды, упакованные и неупакованные продукты, блюда (по фото) и меню ресторанов.'
        },
        {
          q: 'Как работает распознавание блюд?',
          a: 'AI распознаёт блюдо и оценивает возможные ингредиенты. Лучше всего работает при хорошем освещении и правильном ракурсе; скрытые ингредиенты или перекрёстный контакт (контаминация) могут быть не обнаружены.'
        },
        {
          q: 'Можно ли сканировать меню ресторанов?',
          a: 'Да. Приложение подсвечивает возможные аллергены и конфликты с вашей диетой. Меню может быть неполным; всегда уточняйте у персонала ингредиенты, масла, общие фритюрницы, соусы и риск перекрёстного контакта.'
        },
        {
          q: 'Какие состояния поддерживает SafeEat?',
          a: 'Пищевые аллергии, непереносимость (например, лактозы и глютена) и диетические требования (низкое содержание натрия, контроль углеводов, исключение отдельных ингредиентов).'
        },
        {
          q: 'Работает ли SafeEat офлайн?',
          a: 'Обычно для сканирования и AI‑анализа требуется интернет. Сохранённые результаты и списки могут быть доступны офлайн — в зависимости от приложения и устройства.'
        },
        {
          q: 'Насколько точен анализ SafeEat?',
          a: 'Точность зависит от качества фото, текста на этикетке, языка/формата и полноты меню. Это информационный сервис — при высоком риске всегда перепроверяйте.'
        },
        {
          q: 'Что делать, если SafeEat ошибся?',
          a: 'Не полагайтесь на сомнительный результат; сделайте более чёткое фото и сканируйте снова; подтвердите информацию по этикетке/сайту/у персонала; при высоком риске выбирайте самый безопасный вариант.'
        },
        {
          q: 'Можно ли сообщить об ошибочном анализе?',
          a: 'Да: напишите на support@safeeat.app и приложите скриншоты, фото этикетки/меню/блюда, страну/бренд/продукт и что именно кажется неверным.'
        },
        {
          q: 'Почему распознавание блюд может быть менее точным?',
          a: 'Похожие блюда, скрытые ингредиенты, густые соусы/украшения, плохое освещение/смазанные фото, а также сильно кастомизированные или региональные блюда.'
        },
        {
          q: 'SafeEat — это медицинское устройство?',
          a: 'Нет. Приложение предназначено только для информации. За медицинскими рекомендациями обращайтесь к специалисту.'
        },
        {
          q: 'Какова ответственность SafeEat?',
          a: 'Информация предоставляется «как есть»; полнота не гарантируется. Ответственность за выбор несёте вы; возможны неуказанные ингредиенты и перекрёстный контакт.'
        },
        {
          q: 'Можно ли при тяжёлой аллергии полагаться только на SafeEat?',
          a: 'Нет. Читайте этикетки, уточняйте у персонала, следуйте своему плану действий и носите назначенные лекарства; если есть сомнения — избегайте продукта/блюда.'
        },
        {
          q: 'Как вы защищаете мои данные о здоровье?',
          a: 'Мы собираем минимально необходимую информацию и защищаем её мерами безопасности и ограниченным доступом. По вопросам конфиденциальности: support@safeeat.app. Подробнее — в Политике конфиденциальности.'
        },
        {
          q: 'Можно ли использовать SafeEat для детей?',
          a: 'Да, под присмотром взрослых. При тяжёлых аллергиях и сложных ограничениях будьте особенно осторожны и перепроверяйте информацию из надёжных источников.'
        },
        {
          q: 'Что делать, если возникла реакция?',
          a: 'Немедленно обратитесь за медицинской помощью, следуйте своему экстренному плану, при тяжёлых симптомах звоните 112, а затем сообщите об этом поставщику/ресторану/производителю.'
        }
      ]
    },
    cta: {
      title: 'Готовы изменить свой подход к питанию?',
      subtitle: 'Присоединяйтесь к тысячам людей, которые используют SafeEat каждый день',
      description: 'Скачайте сейчас и получите 5 бесплатных AI‑сканов',
      button: 'Скачать в App Store',
      feature1: 'Без банковской карты',
      feature2: '100% конфиденциально и безопасно',
      feature3: 'Мгновенный доступ'
    },
    newsletter: {
      title: 'Будьте в курсе',
      subtitle: 'Получайте обновления и советы по здоровью',
      placeholder: 'Введите ваш email',
      button: 'Подписаться',
      success: 'Спасибо за подписку!',
      error: 'Введите корректный адрес email'
    },
    seo: {
      title: 'Ваш персональный помощник по безопасности питания',
      text: 'SafeEat — это AI‑приложение‑сканер еды для людей с пищевыми аллергиями, непереносимостью и диетическими ограничениями. Сканируйте этикетки, пищевую ценность, штрихкоды, меню ресторанов и готовые блюда, чтобы мгновенно проверять безопасность по вашему профилю. Целиакия, непереносимость лактозы, аллергия на орехи или диета без глютена, веганская или с низким содержанием натрия — SafeEat помогает выбирать безопасные варианты.',
      keywords: [
        'приложение для пищевой аллергии',
        'сканер состава',
        'детектор аллергенов',
        'считыватель пищевой ценности',
        'сканер штрихкодов',
        'сканер меню',
        'проверка без глютена',
        'непереносимость лактозы приложение',
        'трекер диеты',
        'безопасность питания'
      ]
    },
    cookies: {
      bannerText: 'Мы используем cookies: необходимые для работы сайта и аналитические — только с вашего согласия.',
      decline: 'Отказаться',
      accept: 'Принять'
    },
    legal: {
      backHome: 'Вернуться на главную'
    },
    footer: {
      product: 'Продукт',
      features: 'Функции',
      howItWorks: 'Как это работает',
      testimonials: 'Отзывы',
      pricing: 'Цены',
      faq: 'FAQ',
      contacts: 'Контакты',
      legal: 'Юридическая информация',
      privacy: 'Политика конфиденциальности',
      terms: 'Условия использования',
      usePolicy: 'Руководство пользователя',
      disclaimer: 'Отказ от ответственности',
      company: 'FJCode OU',
      register: 'Регистрационный номер 16993109',
      address: 'Viru väljak 2, 10111 Tallinn, Эстония',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. Все права защищены.'
    }
  },
  ar: {
    meta: {
      home: {
        title: 'SafeEat | ماسح طعام بالذكاء الاصطناعي للحساسية وعدم التحمل',
        description: 'امسح الملصقات والباركود وقوائم الطعام والأطباق، وحدّد مسببات الحساسية ومخاطر النظام الغذائي وفق ملفك: الحساسية، وعدم التحمل، والأمراض المزمنة.'
      },
      privacy: {
        title: 'سياسة الخصوصية | SafeEat',
        description: 'تعرّف على كيفية جمع SafeEat لبياناتك واستخدامها وحمايتها.'
      },
      terms: {
        title: 'شروط الاستخدام | SafeEat',
        description: 'اطّلع على الشروط التي تنظّم استخدامك لتطبيق SafeEat.'
      },
      manual: {
        title: 'دليل المستخدم | SafeEat',
        description: 'تعلّم كيفية استخدام SafeEat بأمان لمسح الطعام.'
      },
      disclaimer: {
        title: 'إخلاء المسؤولية | SafeEat',
        description: 'اعرف حدود الاستخدام والتنبيهات المعلوماتية الخاصة بتطبيق SafeEat.'
      }
    },
    nav: {
      about: 'كيف يعمل',
      features: 'الميزات',
      testimonials: 'آراء المستخدمين',
      pricing: 'الأسعار',
      faq: 'الأسئلة الشائعة',
      contacts: 'تواصل معنا',
      getStarted: 'ابدأ الآن'
    },
    about: {
      description: 'ماسح طعام يعمل بالذكاء الاصطناعي يقرأ الملصقات والباركود والأطباق وقوائم الطعام ويُبرز مسببات الحساسية والمكوّنات والمعلومات الغذائية وفق ملفك الصحي.'
    },
    hero: {
      badge: 'أمان الطعام بالذكاء الاصطناعي',
      title: 'اعرف فورًا',
      title2: 'مدى أمان الطعام',
      titleHighlight: 'بالنسبة لك',
      subtitle: 'بناءً على',
      description: 'امسح الطعام والباركود والمكوّنات وقوائم الطعام وتحقق فورًا مما إذا كان مناسبًا لجسمك',
      typing: ['الأمراض المزمنة', 'الحساسية', 'عدم تحمّل الطعام'],
      stat1: '500+',
      stat1Label: 'أمراض مزمنة',
      stat2: '250+',
      stat2Label: 'حساسية',
      stat3: '100+',
      stat3Label: 'عدم تحمّل الطعام',
      downloadButton: 'حمّل من App Store'
    },
    howItWorks: {
      badge: 'كيف يعمل',
      mainTitle: 'كيف يعمل SafeEat',
      subtitle: 'عملية المسح بسيطة وسريعة — نتائج مخصّصة خلال خطوات قليلة.',
      setupBadge: 'إعداد لمرة واحدة',
      setupTitle: 'أنشئ ملفك الصحي',
      setupDesc: 'أضِف معلوماتك الصحية أثناء الإعداد لتخصيص SafeEat. تقوم بذلك مرة واحدة، ثم تحصل على تحليل مناسب لك في كل مرة.',
      cards: {
        diseases: {
          title: 'أمراض مزمنة',
          desc: 'أضِف حالتك المزمنة من قاعدة بيانات تضم 500+ حالة. من السكري إلى مشكلات القلب — نحن نغطيها.',
          statLabel: 'حالة'
        },
        allergies: {
          title: 'حساسية',
          desc: 'اختر حساسياتك من 250+ خيارًا. من المكسرات والمحار الشائعين إلى مسببات حساسية نادرة — لتكون أكثر اطمئنانًا.',
          statLabel: 'حساسية'
        },
        intolerances: {
          title: 'عدم تحمّل الطعام',
          desc: 'حدّد حالات عدم التحمل من 100+ خيار. اللاكتوز، الغلوتين، الفركتوز وغيرها — لتأكل براحة.',
          statLabel: 'عدم تحمّل'
        }
      },
      connectorText: 'ثم، عند كل عملية مسح:',
      stepLabel: 'الخطوة',
      step1Title: 'امسح الطعام',
      step1Desc: 'امسح أي طعام — منتجًا منفردًا، طبقًا جاهزًا، بطاقة القيم الغذائية، باركود، أو حتى قائمة مطعم. فقط وجّه الكاميرا واترك الباقي على SafeEat.',
      step2Title: 'تحليل بالذكاء الاصطناعي',
      step2Desc: 'يحلل التطبيق الصورة ويطابقها مع ملفك (الأمراض، الحساسية، عدم التحمل) لتقييم الملاءمة.',
      step3Title: 'احصل على تقرير',
      step3Desc: 'يقدم SafeEat تقريرًا مفصّلًا يوضح نتائج التحليل ومدى توافقها مع احتياجاتك الصحية والغذائية.',
      step4Title: 'اتخذ القرار',
      step4Desc: 'اعتمد على رؤى مخصّصة لاختيار الطعام الأنسب لك. كُل بثقة وأنت تعرف ما يناسب جسمك.',
      processTitle: 'شاهد كيف تعمل',
      processSubtitle: 'راقب SafeEat وهو يحلل طعامك مباشرة'
    },
    features: {
      title: 'ميزات قوية',
      subtitle: 'كل ما تحتاجه لتناول طعام أكثر أمانًا',
      items: [
        {
          title: 'امسح أي طعام',
          subtitle: 'من الوجبات الخفيفة اليومية إلى الفواكه الغريبة',
          description: 'وجّه الكاميرا لأي طعام معبأ أو غير معبأ — من منتجات السوبرماركت اليومية إلى الفواكه الاستوائية — واحصل على فحص أمان فوري. يتعرّف SafeEat على المنتج ويحلل مكوّناته لمساعدتك على تجنّب العناصر الخطرة أو الضارة.',
          highlights: ['طعام معبأ وغير معبأ', 'فحص أمان فوري', 'تحليل المكوّنات'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'امسح الملصقات الغذائية والباركود',
          subtitle: 'افهم الملصقات المعقدة في ثوانٍ',
          description: 'لا داعي لمعاناة قراءة الخط الصغير. يستطيع SafeEat مسح جدول القيم الغذائية وقائمة المكوّنات والباركود خلال ثوانٍ، ويُبرز فورًا مسببات الحساسية والمواد المضافة أو العناصر المحتمل أن تكون ضارة لتعرف بالضبط ما تتناوله.',
          highlights: ['مسح القيم الغذائية', 'اكتشاف مسببات الحساسية', 'تنبيهات للمواد الضارة'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'امسح الأطباق — تعرّف على المكوّنات',
          subtitle: 'يكفي تصوير طبقك',
          description: 'التقط صورة لوجبتك وسيفكك SafeEat الطبق إلى مكوّنات محتملة. سواء كان طعامًا منزليًا أو من مطعم، يمكن للذكاء الاصطناعي اكتشاف مسببات حساسية محتملة أو مكوّنات لا تناسب نظامك.',
          highlights: ['تعرّف على المكوّنات بالذكاء الاصطناعي', 'يعمل مع أي طبق', 'فحص توافق النظام الغذائي'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'امسح قوائم المطاعم',
          subtitle: 'اعثر على خيارات آمنة قبل الطلب',
          description: 'وجّه الكاميرا إلى قائمة مطعم أو مقهى وسيحلل SafeEat كل بند ويخبرك بما يتوافق مع احتياجاتك. تناول الطعام خارجًا بثقة دون القلق من مسببات حساسية مخفية أو مكوّنات غير مرغوبة.',
          highlights: ['تحليل القائمة', 'اقتراحات لخيارات آمنة', 'كشف مسببات حساسية مخفية'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'أنشئ قائمة طعامك الآمن',
          subtitle: 'ابنِ مكتبة طعام «نظيف» خاصة بك',
          description: 'احفظ الأطعمة المناسبة لك وأنشئ مكتبة طعام «نظيف» يمكنك الرجوع إليها أثناء التسوق أو الخروج. تنمو قاعدة بياناتك الشخصية مع كل مسح وتساعدك على اتخاذ قرارات أسرع.',
          highlights: ['قاعدة طعام شخصية', 'تحقق سريع في أي وقت', 'اقتراحات ذكية'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'موثوق من قبل المستخدمين',
      subtitle: 'موثوق من مجتمع مستخدمين حول العالم.',
      items: [
        {
          name: 'Sarah M.',
          location: 'نيويورك، الولايات المتحدة',
          rating: 5,
          content: 'غيّر SafeEat طريقة تسوّقي تمامًا! لدي داء السيلياك، ومسح المنتجات يستغرق ثوانٍ. وداعًا لقراءة الملصقات الصغيرة في المتجر.'
        },
        {
          name: 'Marcus L.',
          location: 'لندن، المملكة المتحدة',
          rating: 5,
          content: 'بالنسبة لي كشخص لديه عدة حساسية طعام، كان الأكل خارج المنزل دائمًا مرهقًا. الآن أمسح القائمة فقط وأعرف فورًا ما هو آمن. إنه تغيير جذري!'
        },
        {
          name: 'Yuki T.',
          location: 'طوكيو، اليابان',
          rating: 5,
          content: 'أستخدم SafeEat يوميًا لإدارة السكري. ماسح الملصق الغذائي يساعدني على تتبّع الكربوهيدرات والسكر فورًا. أنصح به بشدة!'
        },
        {
          name: 'Anna K.',
          location: 'برلين، ألمانيا',
          rating: 5,
          content: 'ابني لديه حساسية شديدة من المكسرات. يمنحني SafeEat راحة بال عند شراء الوجبات الخفيفة له. ماسح الباركود دقيق جدًا.'
        },
        {
          name: 'Carlos R.',
          location: 'مدريد، إسبانيا',
          rating: 5,
          content: 'لدي عدم تحمّل اللاكتوز وأسافر كثيرًا. يعمل SafeEat بأي لغة — في الخارج أمسح قائمة المطعم وأتناول الطعام بثقة!'
        },
        {
          name: 'Priya S.',
          location: 'مومباي، الهند',
          rating: 5,
          content: 'عند التعامل مع مشاكل قلب زوجي، علينا الانتباه للصوديوم. التحليل الفوري في SafeEat جعل تخطيط الوجبات أسهل بكثير.'
        },
        {
          name: 'Emma W.',
          location: 'سيدني، أستراليا',
          rating: 5,
          content: 'ميزة مسح الأطباق مذهلة! صوّرت وجبة في مطعم فاكتشف مكونات تحتوي على الغلوتين بشكل مخفي. أنقذني من تفاعل سيئ!'
        },
        {
          name: 'Ahmed H.',
          location: 'دبي، الإمارات',
          rating: 5,
          content: 'أتّبع نظامًا غذائيًا حلالًا صارمًا. يساعدني SafeEat على التحقق من مكونات الأطعمة المعبأة التي لا أعرفها. تطبيق لا غنى عنه!'
        },
        {
          name: 'Lisa M.',
          location: 'تورونتو، كندا',
          rating: 5,
          content: 'مع ثلاثة أطفال لكل منهم حساسية مختلفة، كانت التسوّقات كابوسًا. الآن أمسح كل شيء خلال ثوانٍ. هذا التطبيق أنقذني فعلاً!'
        },
        {
          name: 'Johan V.',
          location: 'ستوكهولم، السويد',
          rating: 5,
          content: 'لدي داء كرون وأحتاج لتجنب محفزات معينة. يلتقط ملف SafeEat المخصص مكونات قد أغفل عنها.'
        },
        {
          name: 'Maria G.',
          location: 'ساو باولو، البرازيل',
          rating: 5,
          content: 'ميزة مسح قوائم الطعام مثالية لحساسيتي من المأكولات البحرية. الآن أطلب في مطاعم جديدة بثقة أكبر!'
        },
        {
          name: 'David C.',
          location: 'باريس، فرنسا',
          rating: 5,
          content: 'بصفتي طاهيًا لديه حساسية/حساسية تجاه بعض الأطعمة، أستخدم SafeEat للتحقق من المكونات قبل الطهي. دقة التعرف بالذكاء الاصطناعي مذهلة.'
        },
        {
          name: 'Fatima A.',
          location: 'القاهرة، مصر',
          rating: 5,
          content: 'كان سكري الحمل مربكًا جدًا حتى وجدت SafeEat. مسح ملصقات الطعام يجعل المتابعة سهلة للغاية!'
        },
        {
          name: 'Michael O.',
          location: 'لاغوس، نيجيريا',
          rating: 5,
          content: 'لدي حالة عدم تحمّل نادرة لا تغطيها معظم التطبيقات. سمح لي SafeEat بإضافتها إلى الملف والآن يكتشفها كل مرة!'
        },
        {
          name: 'Sophie L.',
          location: 'أمستردام، هولندا',
          rating: 5,
          content: 'أنا نباتي ولدي حساسية من الصويا — كان العثور على طعام آمن صعبًا جدًا! يتعرف SafeEat على المكونات الحيوانية المخفية وكذلك الصويا. رائع!'
        },
        {
          name: 'Kenji N.',
          location: 'سيول، كوريا الجنوبية',
          rating: 5,
          content: 'قاعدة بيانات الباركود ضخمة! يتعرف حتى على المنتجات المستوردة. ممتاز لفحص الأطعمة الدولية غير المألوفة.'
        },
        {
          name: 'Elena P.',
          location: 'روما، إيطاليا',
          rating: 5,
          content: 'والدتي الكبيرة في السن لديها عدة حالات صحية. أنشأت لها ملفًا والآن تستطيع التسوق بمفردها مجددًا. شكرًا SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'أسعار بسيطة وواضحة',
      subtitle: 'اختر الخطة الأنسب لك',
      free: 'مجاني',
      freeDesc: '5 عمليات مسح مجانية للتجربة',
      freeFeature1: '5 عمليات مسح بالذكاء الاصطناعي',
      freeFeature2: 'تحليل أساسي',
      freeFeature3: 'سجل محدود',
      weekly: 'أسبوعيًا',
      weeklyDesc: 'مثالي للمستخدمين المتكررين',
      weeklyPrice: '$2.99',
      weeklyFeature1: 'مسح غير محدود',
      weeklyFeature2: 'تحليل متقدم',
      weeklyFeature3: 'سجل كامل',
      weeklyFeature4: 'دعم أولوية',
      annual: 'سنويًا',
      annualBadge: 'الأكثر شيوعًا',
      annualDesc: 'أفضل قيمة — وفّر 60%',
      annualPrice: '$29.99',
      annualFeature1: 'كل مزايا الخطة الأسبوعية',
      annualFeature2: 'وفّر $125 سنويًا',
      annualFeature3: 'دعم مميز',
      annualFeature4: 'وصول مبكر للميزات الجديدة',
      button: 'ابدأ الآن'
    },
    faq: {
      title: 'الأسئلة الشائعة',
      items: [
        {
          q: 'ما الذي يمكنني مسحه عبر SafeEat؟',
          a: 'يمكن لـ SafeEat تحليل: قوائم المكونات وجداول القيم الغذائية (صور)، الباركود، الأطعمة المعبأة وغير المعبأة، الأطباق (صور)، وقوائم المطاعم.'
        },
        {
          q: 'كيف تعمل ميزة التعرف على الأطباق؟',
          a: 'يتعرف الذكاء الاصطناعي على الطبق ويقدّر المكونات المحتملة. تعمل بشكل أفضل مع إضاءة جيدة وزاوية واضحة؛ قد لا يتم اكتشاف المكونات المخفية أو التلوث التبادلي.'
        },
        {
          q: 'هل يمكنني مسح قوائم المطاعم؟',
          a: 'نعم. ستظهر مسببات حساسية محتملة أو تعارضات مع نظامك. قد تكون القوائم غير مكتملة؛ اسأل الموظفين عن المكونات والزيوت واستخدام القلايات المشتركة والصلصات واحتمال التلوث التبادلي.'
        },
        {
          q: 'ما الحالات الصحية التي يدعمها SafeEat؟',
          a: 'حساسية الطعام، حالات عدم التحمل (مثل اللاكتوز والغلوتين)، والاحتياجات الغذائية (تقليل الصوديوم، متابعة الكربوهيدرات، تجنب مكونات معينة).'
        },
        {
          q: 'هل يعمل SafeEat دون اتصال؟',
          a: 'عادةً تحتاج عمليات المسح والتحليل بالذكاء الاصطناعي إلى اتصال بالإنترنت. قد تكون النتائج والقوائم المحفوظة متاحة دون اتصال بحسب التطبيق والجهاز.'
        },
        {
          q: 'ما مدى دقة تحليل SafeEat؟',
          a: 'تعتمد الدقة على جودة الصورة، وضوح النص، اللغة/التنسيق، ومدى اكتمال معلومات القائمة. التطبيق للمعلومات فقط — عند وجود مخاطر عالية، تحقّق دائمًا بشكل إضافي.'
        },
        {
          q: 'ماذا لو أخطأ SafeEat؟',
          a: 'لا تعتمد على نتيجة غير مؤكدة؛ أعد المسح بصورة أوضح؛ تحقق من الملصق/الموقع أو اسأل الموظفين؛ وعند المخاطر العالية اختر الخيار الأكثر أمانًا.'
        },
        {
          q: 'هل يمكنني الإبلاغ عن نتيجة تحليل غير صحيحة؟',
          a: 'نعم: أرسل بريدًا إلى support@safeeat.app مع لقطة شاشة وصورة الملصق/القائمة/الطبق، وبلد المنتج/العلامة/الاسم، وما الذي تعتقد أنه غير صحيح.'
        },
        {
          q: 'لماذا قد تكون دقة التعرف على الأطباق أقل؟',
          a: 'تشابه الأطباق في الشكل، المكونات المخفية، الصلصات الثقيلة/الزينة، الإضاءة الضعيفة/الضبابية، والأطباق المعدلة جدًا أو الإقليمية قد تقلل الدقة.'
        },
        {
          q: 'هل SafeEat جهاز طبي؟',
          a: 'لا. هو لأغراض معلوماتية فقط. للحصول على نصيحة طبية، استشر مختصًا.'
        },
        {
          q: 'ما حدود مسؤولية SafeEat؟',
          a: 'تُقدَّم المعلومات «كما هي» ولا نضمن اكتمالها أو خلوها من الأخطاء. أنت مسؤول عن قراراتك؛ قد توجد مكونات غير مذكورة أو تلوث تبادلي.'
        },
        {
          q: 'هل يمكن الاعتماد على SafeEat فقط عند الحساسية الشديدة؟',
          a: 'لا. اقرأ الملصقات، واسأل الموظفين، واتبع خطة الطوارئ الخاصة بك واحتفظ بالأدوية الموصوفة؛ وعند الشك تجنب المنتج/الطبق.'
        },
        {
          q: 'كيف تحمون بياناتي الصحية؟',
          a: 'نجمع الحد الأدنى من البيانات اللازمة ونحميها بإجراءات أمنية وصلاحيات وصول محدودة. للاستفسارات تواصل عبر support@safeeat.app. راجع سياسة الخصوصية.'
        },
        {
          q: 'هل يمكن للأطفال استخدام SafeEat؟',
          a: 'نعم، تحت إشراف ولي الأمر. عند الحساسية الشديدة أو الأنظمة المعقدة، كن أكثر حذرًا وتحقق من مصادر موثوقة.'
        },
        {
          q: 'ماذا أفعل إذا حدثت لديّ ردة فعل تحسسية؟',
          a: 'اطلب المساعدة الطبية فورًا واتبع خطة الطوارئ؛ وعند الأعراض الشديدة اتصل بالإسعاف. ثم أبلغ طبيبك/المطعم/المصنّع.'
        }
      ]
    },
    cta: {
      title: 'جاهز لتغيير علاقتك بالطعام؟',
      subtitle: 'انضم إلى آلاف المستخدمين الذين يستخدمون SafeEat يوميًا',
      description: 'حمّل الآن واحصل على 5 عمليات مسح بالذكاء الاصطناعي مجانًا',
      button: 'حمّل من App Store',
      feature1: 'لا حاجة لبطاقة ائتمان',
      feature2: 'خصوصية وأمان 100%',
      feature3: 'ابدأ فورًا'
    },
    newsletter: {
      title: 'ابقَ على اطلاع',
      subtitle: 'احصل على آخر الأخبار ونصائح صحية',
      placeholder: 'أدخل بريدك الإلكتروني',
      button: 'اشترك',
      error: 'يرجى إدخال بريد إلكتروني صالح',
      success: 'شكرًا لاشتراكك!'
    },
    seo: {
      title: 'مساعدك الشخصي لأمان الطعام',
      text: 'SafeEat هو تطبيق مسح طعام بالذكاء الاصطناعي للأشخاص الذين لديهم حساسية أو عدم تحمّل أو قيود غذائية. امسح فورًا الملصقات، جداول القيم الغذائية، الباركود، قوائم المطاعم، والأطباق الجاهزة للتحقق من توافقها مع ملفك الشخصي. سواء كنت تعاني من السيلياك، عدم تحمّل اللاكتوز، حساسية المكسرات، أو تتبع نظامًا خاليًا من الغلوتين/نباتيًا/منخفض الصوديوم، يساعدك SafeEat على اتخاذ خيارات أكثر أمانًا.',
      keywords: [
        'تطبيق حساسية الطعام',
        'ماسح المكوّنات',
        'كشف مسببات الحساسية',
        'مسح الملصق الغذائي',
        'ماسح الباركود',
        'مسح قوائم المطاعم',
        'فحص خالٍ من الغلوتين',
        'تطبيق عدم تحمّل اللاكتوز',
        'تتبع النظام الغذائي',
        'سلامة الغذاء'
      ]
    },
    cookies: {
      bannerText: 'نستخدم ملفات تعريف الارتباط: ملفات ضرورية لتشغيل الموقع وملفات تحليلية فقط بموافقتك.',
      accept: 'موافقة',
      decline: 'رفض'
    },
    legal: {
      backHome: 'العودة إلى الصفحة الرئيسية'
    },
    footer: {
      product: 'المنتج',
      features: 'الميزات',
      howItWorks: 'كيف يعمل',
      testimonials: 'آراء المستخدمين',
      pricing: 'الأسعار',
      faq: 'الأسئلة الشائعة',
      contacts: 'تواصل معنا',
      legal: 'قانوني',
      privacy: 'سياسة الخصوصية',
      terms: 'شروط الخدمة',
      disclaimer: 'إخلاء المسؤولية',
      usePolicy: 'دليل المستخدم',
      company: 'FJCode OU',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      register: 'رقم التسجيل 16993109',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. جميع الحقوق محفوظة.'
    }
  },
  zh: {
    nav: {
      about: '工作原理',
      features: '功能',
      testimonials: '评价',
      pricing: '价格',
      faq: '常见问题',
      contacts: '联系',
      getStarted: '开始'
    }
  },
  hr: {
    nav: {
      about: 'Kako funkcionira',
      features: 'Značajke',
      testimonials: 'Svjedočanstva',
      pricing: 'Cijene',
      faq: 'Pitanja',
      contacts: 'Kontakt',
      getStarted: 'Počni'
    }
  },
  cs: {
    meta: {
      home: {
        title: 'SafeEat | AI skener potravin pro alergie a intolerance',
        description: 'Skenujte etikety, čárové kódy, jídelní lístky a pokrmy a zvýrazněte alergeny i dietní rizika podle svého profilu: alergie, intolerance a chronická onemocnění.'
      },
      privacy: {
        title: 'Zásady ochrany soukromí | SafeEat',
        description: 'Přečtěte si, jak SafeEat shromažďuje, používá a chrání vaše data.'
      },
      terms: {
        title: 'Podmínky použití | SafeEat',
        description: 'Seznamte se s podmínkami, které upravují používání SafeEatu.'
      },
      manual: {
        title: 'Uživatelská příručka | SafeEat',
        description: 'Zjistěte, jak SafeEat bezpečně používat ke skenování potravin.'
      },
      disclaimer: {
        title: 'Zřeknutí se odpovědnosti | SafeEat',
        description: 'Porozumějte informačním doporučením a omezením aplikace SafeEat.'
      }
    },
    nav: {
      about: 'Jak to funguje',
      features: 'Funkce',
      testimonials: 'Reference',
      pricing: 'Ceník',
      faq: 'FAQ',
      contacts: 'Kontakt',
      getStarted: 'Začít'
    },
    about: {
      description: 'AI skener potravin, který čte etikety, čárové kódy, pokrmy a jídelní lístky a podle vašeho profilu zvýrazní alergeny, složení i výživové informace.'
    },
    hero: {
      badge: 'Bezpečnost jídla s AI',
      title: 'Okamžitě zjistěte',
      title2: 'bezpečnost jídla',
      titleHighlight: 'pro vás',
      subtitle: 'Na základě vašich',
      description: 'Skenujte potraviny, čárové kódy, složení i jídelní lístky a hned ověřte, zda jsou pro vaše tělo vhodné.',
      downloadButton: 'Stáhnout na App Store',
      stat1: '500+',
      stat1Label: 'Chronická onemocnění',
      stat2: '250+',
      stat2Label: 'Alergie',
      stat3: '100+',
      stat3Label: 'Potravinové intolerance',
      typing: ['chronická onemocnění', 'alergie', 'potravinové intolerance']
    },
    howItWorks: {
      badge: 'Jak to funguje',
      mainTitle: 'Jak SafeEat funguje',
      subtitle: 'Náš proces skenování je jednoduchý a rychlý — personalizované výsledky získáte v několika krocích.',
      setupBadge: 'Jednorázové nastavení',
      setupTitle: 'Vytvořte si zdravotní profil',
      setupDesc: 'V úvodním průvodci přidejte informace o svém zdraví a přizpůsobte si SafeEat. Nastavení provedete jednou a analýza bude vždy šitá na míru vám.',
      cards: {
        diseases: {
          title: 'Chronická onemocnění',
          desc: 'Přidejte svá chronická onemocnění z naší databáze s 500+ položkami. Od diabetu po srdeční obtíže — máme pokryto.',
          statLabel: 'diagnóz'
        },
        allergies: {
          title: 'Alergie',
          desc: 'Vyberte si své alergie z 250+ možností. Od běžných ořechů a mořských plodů až po vzácné alergeny — mějte jistotu.',
          statLabel: 'alergií'
        },
        intolerances: {
          title: 'Potravinové intolerance',
          desc: 'Zadejte své intolerance z 100+ možností. Laktóza, lepek, fruktóza a další — jezte pohodlněji.',
          statLabel: 'intolerancí'
        }
      },
      connectorText: 'A pak při každém skenování:',
      stepLabel: 'Krok',
      processTitle: 'Podívejte se, jak to funguje',
      processSubtitle: 'Sledujte, jak SafeEat analyzuje vaši potravinu v reálném čase',
      step1Title: 'Skenujte jídlo',
      step1Desc: 'Skenujte libovolné jídlo — jednotlivé produkty, hotové pokrmy, výživové tabulky, čárové kódy i jídelní lístky. Stačí namířit kameru a SafeEat se postará o zbytek.',
      step2Title: 'AI analýza',
      step2Desc: 'Aplikace analyzuje obrázek a porovná jej s vaším profilem (onemocnění, alergie, intolerance), aby vyhodnotila vhodnost.',
      step3Title: 'Získejte report',
      step3Desc: 'SafeEat připraví detailní report s výsledky a ukáže, jak skenovaná potravina odpovídá vašim potřebám.',
      step4Title: 'Rozhodněte se',
      step4Desc: 'Díky personalizovaným informacím si vyberete jídlo, které vám nejlépe vyhovuje. Jezte s jistotou, že víte, co vašemu tělu sedí.'
    },
    features: {
      title: 'Výkonné funkce',
      subtitle: 'Vše, co potřebujete pro bezpečnější stravování',
      items: [
        {
          title: 'Skenujte jakýkoli produkt',
          subtitle: 'od běžných svačin po exotické ovoce',
          description: 'Namiřte kameru na jakoukoli balenou i nebalenou potravinu — od každodenních nákupů po exotické ovoce — a získáte okamžitou kontrolu bezpečnosti. SafeEat rozpozná produkt a analyzuje jeho složení, aby vám pomohl vyhnout se rizikovým nebo nežádoucím ingrediencím.',
          highlights: ['Balené i nebalené potraviny', 'Okamžitá kontrola', 'Analýza složení'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Skenujte výživové údaje a čárové kódy',
          subtitle: 'okamžitě rozluštěte matoucí etikety',
          description: 'Už žádné luštění drobného písma. SafeEat během pár sekund naskenuje výživové tabulky, seznamy složek i čárové kódy a okamžitě zvýrazní alergeny, aditiva nebo potenciálně rizikové látky, abyste přesně věděli, co jíte.',
          highlights: ['Sken výživové tabulky', 'Detekce alergenů', 'Upozornění na rizika'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Skenujte pokrmy – rozpoznejte ingredience',
          subtitle: 'z fotografie vašeho jídla',
          description: 'Vyfoťte svůj pokrm a SafeEat jej rozloží na pravděpodobné ingredience. Ať jde o domácí jídlo, jídlo z bistra nebo restaurace, AI může odhalit možné alergeny či složky, které nejsou v souladu s vaší dietou.',
          highlights: ['AI rozpoznání ingrediencí', 'Funguje na jakémkoli pokrmu', 'Kontrola vhodnosti'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Skenujte jídelní lístky v restauraci',
          subtitle: 'najděte bezpečné jídlo ještě před objednávkou',
          description: 'Namiřte kameru na jídelní lístek v kavárně či restauraci a SafeEat analyzuje jednotlivé položky a řekne vám, které jsou pro vás bezpečné. Skvělé pro stravování venku bez obav ze skrytých alergenů nebo nežádoucích ingrediencí.',
          highlights: ['Analýza jídelního lístku', 'Doporučení bezpečných jídel', 'Detekce skrytých alergenů'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Vytvořte si osobní seznam bezpečných potravin',
          subtitle: 'vybudujte si knihovnu „čistého“ stravování',
          description: 'Ukládejte potraviny, které vám vyhovují, vytvořte si vlastní knihovnu „čistého“ stravování a mějte ji po ruce při nákupech i na cestách. Vaše osobní databáze roste s každým skenem a pomáhá dělat rychlejší rozhodnutí.',
          highlights: ['Osobní databáze potravin', 'Rychlá kontrola kdykoli', 'Chytrá doporučení'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Uživatelé nám věří',
      subtitle: 'Důvěřuje nám komunita uživatelů po celém světě.',
      items: [
        { name: 'Sarah M.', location: 'New York, USA', rating: 5, content: 'SafeEat úplně změnil způsob, jak nakupuji potraviny! Mám celiakii a skenování produktů trvá pár vteřin. Už žádné čtení titěrných etiket v obchodě.' },
        { name: 'Marcus L.', location: 'Londýn, Spojené království', rating: 5, content: 'Jako člověk s více potravinovými alergiemi pro mě bylo stravování venku vždy stresující. Teď jen naskenuji jídelní lístek a přesně vím, co je bezpečné. Naprostá změna hry!' },
        { name: 'Yuki T.', location: 'Tokio, Japonsko', rating: 5, content: 'SafeEat používám každý den k řízení diabetu. Skener výživových údajů mi pomáhá okamžitě sledovat sacharidy i cukr. Vřele doporučuji!' },
        { name: 'Anna K.', location: 'Berlín, Německo', rating: 5, content: 'Můj syn má těžkou alergii na ořechy. SafeEat mi dává klid při nákupu svačin. Skener čárových kódů je neuvěřitelně přesný.' },
        { name: 'Carlos R.', location: 'Madrid, Španělsko', rating: 5, content: 'Mám intoleranci na laktózu a hodně cestuji. SafeEat funguje v jakémkoli jazyce — v zahraničí si jen naskenuji jídelní lístek a jím bez obav!' },
        { name: 'Priya S.', location: 'Mumbai, Indie', rating: 5, content: 'Při péči o manželovo srdeční onemocnění musíme hlídat příjem sodíku. Okamžitá analýza v SafeEatu mi výrazně usnadnila plánování jídel.' },
        { name: 'Emma W.', location: 'Sydney, Austrálie', rating: 5, content: 'Skener pokrmů je úžasný! Vyfotila jsem jídlo v restauraci a aplikace odhalila skryté ingredience s lepkem. Uchrá­nilo mě to před nepříjemnou reakcí!' },
        { name: 'Ahmed H.', location: 'Dubaj, SAE', rating: 5, content: 'Dodržuji přísnou halal stravu. SafeEat mi pomáhá ověřit složení balených potravin, které neznám. Nezbytná aplikace!' },
        { name: 'Lisa M.', location: 'Toronto, Kanada', rating: 5, content: 'Se třemi dětmi, z nichž každé má jiné alergie, byly nákupy noční můra. Teď vše naskenuji během pár vteřin. Tahle aplikace mi fakt zachraňuje život!' },
        { name: 'Johan V.', location: 'Stockholm, Švédsko', rating: 5, content: 'Mám Crohnovu chorobu a musím se vyhýbat určitým spouštěčům. Personalizovaný profil v SafeEatu zachytí i ingredience, které bych přehlédl/a.' },
        { name: 'Maria G.', location: 'São Paulo, Brazílie', rating: 5, content: 'Funkce skenování jídelních lístků je perfektní pro moji alergii na mořské plody. Teď si v nových restauracích objednávám s jistotou!' },
        { name: 'David C.', location: 'Paříž, Francie', rating: 5, content: 'Jako kuchař s potravinovou citlivostí používám SafeEat ke kontrole ingrediencí před vařením. AI rozpoznání je překvapivě přesné.' },
        { name: 'Fatima A.', location: 'Káhira, Egypt', rating: 5, content: 'Těhotenská cukrovka byla hodně náročná, dokud jsem nenašla SafeEat. Skenování etiket dělá sledování opravdu jednoduché!' },
        { name: 'Michael O.', location: 'Lagos, Nigérie', rating: 5, content: 'Mám vzácnou potravinovou intoleranci, kterou většina aplikací nepokrývá. SafeEat mi ji dovolil přidat do profilu a teď ji zachytí pokaždé!' },
        { name: 'Sophie L.', location: 'Amsterdam, Nizozemsko', rating: 5, content: 'Jsem vegan a mám alergii na sóju — najít bezpečné jídlo bylo hrozně těžké! SafeEat rozpozná skryté živočišné složky i sóju. Miluju to!' },
        { name: 'Kenji N.', location: 'Soul, Jižní Korea', rating: 5, content: 'Databáze čárových kódů je obrovská! Rozpozná i dovážené produkty. Skvělé pro kontrolu neznámých zahraničních potravin.' },
        { name: 'Elena P.', location: 'Řím, Itálie', rating: 5, content: 'Moje starší maminka má několik zdravotních omezení. Nastavil/a jsem jí profil a teď může zase nakupovat samostatně. Děkuji, SafeEat!' }
      ]
    },
    pricing: {
      title: 'Jednoduchý a transparentní ceník',
      subtitle: 'Vyberte si plán, který vám sedí',
      free: 'Zdarma',
      freeDesc: '5 skenů zdarma na vyzkoušení',
      freeFeature1: '5 AI skenů',
      freeFeature2: 'Základní analýza',
      freeFeature3: 'Omezená historie',
      weekly: 'Týdně',
      weeklyDesc: 'Ideální pro pravidelné používání',
      weeklyPrice: '$2.99',
      weeklyFeature1: 'Neomezené skeny',
      weeklyFeature2: 'Pokročilá analýza',
      weeklyFeature3: 'Kompletní historie',
      weeklyFeature4: 'Prioritní podpora',
      annual: 'Ročně',
      annualBadge: 'Nejoblíbenější',
      annualDesc: 'Nejlepší hodnota – ušetřete 60 %',
      annualPrice: '$29.99',
      annualFeature1: 'Vše z týdenního plánu',
      annualFeature2: 'Ušetříte $125 ročně',
      annualFeature3: 'Prémiová podpora',
      annualFeature4: 'Přednostní přístup k novinkám',
      button: 'Začít'
    },
    faq: {
      title: 'Časté dotazy',
      items: [
        { q: 'Co mohu v SafeEatu skenovat?', a: 'SafeEat umí analyzovat: seznamy složek a výživové tabulky (fotografie), čárové kódy, balené i nebalené potraviny, pokrmy (fotografie) a jídelní lístky v restauracích.' },
        { q: 'Jak funguje rozpoznávání pokrmů?', a: 'AI rozpozná pokrm a odhadne pravděpodobné ingredience. Nejlépe funguje při dobrém světle a správném úhlu; skryté složky nebo křížová kontaminace mohou zůstat neodhalené.' },
        { q: 'Mohu skenovat jídelní lístky v restauracích?', a: 'Ano. Aplikace označí možné alergeny a konflikty s dietou. Jídelní lístky mohou být neúplné; ověřte si u personálu složení, oleje, společné fritézy, omáčky i možnost křížové kontaminace.' },
        { q: 'Jaké zdravotní stavy SafeEat podporuje?', a: 'Potravinové alergie, intolerance (např. laktóza, lepek) a dietní potřeby (nízký sodík, kontrola sacharidů, vyhýbání se konkrétním složkám).' },
        { q: 'Funguje SafeEat offline?', a: 'Obvykle je pro skeny a AI analýzu potřeba internet. Uložené výsledky a seznamy mohou být dostupné offline podle aplikace a zařízení.' },
        { q: 'Jak přesná je analýza SafeEatu?', a: 'Záleží na kvalitě fotografie, čitelnosti textu na etiketě, jazyku/formatu a úplnosti jídelního lístku. Je to informační nástroj — při vyšším riziku vždy ověřujte.' },
        { q: 'Co když SafeEat udělá chybu?', a: 'Nespoléhejte se na pochybný výsledek; pořiďte lepší sken; ověřte informace na etiketě/webu nebo u personálu; při vysokém riziku zvolte bezpečnější variantu.' },
        { q: 'Mohu nahlásit nesprávné výsledky analýzy?', a: 'Ano: napište na support@safeeat.app a přiložte screenshot, fotku etikety/jídelního lístku/pokrmu, zemi, značku a produkt a popište, co je podle vás špatně.' },
        { q: 'Proč může být rozpoznávání pokrmů méně přesné?', a: 'Podobně vypadající jídla, skryté ingredience, hutné omáčky/ozdoby, špatné světlo/rozmazání a velmi upravené či regionální pokrmy mohou snížit přesnost.' },
        { q: 'Je SafeEat zdravotnický prostředek?', a: 'Ne. Slouží pouze k informativním účelům. Pro zdravotní doporučení se obraťte na odborníka.' },
        { q: 'Jaká je odpovědnost SafeEatu?', a: 'Informace jsou poskytovány „tak, jak jsou“ a nemůžeme zaručit úplnost. Za svá rozhodnutí odpovídáte vy; neoznačené složky a křížová kontaminace jsou možné.' },
        { q: 'Mám se při těžkých alergiích spoléhat jen na SafeEat?', a: 'Ne. Čtěte etikety, ptejte se personálu, dodržujte svůj akční plán a noste předepsané léky; když si nejste jistí, položce se vyhněte.' },
        { q: 'Jak chráníte má zdravotní data?', a: 'Shromažďujeme minimum dat a chráníme je bezpečnostními opatřeními a omezeným přístupem. V případě dotazů pište na support@safeeat.app. Viz Zásady ochrany soukromí.' },
        { q: 'Mohu SafeEat používat pro své děti?', a: 'Ano, pod dohledem. U těžkých alergií a složitých diet buďte opatrnější a ověřujte informace z důvěryhodných zdrojů.' },
        { q: 'Co mám dělat, když mám reakci?', a: 'Okamžitě vyhledejte lékařskou pomoc, postupujte podle svého nouzového plánu, u vážných příznaků volejte záchrannou službu a poté situaci nahlaste lékaři/restauraci/výrobci.' }
      ]
    },
    cta: {
      title: 'Jste připraveni změnit své stravování?',
      subtitle: 'Přidejte se k tisícům uživatelů, kteří používají SafeEat každý den',
      description: 'Stáhněte si aplikaci a získejte 5 AI skenů zdarma',
      button: 'Stáhnout na App Store',
      feature1: 'Bez kreditní karty',
      feature2: '100% soukromé a bezpečné',
      feature3: 'Začněte hned'
    },
    newsletter: {
      title: 'Zůstaňte v obraze',
      subtitle: 'Získejte novinky a tipy pro bezpečnější stravování',
      placeholder: 'Zadejte svůj e‑mail',
      button: 'Přihlásit se',
      error: 'Zadejte platnou e‑mailovou adresu',
      success: 'Děkujeme za přihlášení!'
    },
    seo: {
      title: 'Váš osobní asistent pro bezpečnost potravin',
      text: 'SafeEat je AI aplikace pro skenování potravin určená lidem s alergiemi, intolerancemi a dietními omezeními. Okamžitě skenujte etikety, výživové tabulky, čárové kódy, jídelní lístky v restauracích i hotové pokrmy a ověřte, zda odpovídají vašemu profilu. Ať už máte celiakii, intoleranci na laktózu, alergii na ořechy nebo dodržujete bezlepkovou, veganskou či nízkosodíkovou dietu, SafeEat vám pomůže dělat bezpečnější rozhodnutí.',
      keywords: ['aplikace pro potravinové alergie', 'skener složení', 'detekce alergenů', 'skenování výživových údajů', 'skener čárových kódů', 'skenování jídelního lístku', 'kontrola bezlepkové diety', 'aplikace pro intoleranci na laktózu', 'sledování stravy', 'bezpečnost potravin']
    },
    cookies: {
      bannerText: 'Používáme cookies: nezbytné cookies pro fungování webu a analytické cookies pouze s vaším souhlasem.',
      accept: 'Přijmout',
      decline: 'Odmítnout'
    },
    legal: {
      backHome: 'Zpět na hlavní stránku'
    },
    footer: {
      product: 'Produkt',
      features: 'Funkce',
      howItWorks: 'Jak to funguje',
      testimonials: 'Reference',
      pricing: 'Ceník',
      faq: 'FAQ',
      contacts: 'Kontakt',
      legal: 'Právní informace',
      privacy: 'Zásady ochrany soukromí',
      terms: 'Podmínky služby',
      usePolicy: 'Uživatelská příručka',
      disclaimer: 'Zřeknutí se odpovědnosti',
      company: 'FJCode OU',
      register: 'Registrační číslo 16993109',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. Všechna práva vyhrazena.'
    }
  },
  da: {
    meta: {
      home: {
        title: 'SafeEat | AI-madscanner til allergier og intolerancer',
        description: 'Scan etiketter, stregkoder, menuer og retter for at markere allergener og kostrisici ud fra din profil: allergier, intolerancer og kroniske sygdomme.'
      },
      privacy: {
        title: 'Privatlivspolitik | SafeEat',
        description: 'Læs, hvordan SafeEat indsamler, bruger og beskytter dine data.'
      },
      terms: {
        title: 'Vilkår for brug | SafeEat',
        description: 'Gennemgå vilkårene, der gælder for din brug af SafeEat.'
      },
      manual: {
        title: 'Brugervejledning | SafeEat',
        description: 'Lær, hvordan du bruger SafeEat til at scanne mad sikkert.'
      },
      disclaimer: {
        title: 'Ansvarsfraskrivelse | SafeEat',
        description: 'Forstå SafeEats information, vejledning og begrænsninger.'
      }
    },
    nav: {
      about: 'Sådan virker det',
      features: 'Funktioner',
      testimonials: 'Anmeldelser',
      pricing: 'Priser',
      faq: 'FAQ',
      contacts: 'Kontakt',
      getStarted: 'Kom i gang'
    },
    about: {
      description: 'AI-drevet madscanner, der læser etiketter, stregkoder, retter og menuer og fremhæver allergener, ingredienser og ernæring baseret på din sundhedsprofil.'
    },
    hero: {
      badge: 'AI-drevet fødevaresikkerhed',
      title: 'Tjek straks',
      title2: 'Fødevaresikkerhed',
      titleHighlight: 'personligt til dig',
      subtitle: 'Baseret på',
      description: 'Scan dagligvarer, stregkoder, ingredienser og menuer for straks at tjekke, om maden er sikker for din krop',
      typing: ['kroniske sygdomme', 'allergier', 'fødevareintolerancer'],
      stat1: '500+',
      stat1Label: 'Kroniske sygdomme',
      stat2: '250+',
      stat2Label: 'Allergier',
      stat3: '100+',
      stat3Label: 'Fødevareintolerancer',
      downloadButton: 'Hent i App Store'
    },
    howItWorks: {
      badge: 'Sådan virker det',
      mainTitle: 'Sådan fungerer SafeEat',
      subtitle: 'Vores madscanningsproces er enkel, hurtig og giver personlige resultater i få, nemme trin.',
      setupBadge: 'Engangsopsætning',
      setupTitle: 'Opret din sundhedsprofil',
      setupDesc: 'Under onboarding tilpasser du SafeEat ved at tilføje dine sundhedsoplysninger. Det gøres én gang og muliggør personlig madanalyse kun til dig.',
      cards: {
        diseases: {
          title: 'Kroniske sygdomme',
          desc: 'Tilføj dine kroniske tilstande fra vores database med 500+ sygdomme. Fra diabetes til hjertesygdomme – vi har dig dækket.',
          statLabel: 'tilstande'
        },
        allergies: {
          title: 'Allergier',
          desc: 'Vælg dine allergier blandt 250+ muligheder. Fra almindelige nødder og skaldyr til sjældne allergener – hold dig beskyttet.',
          statLabel: 'allergier'
        },
        intolerances: {
          title: 'Fødevareintolerancer',
          desc: 'Angiv dine intolerancer blandt 100+ muligheder. Laktose, gluten, fruktose og mere – spis uden ubehag.',
          statLabel: 'intolerancer'
        }
      },
      connectorText: 'Og så – hver gang du scanner:',
      stepLabel: 'Trin',
      step1Title: 'Scan mad',
      step1Desc: 'Scan enhver madvare – enkeltprodukter, tilberedte retter, næringsdeklarationer, stregkoder eller endda restaurantmenuer. Peg bare med kameraet, så klarer SafeEat resten.',
      step2Title: 'AI-analyse',
      step2Desc: 'Appen analyserer billedet og sammenholder det med din profil af sygdomme, allergier og fødevareintolerancer for at vurdere kompatibilitet.',
      step3Title: 'Få din rapport',
      step3Desc: 'SafeEat leverer en detaljeret rapport om de analyserede produkter og viser, hvor godt de matcher din sundhedsprofil og dine kostbehov.',
      step4Title: 'Tag din beslutning',
      step4Desc: 'Med personlige indsigter i hånden vælger du den mad, der er bedst for dig. Spis med ro i sindet – og vid præcis, hvad der fungerer for din krop.',
      processTitle: 'Se det i praksis',
      processSubtitle: 'Se, hvordan SafeEat analyserer din mad i realtid'
    },
    features: {
      title: 'Kraftfulde funktioner',
      subtitle: 'Alt, du skal bruge for at spise sikkert',
      items: [
        {
          title: 'Scan ethvert produkt',
          subtitle: 'fra almindelige snacks til eksotiske frugter',
          description: 'Ret kameraet mod enhver emballeret eller uemballeret fødevare – fra supermarkedets klassikere til sjældne tropiske frugter – og få en øjeblikkelig sikkerhedsvurdering. SafeEat identificerer produktet og analyserer dets sammensætning, så du kan undgå risikable eller skadelige ingredienser.',
          highlights: ['Emballerede og uemballerede fødevarer', 'Øjeblikkelig sikkerhedstjek', 'Analyse af sammensætning'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Scan næringsdeklarationer og stregkoder',
          subtitle: 'afkod forvirrende etiketter med det samme',
          description: 'Slut med at kæmpe med småt tryk eller forvirrende etiketter. SafeEat kan scanne næringsindhold, ingredienslister og stregkoder på få sekunder. Appen fremhæver straks allergener, tilsætningsstoffer eller giftige stoffer, så du ved præcis, hvad du spiser.',
          highlights: ['Scanning af næringsindhold', 'Registrering af allergener', 'Advarsler om giftige stoffer'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Scan retter – identificér ingredienser',
          subtitle: 'ud fra et foto af dit måltid',
          description: 'Tag et foto af dit måltid, og SafeEat bryder det ned i ingredienser. Uanset om det er hjemmelavet, fra et food court eller en gourmetrestaurant, identificerer vores AI potentielle allergener eller ingredienser, der ikke passer til din kost.',
          highlights: ['AI-ingrediensgenkendelse', 'Virker med enhver ret', 'Tjek af kostkompatibilitet'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Scan restaurantmenuer',
          subtitle: 'find sikre retter før du bestiller',
          description: 'Peg kameraet mod en café- eller restaurantmenu, og SafeEat analyserer hver ret og viser dig, hvilke der er sikre i forhold til dine behov. Perfekt til at spise ude uden at bekymre dig om skjulte allergener eller uønskede ingredienser.',
          highlights: ['Menuanalyse', 'Finder sikre retter', 'Registrering af skjulte allergener'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Opret din personlige liste over sikre fødevarer',
          subtitle: 'byg dit bibliotek til "clean eating"',
          description: 'Gem de fødevarer, der er sikre for dig, opbyg dit eget "clean eating"-bibliotek, og få adgang til det når som helst, mens du handler eller spiser ude. Din personlige fødevaredatabase vokser for hver scanning.',
          highlights: ['Personligt fødevarebibliotek', 'Hurtig adgang overalt', 'Smarte anbefalinger'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Betroet af tusindvis',
      subtitle: 'Betroet af et globalt fællesskab af brugere.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat ændrede fuldstændigt, hvordan jeg handler ind! Jeg har cøliaki, og det tager kun få sekunder at scanne produkter. Ikke mere at læse mikroskopiske etiketter i butikken.'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: 'Som en med flere fødevareallergier var det altid stressende at spise ude. Nu scanner jeg bare menuen og ved præcis, hvad der er sikkert. En gamechanger!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'Jeg bruger SafeEat hver dag til at håndtere min diabetes. Scanneren til næringsdeklarationer hjælper mig med at holde styr på kulhydrater og sukker med det samme. Kan varmt anbefales!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: 'Min søn har alvorlig nøddeallergi. SafeEat giver mig ro i sindet, når jeg køber snacks til ham. Stregkodescanneren er utrolig præcis.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: 'Jeg er laktoseintolerant og rejser meget. SafeEat virker på alle sprog – jeg scanner bare restaurantmenuer i udlandet og spiser uden bekymringer!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'At håndtere min mands hjertesygdom betyder, at vi skal holde øje med natrium. SafeEats øjeblikkelige analyse gør madplanlægning meget nemmere.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Ret-scanneren er fantastisk! Jeg fotograferede min restaurantret, og den fandt skjulte gluteningredienser. Den reddede mig fra en dårlig reaktion!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'Jeg følger en streng halal-kost. SafeEat hjælper mig med at tjekke ingredienser i emballerede fødevarer, jeg ikke kender. En uundværlig app!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Med tre børn med forskellige allergier var indkøb et mareridt. Nu scanner jeg alt på få sekunder. Denne app er en livredder!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'Jeg har Crohns sygdom og skal undgå bestemte triggere. SafeEats personlige profil fanger ingredienser, jeg ellers ville have overset.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'Menu-scannerfunktionen er perfekt til min skaldyrsallergi. Jeg føler mig tryg ved at bestille på nye restauranter nu!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'Som kok med følsomheder bruger jeg SafeEat til at tjekke ingredienser, før jeg laver mad. AI-genkendelsen er imponerende præcis.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: 'At håndtere graviditetsdiabetes var overvældende, indtil jeg fandt SafeEat. At scanne madetiketter gør det så enkelt at følge med!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Jeg har en sjælden fødevareintolerans, som de fleste apps ikke dækker. SafeEat lod mig tilføje den til min profil og fanger den nu hver gang!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: 'Veganer med sojaallergi her – det var så svært at finde sikker mad! SafeEat finder skjulte animalske produkter OG soja. Elsker den!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'Stregkodedatabasen er enorm! Selv importerede produkter bliver genkendt. Perfekt til at tjekke ukendte internationale fødevarer.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: 'Min ældre mor har flere lidelser. Jeg satte hendes profil op, og nu kan hun handle selvstændigt igen. Tak, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Enkle, gennemsigtige priser',
      subtitle: 'Vælg den plan, der passer til dig',
      free: 'Gratis',
      freeDesc: '5 gratis scanninger til at prøve',
      freeFeature1: '5 AI-scanninger',
      freeFeature2: 'Grundlæggende analyse',
      freeFeature3: 'Begrænset historik',
      weekly: 'Ugentlig',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Perfekt til regelmæssige brugere',
      weeklyFeature1: 'Ubegrænsede scanninger',
      weeklyFeature2: 'Avanceret analyse',
      weeklyFeature3: 'Fuld historik',
      weeklyFeature4: 'Prioriteret support',
      annual: 'Årlig',
      annualPrice: '$29.99',
      annualDesc: 'Bedste værdi – spar 60%',
      annualBadge: 'Mest populær',
      annualFeature1: 'Alt fra Ugentlig',
      annualFeature2: 'Spar $125/år',
      annualFeature3: 'Premium support',
      annualFeature4: 'Tidlig adgang til funktioner',
      button: 'Kom i gang'
    },
    faq: {
      title: 'Ofte stillede spørgsmål',
      items: [
        {
          q: 'Hvad kan jeg scanne med SafeEat?',
          a: 'SafeEat kan analysere: ingredienslister og næringsdeklarationer (fotos), stregkoder, emballerede og uemballerede fødevarer, retter (fotos) samt restaurantmenuer.'
        },
        {
          q: 'Hvordan virker funktionen til retgenkendelse?',
          a: 'AI\'en identificerer en ret og estimerer sandsynlige ingredienser. Fungerer bedst med godt lys og vinkel; skjulte ingredienser eller krydskontakt kan blive overset.'
        },
        {
          q: 'Kan jeg scanne restaurantmenuer?',
          a: 'Ja. Den markerer potentielle allergener og kostkonflikter. Menuer kan være ufuldstændige; bekræft altid med personalet om ingredienser, olier, fælles friture, saucer og krydskontakt.'
        },
        {
          q: 'Hvilke helbredstilstande understøtter SafeEat?',
          a: 'Fødevareallergier, intolerancer (fx laktose, gluten) og kostbehov (lavt natrium, fokus på kulhydrater, undgåelse af bestemte ingredienser).'
        },
        {
          q: 'Virker SafeEat offline?',
          a: 'Som regel kræves internet til scanninger/AI. Gemte resultater og lister kan være tilgængelige offline afhængigt af app og enhed.'
        },
        {
          q: 'Hvor præcis er SafeEats analyse?',
          a: 'Det afhænger af fotokvalitet, tekst på etiketten, sprog/format og hvor komplette menuer er. Den er kun informativ — dobbelttjek altid, når risikoen er høj.'
        },
        {
          q: 'Hvad hvis SafeEat tager fejl?',
          a: 'Stol ikke på et tvivlsomt resultat; scan igen med bedre billede; verificér via etiket/hjemmeside/personale; vælg den sikreste mulighed ved høj risiko.'
        },
        {
          q: 'Kan jeg rapportere forkerte analyseresultater?',
          a: 'Ja: support@safeeat.app med skærmbilleder, foto af etiket/menu/ret, land/mærke/produkt og hvad der virker forkert.'
        },
        {
          q: 'Hvorfor kan retgenkendelse være mindre præcis?',
          a: 'Retter der ligner hinanden, skjulte ingredienser, kraftige saucer/pynt, lavt lys/slør og meget tilpassede eller regionale retter.'
        },
        {
          q: 'Er SafeEat et medicinsk udstyr?',
          a: 'Nej. Det er kun til information. Kontakt en fagperson for medicinsk rådgivning.'
        },
        {
          q: 'Hvad er SafeEats ansvar?',
          a: 'Informationen leveres "som den er"; fuldstændighed kan ikke garanteres. Du er ansvarlig for dine valg; ikke-deklarerede ingredienser og krydskontakt kan forekomme.'
        },
        {
          q: 'Bør jeg udelukkende stole på SafeEat ved alvorlige allergier?',
          a: 'Nej. Læs etiketter, spørg personalet, følg din handlingsplan og medbring ordineret medicin; undgå varen, hvis du er i tvivl.'
        },
        {
          q: 'Hvordan beskytter I mine sundhedsdata?',
          a: 'Vi indsamler minimale data og beskytter dem med sikkerhedsforanstaltninger, begrænset adgang og privatlivskontakt via support@safeeat.app. Se Privatlivspolitik.'
        },
        {
          q: 'Kan jeg bruge SafeEat til mine børn?',
          a: 'Ja, under opsyn. Vær ekstra forsigtig ved alvorlige allergier/komplekse kostbehov; verificér med pålidelige kilder.'
        },
        {
          q: 'Hvad skal jeg gøre, hvis jeg får en reaktion?',
          a: 'Søg lægehjælp med det samme, følg din nødplan, ring 112 ved alvorlige symptomer, og rapportér derefter til udbyder/restaurant/producent.'
        }
      ]
    },
    cta: {
      title: 'Klar til at forvandle dit madliv?',
      subtitle: 'Bliv en del af tusindvis, der bruger SafeEat hver dag',
      description: 'Hent nu og få 5 gratis AI-scanninger',
      button: 'Hent i App Store',
      feature1: 'Intet kreditkort kræves',
      feature2: '100% privat og sikker',
      feature3: 'Øjeblikkelig adgang'
    },
    newsletter: {
      title: 'Hold dig opdateret',
      subtitle: 'Få de seneste opdateringer og sundhedstips',
      placeholder: 'Indtast din e-mail',
      button: 'Tilmeld',
      success: 'Tak for din tilmelding!',
      error: 'Indtast en gyldig e-mailadresse'
    },
    seo: {
      title: 'Din personlige assistent til fødevaresikkerhed',
      text: 'SafeEat er en AI-drevet madscanner-app til personer med fødevareallergier, intolerancer og kostrestriktioner. Scan straks etiketter, næringsdeklarationer, stregkoder, restaurantmenuer og tilberedte retter for at tjekke sikkerheden ud fra din profil. Uanset om du har cøliaki, laktoseintolerans, nøddeallergi eller følger glutenfri, veganske eller natriumfattige diæter, hjælper SafeEat dig med at vælge sikkert.',
      keywords: [
        'App mod fødevareallergi',
        'Ingrediensscanner',
        'Allergen-detektor',
        'Læser af næringsdeklaration',
        'Stregkodescanner',
        'Menuscanner',
        'Glutenfri tjek',
        'App mod laktoseintolerans',
        'Kosttracker',
        'Fødevaresikkerhed'
      ]
    },
    cookies: {
      bannerText: 'Vi bruger cookies: nødvendige cookies til at drive siden og analyse-cookies kun med dit samtykke.',
      decline: 'Afvis',
      accept: 'Accepter'
    },
    legal: {
      backHome: 'Tilbage til forsiden'
    },
    footer: {
      product: 'Produkt',
      features: 'Funktioner',
      howItWorks: 'Sådan virker det',
      testimonials: 'Anmeldelser',
      pricing: 'Priser',
      faq: 'FAQ',
      contacts: 'Kontakt',
      legal: 'Juridisk',
      privacy: 'Privatlivspolitik',
      terms: 'Servicevilkår',
      usePolicy: 'Brugervejledning',
      disclaimer: 'Ansvarsfraskrivelse',
      company: 'FJCode OU',
      register: 'Registreringsnummer 16993109',
      address: 'Viru väljak 2, 10111 Tallinn, Estland',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. Alle rettigheder forbeholdes.'
    }
  },
  nl: {
    meta: {
      home: {
        title: 'SafeEat | AI-voedselscanner voor allergieën en intoleranties',
        description: 'Scan etiketten, barcodes, menukaarten en gerechten en markeer allergenen en voedingsrisico\'s op basis van jouw profiel: allergieën, intoleranties en chronische aandoeningen.'
      },
      privacy: {
        title: 'Privacybeleid | SafeEat',
        description: 'Lees hoe SafeEat jouw gegevens verzamelt, gebruikt en beschermt.'
      },
      terms: {
        title: 'Gebruiksvoorwaarden | SafeEat',
        description: 'Bekijk de voorwaarden die van toepassing zijn op jouw gebruik van SafeEat.'
      },
      manual: {
        title: 'Gebruikershandleiding | SafeEat',
        description: 'Kom te weten hoe je SafeEat veilig gebruikt om voedsel te scannen.'
      },
      disclaimer: {
        title: 'Disclaimer | SafeEat',
        description: 'Lees meer over de informatie en beperkingen van SafeEat.'
      }
    },
    nav: {
      about: 'Hoe het werkt',
      features: 'Functies',
      testimonials: 'Ervaringen',
      pricing: 'Prijzen',
      faq: 'FAQ',
      contacts: 'Contact',
      getStarted: 'Beginnen'
    },
    about: {
      description: 'Een AI-voedselscanner die etiketten, barcodes, gerechten en menukaarten leest en allergenen, ingrediënten en voedingsinformatie markeert op basis van jouw gezondheidsprofiel.'
    },
    hero: {
      badge: 'Voedselveiligheid met AI',
      title: 'Zie direct',
      title2: 'hoe veilig je eten is',
      titleHighlight: 'voor jou',
      subtitle: 'Op basis van',
      description: 'Scan voedingsmiddelen, barcodes, ingrediënten en menukaarten en controleer direct of ze geschikt zijn voor jouw lichaam.',
      typing: ['chronische aandoeningen', 'allergieën', 'voedselintoleranties'],
      stat1: '500+',
      stat1Label: 'Chronische aandoeningen',
      stat2: '250+',
      stat2Label: 'Allergieën',
      stat3: '100+',
      stat3Label: 'Voedselintoleranties',
      downloadButton: 'Download in de App Store'
    },
    howItWorks: {
      badge: 'Hoe het werkt',
      mainTitle: 'Hoe SafeEat werkt',
      subtitle: 'Ons scanproces is eenvoudig en snel — je krijgt in enkele stappen een persoonlijk resultaat.',
      setupBadge: 'Eenmalige setup',
      setupTitle: 'Maak je gezondheidsprofiel',
      setupDesc: 'Voeg tijdens de onboarding je gezondheidsgegevens toe om SafeEat te personaliseren. Je doet dit één keer; daarna is elke analyse op jou afgestemd.',
      cards: {
        diseases: {
          title: 'Chronische aandoeningen',
          desc: 'Voeg je chronische aandoeningen toe uit onze database met meer dan 500 aandoeningen. Van diabetes tot hartproblemen — we dekken het af.',
          statLabel: 'aandoeningen'
        },
        allergies: {
          title: 'Allergieën',
          desc: 'Kies je allergieën uit meer dan 250 opties. Van veelvoorkomende noten en schaaldieren tot zeldzame allergenen — alles voor meer gemoedsrust.',
          statLabel: 'allergieën'
        },
        intolerances: {
          title: 'Voedselintoleranties',
          desc: 'Geef je intoleranties aan uit meer dan 100 opties. Lactose, gluten, fructose en meer — zodat je comfortabeler kunt eten.',
          statLabel: 'intoleranties'
        }
      },
      connectorText: 'Daarna, bij elke scan:',
      stepLabel: 'Stap',
      step1Title: 'Scan je eten',
      step1Desc: 'Scan elk soort voedsel: een los product, een bereid gerecht, de voedingswaardetabel, een barcode of zelfs een menukaart in een restaurant. Richt de camera en SafeEat doet de rest.',
      step2Title: 'AI-analyse',
      step2Desc: 'De app analyseert de afbeelding en vergelijkt die met je profiel (aandoeningen, allergieën, intoleranties) om de geschiktheid te bepalen.',
      step3Title: 'Ontvang een rapport',
      step3Desc: 'SafeEat geeft een gedetailleerd rapport dat laat zien hoe het gescande product aansluit bij jouw gezondheid en voedingsbehoeften.',
      step4Title: 'Maak je keuze',
      step4Desc: 'Met persoonlijke inzichten kies je het voedsel dat het beste bij je past. Eet met vertrouwen, wetende wat goed is voor jouw lichaam.',
      processTitle: 'Bekijk hoe het werkt',
      processSubtitle: 'Zie hoe SafeEat je eten in real-time analyseert'
    },
    features: {
      title: 'Krachtige functies',
      subtitle: 'Alles wat je nodig hebt voor veiliger eten',
      items: [
        {
          title: 'Scan elk product',
          subtitle: 'van alledaagse snacks tot exotisch fruit',
          description: 'Richt de camera op elk verpakt of onverpakt product — van standaard supermarktartikelen tot zeldzaam tropisch fruit — en krijg direct een veiligheidscheck. SafeEat herkent het product en analyseert de ingrediënten om je te helpen risicovolle of ongewenste stoffen te vermijden.',
          highlights: ['Verpakte en onverpakte producten', 'Directe veiligheidscheck', 'Ingrediëntenanalyse'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Scan voedingslabels en barcodes',
          subtitle: 'begrijp ingewikkelde etiketten in seconden',
          description: 'Geen gepriegel meer met piepkleine letters. SafeEat scant in enkele seconden de voedingswaardetabel, ingrediëntenlijst en barcode en markeert direct allergenen, additieven of mogelijk schadelijke stoffen, zodat je precies weet wat je eet.',
          highlights: ['Scannen van voedingswaardetabellen', 'Allergenendetectie', 'Waarschuwingen voor risicostoffen'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Scan gerechten – herken ingrediënten',
          subtitle: 'maak gewoon een foto van je bord',
          description: 'Maak een foto van je maaltijd en SafeEat splitst het gerecht in waarschijnlijke ingrediënten. Of het nu huisgemaakt is, uit de kantine of uit een restaurant — de AI kan mogelijke allergenen of ingrediënten detecteren die niet bij jouw dieet passen.',
          highlights: ['AI-ingrediëntherkenning', 'Werkt met elk gerecht', 'Dieetcompatibiliteitscheck'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Scan menukaarten in restaurants',
          subtitle: 'vind veilige opties vóórdat je bestelt',
          description: 'Richt de camera op de menukaart in een café of restaurant en SafeEat analyseert elk gerecht om je te vertellen welke opties bij jouw gezondheid en dieet passen. Ga zorgelozer uit eten zonder angst voor verborgen allergenen of ongewenste ingrediënten.',
          highlights: ['Menukaartanalyse', 'Aanbevelingen voor veilige gerechten', 'Detectie van verborgen allergenen'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Maak je eigen lijst met veilige voeding',
          subtitle: 'bouw je persoonlijke \'schone\' eetbibliotheek',
          description: 'Sla voedingsmiddelen op die voor jou werken en bouw een eigen \'schone\' voedingsbibliotheek die je altijd kunt raadplegen tijdens het winkelen of uit eten gaan. Je persoonlijke database groeit met elke scan en helpt je sneller beslissen.',
          highlights: ['Persoonlijke voedingsdatabase', 'Snelle check onderweg', 'Slimme aanbevelingen'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Vertrouwd door gebruikers',
      subtitle: 'Vertrouwd door een wereldwijde community van gebruikers.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, VS',
          rating: 5,
          content: 'SafeEat heeft mijn manier van boodschappen doen volledig veranderd! Ik heb coeliakie en het scannen van producten kost maar een paar seconden. Nooit meer etiketten met kleine lettertjes lezen in de winkel.'
        },
        {
          name: 'Marcus L.',
          location: 'Londen, Verenigd Koninkrijk',
          rating: 5,
          content: 'Als iemand met meerdere voedselallergieën was uit eten gaan altijd stressvol. Nu scan ik gewoon de menukaart en weet ik meteen wat veilig is. Een echte gamechanger!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokio, Japan',
          rating: 5,
          content: 'Ik gebruik SafeEat elke dag om mijn diabetes te managen. De voedingslabelscanner helpt me direct koolhydraten en suiker te volgen. Een dikke aanrader!'
        },
        {
          name: 'Anna K.',
          location: 'Berlijn, Duitsland',
          rating: 5,
          content: 'Mijn zoon heeft een ernstige notenallergie. SafeEat geeft me rust wanneer ik snacks voor hem koop. De barcodescanner is ontzettend nauwkeurig.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spanje',
          rating: 5,
          content: 'Ik heb lactose-intolerantie en reis veel. SafeEat werkt in elke taal — in het buitenland scan ik gewoon de menukaart en eet ik met een geruster gevoel!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'Door de hartproblemen van mijn man moeten we op natrium letten. De directe analyse in SafeEat maakt het plannen van maaltijden veel eenvoudiger.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australië',
          rating: 5,
          content: 'De gerechtenscanner is fantastisch! Ik maakte een foto van een restaurantmaaltijd en de app vond verborgen gluten in de ingrediënten. Het heeft me een heftige reactie bespaard!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, VAE',
          rating: 5,
          content: 'Ik volg een strikt halal dieet. SafeEat helpt me de ingrediënten van verpakte producten die ik niet ken te controleren. Onmisbare app!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Met drie kinderen die allemaal andere allergieën hebben, waren boodschappen doen een nachtmerrie. Nu scan ik alles in een paar seconden. Deze app is echt een lifesaver!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Zweden',
          rating: 5,
          content: 'Ik heb de ziekte van Crohn en moet bepaalde triggers vermijden. Het persoonlijke profiel in SafeEat pikt ingrediënten op die ik anders zou missen.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazilië',
          rating: 5,
          content: 'De menukaartscanner is perfect voor mijn schaaldierallergie. Ik bestel nu met veel meer zelfvertrouwen in nieuwe restaurants!'
        },
        {
          name: 'David C.',
          location: 'Parijs, Frankrijk',
          rating: 5,
          content: 'Als kok met voedselgevoeligheden gebruik ik SafeEat om ingrediënten te controleren voor ik ga koken. De AI-herkenning is verrassend nauwkeurig.'
        },
        {
          name: 'Fatima A.',
          location: 'Caïro, Egypte',
          rating: 5,
          content: 'Zwangerschapsdiabetes was overweldigend tot ik SafeEat vond. Het scannen van voedseletiketten maakt het bijhouden super eenvoudig!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Ik heb een zeldzame intolerantie die de meeste apps niet ondersteunen. In SafeEat kon ik die aan mijn profiel toevoegen en nu wordt die elke keer herkend!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Nederland',
          rating: 5,
          content: 'Ik ben veganistisch en allergisch voor soja — veilige voeding vinden was erg lastig! SafeEat herkent verborgen dierlijke ingrediënten én soja. Ik ben fan!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoel, Zuid-Korea',
          rating: 5,
          content: 'De barcodedatabase is enorm! Zelfs geïmporteerde producten worden herkend. Ideaal om onbekend internationaal voedsel te checken.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italië',
          rating: 5,
          content: 'Mijn oudere moeder heeft meerdere gezondheidsproblemen. Ik heb haar profiel ingesteld en nu kan ze weer zelfstandig boodschappen doen. Dank je, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Eenvoudige, transparante prijzen',
      subtitle: 'Kies het abonnement dat bij je past',
      free: 'Gratis',
      freeDesc: '5 gratis scans om uit te proberen',
      freeFeature1: '5 AI-scans',
      freeFeature2: 'Basisanalyse',
      freeFeature3: 'Beperkte geschiedenis',
      weekly: 'Wekelijks',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Ideaal voor regelmatig gebruik',
      weeklyFeature1: 'Onbeperkte scans',
      weeklyFeature2: 'Geavanceerde analyse',
      weeklyFeature3: 'Volledige geschiedenis',
      weeklyFeature4: 'Prioritaire ondersteuning',
      annual: 'Jaarlijks',
      annualPrice: '$29.99',
      annualDesc: 'Beste waarde — bespaar 60%',
      annualBadge: 'Meest gekozen',
      annualFeature1: 'Alle functies van Wekelijks',
      annualFeature2: 'Bespaar $125 per jaar',
      annualFeature3: 'Premium ondersteuning',
      annualFeature4: 'Vroege toegang tot nieuwe functies',
      button: 'Beginnen'
    },
    faq: {
      title: 'Veelgestelde vragen',
      items: [
        {
          q: 'Wat kan ik scannen met SafeEat?',
          a: 'SafeEat kan het volgende analyseren: ingrediënten- en voedingswaardelijsten (foto), barcodes, verpakte en onverpakte producten, gerechten (foto) en menukaarten in restaurants.'
        },
        {
          q: 'Hoe werkt de gerecht­herkenning?',
          a: 'De AI herkent het gerecht en schat de waarschijnlijke ingrediënten in. Dit werkt het beste bij goed licht en een duidelijke hoek; verborgen ingrediënten of kruisbesmetting kunnen worden gemist.'
        },
        {
          q: 'Kan ik menukaarten in restaurants scannen?',
          a: 'Ja. De app markeert mogelijke allergenen en conflicten met je dieet. Menukaarten kunnen onvolledig zijn; vraag het personeel altijd naar ingrediënten, gebruikte oliën, gedeelde friteuses, sauzen en risico op kruisbesmetting.'
        },
        {
          q: 'Welke gezondheidsproblemen ondersteunt SafeEat?',
          a: 'Voedselallergieën, intoleranties (zoals lactose of gluten) en voedingsbehoeften (weinig natrium, koolhydraatbewust, bepaalde ingrediënten vermijden).'
        },
        {
          q: 'Werkt SafeEat offline?',
          a: 'Voor scans en AI-analyse is meestal een internetverbinding nodig. Opgeslagen resultaten en lijsten kunnen offline beschikbaar zijn, afhankelijk van de app en je apparaat.'
        },
        {
          q: 'Hoe nauwkeurig is de analyse van SafeEat?',
          a: 'De nauwkeurigheid hangt af van de beeldkwaliteit, leesbaarheid van tekst, taal/indeling en hoe volledig de menukaart is. De app is informatief — bij verhoogd risico moet je altijd extra controleren.'
        },
        {
          q: 'Wat als SafeEat een fout maakt?',
          a: 'Vertrouw niet op een twijfelachtige uitkomst; maak een nieuwe, scherpere foto; controleer het etiket of de website en vraag het personeel; kies bij hoog risico altijd voor de veiligere optie.'
        },
        {
          q: 'Kan ik onjuiste analyses melden?',
          a: 'Ja: stuur een e-mail naar support@safeeat.app met een screenshot, een foto van het etiket/de menukaart/het gerecht, het land, merk en productnaam en een beschrijving van wat er volgens jou niet klopt.'
        },
        {
          q: 'Waarom kan de gerecht­herkenning minder nauwkeurig zijn?',
          a: 'Gerechten die er vergelijkbaar uitzien, verborgen ingrediënten, zware sauzen of garnering, slecht licht of onscherpte en sterk aangepaste of regionale gerechten kunnen de nauwkeurigheid verminderen.'
        },
        {
          q: 'Is SafeEat een medisch hulpmiddel?',
          a: 'Nee. De app is uitsluitend bedoeld voor informatieve doeleinden. Voor medisch advies moet je een professional raadplegen.'
        },
        {
          q: 'Wat is de aansprakelijkheid van SafeEat?',
          a: 'Informatie wordt geleverd \'zoals deze is\'; volledigheid of foutloosheid kan niet worden gegarandeerd. Jij bent zelf verantwoordelijk voor je keuzes; niet-vermelde ingrediënten en kruisbesmetting blijven mogelijk.'
        },
        {
          q: 'Kan ik bij ernstige allergieën volledig op SafeEat vertrouwen?',
          a: 'Nee. Lees etiketten, vraag het personeel, volg je noodplan en draag voorgeschreven medicatie bij je; vermijd producten/gerechten wanneer je twijfelt.'
        },
        {
          q: 'Hoe beschermen jullie mijn gezondheidsgegevens?',
          a: 'We verzamelen zo min mogelijk gegevens en beschermen die met beveiligingsmaatregelen en beperkte toegangsrechten. Voor privacyvragen kun je mailen naar support@safeeat.app. Zie ook het Privacybeleid.'
        },
        {
          q: 'Kunnen kinderen SafeEat gebruiken?',
          a: 'Ja, onder toezicht van een volwassene. Bij ernstige allergieën of complexe diëten is extra voorzichtigheid nodig en raden we aan informatie via betrouwbare bronnen te verifiëren.'
        },
        {
          q: 'Wat moet ik doen bij een (allergische) reactie?',
          a: 'Zoek onmiddellijk medische hulp en volg je noodplan; bel bij ernstige symptomen de hulpdiensten. Meld de gebeurtenis daarna bij je arts, restaurant of fabrikant.'
        }
      ]
    },
    cta: {
      title: 'Klaar om je eetpatroon te veranderen?',
      subtitle: 'Sluit je aan bij duizenden mensen die SafeEat elke dag gebruiken',
      description: 'Download nu en ontvang 5 gratis AI-scans',
      button: 'Download in de App Store',
      feature1: 'Geen creditcard nodig',
      feature2: '100% privé en veilig',
      feature3: 'Direct aan de slag'
    },
    newsletter: {
      title: 'Blijf op de hoogte',
      subtitle: 'Ontvang het laatste nieuws en tips voor veilig eten',
      placeholder: 'Vul je e-mailadres in',
      button: 'Aanmelden',
      error: 'Voer een geldig e-mailadres in',
      success: 'Bedankt voor je aanmelding!'
    },
    seo: {
      title: 'Jouw persoonlijke assistent voor voedselveiligheid',
      text: 'SafeEat is een AI-voedselscanner voor mensen met allergieën, intoleranties en dieetbeperkingen. Scan direct etiketten, voedingswaardetabellen, barcodes, restaurantmenukaarten en bereide gerechten om te controleren of ze bij jouw profiel passen. Of je nu coeliakie hebt, lactose-intolerant bent, een notenallergie hebt of een glutenvrij, vegan of natriumarm dieet volgt: SafeEat helpt je veiligere keuzes te maken.',
      keywords: [
        'app voor voedselallergie',
        'ingrediëntenscanner',
        'allergeen­detectie',
        'scannen van voedingslabels',
        'barcode­scanner',
        'menukaart­scanner',
        'glutenvrij checken',
        'app voor lactose-intolerantie',
        'dieet­tracking',
        'voedselveiligheid'
      ]
    },
    cookies: {
      bannerText: 'We gebruiken cookies: noodzakelijke cookies voor de werking van de site en analytische cookies alleen met jouw toestemming.',
      accept: 'Accepteren',
      decline: 'Weigeren'
    },
    legal: {
      backHome: 'Terug naar de homepagina'
    },
    footer: {
      product: 'Product',
      features: 'Functies',
      howItWorks: 'Hoe het werkt',
      testimonials: 'Ervaringen',
      pricing: 'Prijzen',
      faq: 'FAQ',
      contacts: 'Contact',
      legal: 'Juridisch',
      privacy: 'Privacybeleid',
      terms: 'Servicevoorwaarden',
      usePolicy: 'Gebruikershandleiding',
      disclaimer: 'Disclaimer',
      company: 'FJCode OU',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      register: 'Registratienummer 16993109',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. Alle rechten voorbehouden.'
    }
  },
  fi: {
    meta: {
      home: {
        title: 'SafeEat | AI-ruokaskanneri allergioihin ja intoleransseihin',
        description: 'Skannaa etiketit, viivakoodit, ruokalistat ja annokset ja tunnista allergeenit ja ruokavalioriskit profiilisi perusteella: allergiat, intoleranssit ja krooniset sairaudet.'
      },
      privacy: {
        title: 'Tietosuojakäytäntö | SafeEat',
        description: 'Lue, miten SafeEat kerää, käyttää ja suojaa tietojasi.'
      },
      terms: {
        title: 'Käyttöehdot | SafeEat',
        description: 'Tutustu SafeEatin käyttöä koskeviin ehtoihin.'
      },
      manual: {
        title: 'Käyttöopas | SafeEat',
        description: 'Opi käyttämään SafeEatia ruoan skannaamiseen turvallisesti.'
      },
      disclaimer: {
        title: 'Vastuuvapauslauseke | SafeEat',
        description: 'Ymmärrä SafeEatin tiedot, ohjeistus ja rajoitukset.'
      }
    },
    nav: {
      about: 'Näin se toimii',
      features: 'Ominaisuudet',
      testimonials: 'Arvostelut',
      pricing: 'Hinnoittelu',
      faq: 'UKK',
      contacts: 'Yhteystiedot',
      getStarted: 'Aloita'
    },
    about: {
      description: 'AI-pohjainen ruokaskanneri, joka lukee etiketit, viivakoodit, annokset ja ruokalistat ja korostaa allergeenit, ainesosat ja ravintosisällön terveydentilaprofiilisi perusteella.'
    },
    hero: {
      badge: 'AI-pohjainen ruokaturvallisuus',
      title: 'Tarkista heti',
      title2: 'ruokaturvallisuus',
      titleHighlight: 'sinulle henkilökohtaisesti',
      subtitle: 'Perustuen',
      description: 'Skannaa elintarvikkeet, viivakoodit, ainesosat ja ruokalistat ja tarkista heti, onko ruoka turvallista kehollesi',
      typing: ['krooniset sairaudet', 'allergiat', 'ruokaintoleranssit'],
      stat1: '500+',
      stat1Label: 'Krooniset sairaudet',
      stat2: '250+',
      stat2Label: 'Allergiat',
      stat3: '100+',
      stat3Label: 'Ruokaintoleranssit',
      downloadButton: 'Lataa App Storesta'
    },
    howItWorks: {
      badge: 'Näin se toimii',
      mainTitle: 'Näin SafeEat toimii',
      subtitle: 'Skannausprosessi on helppo ja nopea — saat henkilökohtaiset tulokset muutamassa selkeässä vaiheessa.',
      setupBadge: 'Kertaluonteinen käyttöönotto',
      setupTitle: 'Luo terveydentilaprofiilisi',
      setupDesc: 'Onboardingin aikana muokkaat SafeEatin lisäämällä terveystietosi. Tämä tehdään kerran ja mahdollistaa juuri sinulle räätälöidyn ruoka-analyysin.',
      cards: {
        diseases: {
          title: 'Krooniset sairaudet',
          desc: 'Lisää krooniset tilasi tietokannastamme, jossa on yli 500 sairautta. Diabeteksesta sydänsairauksiin — meiltä löydät kaiken.',
          statLabel: 'tilaa'
        },
        allergies: {
          title: 'Allergiat',
          desc: 'Valitse allergiasi yli 250 vaihtoehdosta. Yleisistä pähkinöistä ja äyriäisistä harvinaisiin allergeeneihin — pysy turvassa.',
          statLabel: 'allergiaa'
        },
        intolerances: {
          title: 'Ruokaintoleranssit',
          desc: 'Merkitse intoleranssisi yli 100 vaihtoehdosta. Laktoosi, gluteeni, fruktoosi ja paljon muuta — syö ilman epämukavuutta.',
          statLabel: 'intoleranssia'
        }
      },
      connectorText: 'Ja sitten — joka kerta kun skannaat:',
      stepLabel: 'Vaihe',
      step1Title: 'Skannaa ruoka',
      step1Desc: 'Skannaa mikä tahansa ruoka — yksittäiset tuotteet, valmiit annokset, ravintoarvot, viivakoodit tai jopa ravintolan ruokalistat. Osoita vain kameralla, SafeEat hoitaa loput.',
      step2Title: 'AI-analyysi',
      step2Desc: 'Sovellus analysoi kuvan ja vertaa sitä sairaus-, allergia- ja intoleranssiprofiiliisi arvioidakseen sopivuuden.',
      step3Title: 'Saat raportin',
      step3Desc: 'SafeEat tuottaa yksityiskohtaisen raportin analysoiduista tuotteista ja näyttää, miten hyvin ne sopivat terveydentilaprofiiliisi ja ruokavaliotarpeisiisi.',
      step4Title: 'Tee päätös',
      step4Desc: 'Henkilökohtaisten oivallusten avulla valitset sinulle parhaiten sopivan ruoan. Syö huoletta — ja tiedä tarkalleen, mikä toimii kehollesi.',
      processTitle: 'Katso käytännössä',
      processSubtitle: 'Näe, miten SafeEat analysoi ruokasi reaaliajassa'
    },
    features: {
      title: 'Tehokkaat ominaisuudet',
      subtitle: 'Kaikki mitä tarvitset syödäksesi turvallisesti',
      items: [
        {
          title: 'Skannaa mikä tahansa tuote',
          subtitle: 'tavallisista välipaloista eksoottisiin hedelmiin',
          description: 'Suuntaa kamera mihin tahansa pakattuun tai pakkaamattomaan elintarvikkeeseen — marketin perustuotteista harvinaisiin trooppisiin hedelmiin — ja saat välittömän turvallisuusarvion. SafeEat tunnistaa tuotteen ja analysoi sen koostumuksen, jotta voit välttää riskialttiita tai haitallisia ainesosia.',
          highlights: ['Pakattuja ja pakkaamattomia ruokia', 'Välitön turvallisuustarkistus', 'Koostumusanalyysi'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Skannaa ravintoarvot ja viivakoodit',
          subtitle: 'selvitä hankalat etiketit heti',
          description: 'Ei enää pienen präntin tai sekavien etikettien kanssa kamppailua. SafeEat skannaa ravintoarvot, ainesosaluettelot ja viivakoodit sekunneissa. Sovellus korostaa heti allergeenit, lisäaineet tai haitalliset aineet, jotta tiedät tarkalleen, mitä syöt.',
          highlights: ['Ravintoarvojen skannaus', 'Allergeenien tunnistus', 'Varoitukset haitallisista aineista'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Skannaa annokset — tunnista ainesosat',
          subtitle: 'ateriakuvasta',
          description: 'Ota kuva ateriastasi, ja SafeEat pilkkoo sen ainesosiin. Olipa ruoka kotona tehtyä, food courtista tai fine dining -ravintolasta, AI tunnistaa mahdolliset allergeenit tai ainesosat, jotka eivät sovi ruokavalioosi.',
          highlights: ['AI-ainesosien tunnistus', 'Toimii kaikilla annoksilla', 'Ruokavaliosopivuuden tarkistus'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Skannaa ravintolan ruokalistat',
          subtitle: 'löydä turvalliset annokset ennen tilausta',
          description: 'Osoita kameralla kahvilan tai ravintolan ruokalistaa, ja SafeEat analysoi annokset ja näyttää, mitkä niistä ovat tarpeisiisi nähden turvallisia. Täydellinen ulkona syömiseen ilman huolta piiloallergeeneista tai ei-toivotuista ainesosista.',
          highlights: ['Ruokalistan analyysi', 'Turvallisten annosten löytäminen', 'Piiloallergeenien tunnistus'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Luo henkilökohtainen turvallisten ruokien lista',
          subtitle: 'rakenna oma "clean eating" -kirjastosi',
          description: 'Tallenna sinulle turvalliset ruoat, rakenna oma "clean eating" -kirjastosi ja käytä sitä milloin tahansa kaupassa tai syödessäsi ulkona. Henkilökohtainen ruokadatatietosi kasvaa jokaisen skannauksen myötä.',
          highlights: ['Henkilökohtainen ruokakirjasto', 'Nopea pääsy kaikkialla', 'Älykkäät suositukset'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Tuhansien luottama',
      subtitle: 'Maailmanlaajuinen käyttäjäyhteisö luottaa SafeEatiin.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat muutti täysin tapani tehdä ruokaostoksia! Minulla on keliakia, ja tuotteiden skannaus vie vain sekunteja. Ei enää mikroskooppisten etikettien lukemista kaupassa.'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: 'Useiden ruoka-allergioiden kanssa ulkona syöminen oli aina stressaavaa. Nyt skannaan vain ruokalistan ja tiedän heti, mikä on turvallista. Todellinen pelinmuuttaja!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'Käytän SafeEatia päivittäin diabeteksen hallintaan. Ravintoarvoskanneri auttaa seuraamaan hiilihydraatteja ja sokeria heti. Suosittelen lämpimästi!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: 'Pojallani on vakava pähkinäallergia. SafeEat tuo mielenrauhaa, kun ostan hänelle välipaloja. Viivakoodiskanneri on uskomattoman tarkka.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: 'Olen laktoosi-intolerantti ja matkustan paljon. SafeEat toimii kaikilla kielillä — skannaan ruokalistat ulkomailla ja syön huoletta!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'Miehen sydänsairauden hallinta tarkoittaa, että meidän on seurattava natriumia. SafeEatin välitön analyysi tekee ruokasuunnittelusta paljon helpompaa.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Annoskanneri on loistava! Kuvasin ravintola-annokseni, ja se löysi piilotettuja gluteenilähteitä. Se pelasti minut pahalta reaktiolta!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'Noudatan tiukkaa halal-ruokavaliota. SafeEat auttaa tarkistamaan ainesosat tuntemattomissa pakatuissa tuotteissa. Välttämätön sovellus!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Kolmen lapsen eri allergioiden kanssa ostokset olivat painajainen. Nyt skannaan kaiken muutamassa sekunnissa. Tämä sovellus on pelastus!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'Minulla on Crohnin tauti ja minun täytyy välttää tiettyjä laukaisijoita. SafeEatin henkilökohtainen profiili huomaa ainesosia, jotka muuten jäisivät minulta huomaamatta.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'Ruokalistan skannaustoiminto on täydellinen äyriäisallergialleni. Nyt uskallan tilata uusissa ravintoloissa!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'Kokkina, jolla on herkkyyksiä, käytän SafeEatia tarkistamaan ainesosat ennen ruoanlaittoa. AI-tunnistus on vaikuttavan tarkka.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: 'Raskausdiabeteksen hallinta oli ylivoimaista, kunnes löysin SafeEatin. Ruokaetikettien skannaus tekee seurannasta niin helppoa!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Minulla on harvinainen ruokaintoleranssi, jota useimmat sovellukset eivät kata. SafeEat antoi lisätä sen profiiliini ja nyt se huomaa sen joka kerta!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: 'Vegaani, jolla on soja-allergia täällä — turvallisen ruoan löytäminen oli niin vaikeaa! SafeEat löytää piilotetut eläinperäiset tuotteet JA soijan. Rakastan sitä!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'Viivakooditietokanta on valtava! Jopa tuontituotteet tunnistetaan. Täydellinen tuntemattomien kansainvälisten tuotteiden tarkistamiseen.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: 'Iäkkäällä äidilläni on useita sairauksia. Loin hänelle profiilin ja nyt hän pystyy taas tekemään ostokset itsenäisesti. Kiitos, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Selkeä ja läpinäkyvä hinnoittelu',
      subtitle: 'Valitse sinulle sopiva paketti',
      free: 'Ilmainen',
      freeDesc: '5 ilmaista skannausta kokeiluun',
      freeFeature1: '5 AI-skannausta',
      freeFeature2: 'Perusanalyysi',
      freeFeature3: 'Rajoitettu historia',
      weekly: 'Viikoittainen',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Täydellinen säännöllisille käyttäjille',
      weeklyFeature1: 'Rajoittamattomat skannaukset',
      weeklyFeature2: 'Edistynyt analyysi',
      weeklyFeature3: 'Täysi historia',
      weeklyFeature4: 'Priorisoitu tuki',
      annual: 'Vuosittainen',
      annualPrice: '$29.99',
      annualDesc: 'Paras arvo — säästä 60 %',
      annualBadge: 'Suosituin',
      annualFeature1: 'Kaikki Viikoittaisesta',
      annualFeature2: 'Säästä $125/vuosi',
      annualFeature3: 'Premium-tuki',
      annualFeature4: 'Aikainen pääsy uusiin ominaisuuksiin',
      button: 'Aloita'
    },
    faq: {
      title: 'Usein kysytyt kysymykset',
      items: [
        {
          q: 'Mitä voin skannata SafeEatilla?',
          a: 'SafeEat voi analysoida: ainesosaluettelot ja ravintoarvomerkinnät (valokuvat), viivakoodit, pakatut ja pakkaamattomat elintarvikkeet, annokset (valokuvat) sekä ravintoloiden ruokalistat.'
        },
        {
          q: 'Miten annoksen tunnistus toimii?',
          a: 'AI tunnistaa annoksen ja arvioi todennäköiset ainesosat. Toimii parhaiten hyvässä valaistuksessa ja sopivasta kulmasta; piiloainesosat tai ristikontaminaatio voivat jäädä huomaamatta.'
        },
        {
          q: 'Voinko skannata ravintolan ruokalistan?',
          a: 'Kyllä. Se merkitsee mahdolliset allergeenit ja ruokavalioristiriidat. Ruokalistat voivat olla puutteellisia; varmista aina henkilökunnalta ainesosat, öljyt, yhteiset friteerausöljyt, kastikkeet ja ristikontaminaatio.'
        },
        {
          q: 'Mitä terveydentiloja SafeEat tukee?',
          a: 'Ruoka-allergiat, intoleranssit (esim. laktoosi, gluteeni) ja ruokavaliotarpeet (vähän natriumia, hiilihydraattien seuranta, tiettyjen ainesosien välttäminen).'
        },
        {
          q: 'Toimiiko SafeEat offline-tilassa?',
          a: 'Useimmiten skannaukset/AI vaativat internet-yhteyden. Tallennetut tulokset ja listat voivat olla käytettävissä offline-tilassa sovelluksesta ja laitteesta riippuen.'
        },
        {
          q: 'Kuinka tarkka SafeEatin analyysi on?',
          a: 'Se riippuu kuvan laadusta, etiketin tekstistä, kielestä/formaatista ja siitä, kuinka täydellisiä ruokalistat ovat. Se on vain informatiivinen — tarkista aina uudelleen, kun riski on korkea.'
        },
        {
          q: 'Entä jos SafeEat tekee virheen?',
          a: 'Älä luota epävarmaan tulokseen; skannaa uudelleen paremmalla kuvalla; varmista etiketistä/verkkosivulta/henkilökunnalta; valitse korkean riskin tilanteissa turvallisin vaihtoehto.'
        },
        {
          q: 'Voinko ilmoittaa virheellisistä analyysituloksista?',
          a: 'Kyllä: support@safeeat.app — liitä mukaan kuvakaappaukset, kuva etiketistä/ruokalistasta/annoksesta, maa/merkki/tuote ja mikä vaikuttaa väärältä.'
        },
        {
          q: 'Miksi annoksen tunnistus voi olla epätarkempi?',
          a: 'Samannäköiset annokset, piiloainesosat, vahvat kastikkeet/koristeet, heikko valo/epätarkkuus sekä erittäin muokatut tai alueelliset ruoat.'
        },
        {
          q: 'Onko SafeEat lääkinnällinen laite?',
          a: 'Ei. Se on vain informatiivinen. Käänny terveydenhuollon ammattilaisen puoleen lääketieteellisissä kysymyksissä.'
        },
        {
          q: 'Mikä on SafeEatin vastuu?',
          a: 'Tiedot tarjotaan "sellaisina kuin ne ovat"; täydellisyyttä ei voida taata. Olet itse vastuussa valinnoistasi; ilmoittamattomia ainesosia ja ristikontaminaatiota voi esiintyä.'
        },
        {
          q: 'Pitäisikö minun luottaa vain SafeEatiin vakavissa allergioissa?',
          a: 'Ei. Lue etiketit, kysy henkilökunnalta, noudata toimintasuunnitelmaasi ja pidä määrätty lääkitys mukana; vältä tuotetta, jos olet epävarma.'
        },
        {
          q: 'Miten suojaatte terveystietojani?',
          a: 'Keräämme vain vähimmäismäärän tietoja ja suojaamme ne turvatoimin, rajatulla pääsyllä ja yksityisyysyhteydenotolla osoitteessa support@safeeat.app. Katso Tietosuojakäytäntö.'
        },
        {
          q: 'Voinko käyttää SafeEatia lapsilleni?',
          a: 'Kyllä, valvonnassa. Ole erityisen varovainen vakavissa allergioissa/monimutkaisissa ruokavaliotarpeissa; varmista luotettavista lähteistä.'
        },
        {
          q: 'Mitä teen, jos saan reaktion?',
          a: 'Hakeudu heti hoitoon, noudata hätätoimintasuunnitelmaasi, soita 112 vakavissa oireissa ja ilmoita sen jälkeen ravintolalle/toimittajalle/valmistajalle.'
        }
      ]
    },
    cta: {
      title: 'Valmis muuttamaan ruokailusi?',
      subtitle: 'Liity tuhansiin, jotka käyttävät SafeEatia joka päivä',
      description: 'Lataa nyt ja saat 5 ilmaista AI-skannausta',
      button: 'Lataa App Storesta',
      feature1: 'Ei luottokorttia',
      feature2: '100 % yksityinen ja turvallinen',
      feature3: 'Välitön käyttöoikeus'
    },
    newsletter: {
      title: 'Pysy ajan tasalla',
      subtitle: 'Saat uusimmat päivitykset ja terveystipsit',
      placeholder: 'Syötä sähköpostiosoitteesi',
      button: 'Tilaa',
      success: 'Kiitos tilauksesta!',
      error: 'Syötä kelvollinen sähköpostiosoite'
    },
    seo: {
      title: 'Henkilökohtainen ruokaturvallisuusavustajasi',
      text: 'SafeEat on AI-pohjainen ruokaskannerisovellus ihmisille, joilla on ruoka-allergioita, intoleransseja ja ruokavaliorestriktioita. Skannaa heti etiketit, ravintoarvomerkinnät, viivakoodit, ravintoloiden ruokalistat ja valmiit annokset ja tarkista turvallisuus profiilisi perusteella. Olipa sinulla keliakia, laktoosi-intoleranssi, pähkinäallergia tai noudatat gluteenitonta, vegaanista tai vähäsuolaista ruokavaliota, SafeEat auttaa sinua valitsemaan turvallisesti.',
      keywords: [
        'Ruoka-allergia sovellus',
        'Ainesosaskanneri',
        'Allergeenien tunnistin',
        'Ravintoarvomerkintöjen lukija',
        'Viivakoodiskanneri',
        'Ruokalistaskanneri',
        'Gluteeniton tarkistus',
        'Laktoosi-intoleranssi sovellus',
        'Ruokavaliotrackeri',
        'Ruokaturvallisuus'
      ]
    },
    cookies: {
      bannerText: 'Käytämme evästeitä: välttämättömiä evästeitä sivuston toimintaan ja analytiikkaevästeitä vain suostumuksellasi.',
      decline: 'Hylkää',
      accept: 'Hyväksy'
    },
    legal: {
      backHome: 'Takaisin etusivulle'
    },
    footer: {
      product: 'Tuote',
      features: 'Ominaisuudet',
      howItWorks: 'Näin se toimii',
      testimonials: 'Arvostelut',
      pricing: 'Hinnoittelu',
      faq: 'UKK',
      contacts: 'Yhteystiedot',
      legal: 'Juridinen',
      privacy: 'Tietosuojakäytäntö',
      terms: 'Käyttöehdot',
      usePolicy: 'Käyttöopas',
      disclaimer: 'Vastuuvapauslauseke',
      company: 'FJCode OU',
      register: 'Rekisterinumero 16993109',
      address: 'Viru väljak 2, 10111 Tallinn, Viro',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. Kaikki oikeudet pidätetään.'
    }
  },
  fr: {
    meta: {
      home: {
        title: 'SafeEat | Scanner alimentaire IA pour allergies et intolérances',
        description: 'Scannez les étiquettes, codes-barres, menus et plats pour mettre en évidence les allergènes et risques alimentaires selon votre profil : allergies, intolérances et maladies chroniques.'
      },
      privacy: {
        title: 'Politique de confidentialité | SafeEat',
        description: 'Découvrez comment SafeEat collecte, utilise et protège vos données.'
      },
      terms: {
        title: 'Conditions d\'utilisation | SafeEat',
        description: 'Consultez les conditions qui régissent votre utilisation de SafeEat.'
      },
      manual: {
        title: 'Guide d\'utilisation | SafeEat',
        description: 'Apprenez à utiliser SafeEat en toute sécurité pour scanner vos aliments.'
      },
      disclaimer: {
        title: 'Avertissement | SafeEat',
        description: 'Prenez connaissance des limites d\'utilisation et des informations fournies par SafeEat.'
      }
    },
    nav: {
      about: 'Comment ça marche',
      features: 'Fonctionnalités',
      testimonials: 'Témoignages',
      pricing: 'Tarifs',
      faq: 'FAQ',
      contacts: 'Contact',
      getStarted: 'Commencer'
    },
    about: {
      description: 'Scanner alimentaire basé sur l\'IA qui lit les étiquettes, codes-barres, plats et menus et met en évidence les allergènes, ingrédients et informations nutritionnelles selon votre profil de santé.'
    },
    hero: {
      badge: 'Sécurité alimentaire par l\'IA',
      title: 'Voyez instantanément',
      title2: 'si un aliment est sûr',
      titleHighlight: 'pour vous',
      subtitle: 'Basé sur',
      description: 'Scannez des aliments, des codes-barres, des listes d\'ingrédients et des menus pour vérifier en quelques secondes s\'ils conviennent à votre corps.',
      typing: ['maladies chroniques', 'allergies', 'intolérances alimentaires'],
      stat1: '500+',
      stat1Label: 'Maladies chroniques',
      stat2: '250+',
      stat2Label: 'Allergies',
      stat3: '100+',
      stat3Label: 'Intolérances alimentaires',
      downloadButton: 'Télécharger sur l\'App Store'
    },
    howItWorks: {
      badge: 'Comment ça marche',
      mainTitle: 'Comment fonctionne SafeEat',
      subtitle: 'Notre processus de scan est simple et rapide : obtenez des résultats personnalisés en quelques étapes.',
      setupBadge: 'Configuration unique',
      setupTitle: 'Créez votre profil santé',
      setupDesc: 'Ajoutez vos informations de santé lors de l\'onboarding pour personnaliser SafeEat. Vous ne le faites qu\'une fois, puis chaque analyse est adaptée à votre profil.',
      cards: {
        diseases: {
          title: 'Maladies chroniques',
          desc: 'Ajoutez vos maladies chroniques à partir de notre base de plus de 500 affections. Du diabète aux problèmes cardiaques, nous les couvrons.',
          statLabel: 'affections'
        },
        allergies: {
          title: 'Allergies',
          desc: 'Sélectionnez vos allergies parmi 250+ options. Des fruits à coque et fruits de mer courants aux allergènes plus rares — pour plus de sérénité.',
          statLabel: 'allergies'
        },
        intolerances: {
          title: 'Intolérances alimentaires',
          desc: 'Indiquez vos intolérances parmi plus de 100 options. Lactose, gluten, fructose et bien plus — pour manger avec plus de confort.',
          statLabel: 'intolérances'
        }
      },
      connectorText: 'Ensuite, à chaque scan :',
      stepLabel: 'Étape',
      step1Title: 'Scannez votre aliment',
      step1Desc: 'Scannez n\'importe quel aliment : produit emballé, plat préparé, tableau nutritionnel, code-barres ou même un menu de restaurant. Visez simplement avec la caméra et laissez SafeEat faire le reste.',
      step2Title: 'Analyse par IA',
      step2Desc: 'L\'app analyse l\'image et la compare à votre profil (maladies, allergies, intolérances) pour évaluer la compatibilité.',
      step3Title: 'Recevez un rapport',
      step3Desc: 'SafeEat fournit un rapport détaillé indiquant comment l\'aliment analysé correspond à vos besoins de santé et à votre régime alimentaire.',
      step4Title: 'Prenez votre décision',
      step4Desc: 'Grâce à ces informations personnalisées, choisissez les aliments qui vous conviennent le mieux. Mangez en confiance en sachant ce qui est bon pour votre corps.',
      processTitle: 'Voyez-le en action',
      processSubtitle: 'Regardez SafeEat analyser vos aliments en temps réel'
    },
    features: {
      title: 'Fonctionnalités puissantes',
      subtitle: 'Tout ce dont vous avez besoin pour manger plus en sécurité',
      items: [
        {
          title: 'Scannez n\'importe quel produit',
          subtitle: 'des snacks du quotidien aux fruits exotiques',
          description: 'Pointez la caméra sur n\'importe quel aliment, emballé ou non — des produits de tous les jours au fruit tropical rare — et obtenez instantanément un contrôle de sécurité. SafeEat reconnaît le produit et analyse sa composition pour vous aider à éviter les ingrédients risqués ou indésirables.',
          highlights: ['Produits emballés et non emballés', 'Vérification de sécurité instantanée', 'Analyse des ingrédients'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Scannez les étiquettes et codes-barres',
          subtitle: 'comprenez les étiquettes complexes en quelques secondes',
          description: 'Finies les étiquettes en tout petits caractères. SafeEat scanne en quelques secondes le tableau nutritionnel, la liste d\'ingrédients et le code-barres, et met en évidence immédiatement allergènes, additifs ou substances potentiellement nocives, pour que vous sachiez exactement ce que vous mangez.',
          highlights: ['Scan des tableaux nutritionnels', 'Détection des allergènes', 'Alertes sur les substances à risque'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Scannez des plats — identifiez les ingrédients',
          subtitle: 'une simple photo de votre assiette',
          description: 'Prenez votre assiette en photo et SafeEat en déduira les ingrédients probables. Que le plat soit fait maison, de cantine ou de restaurant, l\'IA peut détecter des allergènes potentiels ou des composants incompatibles avec votre régime.',
          highlights: ['Reconnaissance des ingrédients par IA', 'Fonctionne avec tout type de plat', 'Vérification de compatibilité avec votre régime'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Scannez les menus de restaurants',
          subtitle: 'trouvez des options sûres avant de commander',
          description: 'Pointez la caméra sur le menu d\'un café ou restaurant et SafeEat analysera chaque plat pour vous indiquer lesquels correspondent à vos besoins. Sortez manger plus sereinement, sans crainte des allergènes cachés ou ingrédients non souhaités.',
          highlights: ['Analyse de menus', 'Recommandations de plats sûrs', 'Détection d\'allergènes cachés'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Créez votre liste d\'aliments sûrs',
          subtitle: 'constituez votre bibliothèque d\'aliments « clean »',
          description: 'Enregistrez les aliments qui vous conviennent et créez votre propre bibliothèque d\'aliments « clean » à consulter pendant vos courses ou au restaurant. Votre base personnelle grandit à chaque scan et vous aide à décider plus vite.',
          highlights: ['Base d\'aliments personnelle', 'Consultation rapide en situation', 'Recommandations intelligentes'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Plébiscité par les utilisateurs',
      subtitle: 'Une communauté d\'utilisateurs dans le monde entier nous fait confiance.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, États-Unis',
          rating: 5,
          content: 'SafeEat a complètement changé ma façon de faire les courses ! Je suis atteint(e) de maladie cœliaque et scanner un produit ne prend que quelques secondes. Terminé, les étiquettes illisibles au supermarché.'
        },
        {
          name: 'Marcus L.',
          location: 'Londres, Royaume-Uni',
          rating: 5,
          content: 'Avec plusieurs allergies alimentaires, manger au restaurant a toujours été une source de stress pour moi. Maintenant, je scanne simplement le menu et je sais tout de suite ce qui est sûr. Un vrai game changer !'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japon',
          rating: 5,
          content: 'J\'utilise SafeEat tous les jours pour gérer mon diabète. Le scanner d\'étiquettes nutritionnelles m\'aide à suivre instantanément les glucides et le sucre. Je le recommande vivement !'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Allemagne',
          rating: 5,
          content: 'Mon fils a une allergie sévère aux fruits à coque. SafeEat me rassure lorsque j\'achète des snacks pour lui. Le scanner de codes-barres est incroyablement précis.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Espagne',
          rating: 5,
          content: 'Je suis intolérant(e) au lactose et je voyage beaucoup. SafeEat fonctionne dans toutes les langues — à l\'étranger, je scanne le menu et je peux manger beaucoup plus sereinement !'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, Inde',
          rating: 5,
          content: 'Avec les problèmes cardiaques de mon mari, nous devons surveiller de près le sodium. L\'analyse instantanée de SafeEat rend la planification des repas beaucoup plus simple.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australie',
          rating: 5,
          content: 'La fonction de scan de plats est géniale ! J\'ai pris en photo un plat au restaurant et l\'app a détecté des ingrédients contenant du gluten caché. Elle m\'a évité une mauvaise réaction !'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubaï, Émirats arabes unis',
          rating: 5,
          content: 'Je suis un régime halal strict. SafeEat m\'aide à vérifier les ingrédients des produits emballés que je ne connais pas. Application indispensable !'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Avec trois enfants ayant chacun des allergies différentes, faire les courses était un cauchemar. Maintenant, je scanne tout en quelques secondes. Cette app me sauve la vie !'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Suède',
          rating: 5,
          content: 'Je souffre de la maladie de Crohn et dois éviter certains déclencheurs. Le profil personnalisé de SafeEat repère des ingrédients que j\'aurais facilement pu rater.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brésil',
          rating: 5,
          content: 'La fonction de scan de menus est parfaite pour mon allergie aux fruits de mer. Je commande désormais avec bien plus de confiance dans les nouveaux restaurants !'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'En tant que chef sensible à certains aliments, j\'utilise SafeEat pour vérifier les ingrédients avant de cuisiner. La précision de la reconnaissance par IA est impressionnante.'
        },
        {
          name: 'Fatima A.',
          location: 'Le Caire, Égypte',
          rating: 5,
          content: 'Le diabète gestationnel était très difficile à gérer avant de découvrir SafeEat. Scanner les étiquettes alimentaires rend le suivi tellement plus simple !'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'J\'ai une intolérance rare que la plupart des applis ne gèrent pas. SafeEat m\'a permis de l\'ajouter à mon profil et elle est détectée à chaque fois maintenant !'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Pays-Bas',
          rating: 5,
          content: 'Je suis végan(e) et allergique au soja — trouver des aliments sûrs était un vrai défi ! SafeEat repère les ingrédients animaux cachés ainsi que le soja. J\'adore !'
        },
        {
          name: 'Kenji N.',
          location: 'Séoul, Corée du Sud',
          rating: 5,
          content: 'La base de données de codes-barres est énorme ! Même les produits importés sont reconnus. Idéal pour vérifier des aliments internationaux que je ne connais pas.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italie',
          rating: 5,
          content: 'Ma mère âgée a plusieurs problèmes de santé. Je lui ai créé un profil et maintenant elle peut à nouveau faire ses courses seule. Merci, SafeEat !'
        }
      ]
    },
    pricing: {
      title: 'Des tarifs simples et transparents',
      subtitle: 'Choisissez la formule qui vous convient',
      free: 'Gratuit',
      freeDesc: '5 scans gratuits pour essayer',
      freeFeature1: '5 scans IA',
      freeFeature2: 'Analyse de base',
      freeFeature3: 'Historique limité',
      weekly: 'Hebdomadaire',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Idéal pour un usage régulier',
      weeklyFeature1: 'Scans illimités',
      weeklyFeature2: 'Analyse avancée',
      weeklyFeature3: 'Historique complet',
      weeklyFeature4: 'Support prioritaire',
      annual: 'Annuel',
      annualPrice: '$29.99',
      annualDesc: 'Meilleur rapport qualité-prix — économisez 60 %',
      annualBadge: 'Le plus populaire',
      annualFeature1: 'Toutes les fonctions de l\'offre Hebdomadaire',
      annualFeature2: 'Économisez 125 $ par an',
      annualFeature3: 'Support premium',
      annualFeature4: 'Accès anticipé aux nouvelles fonctionnalités',
      button: 'Commencer'
    },
    faq: {
      title: 'Questions fréquentes',
      items: [
        {
          q: 'Que puis-je scanner avec SafeEat ?',
          a: 'SafeEat peut analyser : des listes d\'ingrédients et tableaux nutritionnels (photo), des codes-barres, des aliments emballés ou non, des plats (photo) ainsi que des menus de restaurant.'
        },
        {
          q: 'Comment fonctionne la reconnaissance de plats ?',
          a: 'L\'IA reconnaît le plat et estime les ingrédients probables. Cela fonctionne mieux avec un bon éclairage et un angle clair ; certains ingrédients cachés ou contaminations croisées peuvent ne pas être détectés.'
        },
        {
          q: 'Puis-je scanner des menus de restaurant ?',
          a: 'Oui. L\'app signale les allergènes potentiels et les conflits avec votre régime. Les menus peuvent toutefois être incomplets ; demandez toujours au personnel des précisions sur les ingrédients, huiles, friteuses partagées, sauces et risques de contamination croisée.'
        },
        {
          q: 'Quels types de problèmes de santé SafeEat prend-il en charge ?',
          a: 'Les allergies alimentaires, les intolérances (par ex. lactose, gluten) et certains besoins nutritionnels (réduction du sodium, attention aux glucides, évitement de certains ingrédients).'
        },
        {
          q: 'SafeEat fonctionne-t-il hors ligne ?',
          a: 'En général, les scans et l\'analyse par IA nécessitent une connexion Internet. Les résultats et listes enregistrés peuvent rester disponibles hors ligne selon l\'appareil et l\'app.'
        },
        {
          q: 'Quelle est la précision de l\'analyse SafeEat ?',
          a: 'Elle dépend de la qualité de la photo, de la lisibilité du texte, de la langue/du format et de l\'exhaustivité du menu. SafeEat fournit des informations ; en cas de risque élevé, vérifiez toujours par d\'autres moyens.'
        },
        {
          q: 'Que faire si SafeEat se trompe ?',
          a: 'Ne vous fiez pas à un résultat douteux ; refaites un scan plus net ; vérifiez l\'étiquette ou le site web, ou demandez au personnel ; en cas de doute ou de risque élevé, choisissez l\'option la plus sûre.'
        },
        {
          q: 'Puis-je signaler une analyse incorrecte ?',
          a: 'Oui : écrivez à support@safeeat.app avec une capture d\'écran, une photo de l\'étiquette/du menu/du plat, le pays, la marque et le produit, ainsi qu\'une explication de ce qui vous semble erroné.'
        },
        {
          q: 'Pourquoi la reconnaissance de plats peut-elle être moins précise ?',
          a: 'Des plats visuellement similaires, des ingrédients cachés, des sauces très couvrantes, un mauvais éclairage ou des photos floues, ainsi que des plats très personnalisés ou régionaux peuvent réduire la précision.'
        },
        {
          q: 'SafeEat est-il un dispositif médical ?',
          a: 'Non. Il s\'agit d\'un outil purement informatif. Pour tout conseil médical, consultez un professionnel de santé.'
        },
        {
          q: 'Quelle est la responsabilité de SafeEat ?',
          a: 'Les informations sont fournies « telles quelles » sans garantie d\'exhaustivité ou d\'absence d\'erreur. Vous restez responsable de vos choix ; des ingrédients non mentionnés ou une contamination croisée restent possibles.'
        },
        {
          q: 'Puis-je me fier uniquement à SafeEat en cas d\'allergies graves ?',
          a: 'Non. Lisez les étiquettes, interrogez le personnel, suivez votre plan d\'urgence et gardez vos médicaments prescrits avec vous ; si vous avez un doute, évitez le produit ou le plat.'
        },
        {
          q: 'Comment protégez-vous mes données de santé ?',
          a: 'Nous collectons un minimum de données et les protégeons par des mesures de sécurité et un accès limité. Pour toute question de confidentialité, contactez support@safeeat.app. Consultez aussi notre Politique de confidentialité.'
        },
        {
          q: 'Les enfants peuvent-ils utiliser SafeEat ?',
          a: 'Oui, sous la supervision d\'un adulte. En cas d\'allergies sévères ou de régimes complexes, faites particulièrement preuve de prudence et vérifiez les informations via des sources fiables.'
        },
        {
          q: 'Que dois-je faire en cas de réaction ?',
          a: 'Cherchez immédiatement une aide médicale et suivez votre plan d\'urgence ; en cas de symptômes graves, appelez les services d\'urgence. Signalez ensuite l\'incident à votre médecin, au restaurant ou au fabricant.'
        }
      ]
    },
    cta: {
      title: 'Prêt(e) à changer votre façon de manger ?',
      subtitle: 'Rejoignez des milliers de personnes qui utilisent SafeEat chaque jour',
      description: 'Téléchargez l\'app maintenant et obtenez 5 scans IA gratuits',
      button: 'Télécharger sur l\'App Store',
      feature1: 'Aucune carte bancaire requise',
      feature2: '100 % privé et sécurisé',
      feature3: 'Prêt à l\'emploi immédiatement'
    },
    newsletter: {
      title: 'Restez informé(e)',
      subtitle: 'Recevez les dernières nouvelles et des conseils pour manger plus sereinement',
      placeholder: 'Entrez votre adresse e-mail',
      button: 'S\'inscrire',
      error: 'Veuillez saisir une adresse e-mail valide',
      success: 'Merci pour votre inscription !'
    },
    seo: {
      title: 'Votre assistant personnel pour la sécurité alimentaire',
      text: 'SafeEat est une application de scan alimentaire basée sur l\'IA, destinée aux personnes souffrant d\'allergies, d\'intolérances ou de restrictions alimentaires. Scannez instantanément les étiquettes, tableaux nutritionnels, codes-barres, menus de restaurant et plats préparés pour vérifier s\'ils correspondent à votre profil. Que vous ayez la maladie cœliaque, une intolérance au lactose, une allergie aux fruits à coque ou que vous suiviez un régime sans gluten, vegan ou pauvre en sodium, SafeEat vous aide à faire des choix plus sûrs.',
      keywords: [
        'application allergies alimentaires',
        'scanner d\'ingrédients',
        'détection des allergènes',
        'scan d\'étiquettes nutritionnelles',
        'lecteur de codes-barres',
        'scanner de menus',
        'vérification sans gluten',
        'application intolérance au lactose',
        'suivi alimentaire',
        'sécurité alimentaire'
      ]
    },
    cookies: {
      bannerText: 'Nous utilisons des cookies : des cookies nécessaires au fonctionnement du site et des cookies d\'analyse uniquement avec votre consentement.',
      accept: 'Accepter',
      decline: 'Refuser'
    },
    legal: {
      backHome: 'Retour à l\'accueil'
    },
    footer: {
      product: 'Produit',
      features: 'Fonctionnalités',
      howItWorks: 'Comment ça marche',
      testimonials: 'Témoignages',
      pricing: 'Tarifs',
      faq: 'FAQ',
      contacts: 'Contact',
      legal: 'Mentions légales',
      privacy: 'Politique de confidentialité',
      terms: 'Conditions de service',
      usePolicy: 'Guide d\'utilisation',
      disclaimer: 'Avertissement',
      company: 'FJCode OU',
      register: 'Numéro d\'enregistrement 16993109',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. Tous droits réservés.'
    }
  },
  de: {
    meta: {
      home: {
        title: 'SafeEat | KI-Lebensmittelscanner für Allergien & Unverträglichkeiten',
        description: 'Scanne Etiketten, Barcodes, Speisekarten und Gerichte, um Allergene und Ernährungsrisiken basierend auf deinem Profil hervorzuheben: Allergien, Unverträglichkeiten und chronische Erkrankungen.'
      },
      privacy: {
        title: 'Datenschutzerklärung | SafeEat',
        description: 'Erfahre, wie SafeEat deine Daten erfasst, nutzt und schützt.'
      },
      terms: {
        title: 'Nutzungsbedingungen | SafeEat',
        description: 'Lies die Bedingungen, die für die Nutzung von SafeEat gelten.'
      },
      manual: {
        title: 'Benutzerhandbuch | SafeEat',
        description: 'Lerne, wie du SafeEat nutzt, um Lebensmittel sicher zu scannen.'
      },
      disclaimer: {
        title: 'Haftungsausschluss | SafeEat',
        description: 'Verstehe SafeEats Informationen, Hinweise und Einschränkungen.'
      }
    },
    nav: {
      about: 'So funktioniert\'s',
      features: 'Funktionen',
      testimonials: 'Bewertungen',
      pricing: 'Preise',
      faq: 'FAQ',
      contacts: 'Kontakt',
      getStarted: 'Loslegen'
    },
    about: {
      description: 'KI-gestützter Lebensmittelscanner, der Etiketten, Barcodes, Gerichte und Speisekarten liest und Allergene, Zutaten und Nährwerte basierend auf deinem Gesundheitsprofil hervorhebt.'
    },
    hero: {
      badge: 'KI-gestützte Lebensmittelsicherheit',
      title: 'Sofort prüfen',
      title2: 'Lebensmittelsicherheit',
      titleHighlight: 'für dich personalisiert',
      subtitle: 'Basierend auf',
      description: 'Scanne Lebensmittel, Barcodes, Zutaten und Speisekarten, um sofort zu prüfen, ob das Essen für deinen Körper sicher ist',
      typing: ['chronische Erkrankungen', 'Allergien', 'Lebensmittelunverträglichkeiten'],
      stat1: '500+',
      stat1Label: 'Chronische Erkrankungen',
      stat2: '250+',
      stat2Label: 'Allergien',
      stat3: '100+',
      stat3Label: 'Lebensmittelunverträglichkeiten',
      downloadButton: 'Im App Store laden'
    },
    howItWorks: {
      badge: 'So funktioniert\'s',
      mainTitle: 'So funktioniert SafeEat',
      subtitle: 'Unser Scan-Prozess ist einfach, schnell und liefert personalisierte Ergebnisse in wenigen, klaren Schritten.',
      setupBadge: 'Einmalige Einrichtung',
      setupTitle: 'Erstelle dein Gesundheitsprofil',
      setupDesc: 'Beim Onboarding passt du SafeEat an, indem du deine Gesundheitsdaten hinzufügst. Das passiert einmalig und ermöglicht eine personalisierte Lebensmittelanalyse — nur für dich.',
      cards: {
        diseases: {
          title: 'Chronische Erkrankungen',
          desc: 'Füge deine chronischen Erkrankungen aus unserer Datenbank mit über 500 Diagnosen hinzu. Von Diabetes bis Herz-Kreislauf-Erkrankungen — wir haben dich abgedeckt.',
          statLabel: 'Erkrankungen'
        },
        allergies: {
          title: 'Allergien',
          desc: 'Wähle deine Allergien aus über 250 Optionen. Von gängigen Nüssen und Meeresfrüchten bis zu seltenen Allergenen — bleib geschützt.',
          statLabel: 'Allergien'
        },
        intolerances: {
          title: 'Lebensmittelunverträglichkeiten',
          desc: 'Gib deine Unverträglichkeiten aus über 100 Optionen an. Laktose, Gluten, Fruktose und mehr — essen ohne Beschwerden.',
          statLabel: 'Unverträglichkeiten'
        }
      },
      connectorText: 'Und dann — jedes Mal, wenn du scannst:',
      stepLabel: 'Schritt',
      step1Title: 'Lebensmittel scannen',
      step1Desc: 'Scanne jedes Lebensmittel — einzelne Produkte, fertige Gerichte, Nährwerttabellen, Barcodes oder sogar Restaurant-Speisekarten. Einfach die Kamera darauf halten — SafeEat erledigt den Rest.',
      step2Title: 'KI-Analyse',
      step2Desc: 'Die App analysiert das Bild und gleicht es mit deinem Profil aus Erkrankungen, Allergien und Unverträglichkeiten ab, um die Verträglichkeit zu bewerten.',
      step3Title: 'Deinen Bericht erhalten',
      step3Desc: 'SafeEat liefert einen detaillierten Bericht zu den analysierten Produkten und zeigt, wie gut sie zu deinem Gesundheitsprofil und deinen Ernährungsbedürfnissen passen.',
      step4Title: 'Entscheidung treffen',
      step4Desc: 'Mit personalisierten Insights triffst du die beste Wahl für dich. Iss mit gutem Gefühl — und weiß genau, was deinem Körper guttut.',
      processTitle: 'In Aktion ansehen',
      processSubtitle: 'Sieh, wie SafeEat dein Essen in Echtzeit analysiert'
    },
    features: {
      title: 'Leistungsstarke Funktionen',
      subtitle: 'Alles, was du brauchst, um sicher zu essen',
      items: [
        {
          title: 'Jedes Produkt scannen',
          subtitle: 'von Snacks bis zu exotischen Früchten',
          description: 'Richte die Kamera auf jedes verpackte oder unverpackte Lebensmittel — von Supermarkt-Klassikern bis zu seltenen tropischen Früchten — und erhalte eine sofortige Sicherheitsbewertung. SafeEat erkennt das Produkt und analysiert die Zusammensetzung, damit du riskante oder schädliche Zutaten vermeiden kannst.',
          highlights: ['Verpackte und unverpackte Lebensmittel', 'Sofortiger Sicherheitscheck', 'Analyse der Zusammensetzung'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Nährwerttabellen und Barcodes scannen',
          subtitle: 'komplizierte Etiketten sofort verstehen',
          description: 'Kein Kampf mehr mit Kleingedrucktem oder verwirrenden Etiketten. SafeEat scannt Nährwerte, Zutatenlisten und Barcodes in Sekunden. Die App hebt sofort Allergene, Zusatzstoffe oder potenziell schädliche Stoffe hervor, damit du genau weißt, was du isst.',
          highlights: ['Nährwert-Scanning', 'Allergen-Erkennung', 'Warnungen vor schädlichen Stoffen'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Gerichte scannen – Zutaten erkennen',
          subtitle: 'anhand eines Fotos deiner Mahlzeit',
          description: 'Mach ein Foto deiner Mahlzeit, und SafeEat zerlegt sie in Zutaten. Egal ob hausgemacht, Food-Court oder Gourmetrestaurant — unsere KI erkennt potenzielle Allergene oder Zutaten, die nicht zu deiner Ernährung passen.',
          highlights: ['KI-Zutatenerkennung', 'Funktioniert mit jedem Gericht', 'Prüfung der Verträglichkeit'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Restaurant-Speisekarten scannen',
          subtitle: 'sichere Gerichte finden, bevor du bestellst',
          description: 'Halte die Kamera auf eine Speisekarte, und SafeEat analysiert jedes Gericht und zeigt dir, welche Optionen für deine Bedürfnisse sicher sind. Perfekt für Essen gehen ohne Sorgen wegen versteckter Allergene oder unerwünschter Zutaten.',
          highlights: ['Speisekartenanalyse', 'Findet sichere Gerichte', 'Erkennt versteckte Allergene'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Deine persönliche Liste sicherer Lebensmittel',
          subtitle: 'baue deine „Clean Eating"-Bibliothek auf',
          description: 'Speichere Lebensmittel, die für dich sicher sind, erstelle deine eigene „Clean Eating"-Bibliothek und greife jederzeit darauf zu — beim Einkaufen oder Essen gehen. Deine persönliche Datenbank wächst mit jedem Scan.',
          highlights: ['Persönliche Lebensmittelbibliothek', 'Schneller Zugriff überall', 'Smarte Empfehlungen'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Von Tausenden vertraut',
      subtitle: 'Eine weltweite Community vertraut auf SafeEat.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat hat komplett verändert, wie ich einkaufe! Ich habe Zöliakie, und das Scannen von Produkten dauert nur Sekunden. Schluss mit winzig gedruckten Etiketten im Laden.'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: 'Mit mehreren Lebensmittelallergien war Essen gehen immer stressig. Jetzt scanne ich einfach die Speisekarte und weiß genau, was sicher ist. Ein echter Gamechanger!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'Ich nutze SafeEat täglich, um meinen Diabetes zu managen. Der Nährwert-Scanner hilft mir sofort, Kohlenhydrate und Zucker im Blick zu behalten. Sehr empfehlenswert!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: 'Mein Sohn hat eine schwere Nussallergie. SafeEat gibt mir Ruhe beim Kauf von Snacks. Der Barcode-Scanner ist unglaublich präzise.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: 'Ich bin laktoseintolerant und reise viel. SafeEat funktioniert in allen Sprachen — ich scanne einfach Speisekarten im Ausland und esse ohne Sorgen!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'Bei der Herzkrankheit meines Mannes müssen wir auf Natrium achten. Die Sofortanalyse von SafeEat macht die Essensplanung viel einfacher.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Der Gericht-Scanner ist genial! Ich habe mein Restaurantgericht fotografiert, und er hat versteckte Glutenquellen erkannt. Das hat mich vor einer üblen Reaktion bewahrt!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'Ich folge einer strengen Halal-Ernährung. SafeEat hilft mir, Zutaten in unbekannten verpackten Lebensmitteln zu prüfen. Eine unverzichtbare App!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Mit drei Kindern und unterschiedlichen Allergien waren Einkäufe ein Albtraum. Jetzt scanne ich alles in Sekunden. Diese App ist ein Lebensretter!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'Ich habe Morbus Crohn und muss bestimmte Trigger vermeiden. SafeEats persönliches Profil findet Zutaten, die ich sonst übersehen hätte.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'Die Speisekarten-Scan-Funktion ist perfekt für meine Schalentierallergie. Ich fühle mich jetzt sicherer, in neuen Restaurants zu bestellen!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'Als Koch mit Unverträglichkeiten nutze ich SafeEat, um Zutaten vor dem Kochen zu prüfen. Die KI-Erkennung ist beeindruckend präzise.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: 'Schwangerschaftsdiabetes zu managen war überwältigend, bis ich SafeEat gefunden habe. Lebensmittel-Etiketten zu scannen macht es so einfach, den Überblick zu behalten!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Ich habe eine seltene Lebensmittelunverträglichkeit, die die meisten Apps nicht abdecken. Mit SafeEat konnte ich sie hinzufügen — und jetzt wird sie jedes Mal erkannt!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: 'Veganerin mit Sojaallergie hier — sichere Lebensmittel zu finden war so schwer! SafeEat findet versteckte tierische Produkte UND Soja. Ich liebe es!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'Die Barcode-Datenbank ist riesig! Sogar importierte Produkte werden erkannt. Perfekt, um unbekannte internationale Lebensmittel zu prüfen.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: 'Meine ältere Mutter hat mehrere Erkrankungen. Ich habe ihr Profil eingerichtet, und jetzt kann sie wieder selbstständig einkaufen. Danke, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Einfache, transparente Preise',
      subtitle: 'Wähle den Plan, der zu dir passt',
      free: 'Kostenlos',
      freeDesc: '5 kostenlose Scans zum Ausprobieren',
      freeFeature1: '5 KI-Scans',
      freeFeature2: 'Basis-Analyse',
      freeFeature3: 'Begrenzter Verlauf',
      weekly: 'Wöchentlich',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Perfekt für regelmäßige Nutzer',
      weeklyFeature1: 'Unbegrenzte Scans',
      weeklyFeature2: 'Erweiterte Analyse',
      weeklyFeature3: 'Voller Verlauf',
      weeklyFeature4: 'Priorisierter Support',
      annual: 'Jährlich',
      annualPrice: '$29.99',
      annualDesc: 'Bestes Angebot — spare 60 %',
      annualBadge: 'Am beliebtesten',
      annualFeature1: 'Alles aus Wöchentlich',
      annualFeature2: 'Spare 125 $/Jahr',
      annualFeature3: 'Premium-Support',
      annualFeature4: 'Früher Zugang zu Funktionen',
      button: 'Loslegen'
    },
    faq: {
      title: 'Häufige Fragen',
      items: [
        {
          q: 'Was kann ich mit SafeEat scannen?',
          a: 'SafeEat kann analysieren: Zutatenlisten und Nährwertangaben (Fotos), Barcodes, verpackte und unverpackte Lebensmittel, Gerichte (Fotos) sowie Restaurant-Speisekarten.'
        },
        {
          q: 'Wie funktioniert die Gerichterkennung?',
          a: 'Die KI erkennt ein Gericht und schätzt wahrscheinliche Zutaten. Am besten bei gutem Licht und passendem Winkel; versteckte Zutaten oder Kreuzkontamination können übersehen werden.'
        },
        {
          q: 'Kann ich Restaurant-Speisekarten scannen?',
          a: 'Ja. Es markiert potenzielle Allergene und Ernährungs-Konflikte. Speisekarten können unvollständig sein; kläre immer mit dem Personal Zutaten, Öle, gemeinsame Fritteusen, Saucen und Kreuzkontamination.'
        },
        {
          q: 'Welche Gesundheitszustände unterstützt SafeEat?',
          a: 'Lebensmittelallergien, Unverträglichkeiten (z. B. Laktose, Gluten) und Ernährungsanforderungen (wenig Natrium, Fokus auf Kohlenhydrate, Vermeidung bestimmter Zutaten).'
        },
        {
          q: 'Funktioniert SafeEat offline?',
          a: 'In der Regel wird für Scans/KI eine Internetverbindung benötigt. Gespeicherte Ergebnisse und Listen können je nach App und Gerät offline verfügbar sein.'
        },
        {
          q: 'Wie genau ist die Analyse von SafeEat?',
          a: 'Das hängt von der Fotoqualität, dem Etikettentext, Sprache/Format und der Vollständigkeit von Speisekarten ab. Es ist nur informativ — bei hohem Risiko immer doppelt prüfen.'
        },
        {
          q: 'Was, wenn SafeEat falsch liegt?',
          a: 'Vertraue keinem zweifelhaften Ergebnis; scanne erneut mit besserem Bild; verifiziere über Etikett/Website/Personal; wähle bei hohem Risiko die sicherste Option.'
        },
        {
          q: 'Kann ich falsche Analyseergebnisse melden?',
          a: 'Ja: support@safeeat.app — mit Screenshots, Foto von Etikett/Speisekarte/Gericht, Land/Marke/Produkt und was genau falsch wirkt.'
        },
        {
          q: 'Warum kann die Gerichterkennung weniger genau sein?',
          a: 'Ähnlich aussehende Gerichte, versteckte Zutaten, starke Saucen/Deko, schlechtes Licht/Unschärfe sowie stark angepasste oder regionale Varianten.'
        },
        {
          q: 'Ist SafeEat ein Medizinprodukt?',
          a: 'Nein. Es dient nur zur Information. Wende dich für medizinischen Rat an Fachpersonal.'
        },
        {
          q: 'Wofür übernimmt SafeEat Verantwortung?',
          a: 'Die Informationen werden „wie sie sind" bereitgestellt; Vollständigkeit kann nicht garantiert werden. Du triffst die Entscheidungen; nicht deklarierte Zutaten und Kreuzkontamination können vorkommen.'
        },
        {
          q: 'Sollte ich mich bei schweren Allergien ausschließlich auf SafeEat verlassen?',
          a: 'Nein. Lies Etiketten, frage das Personal, folge deinem Notfallplan und führe verordnete Medikamente mit; vermeide das Produkt, wenn du unsicher bist.'
        },
        {
          q: 'Wie schützt ihr meine Gesundheitsdaten?',
          a: 'Wir erfassen nur minimale Daten und schützen sie durch Sicherheitsmaßnahmen, eingeschränkten Zugriff und Datenschutzkontakt über support@safeeat.app. Siehe Datenschutzerklärung.'
        },
        {
          q: 'Kann ich SafeEat für meine Kinder nutzen?',
          a: 'Ja, unter Aufsicht. Sei besonders vorsichtig bei schweren Allergien/komplexen Ernährungsbedürfnissen; verifiziere mit verlässlichen Quellen.'
        },
        {
          q: 'Was soll ich tun, wenn ich eine Reaktion bekomme?',
          a: 'Suche sofort medizinische Hilfe, befolge deinen Notfallplan, rufe bei schweren Symptomen 112 an und melde es anschließend dem Anbieter/Restaurant/Hersteller.'
        }
      ]
    },
    cta: {
      title: 'Bereit, dein Essverhalten zu verändern?',
      subtitle: 'Schließe dich Tausenden an, die SafeEat täglich nutzen',
      description: 'Jetzt herunterladen und 5 kostenlose KI-Scans erhalten',
      button: 'Im App Store laden',
      feature1: 'Keine Kreditkarte erforderlich',
      feature2: '100 % privat und sicher',
      feature3: 'Sofortiger Zugriff'
    },
    newsletter: {
      title: 'Bleib auf dem Laufenden',
      subtitle: 'Erhalte die neuesten Updates und Gesundheitstipps',
      placeholder: 'E-Mail-Adresse eingeben',
      button: 'Abonnieren',
      error: 'Bitte eine gültige E-Mail-Adresse eingeben',
      success: 'Danke für deine Anmeldung!'
    },
    seo: {
      title: 'Dein persönlicher Assistent für Lebensmittelsicherheit',
      text: 'SafeEat ist eine KI-gestützte Lebensmittelscanner-App für Menschen mit Lebensmittelallergien, Unverträglichkeiten und Ernährungsrestriktionen. Scanne sofort Etiketten, Nährwertangaben, Barcodes, Restaurant-Speisekarten und zubereitete Gerichte, um die Sicherheit anhand deines Profils zu prüfen. Ob Zöliakie, Laktoseintoleranz, Nussallergie oder glutenfreie, vegane oder natriumarme Ernährung — SafeEat hilft dir, sicher zu wählen.',
      keywords: [
        'App für Lebensmittelallergien',
        'Zutatenscanner',
        'Allergen-Detektor',
        'Nährwertangaben-Leser',
        'Barcode-Scanner',
        'Speisekarten-Scanner',
        'Glutenfrei Check',
        'App für Laktoseintoleranz',
        'Ernährungs-Tracker',
        'Lebensmittelsicherheit'
      ]
    },
    cookies: {
      bannerText: 'Wir verwenden Cookies: notwendige Cookies zum Betrieb der Website und Analyse-Cookies nur mit deiner Zustimmung.',
      decline: 'Ablehnen',
      accept: 'Akzeptieren'
    },
    legal: {
      backHome: 'Zurück zur Startseite'
    },
    footer: {
      product: 'Produkt',
      features: 'Funktionen',
      howItWorks: 'So funktioniert\'s',
      testimonials: 'Bewertungen',
      pricing: 'Preise',
      faq: 'FAQ',
      contacts: 'Kontakt',
      legal: 'Rechtliches',
      privacy: 'Datenschutzerklärung',
      terms: 'Nutzungsbedingungen',
      usePolicy: 'Benutzerhandbuch',
      disclaimer: 'Haftungsausschluss',
      company: 'FJCode OU',
      register: 'Registrierungsnummer 16993109',
      address: 'Viru väljak 2, 10111 Tallinn, Estland',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. Alle Rechte vorbehalten.'
    }
  },
  el: {
    meta: {
      home: {
        title: 'SafeEat | AI σαρωτής τροφίμων για αλλεργίες & δυσανεξίες',
        description: 'Σάρωσε ετικέτες, barcodes, μενού και πιάτα για να επισημάνεις αλλεργιογόνα και διατροφικούς κινδύνους με βάση το προφίλ σου: αλλεργίες, δυσανεξίες και χρόνιες παθήσεις.'
      },
      privacy: {
        title: 'Πολιτική Απορρήτου | SafeEat',
        description: 'Δες πώς το SafeEat συλλέγει, χρησιμοποιεί και προστατεύει τα δεδομένα σου.'
      },
      terms: {
        title: 'Όροι Χρήσης | SafeEat',
        description: 'Διάβασε τους όρους που ισχύουν για τη χρήση του SafeEat.'
      },
      manual: {
        title: 'Οδηγός Χρήσης | SafeEat',
        description: 'Μάθε πώς να χρησιμοποιείς το SafeEat για ασφαλή σάρωση τροφίμων.'
      },
      disclaimer: {
        title: 'Αποποίηση Ευθύνης | SafeEat',
        description: 'Κατανόησε τις πληροφορίες, την καθοδήγηση και τους περιορισμούς του SafeEat.'
      }
    },
    nav: {
      about: 'Πώς λειτουργεί',
      features: 'Λειτουργίες',
      testimonials: 'Αξιολογήσεις',
      pricing: 'Τιμές',
      faq: 'Συχνές ερωτήσεις',
      contacts: 'Επικοινωνία',
      getStarted: 'Ξεκίνα'
    },
    about: {
      description: 'AI σαρωτής τροφίμων που διαβάζει ετικέτες, barcodes, πιάτα και μενού και επισημαίνει αλλεργιογόνα, συστατικά και διατροφικά στοιχεία με βάση το προφίλ υγείας σου.'
    },
    hero: {
      badge: 'Ασφάλεια τροφίμων με AI',
      title: 'Έλεγξε άμεσα',
      title2: 'την ασφάλεια τροφίμων',
      titleHighlight: 'για εσένα',
      subtitle: 'Με βάση',
      description: 'Σάρωσε τρόφιμα, barcodes, συστατικά και μενού για να ελέγξεις άμεσα αν είναι ασφαλή για το σώμα σου',
      typing: ['χρόνιες παθήσεις', 'αλλεργίες', 'τροφικές δυσανεξίες'],
      stat1: '500+',
      stat1Label: 'Χρόνιες παθήσεις',
      stat2: '250+',
      stat2Label: 'Αλλεργίες',
      stat3: '100+',
      stat3Label: 'Τροφικές δυσανεξίες',
      downloadButton: 'Λήψη στο App Store'
    },
    howItWorks: {
      badge: 'Πώς λειτουργεί',
      mainTitle: 'Πώς λειτουργεί το SafeEat',
      subtitle: 'Η διαδικασία σάρωσης είναι απλή και γρήγορη — με εξατομικευμένα αποτελέσματα σε λίγα βήματα.',
      setupBadge: 'Εφάπαξ ρύθμιση',
      setupTitle: 'Δημιούργησε το προφίλ υγείας σου',
      setupDesc: 'Κατά την αρχική ρύθμιση, προσαρμόζεις το SafeEat προσθέτοντας τα στοιχεία υγείας σου. Γίνεται μία φορά και επιτρέπει εξατομικευμένη ανάλυση τροφίμων ειδικά για εσένα.',
      cards: {
        diseases: {
          title: 'Χρόνιες παθήσεις',
          desc: 'Πρόσθεσε τις χρόνιες παθήσεις σου από τη βάση μας με 500+ καταστάσεις. Από διαβήτη μέχρι καρδιαγγειακές παθήσεις — σε καλύπτουμε.',
          statLabel: 'παθήσεις'
        },
        allergies: {
          title: 'Αλλεργίες',
          desc: 'Επίλεξε τις αλλεργίες σου από 250+ επιλογές. Από συνηθισμένους ξηρούς καρπούς και οστρακοειδή μέχρι σπάνια αλλεργιογόνα — μείνε προστατευμένος/η.',
          statLabel: 'αλλεργίες'
        },
        intolerances: {
          title: 'Τροφικές δυσανεξίες',
          desc: 'Δήλωσε τις δυσανεξίες σου από 100+ επιλογές. Λακτόζη, γλουτένη, φρουκτόζη και άλλα — φάε χωρίς ενόχληση.',
          statLabel: 'δυσανεξίες'
        }
      },
      connectorText: 'Και μετά — κάθε φορά που σαρώνεις:',
      stepLabel: 'Βήμα',
      step1Title: 'Σάρωσε το φαγητό',
      step1Desc: 'Σάρωσε οποιοδήποτε τρόφιμο — μεμονωμένα προϊόντα, έτοιμα πιάτα, διατροφικές ετικέτες, barcodes ή ακόμη και μενού εστιατορίων. Απλώς στόχευσε με την κάμερα και το SafeEat κάνει τα υπόλοιπα.',
      step2Title: 'Ανάλυση AI',
      step2Desc: 'Η εφαρμογή αναλύει την εικόνα και τη συγκρίνει με το προφίλ σου (παθήσεις, αλλεργίες και δυσανεξίες) για να αξιολογήσει τη συμβατότητα.',
      step3Title: 'Λάβε την αναφορά σου',
      step3Desc: 'Το SafeEat παρέχει λεπτομερή αναφορά για τα αναλυμένα προϊόντα και δείχνει πόσο ταιριάζουν με το προφίλ υγείας σου και τις διατροφικές σου ανάγκες.',
      step4Title: 'Πάρε την απόφασή σου',
      step4Desc: 'Με εξατομικευμένες πληροφορίες, επιλέγεις το φαγητό που είναι καλύτερο για εσένα. Φάε με ηρεμία — και γνώριζε τι ταιριάζει στο σώμα σου.',
      processTitle: 'Δες το στην πράξη',
      processSubtitle: 'Δες πώς το SafeEat αναλύει το φαγητό σου σε πραγματικό χρόνο'
    },
    features: {
      title: 'Ισχυρές λειτουργίες',
      subtitle: 'Ό,τι χρειάζεσαι για να τρως με ασφάλεια',
      items: [
        {
          title: 'Σάρωσε οποιοδήποτε προϊόν',
          subtitle: 'από καθημερινά σνακ έως εξωτικά φρούτα',
          description: 'Στρέψε την κάμερα σε οποιοδήποτε συσκευασμένο ή μη συσκευασμένο τρόφιμο — από κλασικά προϊόντα σούπερ μάρκετ μέχρι σπάνια τροπικά φρούτα — και λάβε άμεση αξιολόγηση ασφάλειας. Το SafeEat αναγνωρίζει το προϊόν και αναλύει τη σύνθεσή του, ώστε να αποφεύγεις επικίνδυνα ή επιβλαβή συστατικά.',
          highlights: ['Συσκευασμένα & μη συσκευασμένα τρόφιμα', 'Άμεσος έλεγχος ασφάλειας', 'Ανάλυση σύνθεσης'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Σάρωσε διατροφικές ετικέτες και barcodes',
          subtitle: 'αποκωδικοποίησε δύσκολες ετικέτες αμέσως',
          description: 'Τέλος στις δυσκολίες με ψιλά γράμματα ή μπερδεμένες ετικέτες. Το SafeEat μπορεί να σαρώσει διατροφικά στοιχεία, λίστες συστατικών και barcodes σε λίγα δευτερόλεπτα. Η εφαρμογή επισημαίνει άμεσα αλλεργιογόνα, πρόσθετα ή δυνητικά επιβλαβείς ουσίες, ώστε να ξέρεις τι καταναλώνεις.',
          highlights: ['Σάρωση διατροφικών στοιχείων', 'Ανίχνευση αλλεργιογόνων', 'Προειδοποιήσεις για επιβλαβείς ουσίες'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Σάρωσε πιάτα — αναγνώρισε συστατικά',
          subtitle: 'από μια φωτογραφία του γεύματός σου',
          description: 'Τράβηξε φωτογραφία του γεύματός σου και το SafeEat το «σπάει» σε συστατικά. Είτε είναι σπιτικό, από food court ή από γκουρμέ εστιατόριο, η AI μας εντοπίζει πιθανά αλλεργιογόνα ή συστατικά που δεν ταιριάζουν στη διατροφή σου.',
          highlights: ['Αναγνώριση συστατικών με AI', 'Λειτουργεί με κάθε πιάτο', 'Έλεγχος συμβατότητας διατροφής'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Σάρωσε μενού εστιατορίων',
          subtitle: 'βρες ασφαλή πιάτα πριν παραγγείλεις',
          description: 'Στόχευσε με την κάμερα ένα μενού καφέ ή εστιατορίου και το SafeEat αναλύει κάθε πιάτο, δείχνοντάς σου ποια είναι ασφαλή με βάση τις ανάγκες σου. Ιδανικό για έξοδο χωρίς άγχος για κρυμμένα αλλεργιογόνα ή ανεπιθύμητα συστατικά.',
          highlights: ['Ανάλυση μενού', 'Εντοπισμός ασφαλών πιάτων', 'Ανίχνευση κρυφών αλλεργιογόνων'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Φτιάξε την προσωπική σου λίστα ασφαλών τροφίμων',
          subtitle: 'δημιούργησε τη δική σου βιβλιοθήκη «clean eating»',
          description: 'Αποθήκευσε τρόφιμα που είναι ασφαλή για εσένα, δημιούργησε τη δική σου βιβλιοθήκη «clean eating» και πρόσβασε σε αυτήν οποιαδήποτε στιγμή — στις αγορές ή όταν τρως έξω. Η προσωπική σου βάση δεδομένων μεγαλώνει με κάθε σάρωση.',
          highlights: ['Προσωπική βιβλιοθήκη τροφίμων', 'Γρήγορη πρόσβαση παντού', 'Έξυπνες προτάσεις'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Το εμπιστεύονται χιλιάδες',
      subtitle: 'Το εμπιστεύεται μια παγκόσμια κοινότητα χρηστών.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'Το SafeEat άλλαξε εντελώς τον τρόπο που κάνω ψώνια! Έχω κοιλιοκάκη και η σάρωση προϊόντων παίρνει μόνο λίγα δευτερόλεπτα. Τέλος στο διάβασμα μικροσκοπικών ετικετών στο κατάστημα.'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: 'Με πολλαπλές τροφικές αλλεργίες, το να τρώω έξω ήταν πάντα αγχωτικό. Τώρα σαρώνω το μενού και ξέρω ακριβώς τι είναι ασφαλές. Πραγματικός gamechanger!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'Χρησιμοποιώ το SafeEat καθημερινά για να διαχειρίζομαι τον διαβήτη μου. Η σάρωση διατροφικών στοιχείων με βοηθά να παρακολουθώ υδατάνθρακες και ζάχαρη άμεσα. Το προτείνω ανεπιφύλακτα!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: 'Ο γιος μου έχει σοβαρή αλλεργία στους ξηρούς καρπούς. Το SafeEat μου δίνει ηρεμία όταν αγοράζω σνακ για εκείνον. Ο σαρωτής barcode είναι απίστευτα ακριβής.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: 'Έχω δυσανεξία στη λακτόζη και ταξιδεύω πολύ. Το SafeEat λειτουργεί σε όλες τις γλώσσες — απλώς σαρώνω μενού στο εξωτερικό και τρώω χωρίς άγχος!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'Η διαχείριση της καρδιοπάθειας του συζύγου μου σημαίνει ότι πρέπει να προσέχουμε το νάτριο. Η άμεση ανάλυση του SafeEat κάνει τον προγραμματισμό γευμάτων πολύ πιο εύκολο.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Ο σαρωτής πιάτων είναι φοβερός! Φωτογράφισα το πιάτο μου σε εστιατόριο και εντόπισε κρυφά συστατικά με γλουτένη. Με έσωσε από άσχημη αντίδραση!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'Ακολουθώ αυστηρή halal διατροφή. Το SafeEat με βοηθά να ελέγχω συστατικά σε συσκευασμένα τρόφιμα που δεν γνωρίζω. Απαραίτητη εφαρμογή!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Με τρία παιδιά και διαφορετικές αλλεργίες, τα ψώνια ήταν εφιάλτης. Τώρα σαρώνω τα πάντα σε δευτερόλεπτα. Αυτή η εφαρμογή είναι σωτήρια!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'Έχω νόσο Crohn και πρέπει να αποφεύγω συγκεκριμένα «triggers». Το εξατομικευμένο προφίλ του SafeEat εντοπίζει συστατικά που αλλιώς θα μου ξέφευγαν.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'Η σάρωση μενού είναι τέλεια για την αλλεργία μου στα οστρακοειδή. Νιώθω πιο σίγουρος/η όταν παραγγέλνω σε νέα εστιατόρια!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'Ως σεφ με ευαισθησίες, χρησιμοποιώ το SafeEat για να ελέγχω συστατικά πριν μαγειρέψω. Η αναγνώριση με AI είναι εντυπωσιακά ακριβής.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: 'Η διαχείριση του διαβήτη κύησης ήταν δύσκολη μέχρι να βρω το SafeEat. Η σάρωση ετικετών τροφίμων κάνει την παρακολούθηση τόσο απλή!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Έχω μια σπάνια τροφική δυσανεξία που οι περισσότερες εφαρμογές δεν καλύπτουν. Το SafeEat μου επέτρεψε να την προσθέσω στο προφίλ μου και πλέον την εντοπίζει κάθε φορά!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: 'Βίγκαν με αλλεργία στη σόγια εδώ — ήταν τόσο δύσκολο να βρω ασφαλές φαγητό! Το SafeEat βρίσκει κρυφά ζωικά προϊόντα ΚΑΙ σόγια. Το λατρεύω!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'Η βάση δεδομένων barcodes είναι τεράστια! Ακόμα και εισαγόμενα προϊόντα αναγνωρίζονται. Ιδανικό για έλεγχο άγνωστων διεθνών τροφίμων.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: 'Η ηλικιωμένη μητέρα μου έχει πολλαπλές παθήσεις. Έστησα το προφίλ της και τώρα μπορεί να ψωνίζει μόνη της ξανά. Ευχαριστώ, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Απλές, διαφανείς τιμές',
      subtitle: 'Διάλεξε το πλάνο που σου ταιριάζει',
      free: 'Δωρεάν',
      freeDesc: '5 δωρεάν σαρώσεις για δοκιμή',
      freeFeature1: '5 σαρώσεις AI',
      freeFeature2: 'Βασική ανάλυση',
      freeFeature3: 'Περιορισμένο ιστορικό',
      weekly: 'Εβδομαδιαίο',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Ιδανικό για τακτική χρήση',
      weeklyFeature1: 'Απεριόριστες σαρώσεις',
      weeklyFeature2: 'Προηγμένη ανάλυση',
      weeklyFeature3: 'Πλήρες ιστορικό',
      weeklyFeature4: 'Προτεραιότητα υποστήριξης',
      annual: 'Ετήσιο',
      annualPrice: '$29.99',
      annualDesc: 'Καλύτερη αξία — εξοικονόμησε 60%',
      annualBadge: 'Πιο δημοφιλές',
      annualFeature1: 'Όλα από το Εβδομαδιαίο',
      annualFeature2: 'Εξοικονόμησε $125/έτος',
      annualFeature3: 'Premium υποστήριξη',
      annualFeature4: 'Πρόωρη πρόσβαση σε λειτουργίες',
      button: 'Ξεκίνα'
    },
    faq: {
      title: 'Συχνές ερωτήσεις',
      items: [
        {
          q: 'Τι μπορώ να σκανάρω με το SafeEat;',
          a: 'Το SafeEat μπορεί να αναλύσει: λίστες συστατικών και διατροφικές δηλώσεις (φωτογραφίες), barcodes, συσκευασμένα και μη συσκευασμένα τρόφιμα, πιάτα (φωτογραφίες) και μενού εστιατορίων.'
        },
        {
          q: 'Πώς λειτουργεί η αναγνώριση πιάτων;',
          a: 'Η AI αναγνωρίζει ένα πιάτο και εκτιμά πιθανά συστατικά. Λειτουργεί καλύτερα με καλό φωτισμό και σωστή γωνία· κρυφά συστατικά ή διασταυρούμενη επιμόλυνση μπορεί να μην εντοπιστούν.'
        },
        {
          q: 'Μπορώ να σκανάρω μενού εστιατορίων;',
          a: 'Ναι. Επισημαίνει πιθανά αλλεργιογόνα και διατροφικές ασυμβατότητες. Τα μενού μπορεί να είναι ελλιπή· πάντα επιβεβαίωσε με το προσωπικό για συστατικά, έλαια, κοινές φριτέζες, σάλτσες και διασταυρούμενη επιμόλυνση.'
        },
        {
          q: 'Ποιες παθήσεις υποστηρίζει το SafeEat;',
          a: 'Τροφικές αλλεργίες, δυσανεξίες (π.χ. λακτόζη, γλουτένη) και διατροφικές ανάγκες (χαμηλό νάτριο, έλεγχος υδατανθράκων, αποφυγή συγκεκριμένων συστατικών).'
        },
        {
          q: 'Λειτουργεί το SafeEat χωρίς σύνδεση στο διαδίκτυο;',
          a: 'Συνήθως απαιτείται internet για σαρώσεις/AI. Τα αποθηκευμένα αποτελέσματα και οι λίστες μπορεί να είναι διαθέσιμα offline ανάλογα με την εφαρμογή και τη συσκευή.'
        },
        {
          q: 'Πόσο ακριβής είναι η ανάλυση του SafeEat;',
          a: 'Εξαρτάται από την ποιότητα της φωτογραφίας, το κείμενο της ετικέτας, τη γλώσσα/μορφή και το πόσο πλήρη είναι τα μενού. Είναι μόνο ενημερωτικό — έλεγξε πάντα διπλά όταν ο κίνδυνος είναι υψηλός.'
        },
        {
          q: 'Τι γίνεται αν το SafeEat κάνει λάθος;',
          a: 'Μην βασίζεσαι σε αμφίβολο αποτέλεσμα· σάρωσε ξανά με καλύτερη εικόνα· επιβεβαίωσε από ετικέτα/ιστοσελίδα/προσωπικό· σε υψηλό ρίσκο επίλεξε την πιο ασφαλή επιλογή.'
        },
        {
          q: 'Μπορώ να αναφέρω λανθασμένα αποτελέσματα;',
          a: 'Ναι: support@safeeat.app με screenshots, φωτογραφία ετικέτας/μενού/πιάτου, χώρα/μάρκα/προϊόν και τι φαίνεται λάθος.'
        },
        {
          q: 'Γιατί η αναγνώριση πιάτων μπορεί να είναι λιγότερο ακριβής;',
          a: 'Πιάτα που μοιάζουν μεταξύ τους, κρυφά συστατικά, έντονες σάλτσες/γαρνιτούρες, χαμηλός φωτισμός/θόλωση και πολύ προσαρμοσμένες ή τοπικές παραλλαγές.'
        },
        {
          q: 'Είναι το SafeEat ιατρική συσκευή;',
          a: 'Όχι. Είναι μόνο για ενημέρωση. Συμβουλεύσου επαγγελματία υγείας για ιατρικές συμβουλές.'
        },
        {
          q: 'Ποια είναι η ευθύνη του SafeEat;',
          a: 'Οι πληροφορίες παρέχονται «ως έχουν»· δεν μπορεί να εγγυηθεί πληρότητα. Εσύ είσαι υπεύθυνος/η για τις επιλογές σου· μη δηλωμένα συστατικά και διασταυρούμενη επιμόλυνση μπορεί να συμβούν.'
        },
        {
          q: 'Πρέπει να βασίζομαι αποκλειστικά στο SafeEat σε σοβαρές αλλεργίες;',
          a: 'Όχι. Διάβαζε ετικέτες, ρώτα το προσωπικό, ακολούθησε το σχέδιο δράσης σου και να έχεις μαζί σου τα συνταγογραφημένα φάρμακα· αν αμφιβάλλεις, απόφυγε το προϊόν.'
        },
        {
          q: 'Πώς προστατεύετε τα δεδομένα υγείας μου;',
          a: 'Συλλέγουμε τα ελάχιστα δεδομένα και τα προστατεύουμε με μέτρα ασφαλείας, περιορισμένη πρόσβαση και επικοινωνία απορρήτου στο support@safeeat.app. Δες την Πολιτική Απορρήτου.'
        },
        {
          q: 'Μπορώ να χρησιμοποιήσω το SafeEat για τα παιδιά μου;',
          a: 'Ναι, υπό επίβλεψη. Να είσαι ιδιαίτερα προσεκτικός/ή σε σοβαρές αλλεργίες/σύνθετες ανάγκες και επιβεβαίωνε από αξιόπιστες πηγές.'
        },
        {
          q: 'Τι να κάνω αν έχω αντίδραση;',
          a: 'Ζήτησε άμεσα ιατρική βοήθεια, ακολούθησε το σχέδιο έκτακτης ανάγκης, κάλεσε το 112 για σοβαρά συμπτώματα και στη συνέχεια ενημέρωσε πάροχο/εστιατόριο/παραγωγό.'
        }
      ]
    },
    cta: {
      title: 'Έτοιμος/η να αλλάξεις τον τρόπο που τρως;',
      subtitle: 'Γίνε μέλος των χιλιάδων που χρησιμοποιούν το SafeEat κάθε μέρα',
      description: 'Κατέβασε τώρα και πάρε 5 δωρεάν σαρώσεις AI',
      button: 'Λήψη στο App Store',
      feature1: 'Δεν απαιτείται κάρτα',
      feature2: '100% ιδιωτικό & ασφαλές',
      feature3: 'Άμεση πρόσβαση'
    },
    newsletter: {
      title: 'Μείνε ενημερωμένος/η',
      subtitle: 'Λάβε τις τελευταίες ενημερώσεις και συμβουλές υγείας',
      placeholder: 'Εισάγετε το email σας',
      button: 'Εγγραφή',
      error: 'Εισάγετε έγκυρη διεύθυνση email',
      success: 'Ευχαριστούμε για την εγγραφή!'
    },
    seo: {
      title: 'Ο προσωπικός σου βοηθός για την ασφάλεια τροφίμων',
      text: 'Το SafeEat είναι εφαρμογή σαρωτή τροφίμων με AI για άτομα με τροφικές αλλεργίες, δυσανεξίες και διατροφικούς περιορισμούς. Σάρωσε άμεσα ετικέτες, διατροφικές δηλώσεις, barcodes, μενού εστιατορίων και έτοιμα πιάτα για να ελέγξεις την ασφάλεια βάσει του προφίλ σου. Είτε έχεις κοιλιοκάκη, δυσανεξία στη λακτόζη, αλλεργία σε ξηρούς καρπούς ή ακολουθείς δίαιτα χωρίς γλουτένη, vegan ή χαμηλή σε νάτριο, το SafeEat σε βοηθά να επιλέγεις με ασφάλεια.',
      keywords: [
        'Εφαρμογή για τροφικές αλλεργίες',
        'Σαρωτής συστατικών',
        'Ανιχνευτής αλλεργιογόνων',
        'Ανάγνωση διατροφικών ετικετών',
        'Σαρωτής barcode',
        'Σαρωτής μενού',
        'Έλεγχος χωρίς γλουτένη',
        'Εφαρμογή για δυσανεξία στη λακτόζη',
        'Παρακολούθηση διατροφής',
        'Ασφάλεια τροφίμων'
      ]
    },
    cookies: {
      bannerText: 'Χρησιμοποιούμε cookies: απαραίτητα cookies για τη λειτουργία του site και cookies ανάλυσης μόνο με τη συγκατάθεσή σου.',
      decline: 'Απόρριψη',
      accept: 'Αποδοχή'
    },
    legal: {
      backHome: 'Επιστροφή στην αρχική'
    },
    footer: {
      product: 'Προϊόν',
      features: 'Λειτουργίες',
      howItWorks: 'Πώς λειτουργεί',
      testimonials: 'Αξιολογήσεις',
      pricing: 'Τιμές',
      faq: 'Συχνές ερωτήσεις',
      contacts: 'Επικοινωνία',
      legal: 'Νομικά',
      privacy: 'Πολιτική Απορρήτου',
      terms: 'Όροι Χρήσης',
      usePolicy: 'Οδηγός Χρήσης',
      disclaimer: 'Αποποίηση Ευθύνης',
      company: 'FJCode OU',
      register: 'Αριθμός μητρώου 16993109',
      address: 'Viru väljak 2, 10111 Tallinn, Εσθονία',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. Με επιφύλαξη παντός δικαιώματος.'
    }
  },
  he: {
    meta: {
      home: {
        title: 'SafeEat | סורק מזון מבוסס בינה מלאכותית לאלרגיות ואי־סבילות',
        description: 'סרקו תוויות, ברקודים, תפריטים ומנות כדי לסמן אלרגנים וסיכונים תזונתיים לפי הפרופיל האישי שלכם: אלרגיות, אי־סבילות ומחלות כרוניות.'
      },
      privacy: {
        title: 'מדיניות פרטיות | SafeEat',
        description: 'גלו כיצד SafeEat אוספת, משתמשת ומגנה על המידע שלכם.'
      },
      terms: {
        title: 'תנאי שימוש | SafeEat',
        description: 'עיינו בתנאים החלים על השימוש שלכם ב‑SafeEat.'
      },
      manual: {
        title: 'מדריך משתמש | SafeEat',
        description: 'למדו כיצד להשתמש ב‑SafeEat בצורה בטוחה לסריקת מזון.'
      },
      disclaimer: {
        title: 'כתב ויתור | SafeEat',
        description: 'הכירו את מגבלות השימוש והמידע שמספקת SafeEat.'
      }
    },
    nav: {
      about: 'איך זה עובד',
      features: 'יכולות',
      testimonials: 'חוות דעת',
      pricing: 'תמחור',
      faq: 'שאלות נפוצות',
      contacts: 'צור קשר',
      getStarted: 'התחילו עכשיו'
    },
    about: {
      description: 'סורק מזון מבוסס בינה מלאכותית שקורא תוויות, ברקודים, מנות ותפריטים, ומדגיש אלרגנים, רכיבים ומידע תזונתי לפי הפרופיל הרפואי שלכם.'
    },
    hero: {
      badge: 'בטיחות מזון עם בינה מלאכותית',
      title: 'גלו מיד',
      title2: 'אם המזון בטוח',
      titleHighlight: 'עבורכם',
      subtitle: 'בהתאם ל',
      description: 'סרקו מזון, ברקודים, רשימות רכיבים ותפריטים ובדקו בשניות אם הם מתאימים לגוף שלכם.',
      typing: ['מחלות כרוניות', 'אלרגיות', 'אי־סבילות למזון'],
      stat1: '500+',
      stat1Label: 'מחלות כרוניות',
      stat2: '250+',
      stat2Label: 'אלרגיות',
      stat3: '100+',
      stat3Label: 'אי־סבילות למזון',
      downloadButton: 'הורדה ב‑App Store'
    },
    howItWorks: {
      badge: 'איך זה עובד',
      mainTitle: 'איך SafeEat עובד',
      subtitle: 'תהליך הסריקה שלנו פשוט ומהיר — ומספק תוצאות מותאמות אישית בכמה צעדים בלבד.',
      setupBadge: 'הגדרה חד־פעמית',
      setupTitle: 'יצירת פרופיל בריאות אישי',
      setupDesc: 'במהלך ההגדרה הראשונית הוסיפו את המידע הרפואי שלכם כדי להתאים את SafeEat אליכם. עושים זאת פעם אחת, ומאותו רגע כל ניתוח מותאם לפרופיל שלכם.',
      cards: {
        diseases: {
          title: 'מחלות כרוניות',
          desc: 'הוסיפו את המחלות הכרוניות שלכם מתוך מאגר של יותר מ‑500 מצבים רפואיים. מסוכרת ועד בעיות לב — אנחנו מכסים אותם.',
          statLabel: 'מצבים'
        },
        allergies: {
          title: 'אלרגיות',
          desc: 'בחרו את האלרגיות שלכם מתוך למעלה מ‑250 אפשרויות. מאגוזים ופירות ים נפוצים ועד אלרגנים נדירים — לשקט נפשי גדול יותר.',
          statLabel: 'אלרגיות'
        },
        intolerances: {
          title: 'אי־סבילות למזון',
          desc: 'ציינו את סוגי אי־הסבילות שלכם מתוך יותר מ‑100 אפשרויות. לקטוז, גלוטן, פרוקטוז ועוד — כדי שתוכלו לאכול בנוחות.',
          statLabel: 'אי־סבילות'
        }
      },
      connectorText: 'לאחר מכן, בכל סריקה:',
      stepLabel: 'שלב',
      step1Title: 'סרקו את המזון',
      step1Desc: 'סרקו כל מזון — מוצר בודד, מנה מוכנה, טבלת ערכים תזונתיים, ברקוד או אפילו תפריט במסעדה. פשוט כוונו את המצלמה ו‑SafeEat תעשה את השאר.',
      step2Title: 'ניתוח מבוסס בינה מלאכותית',
      step2Desc: 'האפליקציה מנתחת את התמונה ומשווה אותה לפרופיל שלכם (מחלות, אלרגיות, אי־סבילות) כדי להעריך התאמה.',
      step3Title: 'קבלו דו"ח',
      step3Desc: 'SafeEat מציגה דו"ח מפורט שמראה עד כמה המזון שסרקתם מתאים לצרכים הרפואיים והתזונתיים שלכם.',
      step4Title: 'קבלו החלטה מודעת',
      step4Desc: 'בהתבסס על התובנות המותאמות אישית, תוכלו לבחור את המזון שהכי מתאים לכם. אכלו בביטחון, כשאתם יודעים מה טוב לגוף שלכם.',
      processTitle: 'ראו איך זה עובד בפועל',
      processSubtitle: 'צפו ב‑SafeEat מנתחת את המזון שלכם בזמן אמת'
    },
    features: {
      title: 'יכולות חזקות',
      subtitle: 'כל מה שצריך כדי לאכול בבטיחות גבוהה יותר',
      items: [
        {
          title: 'סריקה של כל מוצר מזון',
          subtitle: 'מחטיפים יומיומיים ועד פירות אקזוטיים',
          description: 'כוונו את המצלמה אל כל מזון ארוז או בתפזורת — ממוצרים יומיומיים בסופר ועד פירות טרופיים נדירים — וקבלו מיד בדיקת בטיחות. SafeEat מזהה את המוצר ומנתחת את רכיביו כדי לעזור לכם להימנע מחומרים מסוכנים או לא רצויים.',
          highlights: ['מזון ארוז ובתפזורת', 'בדיקת בטיחות מיידית', 'ניתוח רכיבים'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'סריקת תוויות תזונה וברקודים',
          subtitle: 'הבינו תוויות מורכבות בשניות',
          description: 'לא צריך יותר להתאמץ לקרוא אותיות קטנות. SafeEat סורקת בתוך שניות את טבלת הערכים התזונתיים, רשימת הרכיבים והברקוד, ומדגישה מיד אלרגנים, תוספים או חומרים שעלולים להיות מזיקים — כדי שתדעו בדיוק מה אתם אוכלים.',
          highlights: ['סריקת טבלאות תזונה', 'זיהוי אלרגנים', 'אזהרות על חומרים בסיכון'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'סריקת מנות — זיהוי רכיבים',
          subtitle: 'פשוט צלמו את הצלחת שלכם',
          description: 'צלמו את המנה שלכם ו‑SafeEat תפרק אותה לרכיבים הסתברותיים. בין אם מדובר באוכל ביתי, במזנון או במסעדה — הבינה המלאכותית יכולה לזהות אלרגנים אפשריים או רכיבים שאינם מתאימים לתזונה שלכם.',
          highlights: ['זיהוי רכיבים בעזרת בינה מלאכותית', 'עובד עם כל סוג מנה', 'בדיקת התאמה לתזונה שלכם'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'סריקת תפריטי מסעדות',
          subtitle: 'מצאו מנות בטוחות לפני ההזמנה',
          description: 'כוונו את המצלמה אל תפריט בבית קפה או מסעדה ו‑SafeEat תנתח כל מנה ותראה לכם אילו אפשרויות מתאימות לצרכים הבריאותיים שלכם. צאו לאכול בחוץ בראש שקט, בלי חשש מאלרגנים נסתרים או רכיבים לא רצויים.',
          highlights: ['ניתוח תפריטים', 'המלצות על מנות בטוחות', 'איתור אלרגנים נסתרים'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'יצירת רשימת המזונות הבטוחים שלכם',
          subtitle: 'בנו לעצמכם ספריית תזונה "נקייה"',
          description: 'שמרו מזונות שמתאימים לכם ובנו ספריית מזונות "נקייה" אישית, זמינה לכם בקניות או במסעדה. מאגר המזון האישי שלכם גדל עם כל סריקה ועוזר לכם לקבל החלטות מהירות יותר.',
          highlights: ['מאגר מזון אישי', 'בדיקה מהירה בכל מצב', 'המלצות חכמות'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'מוערכת על ידי משתמשים',
      subtitle: 'קהילת משתמשים מרחבי העולם סומכת עלינו.',
      items: [
        {
          name: 'Sarah M.',
          location: 'ניו יורק, ארצות הברית',
          rating: 5,
          content: 'SafeEat שינתה לחלוטין את הדרך שבה אני עושה קניות! יש לי צליאק וסריקת מוצרים לוקחת כמה שניות בלבד. לא צריך יותר לשבור את העיניים על תוויות קטנות בסופר.'
        },
        {
          name: 'Marcus L.',
          location: 'לונדון, בריטניה',
          rating: 5,
          content: 'כאדם עם כמה אלרגיות למזון, יציאה לאכול בחוץ תמיד הייתה מלחיצה. עכשיו אני פשוט סורק/ת את התפריט ויודע/ת מיד מה בטוח. שינוי משחק אמיתי!'
        },
        {
          name: 'Yuki T.',
          location: 'טוקיו, יפן',
          rating: 5,
          content: 'אני משתמש/ת ב‑SafeEat כל יום כדי לנהל את הסוכרת שלי. סורק התוויות התזונתיות עוזר לי לעקוב מיידית אחרי פחמימות וסוכר. מומלץ בחום!'
        },
        {
          name: 'Anna K.',
          location: 'ברלין, גרמניה',
          rating: 5,
          content: 'לבני יש אלרגיה חמורה לאגוזים. SafeEat נותנת לי שקט כשאני קונה לו חטיפים. סורק הברקודים מדויק בצורה מרשימה.'
        },
        {
          name: 'Carlos R.',
          location: 'מדריד, ספרד',
          rating: 5,
          content: 'יש לי אי־סבילות ללקטוז ואני מטייל/ת הרבה. SafeEat עובד בכל שפה — בחו"ל אני פשוט סורק/ת את התפריט ואוכל/ת בראש שקט!'
        },
        {
          name: 'Priya S.',
          location: 'מומבאי, הודו',
          rating: 5,
          content: 'בגלל בעיות הלב של בן/בת הזוג שלי אנחנו חייבים לשים לב לנתרן. הניתוח המיידי של SafeEat הפך את תכנון הארוחות להרבה יותר פשוט.'
        },
        {
          name: 'Emma W.',
          location: 'סידני, אוסטרליה',
          rating: 5,
          content: 'פיצ\'ר סריקת המנות מדהים! צילמתי מנה במסעדה והאפליקציה זיהתה רכיבים עם גלוטן שהיה מוסתר. זה חסך ממני תגובה קשה!'
        },
        {
          name: 'Ahmed H.',
          location: 'דובאי, איחוד האמירויות',
          rating: 5,
          content: 'אני שומר/ת כשרות מחמירה. SafeEat עוזרת לי לבדוק רכיבים במוצרים ארוזים שאני לא מכיר/ה. אפליקציה שלא מוותרים עליה!'
        },
        {
          name: 'Lisa M.',
          location: 'טורונטו, קנדה',
          rating: 5,
          content: 'עם שלושה ילדים שלכל אחד מהם אלרגיות שונות, קניות היו סיוט. עכשיו אני סורק/ת הכל בתוך שניות. האפליקציה הזו ממש מצילת חיים!'
        },
        {
          name: 'Johan V.',
          location: 'סטוקהולם, שוודיה',
          rating: 5,
          content: 'יש לי מחלת קרוהן ואני חייב/ת להימנע מטריגרים מסוימים. הפרופיל המותאם של SafeEat מזהה רכיבים שהייתי עלול/ה לפספס.'
        },
        {
          name: 'Maria G.',
          location: 'סאו פאולו, ברזיל',
          rating: 5,
          content: 'היכולת לסרוק תפריטים מושלמת בשביל האלרגיה שלי לפירות ים. היום אני מזמינ/ה במסעדות חדשות בביטחון הרבה יותר גדול!'
        },
        {
          name: 'David C.',
          location: 'פריז, צרפת',
          rating: 5,
          content: 'כטבח/ית עם רגישויות למזון, אני משתמש/ת ב‑SafeEat כדי לבדוק רכיבים לפני שאני מבשל/ת. הדיוק של הזיהוי מבוסס ה‑AI מרשים מאוד.'
        },
        {
          name: 'Fatima A.',
          location: 'קהיר, מצרים',
          rating: 5,
          content: 'סוכרת הריונית הייתה עבורי מאתגרת מאוד עד שמצאתי את SafeEat. סריקת תוויות המזון הופכת את המעקב לפשוט במיוחד!'
        },
        {
          name: 'Michael O.',
          location: 'לאגוס, ניגריה',
          rating: 5,
          content: 'יש לי אי־סבילות נדירה שרוב האפליקציות לא תומכות בה. ב‑SafeEat יכולתי להוסיף אותה לפרופיל ועכשיו היא מזוהה בכל פעם!'
        },
        {
          name: 'Sophie L.',
          location: 'אמסטרדם, הולנד',
          rating: 5,
          content: 'אני טבעוני/ת ואלרגי/ת לסויה — למצוא מזון בטוח היה כמעט בלתי אפשרי! SafeEat מזהה גם רכיבים מן החי המוסתרים וגם סויה. אני מאוהב/ת!'
        },
        {
          name: 'Kenji N.',
          location: 'סיאול, דרום קוריאה',
          rating: 5,
          content: 'מאגר הברקודים ענק! אפילו מוצרים מיובאים מזוהים. מושלם לבדיקת מזונות בינלאומיים לא מוכרים.'
        },
        {
          name: 'Elena P.',
          location: 'רומא, איטליה',
          rating: 5,
          content: 'לאמא שלי ישנה כמה בעיות בריאות והיא מבוגרת. יצרתי לה פרופיל ועכשיו היא יכולה שוב לעשות קניות בעצמה. תודה, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'תמחור פשוט ושקוף',
      subtitle: 'בחרו את המסלול שמתאים לכם',
      free: 'חינם',
      freeDesc: '5 סריקות חינמיות להתנסות',
      freeFeature1: '5 סריקות בינה מלאכותית',
      freeFeature2: 'ניתוח בסיסי',
      freeFeature3: 'היסטוריה מוגבלת',
      weekly: 'שבועי',
      weeklyPrice: '$2.99',
      weeklyDesc: 'מתאים לשימוש קבוע',
      weeklyFeature1: 'מספר סריקות בלתי מוגבל',
      weeklyFeature2: 'ניתוח מתקדם',
      weeklyFeature3: 'היסטוריה מלאה',
      weeklyFeature4: 'תמיכה בעדיפות גבוהה',
      annual: 'שנתי',
      annualPrice: '$29.99',
      annualDesc: 'עסקה משתלמת — חיסכון של 60%',
      annualBadge: 'הפופולרי ביותר',
      annualFeature1: 'כולל את כל מה שבמסלול השבועי',
      annualFeature2: 'חיסכון של 125$ בשנה',
      annualFeature3: 'תמיכת פרימיום',
      annualFeature4: 'גישה מוקדמת לפיצ\'רים חדשים',
      button: 'התחילו עכשיו'
    },
    faq: {
      title: 'שאלות נפוצות',
      items: [
        {
          q: 'מה אפשר לסרוק עם SafeEat?',
          a: 'SafeEat יכולה לנתח: רשימות רכיבים וטבלאות ערכים תזונתיים (מתמונה), ברקודים, מזון ארוז ובתפזורת, מנות (מתמונה) ותפריטים במסעדות.'
        },
        {
          q: 'איך עובדת יכולת זיהוי המנות?',
          a: 'הבינה המלאכותית מזהה את המנה ומעריכה מהם הרכיבים האפשריים. היא עובדת הכי טוב עם תאורה טובה וזווית צילום ברורה; רכיבים מוסתרים או זיהום צולב עלולים שלא להתגלות.'
        },
        {
          q: 'האם אפשר לסרוק תפריטים במסעדות?',
          a: 'כן. האפליקציה מסמנת אלרגנים פוטנציאליים והתנגשויות עם התזונה שלכם. תפריטים יכולים להיות לא מלאים; מומלץ לשאול את הצוות על רכיבים, שמנים, שימוש בטיגון משותף, רטבים וסיכון לזיהום צולב.'
        },
        {
          q: 'אילו מצבים רפואיים SafeEat תומכת בהם?',
          a: 'אלרגיות למזון, מצבי אי־סבילות (כמו לקטוז וגלוטן) וכן צרכים תזונתיים מסוימים (הפחתת נתרן, מעקב אחר פחמימות, הימנעות מרכיבים מסוימים).'
        },
        {
          q: 'האם SafeEat עובדת גם בלי חיבור לאינטרנט?',
          a: 'בדרך כלל סריקות וניתוח מבוסס בינה מלאכותית דורשים חיבור לאינטרנט. תוצאות ורשימות שנשמרו עשויות להיות זמינות גם בלי חיבור, בהתאם למכשיר ולאפליקציה.'
        },
        {
          q: 'עד כמה מדויק הניתוח של SafeEat?',
          a: 'הדיוק תלוי באיכות התמונה, בקריאות הטקסט, בשפה/בתבנית התווית ובמידת המלאות של המידע בתפריט. SafeEat מיועדת למידע בלבד — ברמת סיכון גבוהה חשוב לבצע בדיקות נוספות.'
        },
        {
          q: 'מה עושים אם SafeEat טועה?',
          a: 'לא להסתמך על תוצאה שנראית לא בטוחה; בצעו סריקה מחדש עם צילום ברור יותר; בדקו את התווית או האתר הרשמי, ושאלו את הצוות במקום; במצבי סיכון עדיף לבחור באפשרות הבטוחה יותר.'
        },
        {
          q: 'האם אפשר לדווח על תוצאות ניתוח שגויות?',
          a: 'כן: שלחו מייל ל‑support@safeeat.app עם צילום מסך, תמונה של התווית/התפריט/המנה, ארץ המוצר, המותג והשם, ותיאור מה לדעתכם שגוי.'
        },
        {
          q: 'למה זיהוי מנות יכול להיות פחות מדויק?',
          a: 'מנות שנראות דומה, רכיבים מוסתרים, רטבים או קישוטים שמכסים את המנה, תאורה חלשה או תמונות מטושטשות וכן מנות מותאמות אישית או מקומיות במיוחד — כל אלו יכולים להפחית את הדיוק.'
        },
        {
          q: 'האם SafeEat היא מכשיר רפואי?',
          a: 'לא. מדובר בכלי מידע בלבד. לקבלת ייעוץ רפואי יש לפנות לאיש מקצוע.'
        },
        {
          q: 'מהי אחריות SafeEat?',
          a: 'המידע מסופק "כפי שהוא" בלי הבטחה לשלמותו או להיעדר טעויות. האחריות על הבחירות היא שלכם; ייתכנו רכיבים שלא צוינו או זיהום צולב.'
        },
        {
          q: 'האם אפשר להסתמך רק על SafeEat אם יש לי אלרגיות קשות?',
          a: 'לא. חשוב לקרוא תוויות, לשאול את הצוות, לפעול לפי תוכנית החירום האישית שלכם ולהחזיק תרופות מרשם מתאימות; אם אתם לא בטוחים — עדיף להימנע מהמוצר או המנה.'
        },
        {
          q: 'איך אתם מגינים על המידע הרפואי שלי?',
          a: 'אנחנו אוספים מינימום נתונים נדרשים ומגנים עליהם באמצעות אמצעי אבטחה והרשאות גישה מוגבלות. לשאלות בנושא פרטיות אפשר לפנות אלינו ב‑support@safeeat.app. ראו גם את מדיניות הפרטיות.'
        },
        {
          q: 'האם ילדים יכולים להשתמש ב‑SafeEat?',
          a: 'כן, בליווי והשגחה של מבוגר. במקרה של אלרגיות קשות או תזונה מורכבת חשוב לנקוט זהירות מיוחדת ולאמת מידע דרך מקורות אמינים.'
        },
        {
          q: 'מה עליי לעשות אם מתרחשת תגובה אלרגית?',
          a: 'יש לפנות מיד לעזרה רפואית ולפעול לפי תוכנית החירום שלכם; במקרה של תסמינים חמורים יש להתקשר לשירותי החירום. לאחר מכן מומלץ לדווח לרופא המטפל, למסעדה או ליצרן.'
        }
      ]
    },
    cta: {
      title: 'מוכנים לשנות את הדרך שבה אתם אוכלים?',
      subtitle: 'הצטרפו לאלפי משתמשים שכבר משתמשים ב‑SafeEat מדי יום',
      description: 'הורידו עכשיו וקבלו 5 סריקות בינה מלאכותית בחינם',
      button: 'הורדה ב‑App Store',
      feature1: 'אין צורך בכרטיס אשראי',
      feature2: '100% פרטי ומאובטח',
      feature3: 'הפעלה מיידית'
    },
    newsletter: {
      title: 'הישארו מעודכנים',
      subtitle: 'קבלו עדכונים אחרונים וטיפים לאכילה בטוחה יותר',
      placeholder: 'הזינו את כתובת האימייל שלכם',
      button: 'הירשמו',
      error: 'אנא הזינו כתובת אימייל תקינה',
      success: 'תודה שנרשמתם!'
    },
    seo: {
      title: 'העוזר האישי שלכם לבטיחות מזון',
      text: 'SafeEat היא אפליקציית סריקת מזון מבוססת בינה מלאכותית עבור אנשים עם אלרגיות, אי־סבילות או מגבלות תזונתיות. סרקו מיד תוויות, טבלאות ערכים תזונתיים, ברקודים, תפריטי מסעדות ומנות מוכנות כדי לבדוק אם הן מתאימות לפרופיל האישי שלכם. בין אם יש לכם צליאק, אי־סבילות ללקטוז, אלרגיה לאגוזים או שאתם שומרים על תזונה ללא גלוטן, טבעונית או דלת נתרן — SafeEat תעזור לכם לקבל החלטות בטוחות יותר.',
      keywords: [
        'אפליקציה לאלרגיות למזון',
        'סורק רכיבים',
        'זיהוי אלרגנים',
        'סריקת תוויות תזונה',
        'סורק ברקודים',
        'סריקת תפריטי מסעדות',
        'בדיקת ללא גלוטן',
        'אפליקציה לאי־סבילות ללקטוז',
        'מעקב אחרי תזונה',
        'בטיחות מזון'
      ]
    },
    cookies: {
      bannerText: 'אנחנו משתמשים בעוגיות (Cookies): עוגיות הנחוצות להפעלת האתר ועוגיות אנליטיקה רק בהסכמתכם.',
      accept: 'אישור',
      decline: 'דחייה'
    },
    legal: {
      backHome: 'חזרה לדף הבית'
    },
    footer: {
      product: 'מוצר',
      features: 'יכולות',
      howItWorks: 'איך זה עובד',
      testimonials: 'חוות דעת',
      pricing: 'תמחור',
      faq: 'שאלות נפוצות',
      contacts: 'צור קשר',
      legal: 'מידע משפטי',
      privacy: 'מדיניות פרטיות',
      terms: 'תנאי השירות',
      usePolicy: 'מדריך משתמש',
      disclaimer: 'כתב ויתור',
      company: 'FJCode OU',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      register: 'מספר רישום 16993109',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. כל הזכויות שמורות.'
    }
  },
  hi: {
    meta: {
      home: {
        title: 'SafeEat | एलर्जी और असहिष्णुता के लिए AI फ़ूड स्कैनर',
        description: 'अपने प्रोफ़ाइल के आधार पर एलर्जेन्स और डाइट‑रिस्क पहचानने के लिए लेबल, बारकोड, मेनू और डिशेज़ स्कैन करें: एलर्जी, खाद्य असहिष्णुता और क्रॉनिक बीमारियाँ।'
      },
      privacy: {
        title: 'गोपनीयता नीति | SafeEat',
        description: 'जानें कि SafeEat आपके डेटा को कैसे एकत्र, उपयोग और सुरक्षित करता है।'
      },
      terms: {
        title: 'उपयोग की शर्तें | SafeEat',
        description: 'SafeEat के उपयोग पर लागू शर्तों की समीक्षा करें।'
      },
      manual: {
        title: 'उपयोगकर्ता मार्गदर्शिका | SafeEat',
        description: 'SafeEat का उपयोग करके भोजन सुरक्षित रूप से स्कैन करना सीखें।'
      },
      disclaimer: {
        title: 'अस्वीकरण | SafeEat',
        description: 'SafeEat की जानकारी, मार्गदर्शन और सीमाओं को समझें।'
      }
    },
    nav: {
      about: 'कैसे काम करता है',
      features: 'फ़ीचर्स',
      testimonials: 'रिव्यू',
      pricing: 'कीमतें',
      faq: 'FAQ',
      contacts: 'संपर्क',
      getStarted: 'शुरू करें'
    },
    about: {
      description: 'AI‑संचालित फ़ूड स्कैनर जो लेबल, बारकोड, डिशेज़ और मेनू पढ़ता है और आपके हेल्थ प्रोफ़ाइल के आधार पर एलर्जेन्स, सामग्री और पोषण जानकारी हाइलाइट करता है।'
    },
    hero: {
      badge: 'AI‑संचालित खाद्य सुरक्षा',
      title: 'तुरंत जाँचें',
      title2: 'खाद्य सुरक्षा',
      titleHighlight: 'आपके लिए व्यक्तिगत',
      subtitle: 'के आधार पर',
      description: 'किराना, बारकोड, सामग्री और मेनू स्कैन करें और तुरंत देखें कि भोजन आपके शरीर के लिए सुरक्षित है या नहीं',
      typing: ['क्रॉनिक बीमारियाँ', 'एलर्जी', 'खाद्य असहिष्णुता'],
      stat1: '500+',
      stat1Label: 'क्रॉनिक बीमारियाँ',
      stat2: '250+',
      stat2Label: 'एलर्जी',
      stat3: '100+',
      stat3Label: 'खाद्य असहिष्णुता',
      downloadButton: 'App Store से डाउनलोड करें'
    },
    howItWorks: {
      badge: 'कैसे काम करता है',
      mainTitle: 'SafeEat कैसे काम करता है',
      subtitle: 'हमारी स्कैनिंग प्रक्रिया सरल और तेज़ है — कुछ आसान चरणों में व्यक्तिगत परिणाम।',
      setupBadge: 'एक‑बार सेटअप',
      setupTitle: 'अपना हेल्थ प्रोफ़ाइल बनाएं',
      setupDesc: 'ऑनबोर्डिंग के दौरान आप अपनी स्वास्थ्य जानकारी जोड़कर SafeEat को अपने अनुसार सेट करते हैं। यह एक बार किया जाता है और केवल आपके लिए व्यक्तिगत भोजन‑विश्लेषण सक्षम करता है।',
      cards: {
        diseases: {
          title: 'क्रॉनिक बीमारियाँ',
          desc: 'हमारे 500+ कंडीशंस वाले डेटाबेस से अपनी क्रॉनिक स्थितियाँ जोड़ें। डायबिटीज़ से लेकर हृदय रोग तक — हम आपकी मदद करते हैं।',
          statLabel: 'स्थितियाँ'
        },
        allergies: {
          title: 'एलर्जी',
          desc: '250+ विकल्पों में से अपनी एलर्जी चुनें। आम नट्स और सीफ़ूड से लेकर दुर्लभ एलर्जेन्स तक — सुरक्षित रहें।',
          statLabel: 'एलर्जी'
        },
        intolerances: {
          title: 'खाद्य असहिष्णुता',
          desc: '100+ विकल्पों में से अपनी असहिष्णुता चुनें। लैक्टोज़, ग्लूटेन, फ्रक्टोज़ और अधिक — बिना असुविधा के खाएँ।',
          statLabel: 'असहिष्णुता'
        }
      },
      connectorText: 'और फिर — हर बार जब आप स्कैन करते हैं:',
      stepLabel: 'चरण',
      step1Title: 'भोजन स्कैन करें',
      step1Desc: 'किसी भी भोजन को स्कैन करें — पैक्ड प्रोडक्ट्स, तैयार डिशेज़, न्यूट्रिशन लेबल, बारकोड या यहाँ तक कि रेस्टोरेंट मेनू। बस कैमरा दिखाएँ, बाकी SafeEat करेगा।',
      step2Title: 'AI विश्लेषण',
      step2Desc: 'ऐप इमेज का विश्लेषण करता है और आपकी प्रोफ़ाइल (बीमारियाँ, एलर्जी, असहिष्णुता) से मिलाकर संगतता/सुरक्षा का आकलन करता है।',
      step3Title: 'अपनी रिपोर्ट पाएं',
      step3Desc: 'SafeEat एक विस्तृत रिपोर्ट देता है और दिखाता है कि विश्लेषित प्रोडक्ट/डिश आपके हेल्थ प्रोफ़ाइल और डाइट ज़रूरतों के साथ कितना मेल खाता है।',
      step4Title: 'निर्णय लें',
      step4Desc: 'व्यक्तिगत इनसाइट्स के साथ आप अपने लिए सबसे बेहतर भोजन चुनते हैं। निश्चिंत होकर खाएँ — और जानें कि आपके शरीर के लिए क्या सही है।',
      processTitle: 'इसे एक्शन में देखें',
      processSubtitle: 'देखें SafeEat रियल‑टाइम में आपके भोजन का विश्लेषण कैसे करता है'
    },
    features: {
      title: 'शक्तिशाली फ़ीचर्स',
      subtitle: 'सुरक्षित खाने के लिए आपको जो चाहिए',
      items: [
        {
          title: 'किसी भी प्रोडक्ट को स्कैन करें',
          subtitle: 'साधारण स्नैक्स से लेकर एक्सॉटिक फलों तक',
          description: 'कैमरा किसी भी पैक्ड या अनपैक्ड भोजन पर रखें — सुपरमार्केट के रोज़मर्रा के प्रोडक्ट्स से लेकर दुर्लभ ट्रॉपिकल फलों तक — और तुरंत सुरक्षा‑रेटिंग पाएं। SafeEat प्रोडक्ट पहचानकर उसकी संरचना का विश्लेषण करता है, ताकि आप जोखिमभरी या हानिकारक सामग्री से बच सकें।',
          highlights: ['पैक्ड और अनपैक्ड भोजन', 'तुरंत सुरक्षा जाँच', 'संरचना का विश्लेषण'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'न्यूट्रिशन लेबल और बारकोड स्कैन करें',
          subtitle: 'जटिल लेबल तुरंत समझें',
          description: 'छोटे अक्षरों या उलझे लेबल्स से जूझना बंद करें। SafeEat कुछ सेकंड में न्यूट्रिशन, सामग्री सूची और बारकोड स्कैन कर सकता है। ऐप तुरंत एलर्जेन्स, एडिटिव्स या संभावित हानिकारक पदार्थों को हाइलाइट करता है, ताकि आपको पता रहे आप क्या खा रहे हैं।',
          highlights: ['न्यूट्रिशन स्कैनिंग', 'एलर्जेन पहचान', 'हानिकारक पदार्थों की चेतावनी'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'डिशेज़ स्कैन करें — सामग्री पहचानें',
          subtitle: 'अपने भोजन की फोटो से',
          description: 'अपने खाने की फोटो लें और SafeEat उसे सामग्री में तोड़कर दिखाता है। चाहे घर का बना हो, फूड कोर्ट से हो या फाइन‑डाइनिंग से — हमारी AI संभावित एलर्जेन्स या वे सामग्री पहचानती है जो आपकी डाइट के अनुकूल नहीं हैं।',
          highlights: ['AI सामग्री पहचान', 'किसी भी डिश पर काम करता है', 'डाइट‑संगतता जाँच'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'रेस्टोरेंट मेनू स्कैन करें',
          subtitle: 'ऑर्डर से पहले सुरक्षित विकल्प खोजें',
          description: 'कैफ़े/रेस्टोरेंट मेनू पर कैमरा रखें और SafeEat हर डिश का विश्लेषण करके बताएगा कि आपकी ज़रूरतों के हिसाब से कौन‑सी डिश सुरक्षित है। छिपे एलर्जेन्स या अवांछित सामग्री की चिंता के बिना बाहर खाएँ।',
          highlights: ['मेनू विश्लेषण', 'सुरक्षित डिशेज़ ढूँढता है', 'छिपे एलर्जेन्स की पहचान'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'अपनी सुरक्षित खाद्य‑सूची बनाएं',
          subtitle: 'अपनी "क्लीन ईटिंग" लाइब्रेरी तैयार करें',
          description: 'जो भोजन आपके लिए सुरक्षित है उसे सेव करें, अपनी "क्लीन ईटिंग" लाइब्रेरी बनाएं और जब चाहें खरीदारी करते समय या बाहर खाते समय उसे खोलें। हर स्कैन के साथ आपकी व्यक्तिगत फ़ूड‑डेटाबेस बढ़ती जाती है।',
          highlights: ['व्यक्तिगत फ़ूड लाइब्रेरी', 'हर जगह तेज़ पहुँच', 'स्मार्ट सुझाव'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'हज़ारों का भरोसा',
      subtitle: 'दुनिया भर के उपयोगकर्ताओं का समुदाय SafeEat पर भरोसा करता है।',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat ने मेरी खरीदारी का तरीका पूरी तरह बदल दिया! मुझे सीलिएक है और प्रोडक्ट स्कैन करने में बस कुछ सेकंड लगते हैं। अब दुकान में सूक्ष्म अक्षरों वाले लेबल पढ़ने की ज़रूरत नहीं।'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: 'कई फ़ूड एलर्जी होने पर बाहर खाना हमेशा तनावपूर्ण था। अब मैं सिर्फ मेनू स्कैन करता/करती हूँ और तुरंत जान जाता/जाती हूँ क्या सुरक्षित है। वाकई गेम‑चेंजर!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'मैं SafeEat का रोज़ इस्तेमाल अपने डायबिटीज़ मैनेजमेंट के लिए करता/करती हूँ। न्यूट्रिशन स्कैनर तुरंत कार्ब्स और शुगर ट्रैक करने में मदद करता है। ज़ोरदार सिफ़ारिश!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: 'मेरे बेटे को गंभीर नट एलर्जी है। SafeEat से स्नैक्स खरीदते समय मन शांत रहता है। बारकोड स्कैनर बेहद सटीक है।'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: 'मैं लैक्टोज़ असहिष्णु हूँ और बहुत यात्रा करता/करती हूँ। SafeEat हर भाषा में काम करता है — विदेश में मेनू स्कैन करके बिना चिंता खा लेता/लेती हूँ!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'मेरे पति की हृदय‑समस्या के कारण हमें सोडियम पर नज़र रखनी होती है। SafeEat का त्वरित विश्लेषण मील‑प्लानिंग को बहुत आसान बना देता है।'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'डिश‑स्कैनर कमाल है! मैंने रेस्टोरेंट की डिश की फोटो ली और उसने छिपे ग्लूटेन स्रोत पकड़ लिए। एक खराब रिएक्शन से बचा लिया!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'मैं सख़्त हलाल डाइट फॉलो करता/करती हूँ। SafeEat अनजान पैक्ड फ़ूड में सामग्री चेक करने में मदद करता है। ज़रूरी ऐप!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'तीन बच्चों की अलग‑अलग एलर्जी के साथ खरीदारी दुःस्वप्न थी। अब मैं सब कुछ सेकंडों में स्कैन कर लेता/लेती हूँ। यह ऐप सच में जीवन‑रक्षक है!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'मुझे क्रोहन रोग है और कुछ ट्रिगर्स से बचना होता है। SafeEat का पर्सनल प्रोफ़ाइल उन सामग्री को पकड़ लेता है जो मैं अक्सर मिस कर देता/देती।'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'मेनू‑स्कैन फीचर मेरे सीफ़ूड एलर्जी के लिए परफ़ेक्ट है। अब नए रेस्टोरेंट में ऑर्डर करते समय ज़्यादा आत्मविश्वास रहता है!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'शेफ होने के नाते और कुछ संवेदनशीलताओं के साथ, मैं पकाने से पहले सामग्री जांचने के लिए SafeEat इस्तेमाल करता/करती हूँ। AI पहचान बेहद प्रभावशाली है।'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: 'गर्भावस्था‑डायबिटीज़ मैनेज करना कठिन था, जब तक मुझे SafeEat नहीं मिला। फ़ूड लेबल स्कैन करने से ट्रैक करना बहुत आसान हो गया!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'मेरी एक दुर्लभ फ़ूड असहिष्णुता है जिसे ज़्यादातर ऐप्स कवर नहीं करते। SafeEat में मैं उसे प्रोफ़ाइल में जोड़ पाया/पाई और अब यह हर बार पकड़ लेता है!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: 'मैं वेगन हूँ और मुझे सोया एलर्जी है — सुरक्षित खाना ढूँढना बहुत कठिन था! SafeEat छिपे एनिमल प्रोडक्ट्स और सोया दोनों पकड़ लेता है। मुझे बहुत पसंद है!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'बारकोड डेटाबेस बहुत बड़ा है! इम्पोर्टेड प्रोडक्ट्स भी पहचान लेता है। अनजान इंटरनेशनल फ़ूड चेक करने के लिए बेहतरीन।'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: 'मेरी बुज़ुर्ग माँ को कई समस्याएँ हैं। मैंने उनका प्रोफ़ाइल सेट किया और अब वे फिर से स्वतंत्र रूप से खरीदारी कर पाती हैं। धन्यवाद, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'सरल, पारदर्शी कीमतें',
      subtitle: 'अपने लिए सही प्लान चुनें',
      free: 'मुफ़्त',
      freeDesc: 'आजमाने के लिए 5 मुफ्त स्कैन',
      freeFeature1: '5 AI स्कैन',
      freeFeature2: 'बेसिक विश्लेषण',
      freeFeature3: 'सीमित हिस्ट्री',
      weekly: 'साप्ताहिक',
      weeklyPrice: '$2.99',
      weeklyDesc: 'नियमित उपयोगकर्ताओं के लिए',
      weeklyFeature1: 'अनलिमिटेड स्कैन',
      weeklyFeature2: 'एडवांस्ड विश्लेषण',
      weeklyFeature3: 'पूरा इतिहास',
      weeklyFeature4: 'प्रायोरिटी सपोर्ट',
      annual: 'वार्षिक',
      annualPrice: '$29.99',
      annualDesc: 'सबसे अच्छा मूल्य — 60% बचाएँ',
      annualBadge: 'सबसे लोकप्रिय',
      annualFeature1: 'साप्ताहिक की सभी सुविधाएँ',
      annualFeature2: '$125/वर्ष बचाएँ',
      annualFeature3: 'प्रीमियम सपोर्ट',
      annualFeature4: 'फ़ीचर्स का अर्ली एक्सेस',
      button: 'शुरू करें'
    },
    faq: {
      title: 'अक्सर पूछे जाने वाले प्रश्न',
      items: [
        {
          q: 'मैं SafeEat से क्या स्कैन कर सकता/सकती हूँ?',
          a: 'SafeEat यह विश्लेषित कर सकता है: सामग्री सूची और न्यूट्रिशन लेबल (फोटो), बारकोड, पैक्ड और अनपैक्ड खाद्य‑पदार्थ, डिशेज़ (फोटो), और रेस्टोरेंट मेनू।'
        },
        {
          q: 'डिश पहचान (Dish recognition) कैसे काम करती है?',
          a: 'AI डिश को पहचानती है और संभावित सामग्री का अनुमान लगाती है। यह अच्छे प्रकाश और सही एंगल में बेहतर काम करती है; छिपी सामग्री या क्रॉस‑कॉन्टैक्ट/कंटैमिनेशन छूट सकते हैं।'
        },
        {
          q: 'क्या मैं रेस्टोरेंट मेनू स्कैन कर सकता/सकती हूँ?',
          a: 'हाँ। यह संभावित एलर्जेन्स और डाइट‑कॉनफ़्लिक्ट हाइलाइट करता है। मेनू अधूरे हो सकते हैं; सामग्री, तेल, साझा फ्रायर, सॉस और क्रॉस‑कंटैमिनेशन के बारे में स्टाफ से हमेशा पुष्टि करें।'
        },
        {
          q: 'SafeEat किन हेल्थ कंडीशंस को सपोर्ट करता है?',
          a: 'फ़ूड एलर्जी, असहिष्णुता (जैसे लैक्टोज़, ग्लूटेन) और डाइट ज़रूरतें (लो‑सोडियम, कार्ब फोकस, कुछ सामग्री से परहेज़)।'
        },
        {
          q: 'क्या SafeEat ऑफ़लाइन काम करता है?',
          a: 'आमतौर पर स्कैन/AI के लिए इंटरनेट की ज़रूरत होती है। सेव किए गए रिज़ल्ट और लिस्ट कुछ मामलों में ऐप/डिवाइस पर निर्भर करते हुए ऑफ़लाइन उपलब्ध हो सकते हैं।'
        },
        {
          q: 'SafeEat का विश्लेषण कितना सटीक है?',
          a: 'यह फोटो की गुणवत्ता, लेबल पर टेक्स्ट, भाषा/फॉर्मेट और मेनू की पूर्णता पर निर्भर करता है। यह केवल जानकारी के लिए है — उच्च जोखिम में हमेशा दोबारा जाँचें।'
        },
        {
          q: 'अगर SafeEat गलत हो जाए तो क्या?',
          a: 'किसी संदिग्ध परिणाम पर भरोसा न करें; बेहतर फोटो लेकर दोबारा स्कैन करें; लेबल/वेबसाइट/स्टाफ से पुष्टि करें; उच्च जोखिम में सबसे सुरक्षित विकल्प चुनें।'
        },
        {
          q: 'क्या मैं गलत विश्लेषण की रिपोर्ट कर सकता/सकती हूँ?',
          a: 'हाँ: support@safeeat.app पर — स्क्रीनशॉट, लेबल/मेनू/डिश की फोटो, देश/ब्रांड/प्रोडक्ट और क्या गलत लग रहा है, भेजें।'
        },
        {
          q: 'डिश पहचान कम सटीक क्यों हो सकती है?',
          a: 'एक‑जैसी दिखने वाली डिशेज़, छिपी सामग्री, भारी सॉस/गार्निश, कम रोशनी/ब्लर, और बहुत कस्टम या क्षेत्रीय व्यंजन।'
        },
        {
          q: 'क्या SafeEat एक मेडिकल डिवाइस है?',
          a: 'नहीं। यह केवल जानकारी के लिए है। चिकित्सा सलाह के लिए स्वास्थ्य‑विशेषज्ञ से संपर्क करें।'
        },
        {
          q: 'SafeEat की ज़िम्मेदारी क्या है?',
          a: 'जानकारी "जैसी है" वैसी दी जाती है; पूर्णता की गारंटी नहीं। आपके चयन की ज़िम्मेदारी आपकी है; न बताई गई सामग्री और क्रॉस‑कंटैमिनेशन संभव है।'
        },
        {
          q: 'क्या गंभीर एलर्जी में मुझे सिर्फ SafeEat पर निर्भर रहना चाहिए?',
          a: 'नहीं। लेबल पढ़ें, स्टाफ से पूछें, अपनी एक्शन‑प्लान फॉलो करें और निर्धारित दवाइयाँ साथ रखें; संदेह हो तो उस आइटम से बचें।'
        },
        {
          q: 'आप मेरे हेल्थ डेटा को कैसे सुरक्षित रखते हैं?',
          a: 'हम न्यूनतम डेटा एकत्र करते हैं और सुरक्षा उपायों, सीमित एक्सेस और privacy संपर्क (support@safeeat.app) के जरिए संरक्षण करते हैं। गोपनीयता नीति देखें।'
        },
        {
          q: 'क्या मैं बच्चों के लिए SafeEat इस्तेमाल कर सकता/सकती हूँ?',
          a: 'हाँ, अभिभावकीय निगरानी में। गंभीर एलर्जी/जटिल डाइट ज़रूरतों में अतिरिक्त सावधानी बरतें; भरोसेमंद स्रोतों से पुष्टि करें।'
        },
        {
          q: 'अगर मुझे रिएक्शन हो जाए तो क्या करूँ?',
          a: 'तुरंत चिकित्सा सहायता लें, अपना आपातकालीन प्लान फॉलो करें, गंभीर लक्षणों पर 112 पर कॉल करें, और बाद में सेवा‑प्रदाता/रेस्टोरेंट/निर्माता को सूचित करें।'
        }
      ]
    },
    cta: {
      title: 'अपने खाने का तरीका बदलने के लिए तैयार हैं?',
      subtitle: 'हज़ारों लोगों में शामिल हों जो रोज़ SafeEat का उपयोग करते हैं',
      description: 'अभी डाउनलोड करें और 5 मुफ्त AI स्कैन पाएं',
      button: 'App Store से डाउनलोड करें',
      feature1: 'क्रेडिट कार्ड की ज़रूरत नहीं',
      feature2: '100% निजी और सुरक्षित',
      feature3: 'तुरंत एक्सेस'
    },
    newsletter: {
      title: 'अपडेट रहें',
      subtitle: 'नए अपडेट और स्वास्थ्य टिप्स पाएं',
      placeholder: 'अपना ईमेल दर्ज करें',
      button: 'सब्सक्राइब करें',
      error: 'कृपया वैध ईमेल पता दर्ज करें',
      success: 'सब्सक्राइब करने के लिए धन्यवाद!'
    },
    seo: {
      title: 'आपका व्यक्तिगत खाद्य‑सुरक्षा सहायक',
      text: 'SafeEat एक AI‑संचालित फ़ूड‑स्कैनर ऐप है उन लोगों के लिए जिन्हें फ़ूड एलर्जी, असहिष्णुता या डाइट प्रतिबंध हैं। अपने प्रोफ़ाइल के आधार पर सुरक्षा जाँचने के लिए लेबल, न्यूट्रिशन, बारकोड, रेस्टोरेंट मेनू और तैयार डिशेज़ तुरंत स्कैन करें। चाहे आपको सीलिएक, लैक्टोज़ असहिष्णुता, नट एलर्जी हो या आप ग्लूटेन‑फ्री, वेगन या लो‑सोडियम डाइट फॉलो करते हों — SafeEat आपको सुरक्षित विकल्प चुनने में मदद करता है।',
      keywords: [
        'फ़ूड एलर्जी ऐप',
        'सामग्री स्कैनर',
        'एलर्जेन डिटेक्टर',
        'न्यूट्रिशन लेबल रीडर',
        'बारकोड स्कैनर',
        'मेनू स्कैनर',
        'ग्लूटेन‑फ्री चेक',
        'लैक्टोज़ असहिष्णुता ऐप',
        'डाइट ट्रैकर',
        'खाद्य सुरक्षा'
      ]
    },
    cookies: {
      bannerText: 'हम कुकीज़ का उपयोग करते हैं: साइट चलाने के लिए आवश्यक कुकीज़ और आपके सहमति पर ही एनालिटिक्स कुकीज़।',
      decline: 'अस्वीकार करें',
      accept: 'स्वीकार करें'
    },
    legal: {
      backHome: 'होम पर वापस'
    },
    footer: {
      product: 'प्रोडक्ट',
      features: 'फ़ीचर्स',
      howItWorks: 'कैसे काम करता है',
      testimonials: 'रिव्यू',
      pricing: 'कीमतें',
      faq: 'FAQ',
      contacts: 'संपर्क',
      legal: 'कानूनी',
      privacy: 'गोपनीयता नीति',
      terms: 'सेवा की शर्तें',
      usePolicy: 'उपयोगकर्ता मार्गदर्शिका',
      disclaimer: 'अस्वीकरण',
      company: 'FJCode OU',
      register: 'पंजीकरण संख्या 16993109',
      address: 'Viru väljak 2, 10111 Tallinn, एस्टोनिया',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. सर्वाधिकार सुरक्षित।'
    }
  },
  hr: {
    meta: {
      home: {
        title: 'SafeEat | AI skener hrane za alergije i intolerancije',
        description: 'Skeniraj deklaracije, barkodove, jelovnike i jela te označi alergene i prehrambene rizike za svoj profil: alergije, intolerancije i kronična stanja.'
      },
      privacy: {
        title: 'Politika privatnosti | SafeEat',
        description: 'Pročitaj kako SafeEat prikuplja, koristi i štiti tvoje podatke.'
      },
      terms: {
        title: 'Uvjeti korištenja | SafeEat',
        description: 'Pregledaj uvjete koji uređuju tvoje korištenje SafeEata.'
      },
      manual: {
        title: 'Korisnički priručnik | SafeEat',
        description: 'Saznaj kako sigurno koristiti SafeEat za skeniranje hrane.'
      },
      disclaimer: {
        title: 'Odricanje od odgovornosti | SafeEat',
        description: 'Upoznaj se s informativnim smjernicama i ograničenjima aplikacije SafeEat.'
      }
    },
    nav: {
      about: 'Kako radi',
      features: 'Značajke',
      testimonials: 'Iskustva',
      pricing: 'Cijene',
      faq: 'Česta pitanja',
      contacts: 'Kontakt',
      getStarted: 'Započni'
    },
    about: {
      description: 'AI skener hrane koji čita deklaracije, barkodove, jela i jelovnike te prema tvom profilu ističe alergene, sastojke i nutritivne informacije.'
    },
    hero: {
      badge: 'Sigurnost hrane uz pomoć AI-ja',
      title: 'Provjeri odmah',
      title2: 'Sigurnost hrane',
      titleHighlight: 'za tebe',
      subtitle: 'Na temelju tvojih',
      description: 'Skeniraj namirnice, barkodove, sastojke i jelovnike te odmah provjeri sigurnost hrane za svoje tijelo',
      downloadButton: 'Preuzmi na App Storeu',
      stat1: '500+',
      stat1Label: 'Kronične bolesti',
      stat2: '250+',
      stat2Label: 'Alergije',
      stat3: '100+',
      stat3Label: 'Intolerancije na hranu',
      typing: ['kronične bolesti', 'alergije', 'intolerancije na hranu']
    },
    howItWorks: {
      badge: 'Kako radi',
      mainTitle: 'Kako SafeEat radi',
      subtitle: 'Naš postupak skeniranja hrane je jednostavan, brz i daje personalizirane rezultate u samo nekoliko lakih koraka.',
      setupBadge: 'Jednokratno postavljanje',
      setupTitle: 'Izradi svoj zdravstveni profil',
      setupDesc: 'U uvodnom vodiču dodaj svoje zdravstvene informacije i personaliziraj SafeEat. Profil postavljaš jednom, a analiza se zatim prilagođava tebi.',
      cards: {
        diseases: {
          title: 'Kronične bolesti',
          desc: 'Dodaj svoja kronična stanja iz naše baze s 500+ bolesti. Od dijabetesa do srčanih bolesti — pokriveno.',
          statLabel: 'stanja'
        },
        allergies: {
          title: 'Alergije',
          desc: 'Odaberi svoje alergije među 250+ opcija. Od čestih orašastih plodova i školjki do rijetkih alergena — budi zaštićen/a.',
          statLabel: 'alergije'
        },
        intolerances: {
          title: 'Intolerancije na hranu',
          desc: 'Odredi svoje intolerancije među 100+ opcija. Laktoza, gluten, fruktoza i još mnogo toga — jedi bez nelagode.',
          statLabel: 'intolerancije'
        }
      },
      connectorText: 'A zatim, pri svakom skeniranju:',
      stepLabel: 'Korak',
      processTitle: 'Pogledaj kako radi',
      processSubtitle: 'Pogledaj kako SafeEat uživo analizira tvoju hranu',
      step1Title: 'Skeniraj hranu',
      step1Desc: 'Skeniraj bilo koju hranu — pojedinačni proizvod, gotovo jelo, nutritivnu deklaraciju, barkod, pa čak i jelovnik u restoranu. Samo usmjeri kameru, a SafeEat odradi ostalo.',
      step2Title: 'AI analiza',
      step2Desc: 'Aplikacija analizira sliku i uspoređuje je s tvojim profilom bolesti, alergija i intolerancija na hranu kako bi procijenila kompatibilnost.',
      step3Title: 'Dobij izvještaj',
      step3Desc: 'SafeEat pruža detaljan izvještaj o analiziranim proizvodima, pokazujući koliko se podudaraju s tvojim zdravstvenim profilom i prehrambenim potrebama.',
      step4Title: 'Donesi odluku',
      step4Desc: 'Uz personalizirane uvide odabireš hranu koja ti najbolje odgovara. Jedi s povjerenjem, znajući što točno odgovara tvom tijelu.'
    },
    features: {
      title: 'Moćne značajke',
      subtitle: 'Sve što ti treba za sigurniju prehranu',
      items: [
        {
          title: 'Skeniraj bilo koji proizvod',
          subtitle: 'od uobičajenih grickalica do egzotičnog voća',
          description: 'Usmjeri kameru na bilo koju pakiranu ili nepakiranu hranu, od svakodnevnih namirnica do rijetkog tropskog voća, i odmah dobiješ provjeru sigurnosti. SafeEat prepoznaje proizvod i analizira njegov sastav te ti pomaže izbjeći rizične ili štetne sastojke.',
          highlights: ['Pakirana i nepakirana hrana', 'Trenutna provjera sigurnosti', 'Analiza sastava'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Skeniraj nutritivne deklaracije i barkodove',
          subtitle: 'Razumi komplicirane deklaracije u sekundi',
          description: 'Ne muči se više sa sitnim slovima ili nerazumljivim deklaracijama. SafeEat može skenirati nutritivnu tablicu, popis sastojaka i barkod u nekoliko sekundi te odmah istaknuti alergene, aditive ili potencijalno rizične tvari, kako bi znao/la što jedeš.',
          highlights: ['Skeniranje nutritivne tablice', 'Prepoznavanje alergena', 'Upozorenja na rizične tvari'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Skeniraj jela — prepoznaj sastojke',
          subtitle: 'Samo fotografiraj tanjur',
          description: 'Fotografiraj svoj obrok, a SafeEat će ga razložiti na vjerojatne sastojke. Bilo da je riječ o domaćem jelu, jelu iz menze ili restoranskom specijalitetu, AI može prepoznati potencijalne alergene ili sastojke koji ne odgovaraju tvojoj prehrani.',
          highlights: ['AI prepoznavanje sastojaka', 'Radi s bilo kojim jelom', 'Provjera kompatibilnosti prehrane'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Skeniraj jelovnike u restoranu',
          subtitle: 'Pronađi sigurna jela prije narudžbe',
          description: 'Usmjeri kameru na jelovnik u kafiću ili restoranu, a SafeEat će analizirati svako jelo i reći ti koja odgovaraju tvojim zdravstvenim potrebama. Jednostavnije jedi vani bez straha od skrivenih alergena ili neželjenih sastojaka.',
          highlights: ['Analiza jelovnika', 'Preporuke sigurnih jela', 'Otkrivanje skrivenih alergena'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Stvori svoju listu sigurnih namirnica',
          subtitle: 'Izgradi svoju bazu ‘čiste’ prehrane',
          description: 'Spremi hranu koja ti odgovara i izradi vlastitu bazu ‘čiste’ prehrane kojoj možeš pristupiti tijekom kupovine ili izlaska. Tvoja personalizirana baza namirnica raste sa svakim skeniranjem i pomaže ti donositi brže odluke.',
          highlights: ['Osobna baza namirnica', 'Brza provjera u hodu', 'Pametne preporuke'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Korisnici nam vjeruju',
      subtitle: 'Povjerenje zajednice korisnika širom svijeta.',
      items: [
        { name: 'Sarah M.', location: 'New York, SAD', rating: 5, content: 'SafeEat je potpuno promijenio način na koji kupujem namirnice! Imam celijakiju i skeniranje proizvoda traje sekundu-dvije. Nema više čitanja sitnih deklaracija u trgovini.' },
        { name: 'Marcus L.', location: 'London, Ujedinjeno Kraljevstvo', rating: 5, content: 'Kao netko s više alergija na hranu, odlazak u restoran uvijek je bio stresan. Sada samo skeniram jelovnik i odmah znam što je sigurno. Potpuni preokret!' },
        { name: 'Yuki T.', location: 'Tokio, Japan', rating: 5, content: 'SafeEat koristim svaki dan za kontrolu dijabetesa. Skener nutritivne deklaracije pomaže mi odmah pratiti ugljikohidrate i šećer. Topla preporuka!' },
        { name: 'Anna K.', location: 'Berlin, Njemačka', rating: 5, content: 'Moj sin ima tešku alergiju na orašaste plodove. SafeEat mi daje mir kad mu kupujem grickalice. Skener barkodova je nevjerojatno precizan.' },
        { name: 'Carlos R.', location: 'Madrid, Španjolska', rating: 5, content: 'Imam intoleranciju na laktozu i puno putujem. SafeEat radi na bilo kojem jeziku — u inozemstvu samo skeniram jelovnik i jedem bez brige!' },
        { name: 'Priya S.', location: 'Mumbai, Indija', rating: 5, content: 'Kod suprugove srčane bolesti moramo pažljivo pratiti natrij. SafeEatova trenutna analiza čini planiranje obroka puno lakšim.' },
        { name: 'Emma W.', location: 'Sydney, Australija', rating: 5, content: 'Skener jela je odličan! Fotografirala sam obrok u restoranu i prepoznao je skrivene sastojke s glutenom. Spasilo me od loše reakcije!' },
        { name: 'Ahmed H.', location: 'Dubai, UAE', rating: 5, content: 'Držim se stroge halal prehrane. SafeEat mi pomaže provjeriti sastojke u pakiranoj hrani koju ne poznajem. Neizostavna aplikacija!' },
        { name: 'Lisa M.', location: 'Toronto, Kanada', rating: 5, content: 'S troje djece s različitim alergijama kupovina namirnica bila je noćna mora. Sada sve skeniram u par sekundi. Ova aplikacija spašava stvar!' },
        { name: 'Johan V.', location: 'Stockholm, Švedska', rating: 5, content: 'Imam Crohnovu bolest i moram izbjegavati određene okidače. SafeEatov personalizirani profil hvata sastojke koje bih propustio/la.' },
        { name: 'Maria G.', location: 'São Paulo, Brazil', rating: 5, content: 'Značajka skeniranja jelovnika savršena je za moju alergiju na školjke. Sada s više samopouzdanja naručujem u novim restoranima!' },
        { name: 'David C.', location: 'Pariz, Francuska', rating: 5, content: 'Kao kuhar s osjetljivostima na hranu, koristim SafeEat za provjeru sastojaka prije kuhanja. AI prepoznavanje je impresivno precizno.' },
        { name: 'Fatima A.', location: 'Kairo, Egipat', rating: 5, content: 'Gestacijski dijabetes bio mi je preplavljujuć dok nisam pronašla SafeEat. Skeniranje deklaracija čini praćenje nevjerojatno jednostavnim!' },
        { name: 'Michael O.', location: 'Lagos, Nigerija', rating: 5, content: 'Imam rijetku intoleranciju na hranu koju većina aplikacija ne pokriva. U SafeEatu sam je dodao/la u profil i sada je prepoznaje svaki put!' },
        { name: 'Sophie L.', location: 'Amsterdam, Nizozemska', rating: 5, content: 'Vegan sam i imam alergiju na soju — pronaći sigurnu hranu bilo je jako teško! SafeEat prepoznaje skrivene životinjske sastojke I soju. Obožavam!' },
        { name: 'Kenji N.', location: 'Seul, Južna Koreja', rating: 5, content: 'Baza barkodova je ogromna! Prepoznaje čak i uvozne proizvode. Savršeno za provjeru nepoznate međunarodne hrane.' },
        { name: 'Elena P.', location: 'Rim, Italija', rating: 5, content: 'Moja starija mama ima više zdravstvenih stanja. Postavio/la sam joj profil i sada opet može samostalno kupovati. Hvala, SafeEat!' }
      ]
    },
    pricing: {
      title: 'Jednostavne, transparentne cijene',
      subtitle: 'Odaberi plan koji ti odgovara',
      free: 'Besplatno',
      freeDesc: '5 besplatnih skeniranja za isprobavanje',
      freeFeature1: '5 AI skeniranja',
      freeFeature2: 'Osnovna analiza',
      freeFeature3: 'Ograničena povijest',
      weekly: 'Tjedno',
      weeklyDesc: 'Idealno za redovite korisnike',
      weeklyPrice: '$2.99',
      weeklyFeature1: 'Neograničena skeniranja',
      weeklyFeature2: 'Napredna analiza',
      weeklyFeature3: 'Puna povijest',
      weeklyFeature4: 'Prioritetna podrška',
      annual: 'Godišnje',
      annualBadge: 'Najpopularnije',
      annualDesc: 'Najbolja vrijednost – uštedi 60%',
      annualPrice: '$29.99',
      annualFeature1: 'Sve iz Tjednog',
      annualFeature2: 'Uštedi $125/god',
      annualFeature3: 'Premium podrška',
      annualFeature4: 'Rani pristup značajkama',
      button: 'Započni'
    },
    faq: {
      title: 'Česta pitanja',
      items: [
        { q: 'Što mogu skenirati uz SafeEat?', a: 'SafeEat može analizirati: popise sastojaka i nutritivne deklaracije (fotografije), barkodove, pakiranu i nepakiranu hranu, jela (fotografije) te jelovnike u restoranima.' },
        { q: 'Kako radi značajka prepoznavanja jela?', a: 'AI prepoznaje jelo i procjenjuje vjerojatne sastojke. Najbolje radi uz dobro osvjetljenje i kut; skriveni sastojci ili križna kontaminacija mogu promaknuti.' },
        { q: 'Mogu li skenirati jelovnike u restoranima?', a: 'Da. Označava potencijalne alergene i sukobe s prehranom. Jelovnici mogu biti nepotpuni; potvrdi sa osobljem sastojke, ulja, zajedničke friteze, umake i mogućnost križne kontaminacije.' },
        { q: 'Koja zdravstvena stanja SafeEat podržava?', a: 'Alergije na hranu, intolerancije (npr. laktoza, gluten) i prehrambene potrebe (nizak unos natrija, praćenje ugljikohidrata, izbjegavanje određenih sastojaka).' },
        { q: 'Radi li SafeEat offline?', a: 'U pravilu je za skeniranje i AI analizu potrebna internetska veza. Spremljeni rezultati i liste mogu biti dostupni offline, ovisno o aplikaciji i uređaju.' },
        { q: 'Koliko je točna SafeEat analiza?', a: 'Ovisi o kvaliteti fotografije, čitljivosti teksta na deklaraciji, jeziku/formatu i potpunosti informacija na jelovniku. Aplikacija je informativna — pri većem riziku obavezno dodatno provjeri.' },
        { q: 'Što ako SafeEat pogriješi?', a: 'Nemoj se oslanjati na sumnjiv rezultat; ponovno skeniraj uz bolju fotografiju; provjeri na deklaraciji/webu ili s osobljem; ako je rizik visok, odaberi sigurniju opciju.' },
        { q: 'Mogu li prijaviti netočan rezultat analize?', a: 'Da: pošalji na support@safeeat.app uz screenshot, fotografiju deklaracije/jelovnika/jela, državu/brend/proizvod i što ti se čini pogrešnim.' },
        { q: 'Zašto prepoznavanje jela može biti manje točno?', a: 'Slična jela po izgledu, skriveni sastojci, obilni umaci/ukrasi, loše osvjetljenje/zamućenje te izrazito prilagođena ili regionalna jela mogu smanjiti točnost.' },
        { q: 'Je li SafeEat medicinski uređaj?', a: 'Ne. Aplikacija služi samo u informativne svrhe. Za medicinski savjet obrati se stručnjaku.' },
        { q: 'Koja je odgovornost SafeEata?', a: 'Informacije se daju „kakve jesu” i ne možemo jamčiti potpunost. Ti si odgovoran/na za odluke; neoznačeni sastojci i križna kontaminacija su mogući.' },
        { q: 'Mogu li se kod teških alergija osloniti samo na SafeEat?', a: 'Ne. Čitaj deklaracije, pitaj osoblje, slijedi svoj plan postupanja i nosi propisane lijekove; kad nisi siguran/na, izbjegni proizvod/jelo.' },
        { q: 'Kako štitite moje zdravstvene podatke?', a: 'Prikupljamo minimalnu količinu podataka i štitimo ih sigurnosnim mjerama uz ograničen pristup. Za pitanja o privatnosti piši na support@safeeat.app. Pogledaj Politiku privatnosti.' },
        { q: 'Mogu li koristiti SafeEat za svoju djecu?', a: 'Da, uz nadzor. Kod teških alergija i složenih dijeta budi posebno oprezan/na i provjeri informacije iz pouzdanih izvora.' },
        { q: 'Što trebam učiniti ako imam reakciju?', a: 'Odmah potraži medicinsku pomoć, slijedi svoj hitni plan i kod teških simptoma nazovi hitne službe. Zatim prijavi događaj liječniku, restoranu ili proizvođaču.' }
      ]
    },
    cta: {
      title: 'Spreman/na promijeniti svoju prehranu?',
      subtitle: 'Pridruži se tisućama korisnika koji svakodnevno koriste SafeEat',
      description: 'Preuzmi sada i dobiješ 5 besplatnih AI skeniranja',
      button: 'Preuzmi na App Storeu',
      feature1: 'Nije potrebna kreditna kartica',
      feature2: '100% privatno i sigurno',
      feature3: 'Kreni odmah'
    },
    newsletter: {
      title: 'Budi u toku',
      subtitle: 'Primi najnovije novosti i savjete za zdraviju prehranu',
      placeholder: 'Unesi svoju e-mail adresu',
      button: 'Pretplati se',
      error: 'Unesi valjanu e-mail adresu',
      success: 'Hvala na pretplati!'
    },
    seo: {
      title: 'Tvoj osobni asistent za sigurnost hrane',
      text: 'SafeEat je AI aplikacija za skeniranje hrane namijenjena osobama s alergijama, intolerancijama i prehrambenim ograničenjima. Trenutno skeniraj deklaracije, nutritivne tablice, barkodove, jelovnike u restoranima i gotova jela te provjeri odgovara li sve tvom osobnom profilu. Bez obzira imaš li celijakiju, intoleranciju na laktozu, alergiju na orašaste plodove ili slijediš bezglutensku, vegansku ili niskonatrijsku prehranu, SafeEat ti pomaže donositi sigurnije odluke.',
      keywords: ['aplikacija za alergije na hranu', 'skener sastojaka', 'detekcija alergena', 'prepoznavanje nutritivne deklaracije', 'skener barkodova', 'skeniranje jelovnika', 'provjera bezglutenske prehrane', 'aplikacija za intoleranciju na laktozu', 'praćenje prehrane', 'sigurnost hrane']
    },
    cookies: {
      bannerText: 'Koristimo kolačiće: nužne kolačiće za rad stranice te analitičke kolačiće samo uz tvoju privolu.',
      accept: 'Prihvati',
      decline: 'Odbij'
    },
    legal: {
      backHome: 'Natrag na početnu'
    },
    footer: {
      product: 'Proizvod',
      features: 'Značajke',
      howItWorks: 'Kako radi',
      testimonials: 'Iskustva',
      pricing: 'Cijene',
      faq: 'Česta pitanja',
      contacts: 'Kontakt',
      legal: 'Pravno',
      privacy: 'Politika privatnosti',
      terms: 'Uvjeti usluge',
      disclaimer: 'Odricanje od odgovornosti',
      usePolicy: 'Korisnički priručnik',
      company: 'FJCode OU',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      register: 'Registracijski broj 16993109',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. Sva prava pridržana.'
    }
  },
  hu: {
    meta: {
      home: {
        title: 'SafeEat | MI-alapú ételszkenner allergiára és intoleranciára',
        description: 'Szkenneld a címkéket, vonalkódokat, étlapokat és ételeket, és emeld ki az allergéneket és étrendi kockázatokat a saját profilod alapján: allergiák, intoleranciák és krónikus betegségek.'
      },
      privacy: {
        title: 'Adatvédelmi tájékoztató | SafeEat',
        description: 'Tudd meg, hogyan gyűjti, használja és védi adataidat a SafeEat.'
      },
      terms: {
        title: 'Felhasználási feltételek | SafeEat',
        description: 'Olvasd el a feltételeket, amelyek a SafeEat használatára vonatkoznak.'
      },
      manual: {
        title: 'Felhasználói kézikönyg | SafeEat',
        description: 'Ismerd meg, hogyan használd biztonságosan a SafeEat alkalmazást ételek szkennelésére.'
      },
      disclaimer: {
        title: 'Jogi nyilatkozat | SafeEat',
        description: 'Tudd meg, milyen információs jellegű ajánlásokat és korlátozásokat tartalmaz a SafeEat.'
      }
    },
    nav: {
      about: 'Működése',
      features: 'Funkciók',
      testimonials: 'Vélemények',
      pricing: 'Árak',
      faq: 'GYIK',
      contacts: 'Kapcsolat',
      getStarted: 'Kezdés'
    },
    about: {
      description: 'Mesterséges intelligencián alapuló ételszkenner, amely címkéket, vonalkódokat, ételeket és étlapokat olvas, és a profilod alapján kiemeli az allergéneket, összetevőket és tápanyag-információkat.'
    },
    hero: {
      badge: 'Ételbiztonság MI-vel',
      title: 'Tudd meg azonnal',
      title2: 'mennyire biztonságos az étel',
      titleHighlight: 'számodra',
      subtitle: 'Alapján',
      description: 'Szkenneld az ételeket, vonalkódokat, összetevőlistákat és étlapokat, és ellenőrizd másodpercek alatt, megfelelnek-e a szervezetednek.',
      typing: ['krónikus betegségek', 'allergiák', 'ételintoleranciák'],
      stat1: '500+',
      stat1Label: 'Krónikus betegségek',
      stat2: '250+',
      stat2Label: 'Allergiák',
      stat3: '100+',
      stat3Label: 'Ételintoleranciák',
      downloadButton: 'Letöltés az App Store-ból'
    },
    howItWorks: {
      badge: 'Működése',
      mainTitle: 'Hogyan működik a SafeEat',
      subtitle: 'Az ételszkennelés folyamata egyszerű és gyors – néhány lépésben személyre szabott eredményt kapsz.',
      setupBadge: 'Egyszeri beállítás',
      setupTitle: 'Hozd létre az egészségügyi profilodat',
      setupDesc: 'Az első indításkor add meg az egészségügyi adataidat, hogy a SafeEat a neked megfelelő módon elemezzen. Ezt csak egyszer kell megtenned, ezután minden elemzés a profilodra épül.',
      cards: {
        diseases: {
          title: 'Krónikus betegségek',
          desc: 'Add hozzá krónikus állapotaidat az 500+ betegséget tartalmazó adatbázisunkból. A cukorbetegségtől a szívproblémákig sok mindent lefedünk.',
          statLabel: 'állapot'
        },
        allergies: {
          title: 'Allergiák',
          desc: 'Válaszd ki allergiáidat a 250+ lehetőség közül. A gyakori olajos magvaktól és tengeri herkentyűktől a ritkább allergénekig – a nagyobb biztonságért.',
          statLabel: 'allergia'
        },
        intolerances: {
          title: 'Ételintoleranciák',
          desc: 'Add meg ételintoleranciáidat a 100+ opció közül. Laktóz, glutén, fruktóz és sok más – hogy kényelmesebben ehess.',
          statLabel: 'intolerancia'
        }
      },
      connectorText: 'Ezután minden egyes szkennelésnél:',
      stepLabel: 'Lépés',
      step1Title: 'Szkenneld az ételt',
      step1Desc: 'Szkenneld bármelyik ételt — egyetlen terméket, kész fogást, tápanyag­táblázatot, vonalkódot vagy akár egy éttermi étlapot. Csak irányítsd a kamerát, a többit a SafeEat intézi.',
      step2Title: 'MI-alapú elemzés',
      step2Desc: 'Az alkalmazás elemzi a képet, és összeveti a betegségeket, allergiákat és intoleranciákat tartalmazó profiloddal, hogy felmérje a megfelelőséget.',
      step3Title: 'Részletes jelentés',
      step3Desc: 'A SafeEat részletes jelentést ad arról, mennyire illeszkedik az elemzett étel az egészségi állapotodhoz és az étrendi igényeidhez.',
      step4Title: 'Hozz döntést',
      step4Desc: 'A személyre szabott információk alapján kiválaszthatod a számodra legjobb ételt. Egyél magabiztosan, tudva, mi jó a testednek.',
      processTitle: 'Nézd meg működés közben',
      processSubtitle: 'Nézd végig, ahogy a SafeEat valós időben elemzi az ételedet'
    },
    features: {
      title: 'Erős funkciók',
      subtitle: 'Minden, amire a biztonságosabb étkezéshez szükséged van',
      items: [
        {
          title: 'Szkenneld bármelyik terméket',
          subtitle: 'a hétköznapi nassolnivalóktól az egzotikus gyümölcsökig',
          description: 'Irányítsd a kamerát bármilyen csomagolt vagy kimért élelmiszerre — a mindennapi szupermarketes termékektől a ritka trópusi gyümölcsökig —, és azonnali biztonsági ellenőrzést kapsz. A SafeEat felismeri a terméket és elemzi az összetevőit, hogy segítsen elkerülni a kockázatos vagy nem kívánt anyagokat.',
          highlights: ['Csomagolt és kimért élelmiszerek', 'Azonnali biztonsági ellenőrzés', 'Összetevő-elemzés'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Táplálkozási címkék és vonalkódok szkennelése',
          subtitle: 'értsd meg a bonyolult címkéket másodpercek alatt',
          description: 'Felejtsd el az apró betűk bogarászását. A SafeEat másodpercek alatt beolvassa a tápanyag­táblázatot, az összetevőlistát és a vonalkódot, és azonnal kiemeli az allergéneket, adalékanyagokat vagy potenciálisan ártalmas összetevőket, hogy pontosan tudd, mit eszel.',
          highlights: ['Tápanyag­táblázat szkennelése', 'Allergén­felismerés', 'Figyelmeztetés kockázatos anyagokra'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Ételek szkennelése — összetevők felismerése',
          subtitle: 'csak fotózd le a tányérodat',
          description: 'Készíts fotót az ételedről, és a SafeEat valószínű összetevőkre bontja azt. Legyen szó házi ételről, menzáról vagy éttermi fogásról, a mesterséges intelligencia képes felismerni a lehetséges allergéneket vagy az étrendedbe nem illő komponenseket.',
          highlights: ['MI-alapú összetevő­felismerés', 'Bármilyen fogással működik', 'Diétakompatibilitás-ellenőrzés'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Éttermek étlapjának szkennelése',
          subtitle: 'találd meg a biztonságos fogásokat rendelés előtt',
          description: 'Irányítsd a kamerát egy kávézó vagy étterem étlapjára, és a SafeEat elemzi az egyes fogásokat, majd megmutatja, melyek illenek az egészségi állapotodhoz és az étrendedhez. Egyél nyugodtabban házon kívül, rejtett allergénektől és nem kívánt összetevőktől való félelem nélkül.',
          highlights: ['Étlap-elemzés', 'Ajánlott biztonságos fogások', 'Rejtett allergének felismerése'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Készítsd el a saját biztonságos ételeid listáját',
          subtitle: 'építs fel egy személyes „tiszta" élelmiszer-adatbázist',
          description: 'Mentsd el azokat az ételeket, amelyek megfelelnek neked, és hozd létre saját „tiszta" élelmiszerkönyvtáradat, amelyet bármikor átnézhetsz vásárlás vagy éttermi étkezés közben. Személyes adatbázisod minden szkenneléssel bővül, és segít gyorsabban dönteni.',
          highlights: ['Személyes élelmiszer-adatbázis', 'Gyors ellenőrzés útközben', 'Okos ajánlások'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Felhasználók világszerte bíznak benne',
      subtitle: 'Egy világszintű felhasználói közösség támaszkodik a SafeEat-re.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, Amerikai Egyesült Államok',
          rating: 5,
          content: 'A SafeEat teljesen megváltoztatta a bevásárlási szokásaimat! Lisztérzékeny vagyok, és egy termék beszkennelése csak pár másodperc. Vége az apró betűs címkék bogarászásának az üzletben.'
        },
        {
          name: 'Marcus L.',
          location: 'London, Egyesült Királyság',
          rating: 5,
          content: 'Többféle ételallergiával számomra mindig stresszes volt étterembe menni. Most már csak beszkennelem az étlapot, és azonnal látom, mi biztonságos. Igazi game changer!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokió, Japán',
          rating: 5,
          content: 'Minden nap használom a SafeEat-et a cukorbetegségem kezeléséhez. A tápanyagcímke-szkenner segít azonnal követni a szénhidrátot és a cukrot. Nagyon ajánlom!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Németország',
          rating: 5,
          content: 'A fiamnak súlyos dióallergiája van. A SafeEat nyugalmat ad, amikor nassolnivalót veszek neki. A vonalkód-szkenner lenyűgözően pontos.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spanyolország',
          rating: 5,
          content: 'Laktózérzékeny vagyok és sokat utazom. A SafeEat bármelyik nyelven használható — külföldön egyszerűen beszkennelem az étlapot, és sokkal nyugodtabban eszem!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'A férjem szívproblémái miatt oda kell figyelnünk a nátriumbevitelre. A SafeEat azonnali elemzése sokkal könnyebbé tette az étkezés megtervezését.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Ausztrália',
          rating: 5,
          content: 'A fogás-szkennelő funkció fantasztikus! Lefotóztam egy éttermi fogást, és az app rejtett glutént tartalmazó összetevőket is kiszúrt. Megmentett egy csúnya reakciótól!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubaj, Egyesült Arab Emírségek',
          rating: 5,
          content: 'Szigorú halal étrendet követek. A SafeEat segít ellenőrizni a számomra ismeretlen, csomagolt termékek összetevőit. Nélkülözhetetlen alkalmazás!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Kanada',
          rating: 5,
          content: 'Három gyerekem van, mindegyikük más-más allergiával — a bevásárlás rémálom volt. Most pár másodperc alatt mindent beszkennelek. Ez az app szó szerint életmentő!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Svédország',
          rating: 5,
          content: 'Crohn-betegségem van, és bizonyos kiváltó ételeket kerülnöm kell. A személyre szabott SafeEat-profil olyan összetevőkre is rávilágít, amiket másként simán elnéznék.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazília',
          rating: 5,
          content: 'Az étlap-szkenner tökéletes a tenger gyümölcsei iránti allergiámhoz. Sokkal magabiztosabban rendelek új éttermekben!'
        },
        {
          name: 'David C.',
          location: 'Párizs, Franciaország',
          rating: 5,
          content: 'Szakácsként, aki bizonyos ételekre érzékeny, a SafeEat-et használom arra, hogy főzés előtt ellenőrizzem az alapanyagokat. A MI-alapú felismerés meglepően pontos.'
        },
        {
          name: 'Fatima A.',
          location: 'Kairó, Egyiptom',
          rating: 5,
          content: 'A terhességi cukorbetegség kezelése nagyon megterhelő volt, amíg rá nem találtam a SafeEat-re. Az élelmiszercímkék szkennelése hihetetlenül leegyszerűsíti a nyomon követést!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigéria',
          rating: 5,
          content: 'Olyan ritka intoleranciám van, amit a legtöbb alkalmazás nem támogat. A SafeEat-ben hozzáadhattam a profilomhoz, és most már minden alkalommal felismeri!'
        },
        {
          name: 'Sophie L.',
          location: 'Amszterdam, Hollandia',
          rating: 5,
          content: 'Vegán vagyok és szójára is allergiás — biztonságos ételt találni rémálom volt! A SafeEat felismeri a rejtett állati eredetű összetevőket ÉS a szóját is. Imádom!'
        },
        {
          name: 'Kenji N.',
          location: 'Szöul, Dél-Korea',
          rating: 5,
          content: 'A vonalkód-adatbázis hatalmas! Még az importált termékeket is felismeri. Tökéletes az ismeretlen nemzetközi élelmiszerek ellenőrzésére.'
        },
        {
          name: 'Elena P.',
          location: 'Róma, Olaszország',
          rating: 5,
          content: 'Idős édesanyámnak több egészségügyi problémája is van. Létrehoztam neki egy profilt, és most újra önállóan tud bevásárolni. Köszönöm, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Egyszerű, átlátható árak',
      subtitle: 'Válaszd ki a hozzád illő csomagot',
      free: 'Ingyenes',
      freeDesc: '5 ingyenes szkennelés kipróbáláshoz',
      freeFeature1: '5 MI-alapú szkennelés',
      freeFeature2: 'Alap elemzés',
      freeFeature3: 'Korlátozott előzmények',
      weekly: 'Heti',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Ideális rendszeres használathoz',
      weeklyFeature1: 'Korlátlan szkennelés',
      weeklyFeature2: 'Fejlett elemzés',
      weeklyFeature3: 'Teljes előzménylista',
      weeklyFeature4: 'Elsőbbségi ügyfélszolgálat',
      annual: 'Éves',
      annualPrice: '$29.99',
      annualDesc: 'A legjobb ár-érték arány — 60% megtakarítás',
      annualBadge: 'Legnépszerűbb',
      annualFeature1: 'Minden benne van, ami a heti csomagban',
      annualFeature2: 'Évente 125 $ megtakarítás',
      annualFeature3: 'Prémium támogatás',
      annualFeature4: 'Koraszériás hozzáférés az új funkciókhoz',
      button: 'Kezdés'
    },
    faq: {
      title: 'Gyakran ismételt kérdések',
      items: [
        {
          q: 'Mit tudok a SafeEat segítségével beszkennelni?',
          a: 'A SafeEat képes elemezni: összetevőlistákat és tápanyag­táblázatokat (fotóról), vonalkódokat, csomagolt és kimért élelmiszereket, kész ételeket (fotóról), valamint éttermi étlapokat.'
        },
        {
          q: 'Hogyan működik az étel-/fogásfelismerés?',
          a: 'A mesterséges intelligencia felismeri a fogást, és megbecsüli a valószínű összetevőket. Jó megvilágítás és tiszta nézőpont mellett működik a legjobban; a rejtett összetevőket vagy a kereszt­szennyeződést előfordulhat, hogy nem ismeri fel.'
        },
        {
          q: 'Lehet éttermi étlapokat is szkennelni?',
          a: 'Igen. Az alkalmazás jelzi a lehetséges allergéneket és a diétáddal ütköző összetevőket. Az étlapok azonban lehetnek hiányosak, ezért mindig érdemes rákérdezni a személyzetnél az összetevőkre, az olajokra, a közös olajsütő használatára, a szószokra és a kereszt­szennyeződés kockázatára.'
        },
        {
          q: 'Milyen egészségügyi állapotokat támogat a SafeEat?',
          a: 'Ételallergiákat, intoleranciákat (például laktóz- vagy gluténérzékenységet), valamint bizonyos étrendi igényeket (alacsony nátrium, szénhidrát­tudatos étrend, bizonyos összetevők kerülése).'
        },
        {
          q: 'Működik a SafeEat internetkapcsolat nélkül is?',
          a: 'A szkenneléshez és az MI-alapú elemzéshez általában internetkapcsolat szükséges. A korábban elmentett eredmények és listák eszköztől és alkalmazás­beállítástól függően offline is elérhetők lehetnek.'
        },
        {
          q: 'Mennyire pontos a SafeEat elemzése?',
          a: 'A pontosság függ a fotó minőségétől, a szöveg olvashatóságától, a nyelvtől/formátumtól, illetve a menü információinak teljességétől. A SafeEat tájékoztató eszköz — magas kockázat esetén mindig ellenőrizd más forrásból is az információt.'
        },
        {
          q: 'Mi történik, ha a SafeEat téved?',
          a: 'Ne hagyatkozz olyan eredményre, amely gyanúsnak tűnik; készíts új, élesebb fotót; ellenőrizd a címkét, a gyártó weboldalát, vagy kérdezd meg a személyzetet; magas kockázat esetén inkább válaszd a biztonságosabb lehetőséget.'
        },
        {
          q: 'Be tudom jelenteni a hibás elemzéseket?',
          a: 'Igen: írj a support@safeeat.app címre, és csatolj képernyőképet, fotót a címkéről/étlapról/ételről, add meg az országot, márkát, terméknevet, és írd le, szerinted mi a hiba.'
        },
        {
          q: 'Miért lehet kevésbé pontos az étel-/fogásfelismerés?',
          a: 'A hasonló kinézetű ételek, a rejtett összetevők, a vastag szószok vagy díszítések, a gyenge fényviszonyok vagy életlen fotók, valamint a nagyon egyedire szabott vagy regionális fogások mind ronthatják a pontosságot.'
        },
        {
          q: 'Orvostechnikai eszköznek számít a SafeEat?',
          a: 'Nem. A SafeEat kizárólag tájékoztató jellegű eszköz. Orvosi tanácsért mindig egészségügyi szakemberhez fordulj.'
        },
        {
          q: 'Milyen felelősséget vállal a SafeEat?',
          a: 'Az információk „jelen állapotukban" kerülnek megadásra, és nem garantálható, hogy teljesek vagy hibamentesek. A döntéseidért te vagy felelős; előfordulhatnak fel nem tüntetett összetevők és kereszt­szennyeződés is.'
        },
        {
          q: 'Teljes mértékben támaszkodhatok a SafeEat-re súlyos allergiák esetén?',
          a: 'Nem. Fontos, hogy elolvasd a címkéket, megkérdezd a személyzetet, kövesd a saját vészhelyzeti tervedet, és tartsd magadnál a felírt gyógyszereket. Ha bizonytalan vagy, inkább kerüld a terméket vagy fogást.'
        },
        {
          q: 'Hogyan véditek az egészségügyi adataimat?',
          a: 'A lehető legkevesebb adatot gyűjtjük, és biztonsági intézkedésekkel, valamint korlátozott hozzáféréssel védjük azokat. Adatvédelmi kérdések esetén írj a support@safeeat.app címre. További részletek az Adatvédelmi tájékoztatóban.'
        },
        {
          q: 'Használhatják a gyerekek is a SafeEat-et?',
          a: 'Igen, felnőtt felügyelete mellett. Súlyos allergiák vagy bonyolult étrend esetén különösen fontos az óvatosság, és hogy megbízható forrásból is ellenőrizd az információkat.'
        },
        {
          q: 'Mit tegyek, ha allergiás reakcióm van?',
          a: 'Azonnal kérj orvosi segítséget, kövesd a saját vészhelyzeti tervedet, és életveszélyes tünetek esetén hívd a segélyhívót. Ezután érdemes jelenteni az esetet a kezelőorvosnak, az étteremnek vagy a gyártónak.'
        }
      ]
    },
    cta: {
      title: 'Készen állsz megváltoztatni az étkezési szokásaidat?',
      subtitle: 'Csatlakozz ahhoz a több ezer emberhez, akik nap mint nap használják a SafeEat-et',
      description: 'Töltsd le most, és kapj 5 ingyenes MI-alapú szkennelést',
      button: 'Letöltés az App Store-ból',
      feature1: 'Nem szükséges bankkártya',
      feature2: '100% privát és biztonságos',
      feature3: 'Azonnal használható'
    },
    newsletter: {
      title: 'Maradj naprakész',
      subtitle: 'Kapj friss híreket és tippeket a biztonságosabb étkezéshez',
      placeholder: 'Add meg az e-mail címed',
      button: 'Feliratkozás',
      error: 'Adj meg egy érvényes e-mail címet',
      success: 'Köszönjük a feliratkozást!'
    },
    seo: {
      title: 'Személyes asszisztensed az ételbiztonsághoz',
      text: 'A SafeEat mesterséges intelligencián alapuló ételszkenner olyan emberek számára, akik ételallergiával, intoleranciával vagy étrendi korlátozásokkal élnek. Azonnal beszkennelheted a címkéket, tápanyag­táblázatokat, vonalkódokat, éttermi étlapokat és kész ételeket, hogy ellenőrizd, illeszkednek-e a profilodhoz. Akár lisztérzékeny vagy, laktózérzékeny, dióallergiás vagy gluténmentes, vegán vagy sószegény étrendet követsz, a SafeEat segít biztonságosabb döntéseket hozni.',
      keywords: [
        'ételallergia alkalmazás',
        'összetevő-szkenner',
        'allergénfelismerés',
        'tápértékcímke-szkennelés',
        'vonalkódolvasó',
        'étlap-szkenner',
        'gluténmentes ellenőrzés',
        'laktózérzékenység app',
        'étrendkövetés',
        'ételbiztonság'
      ]
    },
    cookies: {
      bannerText: 'Cookie-kat használunk: a webhely működéséhez szükséges cookie-kat, valamint elemző cookie-kat csak a hozzájárulásoddal.',
      decline: 'Elutasítás',
      accept: 'Elfogadás'
    },
    legal: {
      backHome: 'Vissza a főoldalra'
    },
    footer: {
      product: 'Termék',
      features: 'Funkciók',
      howItWorks: 'Működése',
      testimonials: 'Vélemények',
      pricing: 'Árak',
      faq: 'GYIK',
      contacts: 'Kapcsolat',
      legal: 'Jogi információk',
      privacy: 'Adatvédelmi tájékoztató',
      terms: 'Szolgáltatási feltételek',
      usePolicy: 'Felhasználói kézikönyg',
      disclaimer: 'Jogi nyilatkozat',
      company: 'FJCode OU',
      register: 'Cégjegyzékszám 16993109',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. Minden jog fenntartva.'
    }
  },
  id: {
    meta: {
      home: {
        title: 'SafeEat | Pemindai makanan AI untuk alergi & intoleransi',
        description: 'Pindai label, barcode, menu, dan hidangan untuk menyorot alergen serta risiko diet berdasarkan profilmu: alergi, intoleransi, dan penyakit kronis.'
      },
      privacy: {
        title: 'Kebijakan Privasi | SafeEat',
        description: 'Pelajari bagaimana SafeEat mengumpulkan, menggunakan, dan melindungi data Anda.'
      },
      terms: {
        title: 'Syarat Penggunaan | SafeEat',
        description: 'Tinjau ketentuan yang berlaku untuk penggunaan SafeEat.'
      },
      manual: {
        title: 'Panduan Pengguna | SafeEat',
        description: 'Pelajari cara menggunakan SafeEat untuk memindai makanan dengan aman.'
      },
      disclaimer: {
        title: 'Penafian | SafeEat',
        description: 'Pahami informasi, panduan, dan batasan SafeEat.'
      }
    },
    nav: {
      about: 'Cara kerja',
      features: 'Fitur',
      testimonials: 'Ulasan',
      pricing: 'Harga',
      faq: 'FAQ',
      contacts: 'Kontak',
      getStarted: 'Mulai'
    },
    about: {
      description: 'Pemindai makanan bertenaga AI yang membaca label, barcode, hidangan, dan menu, lalu menyorot alergen, bahan, dan informasi nutrisi berdasarkan profil kesehatan Anda.'
    },
    hero: {
      badge: 'Keamanan makanan bertenaga AI',
      title: 'Periksa segera',
      title2: 'keamanan makanan',
      titleHighlight: 'khusus untuk Anda',
      subtitle: 'Berdasarkan',
      description: 'Pindai bahan makanan, barcode, komposisi, dan menu untuk segera mengecek apakah makanan aman bagi tubuh Anda',
      typing: ['penyakit kronis', 'alergi', 'intoleransi makanan'],
      stat1: '500+',
      stat1Label: 'Penyakit kronis',
      stat2: '250+',
      stat2Label: 'Alergi',
      stat3: '100+',
      stat3Label: 'Intoleransi makanan',
      downloadButton: 'Unduh di App Store'
    },
    howItWorks: {
      badge: 'Cara kerja',
      mainTitle: 'Cara kerja SafeEat',
      subtitle: 'Proses pemindaian kami sederhana, cepat, dan memberikan hasil personal dalam beberapa langkah mudah.',
      setupBadge: 'Pengaturan sekali saja',
      setupTitle: 'Buat profil kesehatan Anda',
      setupDesc: 'Saat onboarding, Anda menyesuaikan SafeEat dengan menambahkan informasi kesehatan Anda. Ini dilakukan sekali dan memungkinkan analisis makanan yang dipersonalisasi khusus untuk Anda.',
      cards: {
        diseases: {
          title: 'Penyakit kronis',
          desc: 'Tambahkan kondisi kronis Anda dari database kami dengan 500+ penyakit. Dari diabetes hingga penyakit jantung — kami siap membantu.',
          statLabel: 'kondisi'
        },
        allergies: {
          title: 'Alergi',
          desc: 'Pilih alergi Anda dari 250+ opsi. Dari kacang dan seafood yang umum hingga alergen langka — tetap terlindungi.',
          statLabel: 'alergi'
        },
        intolerances: {
          title: 'Intoleransi makanan',
          desc: 'Cantumkan intoleransi Anda dari 100+ opsi. Laktosa, gluten, fruktosa, dan lainnya — makan tanpa rasa tidak nyaman.',
          statLabel: 'intoleransi'
        }
      },
      connectorText: 'Lalu — setiap kali Anda memindai:',
      stepLabel: 'Langkah',
      step1Title: 'Pindai makanan',
      step1Desc: 'Pindai makanan apa pun — produk satuan, hidangan siap saji, label nutrisi, barcode, bahkan menu restoran. Cukup arahkan kamera, SafeEat akan mengurus sisanya.',
      step2Title: 'Analisis AI',
      step2Desc: 'Aplikasi menganalisis gambar dan mencocokkannya dengan profil penyakit, alergi, serta intoleransi makanan untuk menilai kecocokan.',
      step3Title: 'Dapatkan laporan Anda',
      step3Desc: 'SafeEat memberikan laporan terperinci tentang produk yang dianalisis dan menunjukkan seberapa baik kecocokannya dengan profil kesehatan serta kebutuhan diet Anda.',
      step4Title: 'Ambil keputusan',
      step4Desc: 'Dengan wawasan yang dipersonalisasi, Anda dapat memilih makanan terbaik untuk Anda. Makan dengan tenang — dan ketahui persis apa yang cocok untuk tubuh Anda.',
      processTitle: 'Lihat dalam aksi',
      processSubtitle: 'Lihat bagaimana SafeEat menganalisis makanan Anda secara real-time'
    },
    features: {
      title: 'Fitur yang kuat',
      subtitle: 'Semua yang Anda butuhkan untuk makan dengan aman',
      items: [
        {
          title: 'Pindai produk apa pun',
          subtitle: 'dari camilan biasa hingga buah eksotis',
          description: 'Arahkan kamera ke makanan kemasan atau tanpa kemasan — dari produk supermarket sehari-hari hingga buah tropis langka — dan dapatkan penilaian keamanan seketika. SafeEat mengidentifikasi produk dan menganalisis komposisinya, sehingga Anda dapat menghindari bahan berisiko atau berbahaya.',
          highlights: ['Makanan kemasan & tanpa kemasan', 'Pemeriksaan keamanan instan', 'Analisis komposisi'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Pindai label nutrisi dan barcode',
          subtitle: 'pahami label yang membingungkan dengan cepat',
          description: 'Tak perlu lagi kesulitan membaca tulisan kecil atau label yang membingungkan. SafeEat dapat memindai informasi nutrisi, daftar bahan, dan barcode dalam hitungan detik. Aplikasi langsung menyorot alergen, aditif, atau zat yang berpotensi berbahaya, sehingga Anda tahu persis apa yang Anda konsumsi.',
          highlights: ['Pemindaian informasi nutrisi', 'Deteksi alergen', 'Peringatan zat berbahaya'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Pindai hidangan — identifikasi bahan',
          subtitle: 'dari foto makanan Anda',
          description: 'Ambil foto makanan Anda, dan SafeEat memecahnya menjadi bahan-bahan. Baik masakan rumahan, food court, maupun restoran fine dining, AI kami mengidentifikasi alergen potensial atau bahan yang tidak sesuai dengan diet Anda.',
          highlights: ['Pengenalan bahan dengan AI', 'Bisa untuk semua hidangan', 'Cek kecocokan diet'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Pindai menu restoran',
          subtitle: 'temukan pilihan aman sebelum memesan',
          description: 'Arahkan kamera ke menu kafe atau restoran, dan SafeEat menganalisis tiap hidangan serta menunjukkan mana yang aman untuk kebutuhan Anda. Sempurna untuk makan di luar tanpa khawatir tentang alergen tersembunyi atau bahan yang tidak diinginkan.',
          highlights: ['Analisis menu', 'Menemukan hidangan aman', 'Deteksi alergen tersembunyi'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Buat daftar makanan aman pribadi Anda',
          subtitle: 'bangun perpustakaan "clean eating" Anda',
          description: 'Simpan makanan yang aman bagi Anda, bangun perpustakaan "clean eating" pribadi, dan akses kapan saja saat berbelanja atau makan di luar. Database makanan personal Anda akan berkembang dengan setiap pemindaian.',
          highlights: ['Perpustakaan makanan personal', 'Akses cepat di mana saja', 'Rekomendasi cerdas'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Dipercaya oleh ribuan orang',
      subtitle: 'Dipercaya oleh komunitas pengguna di seluruh dunia.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat benar-benar mengubah cara saya berbelanja! Saya punya celiac, dan memindai produk hanya butuh beberapa detik. Tidak perlu lagi membaca label super kecil di toko.'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: 'Sebagai seseorang dengan banyak alergi makanan, makan di luar selalu bikin stres. Sekarang saya cukup memindai menu dan tahu persis mana yang aman. Game changer!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'Saya memakai SafeEat setiap hari untuk mengelola diabetes. Pemindai label nutrisi membantu saya memantau karbohidrat dan gula seketika. Sangat direkomendasikan!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: 'Anak saya alergi kacang yang parah. SafeEat memberi saya ketenangan saat membeli camilan untuknya. Pemindai barcode-nya sangat akurat.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: 'Saya intoleransi laktosa dan sering bepergian. SafeEat bekerja di semua bahasa — saya tinggal memindai menu restoran di luar negeri dan makan tanpa khawatir!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'Mengelola penyakit jantung suami saya berarti kami harus memperhatikan natrium. Analisis instan SafeEat membuat perencanaan makan jauh lebih mudah.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Pemindai hidangan itu luar biasa! Saya memotret hidangan restoran dan aplikasi menemukan bahan gluten tersembunyi. Saya terselamatkan dari reaksi buruk!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'Saya mengikuti diet halal yang ketat. SafeEat membantu saya mengecek bahan pada makanan kemasan yang tidak saya kenal. Aplikasi yang wajib punya!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Dengan tiga anak yang punya alergi berbeda-beda, belanja jadi mimpi buruk. Sekarang saya memindai semuanya dalam hitungan detik. Aplikasi ini penyelamat!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'Saya punya penyakit Crohn dan harus menghindari pemicu tertentu. Profil personal SafeEat menangkap bahan yang biasanya saya lewatkan.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'Fitur pemindai menu sempurna untuk alergi seafood saya. Sekarang saya lebih percaya diri memesan di restoran baru!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'Sebagai koki dengan sensitivitas tertentu, saya memakai SafeEat untuk mengecek bahan sebelum memasak. Pengenalan AI-nya sangat mengesankan dan akurat.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: 'Mengelola diabetes gestasional terasa berat sampai saya menemukan SafeEat. Memindai label makanan membuatnya jadi sangat sederhana!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Saya punya intoleransi makanan yang langka dan sebagian besar aplikasi tidak mendukungnya. SafeEat memungkinkan saya menambahkannya ke profil, dan sekarang selalu terdeteksi!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: 'Vegan dengan alergi kedelai di sini — mencari makanan aman itu sulit! SafeEat menemukan produk hewani tersembunyi DAN kedelai. Saya suka!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'Database barcode-nya besar sekali! Bahkan produk impor pun dikenali. Cocok untuk mengecek makanan internasional yang tidak familiar.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: 'Ibu saya yang sudah lanjut usia punya beberapa kondisi kesehatan. Saya membuatkan profilnya, dan sekarang ia bisa belanja mandiri lagi. Terima kasih, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Harga sederhana dan transparan',
      subtitle: 'Pilih paket yang cocok untuk Anda',
      free: 'Gratis',
      freeDesc: '5 pemindaian gratis untuk mencoba',
      freeFeature1: '5 pemindaian AI',
      freeFeature2: 'Analisis dasar',
      freeFeature3: 'Riwayat terbatas',
      weekly: 'Mingguan',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Cocok untuk pengguna rutin',
      weeklyFeature1: 'Pemindaian tak terbatas',
      weeklyFeature2: 'Analisis lanjutan',
      weeklyFeature3: 'Riwayat lengkap',
      weeklyFeature4: 'Dukungan prioritas',
      annual: 'Tahunan',
      annualPrice: '$29.99',
      annualDesc: 'Nilai terbaik — hemat 60%',
      annualBadge: 'Paling populer',
      annualFeature1: 'Semua dari Mingguan',
      annualFeature2: 'Hemat $125/tahun',
      annualFeature3: 'Dukungan premium',
      annualFeature4: 'Akses awal fitur baru',
      button: 'Mulai'
    },
    faq: {
      title: 'Pertanyaan yang sering diajukan',
      items: [
        {
          q: 'Apa saja yang bisa saya pindai dengan SafeEat?',
          a: 'SafeEat dapat menganalisis: daftar bahan dan label nutrisi (foto), barcode, makanan kemasan maupun tanpa kemasan, hidangan (foto), serta menu restoran.'
        },
        {
          q: 'Bagaimana fitur pengenalan hidangan bekerja?',
          a: 'AI mengidentifikasi hidangan dan memperkirakan bahan yang kemungkinan ada. Hasil terbaik dengan pencahayaan dan sudut yang baik; bahan tersembunyi atau kontaminasi silang bisa terlewat.'
        },
        {
          q: 'Apakah saya bisa memindai menu restoran?',
          a: 'Ya. Aplikasi menandai alergen potensial dan konflik diet. Menu bisa tidak lengkap; selalu konfirmasi dengan staf tentang bahan, minyak, fryer bersama, saus, dan kontaminasi silang.'
        },
        {
          q: 'Kondisi kesehatan apa yang didukung SafeEat?',
          a: 'Alergi makanan, intoleransi (mis. laktosa, gluten), dan kebutuhan diet (rendah natrium, fokus karbohidrat, menghindari bahan tertentu).'
        },
        {
          q: 'Apakah SafeEat bisa digunakan offline?',
          a: 'Biasanya pemindaian/AI memerlukan internet. Hasil yang tersimpan dan daftar Anda mungkin tersedia offline tergantung aplikasi dan perangkat.'
        },
        {
          q: 'Seberapa akurat analisis SafeEat?',
          a: 'Tergantung kualitas foto, teks pada label, bahasa/format, dan kelengkapan menu. Ini hanya bersifat informatif — selalu cek ulang saat risikonya tinggi.'
        },
        {
          q: 'Bagaimana jika SafeEat salah?',
          a: 'Jangan percaya hasil yang meragukan; pindai ulang dengan foto lebih baik; verifikasi lewat label/situs web/staf; pilih opsi paling aman saat risikonya tinggi.'
        },
        {
          q: 'Bisakah saya melaporkan hasil analisis yang salah?',
          a: 'Ya: support@safeeat.app dengan tangkapan layar, foto label/menu/hidangan, negara/merek/produk, dan apa yang terasa salah.'
        },
        {
          q: 'Mengapa pengenalan hidangan bisa kurang akurat?',
          a: 'Hidangan yang mirip, bahan tersembunyi, saus/ garnish yang kuat, pencahayaan rendah/blur, serta hidangan yang sangat dikustomisasi atau regional.'
        },
        {
          q: 'Apakah SafeEat merupakan perangkat medis?',
          a: 'Tidak. Ini hanya untuk informasi. Konsultasikan dengan tenaga profesional untuk saran medis.'
        },
        {
          q: 'Apa tanggung jawab SafeEat?',
          a: 'Informasi diberikan "apa adanya"; kelengkapan tidak dapat dijamin. Anda bertanggung jawab atas pilihan Anda; bahan yang tidak dicantumkan dan kontaminasi silang dapat terjadi.'
        },
        {
          q: 'Apakah saya harus mengandalkan SafeEat saja untuk alergi berat?',
          a: 'Tidak. Baca label, tanyakan pada staf, ikuti rencana tindakan Anda, dan bawa obat yang diresepkan; hindari jika ragu.'
        },
        {
          q: 'Bagaimana Anda melindungi data kesehatan saya?',
          a: 'Kami mengumpulkan data seminimal mungkin dan melindunginya dengan langkah keamanan, akses terbatas, serta kontak privasi melalui support@safeeat.app. Lihat Kebijakan Privasi.'
        },
        {
          q: 'Bisakah saya memakai SafeEat untuk anak saya?',
          a: 'Ya, dengan pengawasan. Lebih berhati-hati untuk alergi berat/kebutuhan diet kompleks; verifikasi dengan sumber tepercaya.'
        },
        {
          q: 'Apa yang harus saya lakukan jika mengalami reaksi?',
          a: 'Segera cari bantuan medis, ikuti rencana darurat Anda, hubungi 112 untuk gejala berat, lalu laporkan kepada penyedia/restoran/produsen.'
        }
      ]
    },
    cta: {
      title: 'Siap mengubah cara Anda makan?',
      subtitle: 'Bergabunglah dengan ribuan orang yang menggunakan SafeEat setiap hari',
      description: 'Unduh sekarang dan dapatkan 5 pemindaian AI gratis',
      button: 'Unduh di App Store',
      feature1: 'Tanpa kartu kredit',
      feature2: '100% privat & aman',
      feature3: 'Akses instan'
    },
    newsletter: {
      title: 'Tetap terbarui',
      subtitle: 'Dapatkan pembaruan terbaru dan tips kesehatan',
      placeholder: 'Masukkan email Anda',
      button: 'Berlangganan',
      success: 'Terima kasih sudah berlangganan!',
      error: 'Masukkan alamat email yang valid'
    },
    seo: {
      title: 'Asisten pribadi Anda untuk keamanan makanan',
      text: 'SafeEat adalah aplikasi pemindai makanan bertenaga AI untuk orang dengan alergi makanan, intoleransi, dan pembatasan diet. Pindai label, informasi nutrisi, barcode, menu restoran, dan hidangan siap saji untuk mengecek keamanan berdasarkan profil Anda. Baik Anda memiliki celiac, intoleransi laktosa, alergi kacang, atau mengikuti diet bebas gluten, vegan, atau rendah natrium, SafeEat membantu Anda memilih dengan aman.',
      keywords: [
        'Aplikasi alergi makanan',
        'Pemindai bahan',
        'Detektor alergen',
        'Pembaca label nutrisi',
        'Pemindai barcode',
        'Pemindai menu',
        'Cek bebas gluten',
        'Aplikasi intoleransi laktosa',
        'Pelacak diet',
        'Keamanan makanan'
      ]
    },
    cookies: {
      bannerText: 'Kami menggunakan cookie: cookie yang diperlukan untuk menjalankan situs dan cookie analitik hanya dengan persetujuan Anda.',
      decline: 'Tolak',
      accept: 'Terima'
    },
    legal: {
      backHome: 'Kembali ke beranda'
    },
    footer: {
      product: 'Produk',
      features: 'Fitur',
      howItWorks: 'Cara kerja',
      testimonials: 'Ulasan',
      pricing: 'Harga',
      faq: 'FAQ',
      contacts: 'Kontak',
      legal: 'Legal',
      privacy: 'Kebijakan Privasi',
      terms: 'Syarat Layanan',
      usePolicy: 'Panduan Pengguna',
      disclaimer: 'Penafian',
      company: 'FJCode OU',
      register: 'Nomor registrasi 16993109',
      address: 'Viru väljak 2, 10111 Tallinn, Estonia',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. Hak cipta dilindungi.'
    }
  },
  it: {
    meta: {
      home: {
        title: 'SafeEat | Scanner alimentare con IA per allergie e intolleranze',
        description: 'Scansiona etichette, codici a barre, menu e piatti per segnalare allergeni e rischi per il tuo profilo: allergie, intolleranze e condizioni croniche.'
      },
      privacy: {
        title: 'Informativa sulla Privacy | SafeEat',
        description: 'Scopri come SafeEat raccoglie, utilizza e protegge i tuoi dati.'
      },
      terms: {
        title: "Termini d'Uso | SafeEat",
        description: "Consulta i termini che regolano l'utilizzo di SafeEat."
      },
      manual: {
        title: 'Manuale Utente | SafeEat',
        description: 'Scopri come usare SafeEat per scansionare gli alimenti in sicurezza.'
      },
      disclaimer: {
        title: 'Disclaimer | SafeEat',
        description: 'Comprendi le indicazioni informative e i limiti di SafeEat.'
      }
    },
    nav: {
      about: 'Come funziona',
      features: 'Caratteristiche',
      testimonials: 'Testimonianze',
      pricing: 'Prezzi',
      faq: 'FAQ',
      contacts: 'Contatti',
      getStarted: 'Inizia'
    },
    about: {
      description: 'Scanner alimentare con IA che legge etichette, codici a barre, piatti e menu per evidenziare allergeni, ingredienti e rischi in base al tuo profilo.'
    },
    hero: {
      badge: 'Sicurezza alimentare con IA',
      title: 'Controlla subito',
      title2: 'la sicurezza dei cibi',
      titleHighlight: 'Personalizzata per te',
      subtitle: 'Basato su',
      description: 'Scansiona spesa, codici a barre, ingredienti e menu per verificare subito la sicurezza per il tuo corpo',
      typing: ['malattie croniche', 'allergie', 'intolleranze alimentari'],
      stat1: '500+',
      stat1Label: 'Malattie croniche',
      stat2: '250+',
      stat2Label: 'Allergie',
      stat3: '100+',
      stat3Label: 'Intolleranze',
      downloadButton: "Scarica su App Store"
    },
    howItWorks: {
      mainTitle: 'Come funziona',
      badge: 'Come funziona',
      subtitle: 'Il processo di scansione è semplice, veloce e produce risultati personalizzati in pochi passaggi.',
      setupBadge: 'Configurazione una tantum',
      setupTitle: 'Configura il tuo profilo salute',
      setupDesc: "Durante l'onboarding, personalizza SafeEat aggiungendo le tue informazioni sanitarie. Lo fai una sola volta e abiliti l'analisi personalizzata del cibo.",
      cards: {
        diseases: {
          title: 'Malattie croniche',
          desc: 'Aggiungi le tue condizioni croniche dal nostro database di 500+ malattie. Dal diabete alle patologie cardiache: ti copriamo noi.',
          statLabel: 'patologie'
        },
        allergies: {
          title: 'Allergie',
          desc: 'Seleziona le tue allergie tra oltre 250 opzioni. Da frutta a guscio e crostacei comuni ad allergeni rari: resta protetto.',
          statLabel: 'allergie'
        },
        intolerances: {
          title: 'Intolleranze alimentari',
          desc: 'Indica le tue intolleranze tra oltre 100 opzioni. Lattosio, glutine, fruttosio e altro: mangia senza disagi.',
          statLabel: 'intolleranze'
        }
      },
      connectorText: 'Poi, ogni volta che scansioni:',
      processTitle: 'Guarda come funziona',
      processSubtitle: 'Guarda SafeEat analizzare il tuo cibo in tempo reale',
      stepLabel: 'Step',
      step1Title: 'Scansiona il cibo',
      step1Desc: 'Scansiona qualsiasi alimento - confezionati, piatti pronti, etichette, codici a barre o persino menu dei ristoranti.',
      step2Title: 'Analisi IA',
      step2Desc: "L'app confronta l'immagine con il tuo profilo (malattie, allergie, intolleranze) per valutarne la compatibilità.",
      step3Title: 'Ottieni il report',
      step3Desc: 'SafeEat fornisce un report dettagliato su quanto i prodotti siano adatti al tuo profilo.',
      step4Title: 'Decidi cosa mangiare',
      step4Desc: 'Con insight personalizzati, scegli cosa è meglio per te e mangia con sicurezza.'
    },
    features: {
      title: 'Funzionalità potenti',
      subtitle: 'Tutto ciò che serve per mangiare in sicurezza',
      items: [
        {
          title: 'Scansiona qualsiasi prodotto',
          subtitle: 'da snack comuni a frutta esotica',
          description: 'Inquadra qualsiasi alimento confezionato o sfuso, dai prodotti del supermercato alla frutta tropicale, e ottieni subito un controllo di sicurezza. SafeEat identifica il prodotto e analizza la composizione per aiutarti a evitare ingredienti rischiosi.',
          highlights: ['Cibi confezionati e sfusi', 'Controllo immediato', 'Analisi della composizione'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Scansiona etichette e codici a barre',
          subtitle: 'decifra subito etichette complicate',
          description: 'Basta lottare con testi minuscoli. SafeEat scansiona valori nutrizionali, liste ingredienti e codici a barre in pochi secondi. Evidenzia subito allergeni, additivi o sostanze indesiderate così sai esattamente cosa stai mangiando.',
          highlights: ['Scan dei valori nutrizionali', 'Rilevamento allergeni', 'Avvisi su sostanze indesiderate'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Scansiona i piatti – rileva gli ingredienti',
          subtitle: 'da una foto del tuo pasto',
          description: 'Fotografa il piatto e SafeEat lo scompone negli ingredienti. Che sia fatto in casa, in food court o in un ristorante, l’IA individua potenziali allergeni o elementi non compatibili con la tua dieta.',
          highlights: ['Riconoscimento ingredienti con IA', 'Funziona con qualsiasi piatto', 'Verifica compatibilità dieta'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Scansiona i menu dei ristoranti',
          subtitle: 'trova i piatti sicuri prima di ordinare',
          description: 'Inquadra il menu di un bar o ristorante: SafeEat analizza ogni piatto e ti mostra quali sono adatti alle tue esigenze. Perfetto per mangiare fuori senza preoccuparti di allergeni nascosti o ingredienti indesiderati.',
          highlights: ['Analisi dei menu', 'Trova piatti sicuri', 'Allergeni nascosti sotto controllo'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Crea la tua lista di cibi sicuri',
          subtitle: 'costruisci la tua libreria “clean eating”',
          description: 'Salva i cibi che non vanno bene per te, costruisci la tua libreria di alimenti “puliti” e accedici sempre quando fai la spesa o mangi fuori. Il tuo database personalizzato cresce con ogni scansione.',
          highlights: ['Libreria personale di cibi', 'Accesso rapido ovunque', 'Raccomandazioni intelligenti'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Scelto da migliaia di utenti',
      subtitle: 'Apprezzato da una comunità globale di utenti.',
      items: [
        { name: 'Sarah M.', location: 'New York, USA', rating: 5, content: 'SafeEat ha cambiato il modo in cui faccio la spesa! Ho la celiachia e scansiono i prodotti in pochi secondi. Basta etichette minuscole.' },
        { name: 'Marcus L.', location: 'Londra, UK', rating: 5, content: 'Con più allergie alimentari, cenare fuori era stressante. Ora scansiono il menu e so subito cosa è sicuro. Rivoluzionario!' },
        { name: 'Yuki T.', location: 'Tokyo, Giappone', rating: 5, content: 'Uso SafeEat ogni giorno per gestire il diabete. Lo scanner di etichette mi aiuta a monitorare carboidrati e zuccheri all’istante.' },
        { name: 'Anna K.', location: 'Berlino, Germania', rating: 5, content: 'Mio figlio ha gravi allergie alle noci. SafeEat mi dà tranquillità quando compro snack. Lo scanner di codici è precisissimo.' },
        { name: 'Carlos R.', location: 'Madrid, Spagna', rating: 5, content: 'Sono intollerante al lattosio e viaggio molto. SafeEat funziona in ogni lingua: scansiono i menu all’estero e mangio sereno.' },
        { name: 'Priya S.', location: 'Mumbai, India', rating: 5, content: 'Per il cuore di mio marito devo controllare il sodio. L’analisi immediata di SafeEat rende la pianificazione dei pasti molto più semplice.' },
        { name: 'Emma W.', location: 'Sydney, Australia', rating: 5, content: 'Lo scanner dei piatti è fantastico! Ho fotografato il mio piatto al ristorante e ha trovato glutine nascosto. Mi ha evitato una reazione.' },
        { name: 'Ahmed H.', location: 'Dubai, UAE', rating: 5, content: 'Seguo una dieta halal rigorosa. SafeEat mi aiuta a verificare ingredienti di prodotti che non conosco. App indispensabile.' },
        { name: 'Lisa M.', location: 'Toronto, Canada', rating: 5, content: 'Tre figli con allergie diverse: fare la spesa era un incubo. Ora scansiono tutto in secondi. SafeEat è un salvavita!' },
        { name: 'Johan V.', location: 'Stoccolma, Svezia', rating: 5, content: 'Ho il morbo di Crohn e devo evitare certi trigger. Il profilo personalizzato di SafeEat intercetta ingredienti che avrei mancato.' },
        { name: 'Maria G.', location: 'San Paolo, Brasile', rating: 5, content: 'Lo scanner dei menu è perfetto per la mia allergia ai crostacei. Ora ordino in nuovi ristoranti con più sicurezza.' },
        { name: 'David C.', location: 'Parigi, Francia', rating: 5, content: 'Come chef con sensibilità alimentari, uso SafeEat per controllare gli ingredienti prima di cucinare. L’IA è davvero accurata.' },
        { name: 'Fatima A.', location: 'Il Cairo, Egitto', rating: 5, content: 'Gestire il diabete gestazionale era stressante finché non ho trovato SafeEat. Scansionare le etichette rende il tracking semplicissimo.' },
        { name: 'Michael O.', location: 'Lagos, Nigeria', rating: 5, content: 'Ho un’intolleranza rara che molte app non coprono. SafeEat me l’ha fatta aggiungere e la rileva ogni volta.' },
        { name: 'Sophie L.', location: 'Amsterdam, Paesi Bassi', rating: 5, content: 'Vegana con allergia alla soia: era difficilissimo. SafeEat trova prodotti animali nascosti e la soia. Fantastico!' },
        { name: 'Kenji N.', location: 'Seoul, Corea del Sud', rating: 5, content: 'Il database di codici a barre è enorme! Riconosce anche prodotti importati. Perfetto per cibi internazionali.' },
        { name: 'Elena P.', location: 'Roma, Italia', rating: 5, content: 'Mia madre anziana ha più patologie. Le ho impostato il profilo e ora può fare la spesa in autonomia. Grazie SafeEat!' }
      ]
    },
    pricing: {
      title: 'Prezzi semplici e trasparenti',
      subtitle: 'Scegli il piano adatto a te',
      free: 'Gratis',
      freeDesc: '5 scansioni gratuite per provare',
      freeFeature1: '5 scansioni IA',
      freeFeature2: 'Analisi base',
      freeFeature3: 'Cronologia limitata',
      weekly: 'Settimanale',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Perfetto per uso regolare',
      weeklyFeature1: 'Scansioni illimitate',
      weeklyFeature2: 'Analisi avanzata',
      weeklyFeature3: 'Cronologia completa',
      weeklyFeature4: 'Supporto prioritario',
      annual: 'Annuale',
      annualPrice: '$29.99',
      annualDesc: 'Miglior valore - Risparmia 60%',
      annualBadge: 'Più popolare',
      annualFeature1: 'Tutto nel Settimanale',
      annualFeature2: 'Risparmia $125/anno',
      annualFeature3: 'Supporto premium',
      annualFeature4: 'Accesso anticipato alle novità',
      button: 'Inizia ora'
    },
    faq: {
      title: 'Domande frequenti',
      items: [
        { q: 'Cosa posso scansionare con SafeEat?', a: 'SafeEat analizza: liste ingredienti e valori nutrizionali (foto), codici a barre, cibi confezionati e sfusi, piatti (foto) e menu dei ristoranti.' },
        { q: "Come funziona il riconoscimento dei piatti?", a: "L'IA identifica il piatto e stima gli ingredienti. Funziona meglio con buona luce e inquadratura; ingredienti nascosti o cross-contamination possono sfuggire." },
        { q: 'Posso scansionare i menu dei ristoranti?', a: 'Sì. Evidenzia allergeni e conflitti dietetici. I menu possono essere incompleti: conferma con lo staff ingredienti, oli, friggitrici condivise, salse, cross-contamination.' },
        { q: 'Quali condizioni supporta SafeEat?', a: 'Allergie alimentari, intolleranze (es. lattosio, glutine) e necessità dietetiche (basso sodio, controllo carboidrati, esclusione ingredienti).' },
        { q: 'SafeEat funziona offline?', a: 'Di solito serve internet per scansioni/IA. Risultati salvati/lista possono essere accessibili offline a seconda di app/dispositivo.' },
        { q: "Quanto è accurata l'analisi di SafeEat?", a: "Dipende da qualità foto, testo etichette, lingua/formato e completezza del menu. È informativo: verifica quando il rischio è alto." },
        { q: 'E se SafeEat sbaglia?', a: 'Non fare affidamento su un risultato dubbio; riprova con migliore luce; verifica etichetta/sito/staff; scegli l’opzione più sicura se il rischio è elevato.' },
        { q: 'Posso segnalare analisi errate?', a: 'Sì: support@safeeat.app con screenshot, foto di etichetta/menu/piatto, paese/marca/prodotto e cosa non torna.' },
        { q: 'Perché il riconoscimento piatti può essere meno accurato?', a: 'Piatti simili, ingredienti nascosti, salse/garnish pesanti, poca luce/sfocatura, piatti molto personalizzati o regionali.' },
        { q: 'SafeEat è un dispositivo medico?', a: 'No. È solo supporto informativo. Per consigli medici rivolgiti a un professionista.' },
        { q: 'Qual è la responsabilità di SafeEat?', a: 'Le info sono “as is”; non si garantisce completezza. Sei tu responsabile delle scelte; ingredienti non dichiarati e cross-contamination sono possibili.' },
        { q: 'Posso fare affidamento solo su SafeEat per allergie gravi?', a: 'No. Leggi le etichette, chiedi allo staff, segui il tuo action plan, porta i farmaci prescritti; se hai dubbi, evita.' },
        { q: 'Come proteggete i miei dati sanitari?', a: 'Raccogliamo solo il necessario, proteggiamo con misure tecniche/organizzative, accesso limitato, contatto privacy a support@safeeat.app. Vedi l’Informativa.' },
        { q: 'Posso usare SafeEat per i miei figli?', a: 'Sì, con supervisione. Per allergie gravi o diete complesse, sii prudente e verifica con fonti affidabili.' },
        { q: 'Cosa fare in caso di reazione?', a: 'Cerca subito assistenza medica, segui il piano di emergenza, chiama i soccorsi per sintomi gravi, poi segnala a medico/ristorante/produttore.' }
      ]
    },
    cta: {
      title: 'Pronto a trasformare il tuo modo di mangiare?',
      subtitle: 'Unisciti a migliaia di utenti ogni giorno',
      description: 'Scarica ora e ottieni 5 scansioni IA gratuite',
      button: "Scarica su App Store",
      feature1: 'Nessuna carta richiesta',
      feature2: '100% privato e sicuro',
      feature3: 'Accesso immediato'
    },
    newsletter: {
      title: 'Resta aggiornato',
      subtitle: 'Ricevi novità e consigli sulla salute',
      placeholder: 'Inserisci la tua email',
      button: 'Iscriviti',
      success: 'Grazie per esserti iscritto!',
      error: 'Inserisci un indirizzo email valido'
    },
    seo: {
      title: 'Il tuo assistente personale per la sicurezza alimentare',
      text: 'SafeEat è uno scanner alimentare con IA per persone con allergie, intolleranze e restrizioni dietetiche. Scansiona subito etichette, valori nutrizionali, codici a barre, menu dei ristoranti e piatti pronti per verificare la sicurezza rispetto al tuo profilo. Che tu abbia celiachia, intolleranza al lattosio, allergie alle noci o segua diete gluten-free, vegane o a basso contenuto di sodio, SafeEat ti aiuta a scegliere in sicurezza.',
      keywords: [
        'App per allergie alimentari',
        'Scanner ingredienti',
        'Rilevatore allergeni',
        'Lettore etichette nutrizionali',
        'Scanner codici a barre',
        'Scanner menu',
        'Verifica senza glutine',
        'App intolleranza al lattosio',
        'Diet tracker',
        'Sicurezza alimentare'
      ]
    },
    footer: {
      product: 'Prodotto',
      features: 'Funzionalità',
      howItWorks: 'Come funziona',
      testimonials: 'Testimonianze',
      pricing: 'Prezzi',
      faq: 'FAQ',
      contacts: 'Contatti',
      legal: 'Note legali',
      privacy: 'Informativa sulla Privacy',
      terms: 'Termini di servizio',
      usePolicy: 'Manuale Utente',
      disclaimer: 'Esclusione di responsabilità',
      company: 'FJCode OU',
      register: 'Registro n. 16993109',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. Tutti i diritti riservati.'
    },
    cookies: {
      bannerText: 'Usiamo cookie necessari e cookie di analisi solo con il tuo consenso.',
      decline: 'Rifiuta',
      accept: 'Accetta'
    },
    legal: {
      backHome: 'Torna alla Home'
    },
    notFound: {
      title: 'Sembra che questo ingrediente manchi.',
      body: 'La pagina che cerchi non è nel menu. Torniamo in un posto sicuro.',
      backHome: 'Torna alla Home',
      goFaq: 'Vai alle FAQ'
    }
  },
  ja: {
    meta: {
      home: {
        title: 'SafeEat｜アレルギー＆不耐症向けAIフードスキャナー',
        description: 'ラベル、バーコード、メニュー、料理をスキャンして、あなたのプロフィール（アレルギー・不耐症・慢性疾患）に基づきアレルゲンや食事上のリスクを見つけます。'
      },
      privacy: {
        title: 'プライバシーポリシー｜SafeEat',
        description: 'SafeEatがデータをどのように収集・利用・保護するかをご確認ください。'
      },
      terms: {
        title: '利用規約｜SafeEat',
        description: 'SafeEatの利用に適用される規約をご確認ください。'
      },
      manual: {
        title: 'ユーザーガイド｜SafeEat',
        description: 'SafeEatで食品を安全にスキャンする方法を学びましょう。'
      },
      disclaimer: {
        title: '免責事項｜SafeEat',
        description: 'SafeEatの情報、ガイダンス、制限事項について理解しましょう。'
      }
    },
    nav: {
      about: '仕組み',
      features: '機能',
      testimonials: 'レビュー',
      pricing: '料金',
      faq: 'よくある質問',
      contacts: 'お問い合わせ',
      getStarted: 'はじめる'
    },
    about: {
      description: 'AI搭載のフードスキャナー。ラベル、バーコード、料理、メニューを読み取り、あなたの健康プロフィールに基づいてアレルゲン、原材料、栄養情報を強調表示します。'
    },
    hero: {
      badge: 'AIで食品をもっと安全に',
      title: '今すぐチェック',
      title2: '食品の安全性',
      titleHighlight: 'あなた向けに',
      subtitle: 'に基づく',
      description: '食料品、バーコード、原材料、メニューをスキャンして、あなたの体にとって安全かどうかをすぐに確認',
      typing: ['慢性疾患', 'アレルギー', '食物不耐症'],
      stat1: '500+',
      stat1Label: '慢性疾患',
      stat2: '250+',
      stat2Label: 'アレルギー',
      stat3: '100+',
      stat3Label: '食物不耐症',
      downloadButton: 'App Storeで入手'
    },
    howItWorks: {
      badge: '仕組み',
      mainTitle: 'SafeEatの使い方',
      subtitle: 'シンプルでスピーディー。わずかなステップでパーソナルな結果が得られます。',
      setupBadge: '初回だけの設定',
      setupTitle: '健康プロフィールを作成',
      setupDesc: 'オンボーディングで健康情報を追加してSafeEatをカスタマイズします。設定は一度だけで、あなた専用の食品分析が可能になります。',
      cards: {
        diseases: {
          title: '慢性疾患',
          desc: '500種類以上の疾患データベースから、あなたの慢性疾患を追加できます。糖尿病から心疾患まで幅広く対応。',
          statLabel: '疾患'
        },
        allergies: {
          title: 'アレルギー',
          desc: '250種類以上からアレルギーを選択。一般的なナッツや甲殻類から希少なアレルゲンまでカバーします。',
          statLabel: 'アレルギー'
        },
        intolerances: {
          title: '食物不耐症',
          desc: '100種類以上から不耐症を登録。乳糖、グルテン、果糖など—不快感のない食生活へ。',
          statLabel: '不耐症'
        }
      },
      connectorText: 'そして、スキャンするたびに：',
      stepLabel: 'ステップ',
      step1Title: '食品をスキャン',
      step1Desc: '単品の商品、調理済みの料理、栄養成分表示、バーコード、さらにはレストランのメニューまでスキャンできます。カメラを向けるだけでOK。',
      step2Title: 'AIが解析',
      step2Desc: '画像を解析し、あなたの疾患・アレルギー・不耐症プロフィールと照合して適合性を判定します。',
      step3Title: 'レポートを受け取る',
      step3Desc: '分析結果を詳細レポートで表示。健康プロフィールや食事要件にどれだけ合うかが分かります。',
      step4Title: '納得して選ぶ',
      step4Desc: 'パーソナルなインサイトをもとに、あなたに最適な食事を選べます。安心して食べ、体に合うものを把握しましょう。',
      processTitle: '実際の動きを見る',
      processSubtitle: 'SafeEatがリアルタイムで食品を解析する様子をご覧ください'
    },
    features: {
      title: '強力な機能',
      subtitle: '安全に食べるために必要なものをひとつに',
      items: [
        {
          title: 'どんな食品でもスキャン',
          subtitle: '定番スナックから珍しいフルーツまで',
          description: '包装済み／未包装を問わず、あらゆる食品にカメラを向けるだけ。スーパーの定番から希少なトロピカルフルーツまで、即座に安全性評価を受け取れます。SafeEatが食品を識別し、成分を解析して、リスクのある／避けたい原材料を見つけます。',
          highlights: ['包装・未包装どちらも対応', '即時の安全性チェック', '成分の解析'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: '栄養成分表示とバーコードをスキャン',
          subtitle: 'ややこしい表示もすぐ理解',
          description: '小さな文字や分かりにくい表示に悩む必要はありません。SafeEatは栄養成分、原材料表示、バーコードを数秒で読み取ります。アレルゲン、添加物、注意すべき成分をすぐにハイライトし、何を食べるかを明確にします。',
          highlights: ['栄養情報スキャン', 'アレルゲン検出', '注意成分のアラート'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: '料理をスキャンして原材料を推定',
          subtitle: '食事の写真から判定',
          description: '食事を撮影すると、SafeEatが推定される原材料に分解して表示します。自炊、フードコート、レストランの料理まで対応し、あなたの食事に合わない可能性のある成分やアレルゲンを検出します。',
          highlights: ['AIによる原材料推定', 'あらゆる料理に対応', '食事適合性チェック'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'レストランのメニューをスキャン',
          subtitle: '注文前に安全な料理を見つける',
          description: 'カフェやレストランのメニューにカメラを向けるだけで、SafeEatが各料理を解析し、あなたにとって安全な選択肢を提示します。隠れたアレルゲンや避けたい原材料の不安を減らせます。',
          highlights: ['メニュー解析', '安全な料理を提案', '隠れアレルゲン検出'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: '安全な食品の個人リストを作成',
          subtitle: 'あなたの「クリーンイーティング」ライブラリ',
          description: 'あなたにとって安全な食品を保存し、自分だけの「クリーンイーティング」ライブラリを構築。買い物中や外食時にもいつでもアクセスできます。スキャンするほど、あなた専用の食品データベースが育ちます。',
          highlights: ['パーソナル食品ライブラリ', 'どこでも素早くアクセス', 'スマートなおすすめ'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: '多くのユーザーに選ばれています',
      subtitle: '世界中のユーザーコミュニティに信頼されています。',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEatで買い物の仕方が一変しました！私はセリアック病ですが、商品スキャンは数秒で完了。お店で小さな文字を必死に読む必要がなくなりました。'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: '複数の食物アレルギーがあると外食はいつも不安でした。今はメニューをスキャンするだけで安全な料理が分かります。本当に助かっています！'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: '糖尿病の管理に毎日SafeEatを使っています。栄養表示スキャナーで糖質や糖分をすぐ把握できて、とても便利です。'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: '息子は重いナッツアレルギーがあります。SafeEatのおかげでおやつ選びが安心に。バーコードスキャンが驚くほど正確です。'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: '乳糖不耐症でよく旅行します。SafeEatはどの言語でも使えるので、海外でもメニューをスキャンして安心して食べられます！'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: '夫の心疾患管理で塩分を気にしています。SafeEatの即時解析で食事計画がずっと楽になりました。'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: '料理スキャナーがすごい！外食の料理を撮影したら、隠れたグルテンの可能性を示してくれて、体調不良を避けられました。'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'ハラール食を厳格に守っています。SafeEatは知らない食品でも原材料を確認できるので欠かせません。'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: '子ども3人それぞれにアレルギーがあり、買い物は大変でした。今は何でも数秒でスキャンできて本当に助かります。'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'クローン病で避けたいトリガーがあります。SafeEatの個別プロフィールが、見落としがちな成分を拾ってくれます。'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'メニュースキャンは甲殻類アレルギーの私にぴったり。初めてのお店でも注文がしやすくなりました。'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: '感受性のあるシェフとして、調理前にSafeEatで原材料を確認しています。AIの精度に驚きます。'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: '妊娠糖尿病の管理は大変でしたが、SafeEatを見つけてから楽になりました。食品ラベルの確認がとても簡単です。'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: '珍しい不耐症があり、他のアプリでは対応できませんでした。SafeEatはプロフィールに追加でき、毎回しっかり検出してくれます。'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: '大豆アレルギーのあるヴィーガンです。安全な食品を探すのが難しかったのですが、SafeEatは動物性原料も大豆も見つけてくれて最高です！'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'バーコードのデータベースがとても充実していて、輸入品でも認識されました。海外食品のチェックに最適です。'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: '高齢の母はいくつも持病があります。母のプロフィールを設定したら、また一人で買い物できるようになりました。ありがとう、SafeEat！'
        }
      ]
    },
    pricing: {
      title: 'シンプルで分かりやすい料金',
      subtitle: 'あなたに合うプランを選択',
      free: '無料',
      freeDesc: 'お試し用に無料スキャン5回',
      freeFeature1: 'AIスキャン5回',
      freeFeature2: '基本分析',
      freeFeature3: '履歴は限定',
      weekly: '週間',
      weeklyPrice: '$2.99',
      weeklyDesc: '定期的に使う人に最適',
      weeklyFeature1: 'スキャン無制限',
      weeklyFeature2: '高度な分析',
      weeklyFeature3: '履歴フルアクセス',
      weeklyFeature4: '優先サポート',
      annual: '年間',
      annualPrice: '$29.99',
      annualDesc: '最もお得—60%節約',
      annualBadge: '一番人気',
      annualFeature1: '週間プランの全機能',
      annualFeature2: '年間$125節約',
      annualFeature3: 'プレミアムサポート',
      annualFeature4: '新機能への先行アクセス',
      button: 'はじめる'
    },
    faq: {
      title: 'よくある質問',
      items: [
        {
          q: 'SafeEatで何をスキャンできますか？',
          a: 'SafeEatが分析できるもの：原材料表示・栄養成分表示（写真）、バーコード、包装済み／未包装の食品、料理（写真）、レストランのメニュー。'
        },
        {
          q: '料理認識機能はどのように動作しますか？',
          a: 'AIが料理を識別し、含まれる可能性のある原材料を推定します。十分な明るさと適切な角度で最も効果的です。隠れた原材料や交差接触（コンタミ）は見落とされる場合があります。'
        },
        {
          q: 'レストランのメニューをスキャンできますか？',
          a: 'はい。潜在的なアレルゲンや食事上の不一致を示します。メニューは不完全な場合があるため、原材料、油、共用フライヤー、ソース、交差接触について必ずスタッフに確認してください。'
        },
        {
          q: 'SafeEatはどのような健康状態に対応していますか？',
          a: '食物アレルギー、不耐症（乳糖・グルテンなど）、食事要件（減塩、糖質重視、特定成分の回避など）。'
        },
        {
          q: 'SafeEatはオフラインで使えますか？',
          a: '通常、スキャン／AI解析にはインターネット接続が必要です。保存した結果やリストは、アプリや端末によってはオフラインで参照できる場合があります。'
        },
        {
          q: 'SafeEatの分析精度はどれくらいですか？',
          a: '写真の品質、ラベルの文字、言語／形式、メニューの情報量によって変わります。これは情報提供目的です。リスクが高い場合は必ず再確認してください。'
        },
        {
          q: 'SafeEatが間違えたらどうすればいいですか？',
          a: '判断に迷う結果は信用せず、より良い写真で再スキャンし、ラベル／公式サイト／スタッフで確認してください。高リスクの場合は最も安全な選択を優先しましょう。'
        },
        {
          q: '誤った分析結果を報告できますか？',
          a: 'はい。support@safeeat.app へ、スクリーンショット、ラベル／メニュー／料理の写真、国・ブランド・商品名、何が誤りに見えるかをお送りください。'
        },
        {
          q: '料理認識が不正確になる理由は？',
          a: '似た見た目の料理、隠れた原材料、濃いソースやトッピング、暗い環境やブレ、地域性が強い／カスタム度が高い料理など。'
        },
        {
          q: 'SafeEatは医療機器ですか？',
          a: 'いいえ。情報提供のみを目的としています。医療上の判断は専門家にご相談ください。'
        },
        {
          q: 'SafeEatの責任範囲は？',
          a: '情報は「現状有姿」で提供され、完全性は保証できません。最終的な判断はユーザーの責任です。未表示の原材料や交差接触が起こり得ます。'
        },
        {
          q: '重度のアレルギーでもSafeEatだけに頼るべきですか？',
          a: 'いいえ。ラベルを読み、スタッフに確認し、行動計画に従い、処方薬を携帯してください。不安がある場合は避けましょう。'
        },
        {
          q: '健康データはどのように保護されていますか？',
          a: '必要最小限のデータのみを収集し、セキュリティ対策とアクセス制限で保護します。プライバシーに関するお問い合わせは support@safeeat.app まで。詳しくはプライバシーポリシーをご覧ください。'
        },
        {
          q: '子どもにSafeEatを使えますか？',
          a: 'はい。ただし保護者の監督下でご利用ください。重度アレルギーや複雑な食事要件では、信頼できる情報源での確認を徹底してください。'
        },
        {
          q: '反応が出た場合はどうすればよいですか？',
          a: '直ちに医療機関を受診し、緊急時の計画に従ってください。重い症状の場合は112に連絡し、その後、提供元／レストラン／製造元へ報告してください。'
        }
      ]
    },
    cta: {
      title: '食生活を変える準備はできましたか？',
      subtitle: '毎日SafeEatを使う何千人もの仲間に加わりましょう',
      description: '今すぐダウンロードしてAIスキャンを無料で5回',
      button: 'App Storeで入手',
      feature1: 'クレジットカード不要',
      feature2: '100%プライベート＆安全',
      feature3: 'すぐに使える'
    },
    newsletter: {
      title: '最新情報を受け取る',
      subtitle: '最新アップデートと健康ヒントをお届けします',
      placeholder: 'メールアドレスを入力',
      button: '登録',
      success: 'ご登録ありがとうございます！',
      error: '有効なメールアドレスを入力してください'
    },
    seo: {
      title: 'あなたのための食品安全アシスタント',
      text: 'SafeEatは、食物アレルギー・不耐症・食事制限のある方のためのAIフードスキャナーアプリです。ラベル、栄養成分表示、バーコード、レストランメニュー、調理済み料理をスキャンし、プロフィールに基づいて安全性を確認できます。セリアック病、乳糖不耐症、ナッツアレルギー、グルテンフリー、ヴィーガン、減塩など、さまざまな食事ニーズに対応し、安全な選択をサポートします。',
      keywords: [
        '食物アレルギー アプリ',
        '原材料スキャナー',
        'アレルゲン検出',
        '栄養成分表示 読み取り',
        'バーコードスキャナー',
        'メニュースキャナー',
        'グルテンフリー チェック',
        '乳糖不耐症 アプリ',
        '食事トラッカー',
        '食品安全'
      ]
    },
    cookies: {
      bannerText: '当サイトはCookieを使用します：サイト運営に必要なCookieと、同意がある場合のみ分析Cookieを使用します。',
      decline: '拒否',
      accept: '同意する'
    },
    legal: {
      backHome: 'ホームに戻る'
    },
    footer: {
      product: '製品',
      features: '機能',
      howItWorks: '仕組み',
      testimonials: 'レビュー',
      pricing: '料金',
      faq: 'よくある質問',
      contacts: 'お問い合わせ',
      legal: '法的情報',
      privacy: 'プライバシーポリシー',
      terms: '利用規約',
      usePolicy: 'ユーザーガイド',
      disclaimer: '免責事項',
      company: 'FJCode OU',
      register: '登録番号 16993109',
      address: 'Viru väljak 2, 10111 Tallinn, Estonia',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. All rights reserved.'
    }
  },
  ko: {
    meta: {
      home: {
        title: 'SafeEat | 알레르기·불내증을 위한 AI 음식 스캐너',
        description: '라벨, 바코드, 메뉴, 요리를 스캔해 프로필(알레르기, 불내증, 만성 질환)에 따라 알레르겐과 식단 위험 요소를 표시합니다.'
      },
      privacy: {
        title: '개인정보처리방침 | SafeEat',
        description: 'SafeEat이 데이터를 수집, 사용, 보호하는 방법을 확인하세요.'
      },
      terms: {
        title: '이용약관 | SafeEat',
        description: 'SafeEat 이용에 적용되는 약관을 확인하세요.'
      },
      manual: {
        title: '사용자 가이드 | SafeEat',
        description: 'SafeEat로 음식을 안전하게 스캔하는 방법을 알아보세요.'
      },
      disclaimer: {
        title: '면책조항 | SafeEat',
        description: 'SafeEat의 정보, 안내, 제한 사항을 이해하세요.'
      }
    },
    nav: {
      about: '작동 방식',
      features: '기능',
      testimonials: '리뷰',
      pricing: '요금',
      faq: '자주 묻는 질문',
      contacts: '문의',
      getStarted: '시작하기'
    },
    about: {
      description: 'AI 기반 음식 스캐너로 라벨, 바코드, 요리, 메뉴를 읽고 건강 프로필에 맞춰 알레르겐, 성분, 영양 정보를 강조 표시합니다.'
    },
    hero: {
      badge: 'AI 기반 식품 안전',
      title: '즉시 확인',
      title2: '식품 안전',
      titleHighlight: '나에게 맞게',
      subtitle: '기반',
      description: '식료품, 바코드, 성분표, 메뉴를 스캔해 음식이 내 몸에 안전한지 즉시 확인하세요',
      typing: ['만성 질환', '알레르기', '음식 불내증'],
      stat1: '500+',
      stat1Label: '만성 질환',
      stat2: '250+',
      stat2Label: '알레르기',
      stat3: '100+',
      stat3Label: '음식 불내증',
      downloadButton: 'App Store에서 다운로드'
    },
    howItWorks: {
      badge: '작동 방식',
      mainTitle: 'SafeEat 사용 방법',
      subtitle: '간단하고 빠른 스캔 과정으로 몇 단계만에 개인화된 결과를 제공합니다.',
      setupBadge: '한 번만 설정',
      setupTitle: '건강 프로필 만들기',
      setupDesc: '온보딩에서 건강 정보를 추가해 SafeEat을 맞춤 설정합니다. 한 번만 설정하면 나에게 최적화된 음식 분석이 가능해집니다.',
      cards: {
        diseases: {
          title: '만성 질환',
          desc: '500개+ 질환 데이터베이스에서 본인의 만성 질환을 추가하세요. 당뇨부터 심장 질환까지 폭넓게 지원합니다.',
          statLabel: '질환'
        },
        allergies: {
          title: '알레르기',
          desc: '250개+ 항목 중에서 알레르기를 선택하세요. 흔한 견과류·해산물부터 드문 알레르겐까지, 안전하게 관리할 수 있어요.',
          statLabel: '알레르기'
        },
        intolerances: {
          title: '음식 불내증',
          desc: '100개+ 항목 중에서 불내증을 입력하세요. 유당, 글루텐, 과당 등 — 불편함 없이 식사하세요.',
          statLabel: '불내증'
        }
      },
      connectorText: '그리고 — 스캔할 때마다:',
      stepLabel: '단계',
      step1Title: '음식 스캔',
      step1Desc: '어떤 음식이든 스캔하세요 — 단품 제품, 조리된 요리, 영양성분표, 바코드, 심지어 레스토랑 메뉴까지. 카메라만 비추면 SafeEat이 알아서 처리합니다.',
      step2Title: 'AI 분석',
      step2Desc: '앱이 이미지를 분석하고 질환·알레르기·불내증 프로필과 대조하여 적합성과 위험 요소를 평가합니다.',
      step3Title: '리포트 받기',
      step3Desc: 'SafeEat이 상세 리포트를 제공하고, 분석 대상이 내 건강 프로필과 식단 요구사항에 얼마나 잘 맞는지 보여줍니다.',
      step4Title: '결정하기',
      step4Desc: '개인화된 인사이트를 바탕으로 나에게 가장 적합한 음식을 선택하세요. 안심하고 먹으면서, 내 몸에 맞는 것을 정확히 알 수 있습니다.',
      processTitle: '직접 확인해 보세요',
      processSubtitle: 'SafeEat이 실시간으로 음식을 분석하는 과정을 확인하세요'
    },
    features: {
      title: '강력한 기능',
      subtitle: '안전하게 먹기 위해 필요한 모든 것',
      items: [
        {
          title: '어떤 제품이든 스캔',
          subtitle: '일상 간식부터 이국적인 과일까지',
          description: '포장 제품이든 비포장 식품이든, 카메라를 비추기만 하세요 — 슈퍼마켓의 흔한 제품부터 드문 열대 과일까지 즉시 안전성 평가를 받을 수 있습니다. SafeEat이 제품을 식별하고 구성 성분을 분석해, 위험하거나 피해야 할 재료를 알려줍니다.',
          highlights: ['포장/비포장 식품 모두', '즉시 안전성 체크', '구성 성분 분석'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: '영양성분표와 바코드 스캔',
          subtitle: '복잡한 라벨도 바로 이해',
          description: '작은 글씨나 복잡한 표기 때문에 더 이상 고민하지 마세요. SafeEat은 영양 정보, 성분 목록, 바코드를 몇 초 만에 스캔합니다. 앱이 알레르겐, 첨가물, 주의 성분을 즉시 강조 표시해, 무엇을 먹는지 정확히 알 수 있습니다.',
          highlights: ['영양 정보 스캔', '알레르겐 감지', '주의 성분 경고'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: '요리를 스캔해 재료 식별',
          subtitle: '식사 사진 한 장으로',
          description: '식사를 촬영하면 SafeEat이 요리를 재료 단위로 추정해 보여줍니다. 집밥, 푸드코트, 파인다이닝까지 — AI가 잠재적 알레르겐이나 내 식단과 맞지 않는 재료를 찾아줍니다.',
          highlights: ['AI 재료 인식', '어떤 요리든 가능', '식단 적합성 확인'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: '레스토랑 메뉴 스캔',
          subtitle: '주문 전에 안전한 메뉴 찾기',
          description: '카페나 레스토랑 메뉴에 카메라를 비추면 SafeEat이 각 메뉴를 분석해 내 기준에서 안전한 선택지를 알려줍니다. 숨은 알레르겐이나 원치 않는 재료에 대한 걱정 없이 외식할 수 있어요.',
          highlights: ['메뉴 분석', '안전한 메뉴 추천', '숨은 알레르겐 감지'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: '개인 안전 식품 리스트 만들기',
          subtitle: '나만의 ‘클린 이팅’ 라이브러리',
          description: '나에게 안전한 식품을 저장하고 ‘클린 이팅’ 라이브러리를 만들어 보세요. 장보거나 외식할 때 언제든 빠르게 확인할 수 있습니다. 스캔할수록 나만의 식품 데이터베이스가 커집니다.',
          highlights: ['개인 식품 라이브러리', '언제든 빠른 접근', '스마트 추천'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: '수많은 사용자가 신뢰합니다',
      subtitle: '전 세계 사용자 커뮤니티가 SafeEat을 선택했습니다.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat 덕분에 장보는 방식이 완전히 달라졌어요! 저는 셀리악병이 있는데, 제품 스캔이 몇 초면 끝나요. 매장에서 작은 글씨 라벨을 읽느라 고생할 필요가 없어요.'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: '여러 음식 알레르기가 있어서 외식이 늘 스트레스였는데, 이제는 메뉴만 스캔하면 안전한 선택을 바로 알 수 있어요. 정말 게임 체인저예요!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: '당뇨 관리를 위해 SafeEat을 매일 사용해요. 영양성분표 스캐너가 탄수화물과 당을 즉시 확인하게 해줘서 정말 유용합니다. 강력 추천!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: '아들이 심한 견과류 알레르기가 있어요. SafeEat 덕분에 간식을 고를 때 마음이 놓입니다. 바코드 스캐너가 정말 정확해요.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: '저는 유당 불내증이고 여행을 자주 해요. SafeEat은 어떤 언어에서도 잘 작동해서 해외에서도 메뉴를 스캔하고 걱정 없이 먹을 수 있어요!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: '남편의 심장 질환 때문에 나트륨을 신경 써야 하는데, SafeEat의 즉시 분석 덕분에 식단 계획이 훨씬 쉬워졌어요.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: '요리 스캐너가 정말 대단해요! 레스토랑에서 먹은 음식을 찍었더니 숨은 글루텐 재료를 잡아줬어요. 큰 반응을 피할 수 있었습니다!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: '저는 엄격한 할랄 식단을 지켜요. SafeEat은 낯선 포장식품의 성분을 확인할 때 큰 도움이 됩니다. 꼭 필요한 앱이에요!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: '아이 셋이 각각 다른 알레르기가 있어서 장보기가 악몽이었는데, 이제는 몇 초 만에 다 스캔할 수 있어요. 이 앱은 정말 생명의 은인이에요!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: '크론병이 있어서 특정 트리거를 피해야 해요. SafeEat의 개인 프로필이 제가 놓치기 쉬운 성분까지 잘 잡아줍니다.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: '메뉴 스캔 기능은 제 해산물 알레르기에 딱이에요. 이제 새로운 식당에서도 더 안심하고 주문할 수 있어요!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: '민감한 체질의 셰프로서 요리 전에 재료를 확인하려고 SafeEat을 써요. AI 인식 정확도가 정말 인상적입니다.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: '임신성 당뇨를 관리하는 게 힘들었는데 SafeEat을 찾고 나서 훨씬 쉬워졌어요. 식품 라벨을 스캔하면 관리가 간단해집니다!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: '저는 드문 음식 불내증이 있는데 대부분의 앱은 지원하지 않더라고요. SafeEat에서는 프로필에 추가할 수 있었고, 이제 매번 제대로 감지해 줍니다!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: '저는 비건인데 대두 알레르기가 있어요. 안전한 음식을 찾기가 정말 어려웠는데, SafeEat은 숨은 동물성 성분도, 대두도 찾아줘서 너무 좋아요!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: '바코드 데이터베이스가 정말 방대해요! 수입 제품도 잘 인식합니다. 낯선 해외 식품을 확인하기에 딱이에요.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: '나이 드신 어머니가 여러 질환이 있어요. 어머니 프로필을 설정해 드렸더니 다시 혼자 장을 볼 수 있게 됐어요. 고마워요, SafeEat!'
        }
      ]
    },
    pricing: {
      title: '간단하고 투명한 요금',
      subtitle: '나에게 맞는 플랜을 선택하세요',
      free: '무료',
      freeDesc: '체험용 무료 스캔 5회',
      freeFeature1: 'AI 스캔 5회',
      freeFeature2: '기본 분석',
      freeFeature3: '제한된 기록',
      weekly: '주간',
      weeklyPrice: '$2.99',
      weeklyDesc: '정기적으로 사용하는 분께',
      weeklyFeature1: '스캔 무제한',
      weeklyFeature2: '고급 분석',
      weeklyFeature3: '전체 기록',
      weeklyFeature4: '우선 지원',
      annual: '연간',
      annualPrice: '$29.99',
      annualDesc: '가장 큰 혜택 — 60% 절약',
      annualBadge: '가장 인기',
      annualFeature1: '주간 플랜의 모든 기능',
      annualFeature2: '연간 $125 절약',
      annualFeature3: '프리미엄 지원',
      annualFeature4: '신기능 우선 이용',
      button: '시작하기'
    },
    faq: {
      title: '자주 묻는 질문',
      items: [
        {
          q: 'SafeEat으로 무엇을 스캔할 수 있나요?',
          a: 'SafeEat은 다음을 분석할 수 있습니다: 성분 목록 및 영양성분표(사진), 바코드, 포장/비포장 식품, 요리(사진), 레스토랑 메뉴.'
        },
        {
          q: '요리 인식 기능은 어떻게 작동하나요?',
          a: 'AI가 요리를 식별하고 포함될 가능성이 있는 재료를 추정합니다. 좋은 조명과 각도에서 더 잘 작동하며, 숨은 재료나 교차 오염(컨타미)은 놓칠 수 있습니다.'
        },
        {
          q: '레스토랑 메뉴도 스캔할 수 있나요?',
          a: '네. 잠재적 알레르겐과 식단 충돌 요소를 표시합니다. 메뉴 정보가 불완전할 수 있으므로, 재료, 사용 기름, 공용 튀김기, 소스, 교차 오염 여부는 직원에게 반드시 확인하세요.'
        },
        {
          q: 'SafeEat은 어떤 건강 상태를 지원하나요?',
          a: '음식 알레르기, 불내증(예: 유당, 글루텐) 및 식단 요구사항(저나트륨, 탄수화물 관리, 특정 재료 회피 등).'
        },
        {
          q: 'SafeEat은 오프라인에서 작동하나요?',
          a: '대부분 스캔/AI 기능에는 인터넷 연결이 필요합니다. 저장된 결과와 목록은 앱과 기기에 따라 오프라인에서 볼 수 있을 수도 있습니다.'
        },
        {
          q: 'SafeEat 분석은 얼마나 정확한가요?',
          a: '사진 품질, 라벨 텍스트, 언어/형식, 메뉴의 정보량에 따라 달라집니다. 참고용 정보이므로, 위험이 큰 경우에는 반드시 재확인하세요.'
        },
        {
          q: 'SafeEat이 틀리면 어떻게 해야 하나요?',
          a: '의심스러운 결과는 믿지 말고, 더 선명한 사진으로 다시 스캔한 뒤 라벨/공식 사이트/직원 확인으로 검증하세요. 위험이 큰 경우 가장 안전한 선택을 하세요.'
        },
        {
          q: '잘못된 분석 결과를 신고할 수 있나요?',
          a: '네. support@safeeat.app 로 스크린샷, 라벨/메뉴/요리 사진, 국가/브랜드/제품 정보, 무엇이 잘못됐는지 보내주세요.'
        },
        {
          q: '요리 인식이 덜 정확할 수 있는 이유는 무엇인가요?',
          a: '비슷해 보이는 요리, 숨은 재료, 진한 소스/토핑, 어두운 환경/흔들림, 매우 커스터마이즈된 또는 지역 특색이 강한 요리 등.'
        },
        {
          q: 'SafeEat은 의료기기인가요?',
          a: '아니요. 정보 제공 목적입니다. 의료 상담은 전문가에게 문의하세요.'
        },
        {
          q: 'SafeEat의 책임 범위는 무엇인가요?',
          a: '정보는 "있는 그대로" 제공되며 완전성을 보장할 수 없습니다. 선택의 책임은 사용자에게 있으며, 표기되지 않은 성분이나 교차 오염이 발생할 수 있습니다.'
        },
        {
          q: '심각한 알레르기가 있어도 SafeEat만 믿어도 되나요?',
          a: '아니요. 라벨을 읽고 직원에게 확인하며, 본인의 대응 계획을 따르고 처방약을 지참하세요. 확신이 없다면 해당 제품/메뉴를 피하세요.'
        },
        {
          q: '건강 데이터는 어떻게 보호하나요?',
          a: '최소한의 데이터만 수집하고 보안 조치 및 접근 제한으로 보호합니다. 개인정보 관련 문의는 support@safeeat.app 로 연락하세요. 자세한 내용은 개인정보처리방침을 참고하세요.'
        },
        {
          q: '아이에게도 SafeEat을 사용할 수 있나요?',
          a: '네, 보호자 감독하에 사용할 수 있습니다. 심각한 알레르기/복잡한 식단 요구사항의 경우 더욱 신중하게, 신뢰할 수 있는 정보로 재확인하세요.'
        },
        {
          q: '반응이 생기면 어떻게 해야 하나요?',
          a: '즉시 의료 도움을 받고, 비상 계획을 따르세요. 증상이 심하면 112에 연락하고, 이후 제공업체/레스토랑/제조사에 보고하세요.'
        }
      ]
    },
    cta: {
      title: '식생활을 바꿀 준비가 되셨나요?',
      subtitle: '매일 SafeEat을 사용하는 수천 명과 함께하세요',
      description: '지금 다운로드하고 AI 스캔 5회를 무료로 받으세요',
      button: 'App Store에서 다운로드',
      feature1: '신용카드 필요 없음',
      feature2: '100% 프라이빗 & 안전',
      feature3: '즉시 사용 가능'
    },
    newsletter: {
      title: '최신 소식을 받아보세요',
      subtitle: '최신 업데이트와 건강 팁을 받아보세요',
      placeholder: '이메일을 입력하세요',
      button: '구독하기',
      success: '구독해 주셔서 감사합니다!',
      error: '유효한 이메일 주소를 입력하세요'
    },
    seo: {
      title: '나만의 식품 안전 도우미',
      text: 'SafeEat은 음식 알레르기, 불내증, 식단 제한이 있는 사람을 위한 AI 음식 스캐너 앱입니다. 라벨, 영양성분표, 바코드, 레스토랑 메뉴, 조리된 요리를 스캔하여 프로필에 맞춰 안전성을 즉시 확인하세요. 셀리악병, 유당 불내증, 견과류 알레르기, 글루텐 프리·비건·저나트륨 식단 등 다양한 요구에 맞춰 안전한 선택을 돕습니다.',
      keywords: [
        '식품 알레르기 앱',
        '성분 스캐너',
        '알레르겐 감지',
        '영양성분표 리더',
        '바코드 스캐너',
        '메뉴 스캐너',
        '글루텐 프리 확인',
        '유당 불내증 앱',
        '식단 트래커',
        '식품 안전'
      ]
    },
    cookies: {
      bannerText: '쿠키를 사용합니다: 사이트 운영에 필요한 쿠키와, 동의한 경우에만 분석 쿠키를 사용합니다.',
      decline: '거부',
      accept: '동의'
    },
    legal: {
      backHome: '홈으로 돌아가기'
    },
    footer: {
      product: '제품',
      features: '기능',
      howItWorks: '작동 방식',
      testimonials: '리뷰',
      pricing: '요금',
      faq: '자주 묻는 질문',
      contacts: '문의',
      legal: '법적 고지',
      privacy: '개인정보처리방침',
      terms: '서비스 이용약관',
      usePolicy: '사용자 가이드',
      disclaimer: '면책조항',
      company: 'FJCode OU',
      register: '등록번호 16993109',
      address: 'Viru väljak 2, 10111 Tallinn, 에스토니아',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. 모든 권리 보유.'
    }
  },
  ms: {
    meta: {
      home: {
        title: 'SafeEat | Pengimbas makanan AI untuk alahan & intolerans',
        description: 'Imbas label, kod bar, menu dan hidangan untuk mengenal pasti alergen serta risiko pemakanan berdasarkan profil anda: alahan, intolerans, dan penyakit kronik.'
      },
      privacy: {
        title: 'Dasar Privasi | SafeEat',
        description: 'Ketahui cara SafeEat mengumpul, menggunakan dan melindungi data anda.'
      },
      terms: {
        title: 'Terma Penggunaan | SafeEat',
        description: 'Semak terma yang terpakai untuk penggunaan SafeEat.'
      },
      manual: {
        title: 'Panduan Pengguna | SafeEat',
        description: 'Pelajari cara menggunakan SafeEat untuk mengimbas makanan dengan selamat.'
      },
      disclaimer: {
        title: 'Penafian | SafeEat',
        description: 'Fahami maklumat, panduan dan had SafeEat.'
      }
    },
    nav: {
      about: 'Cara ia berfungsi',
      features: 'Ciri',
      testimonials: 'Ulasan',
      pricing: 'Harga',
      faq: 'Soalan Lazim',
      contacts: 'Hubungi',
      getStarted: 'Mula'
    },
    about: {
      description: 'Pengimbas makanan berkuasa AI yang membaca label, kod bar, hidangan dan menu, serta menonjolkan alergen, bahan-bahan dan maklumat nutrisi berdasarkan profil kesihatan anda.'
    },
    hero: {
      badge: 'Keselamatan makanan berkuasa AI',
      title: 'Semak serta‑merta',
      title2: 'keselamatan makanan',
      titleHighlight: 'untuk anda',
      subtitle: 'Berdasarkan',
      description: 'Imbas barangan runcit, kod bar, bahan-bahan dan menu untuk menyemak serta‑merta sama ada makanan selamat untuk tubuh anda',
      typing: ['penyakit kronik', 'alahan', 'intolerans makanan'],
      stat1: '500+',
      stat1Label: 'Penyakit kronik',
      stat2: '250+',
      stat2Label: 'Alahan',
      stat3: '100+',
      stat3Label: 'Intolerans makanan',
      downloadButton: 'Muat turun di App Store'
    },
    howItWorks: {
      badge: 'Cara ia berfungsi',
      mainTitle: 'Cara SafeEat berfungsi',
      subtitle: 'Proses imbasan kami mudah dan pantas — dapatkan hasil peribadi dalam beberapa langkah ringkas.',
      setupBadge: 'Persediaan sekali sahaja',
      setupTitle: 'Cipta profil kesihatan anda',
      setupDesc: 'Semasa onboarding, anda menyesuaikan SafeEat dengan menambah maklumat kesihatan anda. Ini dilakukan sekali dan membolehkan analisis makanan yang diperibadikan khusus untuk anda.',
      cards: {
        diseases: {
          title: 'Penyakit kronik',
          desc: 'Tambah keadaan kronik anda daripada pangkalan data kami dengan 500+ penyakit. Dari diabetes hingga penyakit jantung — kami sedia membantu.',
          statLabel: 'keadaan'
        },
        allergies: {
          title: 'Alahan',
          desc: 'Pilih alahan anda daripada 250+ pilihan. Daripada kekacang dan makanan laut yang biasa hingga alergen yang jarang — kekal selamat.',
          statLabel: 'alahan'
        },
        intolerances: {
          title: 'Intolerans makanan',
          desc: 'Nyatakan intolerans anda daripada 100+ pilihan. Laktosa, gluten, fruktosa dan banyak lagi — makan tanpa ketidakselesaan.',
          statLabel: 'intolerans'
        }
      },
      connectorText: 'Kemudian — setiap kali anda mengimbas:',
      stepLabel: 'Langkah',
      step1Title: 'Imbas makanan',
      step1Desc: 'Imbas apa sahaja — produk tunggal, hidangan siap, label nutrisi, kod bar atau bahkan menu restoran. Hanya halakan kamera, SafeEat akan buat selebihnya.',
      step2Title: 'Analisis AI',
      step2Desc: 'Aplikasi menganalisis imej dan membandingkannya dengan profil penyakit, alahan dan intolerans anda untuk menilai kesesuaian.',
      step3Title: 'Dapatkan laporan anda',
      step3Desc: 'SafeEat menghasilkan laporan terperinci tentang item yang dianalisis dan menunjukkan sejauh mana ia sesuai dengan profil kesihatan serta keperluan diet anda.',
      step4Title: 'Buat keputusan',
      step4Desc: 'Dengan wawasan yang diperibadikan, anda memilih makanan terbaik untuk anda. Makan dengan yakin — dan ketahui dengan tepat apa yang sesuai untuk tubuh anda.',
      processTitle: 'Lihat dalam tindakan',
      processSubtitle: 'Lihat cara SafeEat menganalisis makanan anda secara masa nyata'
    },
    features: {
      title: 'Ciri yang berkuasa',
      subtitle: 'Semua yang anda perlukan untuk makan dengan selamat',
      items: [
        {
          title: 'Imbas sebarang produk',
          subtitle: 'daripada snek biasa hingga buah eksotik',
          description: 'Halakan kamera pada makanan berbungkus atau tidak berbungkus — daripada produk pasar raya harian hingga buah tropika yang jarang — dan dapatkan penilaian keselamatan serta‑merta. SafeEat mengenal pasti produk dan menganalisis komposisinya supaya anda boleh mengelakkan bahan berisiko atau berbahaya.',
          highlights: ['Makanan berbungkus & tidak berbungkus', 'Semakan keselamatan segera', 'Analisis komposisi'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Imbas label nutrisi dan kod bar',
          subtitle: 'fahami label yang rumit dengan cepat',
          description: 'Tidak perlu lagi bergelut dengan tulisan kecil atau label yang mengelirukan. SafeEat boleh mengimbas maklumat nutrisi, senarai bahan, dan kod bar dalam beberapa saat. Aplikasi segera menonjolkan alergen, bahan tambahan, atau bahan yang berpotensi berbahaya supaya anda tahu apa yang anda makan.',
          highlights: ['Imbasan maklumat nutrisi', 'Pengesanan alergen', 'Amaran bahan berbahaya'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Imbas hidangan — kenal pasti bahan',
          subtitle: 'daripada foto makanan anda',
          description: 'Ambil foto hidangan anda dan SafeEat akan memecahkannya kepada bahan-bahan. Sama ada masakan rumah, food court atau restoran fine dining, AI kami mengenal pasti alergen berpotensi atau bahan yang tidak sesuai dengan diet anda.',
          highlights: ['Pengecaman bahan dengan AI', 'Berfungsi untuk semua hidangan', 'Semakan kesesuaian diet'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Imbas menu restoran',
          subtitle: 'cari pilihan selamat sebelum memesan',
          description: 'Halakan kamera pada menu kafe atau restoran dan SafeEat menganalisis setiap hidangan untuk menunjukkan mana yang selamat untuk keperluan anda. Sesuai untuk makan di luar tanpa risau tentang alergen tersembunyi atau bahan yang tidak diingini.',
          highlights: ['Analisis menu', 'Mencari hidangan selamat', 'Mengesan alergen tersembunyi'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Bina senarai makanan selamat peribadi',
          subtitle: 'wujudkan perpustakaan "clean eating" anda',
          description: 'Simpan makanan yang selamat untuk anda, bina perpustakaan "clean eating" anda, dan akses bila-bila masa ketika membeli-belah atau makan di luar. Pangkalan data makanan peribadi anda berkembang dengan setiap imbasan.',
          highlights: ['Perpustakaan makanan peribadi', 'Akses pantas di mana-mana', 'Cadangan pintar'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Dipercayai oleh ribuan orang',
      subtitle: 'Komuniti pengguna global mempercayai SafeEat.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat benar-benar mengubah cara saya membeli-belah! Saya ada celiac, dan mengimbas produk hanya mengambil beberapa saat. Tidak perlu lagi membaca label kecil di kedai.'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: 'Dengan beberapa alahan makanan, makan di luar selalu membuat saya tertekan. Sekarang saya hanya imbas menu dan terus tahu apa yang selamat. Memang mengubah segalanya!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'Saya gunakan SafeEat setiap hari untuk mengurus diabetes. Pengimbas nutrisi membantu saya menjejak karbohidrat dan gula dengan segera. Sangat disyorkan!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: 'Anak saya ada alahan kacang yang teruk. SafeEat memberikan ketenangan ketika membeli snek untuknya. Pengimbas kod bar sangat tepat.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: 'Saya intoleran laktosa dan banyak mengembara. SafeEat berfungsi dalam semua bahasa — saya imbas menu di luar negara dan makan tanpa risau!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'Mengurus penyakit jantung suami saya bermakna kami perlu memerhati natrium. Analisis segera SafeEat menjadikan perancangan makanan jauh lebih mudah.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Pengimbas hidangan memang hebat! Saya mengambil foto hidangan restoran dan ia menemui sumber gluten tersembunyi. Ia menyelamatkan saya daripada reaksi yang teruk!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'Saya mengikuti diet halal yang ketat. SafeEat membantu saya menyemak bahan dalam makanan berbungkus yang tidak saya kenal. Aplikasi yang wajib ada!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Dengan tiga anak yang mempunyai alahan berbeza, membeli-belah dulu macam mimpi ngeri. Kini saya imbas semuanya dalam beberapa saat. Aplikasi ini benar-benar penyelamat!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'Saya ada penyakit Crohn dan perlu mengelakkan pencetus tertentu. Profil peribadi SafeEat mengesan bahan yang biasanya saya terlepas pandang.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'Ciri imbas menu sangat sesuai untuk alahan makanan laut saya. Kini saya lebih yakin untuk memesan di restoran baharu!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'Sebagai tukang masak dengan sensitiviti tertentu, saya gunakan SafeEat untuk menyemak bahan sebelum memasak. Pengecaman AI sangat mengagumkan.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: 'Mengurus diabetes gestasi terasa berat sehingga saya jumpa SafeEat. Mengimbas label makanan menjadikan pemantauan sangat mudah!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Saya ada intolerans makanan yang jarang dan kebanyakan aplikasi tidak menyokongnya. SafeEat membenarkan saya menambahkannya ke profil — dan kini ia dikesan setiap kali!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: 'Saya vegan dan alah kepada soya — mencari makanan selamat memang sukar! SafeEat mengesan produk haiwan tersembunyi DAN soya. Saya suka!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'Pangkalan data kod bar sangat besar! Produk import pun dikenali. Sesuai untuk menyemak makanan antarabangsa yang tidak biasa.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: 'Ibu saya yang sudah berusia mempunyai beberapa keadaan kesihatan. Saya buatkan profilnya dan kini dia boleh membeli-belah sendiri semula. Terima kasih, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Harga yang ringkas dan telus',
      subtitle: 'Pilih pelan yang sesuai untuk anda',
      free: 'Percuma',
      freeDesc: '5 imbasan percuma untuk mencuba',
      freeFeature1: '5 imbasan AI',
      freeFeature2: 'Analisis asas',
      freeFeature3: 'Sejarah terhad',
      weekly: 'Mingguan',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Sesuai untuk pengguna tetap',
      weeklyFeature1: 'Imbasan tanpa had',
      weeklyFeature2: 'Analisis lanjutan',
      weeklyFeature3: 'Sejarah penuh',
      weeklyFeature4: 'Sokongan keutamaan',
      annual: 'Tahunan',
      annualPrice: '$29.99',
      annualDesc: 'Nilai terbaik — jimat 60%',
      annualBadge: 'Paling popular',
      annualFeature1: 'Semua daripada Mingguan',
      annualFeature2: 'Jimat $125/tahun',
      annualFeature3: 'Sokongan premium',
      annualFeature4: 'Akses awal ciri baharu',
      button: 'Mula'
    },
    faq: {
      title: 'Soalan Lazim',
      items: [
        {
          q: 'Apa yang boleh saya imbas dengan SafeEat?',
          a: 'SafeEat boleh menganalisis: senarai bahan dan label nutrisi (foto), kod bar, makanan berbungkus dan tidak berbungkus, hidangan (foto), serta menu restoran.'
        },
        {
          q: 'Bagaimana pengecaman hidangan berfungsi?',
          a: 'AI mengenal pasti hidangan dan menganggar bahan yang berkemungkinan ada. Ia berfungsi terbaik dengan pencahayaan yang baik dan sudut yang sesuai; bahan tersembunyi atau pencemaran silang mungkin terlepas.'
        },
        {
          q: 'Bolehkah saya mengimbas menu restoran?',
          a: 'Ya. Ia menandakan alergen berpotensi dan konflik diet. Menu boleh tidak lengkap; sentiasa sahkan dengan staf tentang bahan, minyak, fryer berkongsi, sos, dan pencemaran silang.'
        },
        {
          q: 'Keadaan kesihatan apa yang disokong SafeEat?',
          a: 'Alahan makanan, intolerans (cth. laktosa, gluten) dan keperluan diet (rendah natrium, fokus karbohidrat, elak bahan tertentu).'
        },
        {
          q: 'Adakah SafeEat berfungsi secara offline?',
          a: 'Biasanya imbasan/AI memerlukan sambungan internet. Keputusan dan senarai yang disimpan mungkin tersedia offline bergantung pada aplikasi dan peranti.'
        },
        {
          q: 'Sejauh mana tepatnya analisis SafeEat?',
          a: 'Ia bergantung pada kualiti foto, teks pada label, bahasa/format dan kelengkapan menu. Ia hanya untuk maklumat — sentiasa semak semula apabila risikonya tinggi.'
        },
        {
          q: 'Bagaimana jika SafeEat membuat kesilapan?',
          a: 'Jangan bergantung pada keputusan yang meragukan; imbas semula dengan foto yang lebih baik; sahkan melalui label/laman web/staf; untuk risiko tinggi, pilih pilihan paling selamat.'
        },
        {
          q: 'Bolehkah saya melaporkan keputusan analisis yang salah?',
          a: 'Ya: support@safeeat.app — sertakan tangkapan skrin, foto label/menu/hidangan, negara/jenama/produk dan apa yang nampak salah.'
        },
        {
          q: 'Mengapa pengecaman hidangan boleh kurang tepat?',
          a: 'Hidangan yang kelihatan serupa, bahan tersembunyi, sos/hiasan yang kuat, pencahayaan rendah/kabur, serta hidangan yang sangat diubah suai atau bersifat setempat.'
        },
        {
          q: 'Adakah SafeEat peranti perubatan?',
          a: 'Tidak. Ia hanya untuk maklumat. Rujuk profesional kesihatan untuk nasihat perubatan.'
        },
        {
          q: 'Apakah tanggungjawab SafeEat?',
          a: 'Maklumat diberi "seadanya"; kelengkapan tidak boleh dijamin. Anda bertanggungjawab atas pilihan anda; bahan yang tidak dinyatakan dan pencemaran silang boleh berlaku.'
        },
        {
          q: 'Patutkah saya bergantung pada SafeEat sahaja untuk alahan serius?',
          a: 'Tidak. Baca label, tanya staf, ikut pelan tindakan anda dan bawa ubat yang ditetapkan; elakkan jika ragu.'
        },
        {
          q: 'Bagaimana anda melindungi data kesihatan saya?',
          a: 'Kami mengumpul data minimum dan melindunginya dengan langkah keselamatan serta akses terhad. Untuk privasi, hubungi support@safeeat.app. Lihat Dasar Privasi.'
        },
        {
          q: 'Bolehkah saya guna SafeEat untuk anak saya?',
          a: 'Ya, di bawah pengawasan. Lebih berhati-hati untuk alahan serius/keperluan diet kompleks; sahkan dengan sumber yang dipercayai.'
        },
        {
          q: 'Apa yang perlu saya buat jika saya mengalami reaksi?',
          a: 'Dapatkan bantuan perubatan segera, ikut pelan kecemasan anda, hubungi 112 untuk gejala serius, dan selepas itu laporkan kepada penyedia/restoran/pengilang.'
        }
      ]
    },
    cta: {
      title: 'Bersedia untuk mengubah cara anda makan?',
      subtitle: 'Sertai ribuan orang yang menggunakan SafeEat setiap hari',
      description: 'Muat turun sekarang dan dapatkan 5 imbasan AI percuma',
      button: 'Muat turun di App Store',
      feature1: 'Tiada kad kredit',
      feature2: '100% peribadi & selamat',
      feature3: 'Akses segera'
    },
    newsletter: {
      title: 'Kekal terkini',
      subtitle: 'Dapatkan kemas kini terkini dan tip kesihatan',
      placeholder: 'Masukkan e-mel anda',
      button: 'Langgan',
      success: 'Terima kasih kerana melanggan!',
      error: 'Masukkan alamat e-mel yang sah'
    },
    seo: {
      title: 'Pembantu peribadi anda untuk keselamatan makanan',
      text: 'SafeEat ialah aplikasi pengimbas makanan berkuasa AI untuk mereka yang mempunyai alahan makanan, intolerans dan sekatan diet. Imbas label, maklumat nutrisi, kod bar, menu restoran dan hidangan siap untuk menyemak keselamatan berdasarkan profil anda. Sama ada anda ada celiac, intolerans laktosa, alahan kacang, atau mengikuti diet bebas gluten, vegan atau rendah natrium, SafeEat membantu anda memilih dengan selamat.',
      keywords: [
        'Aplikasi alahan makanan',
        'Pengimbas bahan',
        'Pengesan alergen',
        'Pembaca label nutrisi',
        'Pengimbas kod bar',
        'Pengimbas menu',
        'Semak bebas gluten',
        'Aplikasi intolerans laktosa',
        'Penjejak diet',
        'Keselamatan makanan'
      ]
    },
    cookies: {
      bannerText: 'Kami menggunakan kuki: kuki yang diperlukan untuk menjalankan laman dan kuki analitik hanya dengan persetujuan anda.',
      decline: 'Tolak',
      accept: 'Terima'
    },
    legal: {
      backHome: 'Kembali ke laman utama'
    },
    footer: {
      product: 'Produk',
      features: 'Ciri',
      howItWorks: 'Cara ia berfungsi',
      testimonials: 'Ulasan',
      pricing: 'Harga',
      faq: 'Soalan Lazim',
      contacts: 'Hubungi',
      legal: 'Perundangan',
      privacy: 'Dasar Privasi',
      terms: 'Terma Perkhidmatan',
      usePolicy: 'Panduan Pengguna',
      disclaimer: 'Penafian',
      company: 'FJCode OU',
      register: 'Nombor pendaftaran 16993109',
      address: 'Viru väljak 2, 10111 Tallinn, Estonia',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. Hak cipta terpelihara.'
    }
  },
  no: {
    meta: {
      home: {
        title: 'SafeEat | AI‑basert matskanner for allergier og intoleranser',
        description: 'Skann etiketter, strekkoder, menyer og retter og få markert allergener og kostholdsrisikoer basert på profilen din: allergier, intoleranser og kroniske sykdommer.'
      },
      privacy: {
        title: 'Personvernerklæring | SafeEat',
        description: 'Les hvordan SafeEat samler inn, bruker og beskytter opplysningene dine.'
      },
      terms: {
        title: 'Vilkår for bruk | SafeEat',
        description: 'Se vilkårene som gjelder når du bruker SafeEat.'
      },
      manual: {
        title: 'Brukerveiledning | SafeEat',
        description: 'Lær hvordan du bruker SafeEat trygt til å skanne mat.'
      },
      disclaimer: {
        title: 'Ansvarsfraskrivelse | SafeEat',
        description: 'Forstå hvilke informative anbefalinger og begrensninger som gjelder for SafeEat.'
      }
    },
    nav: {
      about: 'Hvordan det fungerer',
      features: 'Funksjoner',
      testimonials: 'Tilbakemeldinger',
      pricing: 'Priser',
      faq: 'FAQ',
      contacts: 'Kontakt',
      getStarted: 'Kom i gang'
    },
    about: {
      description: 'En AI‑basert matskanner som leser etiketter, strekkoder, retter og menyer og fremhever allergener, ingredienser og næringsinformasjon basert på helseprofilen din.'
    },
    hero: {
      badge: 'Mattrygghet med AI',
      title: 'Se med en gang',
      title2: 'hvor trygg maten er',
      titleHighlight: 'for deg',
      subtitle: 'Basert på',
      description: 'Skann matvarer, strekkoder, ingredienslister og menyer og sjekk på sekunder om de passer for kroppen din.',
      typing: ['kroniske sykdommer', 'allergier', 'matintoleranser'],
      stat1: '500+',
      stat1Label: 'Kroniske sykdommer',
      stat2: '250+',
      stat2Label: 'Allergier',
      stat3: '100+',
      stat3Label: 'Matintoleranser',
      downloadButton: 'Last ned i App Store'
    },
    howItWorks: {
      badge: 'Hvordan det fungerer',
      mainTitle: 'Hvordan SafeEat fungerer',
      subtitle: 'Skanningen er enkel og rask – du får personlige resultater på noen få steg.',
      setupBadge: 'Engangsoppsett',
      setupTitle: 'Opprett helseprofilen din',
      setupDesc: 'Legg inn helseopplysningene dine i oppsettet for å tilpasse SafeEat til deg. Du gjør det én gang – deretter er alle analyser basert på profilen din.',
      cards: {
        diseases: {
          title: 'Kroniske sykdommer',
          desc: 'Legg til kroniske tilstander fra databasen vår med 500+ diagnoser. Fra diabetes til hjerteproblemer – vi har det dekket.',
          statLabel: 'diagnoser'
        },
        allergies: {
          title: 'Allergier',
          desc: 'Velg allergiene dine blant over 250 alternativer. Fra vanlige nøtter og skalldyr til sjeldne allergener – for mer trygghet i hverdagen.',
          statLabel: 'allergier'
        },
        intolerances: {
          title: 'Matintoleranser',
          desc: 'Registrer intoleransene dine blant mer enn 100 alternativer. Laktose, gluten, fruktose og mer – så du kan spise mer komfortabelt.',
          statLabel: 'intoleranser'
        }
      },
      connectorText: 'Deretter, ved hver skanning:',
      stepLabel: 'Trinn',
      step1Title: 'Skann maten',
      step1Desc: 'Skann hvilken som helst matvare – enkeltprodukt, ferdigrett, næringstabell, strekkode eller til og med en restaurantmeny. Bare pek med kameraet, så gjør SafeEat resten.',
      step2Title: 'AI‑analyse',
      step2Desc: 'Appen analyserer bildet og sammenligner det med profilen din (sykdommer, allergier, intoleranser) for å vurdere om maten er egnet.',
      step3Title: 'Få en rapport',
      step3Desc: 'SafeEat gir deg en detaljert rapport som viser hvor godt den skannede maten passer til helsetilstanden og kostholdsbehovene dine.',
      step4Title: 'Ta et valg',
      step4Desc: 'Med personlige innsikter kan du velge maten som passer best for deg. Spis med trygghet fordi du vet hva som er bra for kroppen din.',
      processTitle: 'Se hvordan det fungerer',
      processSubtitle: 'Se SafeEat analysere maten din i sanntid'
    },
    features: {
      title: 'Kraftige funksjoner',
      subtitle: 'Alt du trenger for tryggere matvalg',
      items: [
        {
          title: 'Skann enhver matvare',
          subtitle: 'fra hverdagslige snacks til eksotisk frukt',
          description: 'Pek kameraet mot hvilken som helst pakket eller upakket matvare – fra vanlige dagligvarer til sjeldne tropiske frukter – og få en umiddelbar sikkerhetssjekk. SafeEat gjenkjenner produktet og analyserer ingrediensene for å hjelpe deg å unngå risikable eller uønskede stoffer.',
          highlights: ['Pakkede og upakkede produkter', 'Umiddelbar sikkerhetssjekk', 'Ingrediensanalyse'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Skann næringsetiketter og strekkoder',
          subtitle: 'forstå kompliserte etiketter på sekunder',
          description: 'Slipp å myse på ørsmå bokstaver. SafeEat skanner næringstabellen, ingredienslisten og strekkoden på få sekunder og fremhever direkte allergener, tilsetningsstoffer eller potensielt skadelige stoffer, slik at du vet nøyaktig hva du spiser.',
          highlights: ['Skanning av næringstabeller', 'Allergengjenkjenning', 'Varsler om risikostoffer'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Skann retter – oppdag ingrediensene',
          subtitle: 'bare ta et bilde av tallerkenen din',
          description: 'Ta et bilde av måltidet ditt, så bryter SafeEat det ned i sannsynlige ingredienser. Enten det er hjemmelaget mat, kantinemat eller restaurantretter, kan AI‑en avsløre mulige allergener eller komponenter som ikke passer kostholdet ditt.',
          highlights: ['AI‑basert ingrediensgjenkjenning', 'Fungerer på alle retter', 'Sjekk mot kostholdet ditt'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Skann restaurantmenyer',
          subtitle: 'finn trygge alternativer før du bestiller',
          description: 'Pek kameraet på menyen på kafé eller restaurant, så analyserer SafeEat rettene og viser hvilke som passer dine behov. Spis ute med mindre stress – uten å bekymre deg for skjulte allergener eller uønskede ingredienser.',
          highlights: ['Menyanalyse', 'Anbefaling av trygge retter', 'Oppdager skjulte allergener'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Bygg din egen liste over trygge matvarer',
          subtitle: 'lag ditt eget «rene» matbibliotek',
          description: 'Lagre matvarer som fungerer for deg, og bygg opp et personlig «rent» matbibliotek som du kan slå opp i i butikken eller på farten. Din personlige database vokser med hver skanning og hjelper deg å ta raskere beslutninger.',
          highlights: ['Personlig matdatabase', 'Rask sjekk når som helst', 'Smartere anbefalinger'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Brukt og anbefalt av brukere',
      subtitle: 'En global brukercommunity stoler på SafeEat.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat har totalt endret hvordan jeg handler mat! Jeg har cøliaki, og det tar bare noen sekunder å skanne et produkt. Ingen flere uleselige etiketter i butikken.'
        },
        {
          name: 'Marcus L.',
          location: 'London, Storbritannia',
          rating: 5,
          content: 'Som en person med flere matallergier har det alltid vært stressende å spise ute. Nå skanner jeg bare menyen og ser med én gang hva som er trygt. En skikkelig game changer!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'Jeg bruker SafeEat hver dag for å håndtere diabetesen min. Skanneren for næringsetiketter hjelper meg å følge med på karbohydrater og sukker umiddelbart. Anbefales på det sterkeste!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Tyskland',
          rating: 5,
          content: 'Sønnen min har alvorlig nøtteallergi. SafeEat gir meg ro i sjelen når jeg kjøper snacks til ham. Strekkodeskanneren er utrolig presis.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spania',
          rating: 5,
          content: 'Jeg har laktoseintoleranse og reiser mye. SafeEat fungerer på alle språk – i utlandet skanner jeg bare menyen og kan spise mye tryggere!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'På grunn av hjertesykdommen til partneren min må vi passe godt på natriuminntaket. Den umiddelbare analysen i SafeEat gjør måltidsplanleggingen mye enklere.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Funksjonen for å skanne retter er fantastisk! Jeg tok bilde av en restaurantrett, og appen oppdaget skjulte ingredienser med gluten. Det sparte meg for en skikkelig dårlig reaksjon!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, De forente arabiske emirater',
          rating: 5,
          content: 'Jeg følger et strengt halal‑kosthold. SafeEat hjelper meg å sjekke ingrediensene i pakkede produkter jeg ikke kjenner. En uunnværlig app!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Med tre barn som alle har ulike allergier, var matshopping et mareritt. Nå skanner jeg alt på få sekunder. Denne appen er virkelig livredderen min!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sverige',
          rating: 5,
          content: 'Jeg har Crohns sykdom og må unngå visse triggere. Den personlige profilen i SafeEat fanger opp ingredienser jeg ellers lett kunne oversett.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brasil',
          rating: 5,
          content: 'Meny‑skanneren er perfekt for skalldyrallergien min. Nå bestiller jeg i nye restauranter med mye mer trygghet!'
        },
        {
          name: 'David C.',
          location: 'Paris, Frankrike',
          rating: 5,
          content: 'Som kokk med matfølsomhet bruker jeg SafeEat til å dobbeltsjekke ingrediensene før jeg lager mat. Nøyaktigheten i AI‑gjenkjenningen er imponerende.'
        },
        {
          name: 'Fatima A.',
          location: 'Kairo, Egypt',
          rating: 5,
          content: 'Svangerskapsdiabetes var veldig krevende å håndtere før jeg fant SafeEat. Å skanne matetiketter gjør oppfølgingen superenkel!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Jeg har en sjelden matintoleranse som de fleste apper ikke støtter. I SafeEat kunne jeg legge den til profilen min, og nå blir den fanget opp hver gang!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Nederland',
          rating: 5,
          content: 'Jeg er veganer og allergisk mot soya – å finne trygg mat var nesten umulig! SafeEat oppdager skjulte animalske ingredienser og soya. Jeg elsker den!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, Sør-Korea',
          rating: 5,
          content: 'Strekkodedatabasen er enorm! Selv importerte produkter blir gjenkjent. Perfekt for å sjekke ukjente internasjonale matvarer.'
        },
        {
          name: 'Elena P.',
          location: 'Roma, Italia',
          rating: 5,
          content: 'Moren min er eldre og har flere helseutfordringer. Jeg satte opp en profil for henne, og nå kan hun handle selv igjen. Tusen takk, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Enkle og transparente priser',
      subtitle: 'Velg abonnementet som passer for deg',
      free: 'Gratis',
      freeDesc: '5 gratis skanninger for å teste',
      freeFeature1: '5 AI‑skanninger',
      freeFeature2: 'Grunnleggende analyse',
      freeFeature3: 'Begrenset historikk',
      weekly: 'Ukentlig',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Ideell for jevnlig bruk',
      weeklyFeature1: 'Ubegrenset antall skanninger',
      weeklyFeature2: 'Avansert analyse',
      weeklyFeature3: 'Full historikk',
      weeklyFeature4: 'Prioritert brukerstøtte',
      annual: 'Årlig',
      annualPrice: '$29.99',
      annualDesc: 'Best verdi – spar 60 %',
      annualBadge: 'Mest populær',
      annualFeature1: 'Alt i det ukentlige abonnementet',
      annualFeature2: 'Spar 125 $ per år',
      annualFeature3: 'Premium‑støtte',
      annualFeature4: 'Tidlig tilgang til nye funksjoner',
      button: 'Kom i gang'
    },
    faq: {
      title: 'Ofte stilte spørsmål',
      items: [
        {
          q: 'Hva kan jeg skanne med SafeEat?',
          a: 'SafeEat kan analysere: ingredienslister og næringstabeller (fra bilde), strekkoder, pakkede og upakkede matvarer, retter (fra bilde) og restaurantmenyer.'
        },
        {
          q: 'Hvordan fungerer gjenkjenning av retter?',
          a: 'AI‑en gjenkjenner retten og anslår sannsynlige ingredienser. Den fungerer best med godt lys og tydelig vinkel; skjulte ingredienser eller krysskontaminering kan noen ganger bli oversett.'
        },
        {
          q: 'Kan jeg skanne menyer på restauranter?',
          a: 'Ja. Appen markerer mulige allergener og konflikter med kostholdet ditt. Menyer kan være ufullstendige, så spør alltid de ansatte om ingredienser, olje, delte frityrer, sauser og risiko for krysskontaminering.'
        },
        {
          q: 'Hvilke helsetilstander støtter SafeEat?',
          a: 'Matallergier, intoleranser (for eksempel laktose eller gluten) og kostholdsbehov (lavt natrium, fokus på karbohydrater, unngå bestemte ingredienser).'
        },
        {
          q: 'Fungerer SafeEat uten internett?',
          a: 'Som regel krever skanning og AI‑analyse internettforbindelse. Lagrede resultater og lister kan være tilgjengelige offline, avhengig av appen og enheten.'
        },
        {
          q: 'Hvor nøyaktig er analysen i SafeEat?',
          a: 'Nøyaktigheten avhenger av bildekvalitet, lesbarhet av tekst, språk/format og hvor komplett meny‑informasjonen er. SafeEat er et informasjonsverktøy – ved høy risiko bør du alltid dobbeltsjekke.'
        },
        {
          q: 'Hva gjør jeg hvis SafeEat tar feil?',
          a: 'Ikke stol på et resultat du er usikker på; ta et nytt, klarere bilde; sjekk etiketten eller produsentens nettside, eller spør de ansatte; ved høy risiko bør du velge det tryggere alternativet.'
        },
        {
          q: 'Kan jeg rapportere feil analyse?',
          a: 'Ja: send en e‑post til support@safeeat.app med skjermbilde, bilde av etikett/meny/rett, land, merke og produktnavn, og forklar hva du mener er feil.'
        },
        {
          q: 'Hvorfor kan gjenkjenning av retter være mindre nøyaktig?',
          a: 'Retter som ser like ut, skjulte ingredienser, tunge sauser eller pynt, dårlig lys eller uskarpe bilder, samt svært tilpassede eller lokale retter kan redusere nøyaktigheten.'
        },
        {
          q: 'Er SafeEat et medisinsk utstyr?',
          a: 'Nei. Det er kun ment som et informasjonsverktøy. For medisinske råd må du kontakte helsepersonell.'
        },
        {
          q: 'Hvilket ansvar har SafeEat?',
          a: 'Informasjonen gis «som den er», uten garanti for fullstendighet eller feilfrihet. Du er selv ansvarlig for valgene dine; uoppførte ingredienser og krysskontaminering kan forekomme.'
        },
        {
          q: 'Bør jeg stole bare på SafeEat ved alvorlige allergier?',
          a: 'Nei. Les etiketter, spør personalet, følg din egen beredskapsplan og ha med foreskrevet medisin; hvis du er i tvil, bør du unngå produktet eller retten.'
        },
        {
          q: 'Hvordan beskytter dere helseopplysningene mine?',
          a: 'Vi samler inn et minimum av nødvendige data og beskytter dem med sikkerhetstiltak og begrenset tilgang. For spørsmål om personvern kan du kontakte support@safeeat.app. Se også personvernerklæringen.'
        },
        {
          q: 'Kan barn bruke SafeEat?',
          a: 'Ja, under tilsyn av en voksen. Ved alvorlige allergier eller komplekse dietter bør du være ekstra forsiktig og verifisere informasjon via pålitelige kilder.'
        },
        {
          q: 'Hva bør jeg gjøre hvis jeg får en reaksjon?',
          a: 'Søk straks medisinsk hjelp og følg beredskapsplanen din; ved alvorlige symptomer må du kontakte nødetatene. Deretter bør du informere legen, restauranten eller produsenten om hendelsen.'
        }
      ]
    },
    cta: {
      title: 'Klar til å endre forholdet ditt til mat?',
      subtitle: 'Bli med tusenvis av mennesker som bruker SafeEat hver dag',
      description: 'Last ned nå og få 5 gratis AI‑skanninger',
      button: 'Last ned i App Store',
      feature1: 'Ingen kredittkort nødvendig',
      feature2: '100 % privat og sikkert',
      feature3: 'Kom i gang på sekunder'
    },
    newsletter: {
      title: 'Hold deg oppdatert',
      subtitle: 'Få nyheter og tips for tryggere matvalg',
      placeholder: 'Skriv inn e‑postadressen din',
      button: 'Meld meg på',
      error: 'Skriv inn en gyldig e‑postadresse',
      success: 'Takk for påmeldingen!'
    },
    seo: {
      title: 'Din personlige assistent for mattrygghet',
      text: 'SafeEat er en AI‑drevet matskanner for personer med allergier, intoleranser og kostholdsbegrensninger. Skann umiddelbart etiketter, næringstabeller, strekkoder, restaurantmenyer og ferdigretter for å sjekke om de passer til profilen din. Enten du har cøliaki, laktoseintoleranse, nøtteallergi eller følger et glutenfritt, vegansk eller natriumredusert kosthold, hjelper SafeEat deg å ta tryggere valg.',
      keywords: [
        'app for matallergi',
        'ingredienstskanner',
        'allergendeteksjon',
        'skanning av næringsetiketter',
        'strekkodeskanner',
        'menyskanner',
        'sjekk for glutenfritt',
        'app for laktoseintoleranse',
        'kostholdsoppfølging',
        'mattrygghet'
      ]
    },
    cookies: {
      bannerText: 'Vi bruker informasjonskapsler (cookies): nødvendige kapsler for at nettstedet skal fungere, og analyse‑cookies bare med ditt samtykke.',
      accept: 'Godta',
      decline: 'Avslå'
    },
    legal: {
      backHome: 'Tilbake til forsiden'
    },
    footer: {
      product: 'Produkt',
      features: 'Funksjoner',
      howItWorks: 'Hvordan det fungerer',
      testimonials: 'Tilbakemeldinger',
      pricing: 'Priser',
      faq: 'FAQ',
      contacts: 'Kontakt',
      legal: 'Juridisk informasjon',
      privacy: 'Personvernerklæring',
      terms: 'Vilkår for tjenesten',
      disclaimer: 'Ansvarsfraskrivelse',
      usePolicy: 'Brukerveiledning',
      company: 'FJCode OU',
      register: 'Organisasjonsnummer 16993109',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. Alle rettigheter forbeholdt.'
    }
  },
  pl: {
    meta: {
      home: {
        title: 'SafeEat | Skaner żywności z AI dla alergii i nietolerancji',
        description: 'Skanuj etykiety, kody kreskowe, menu i dania, aby wykrywać alergeny i ryzyka dietetyczne na podstawie Twojego profilu: alergie, nietolerancje i choroby przewlekłe.'
      },
      privacy: {
        title: 'Polityka prywatności | SafeEat',
        description: 'Dowiedz się, w jaki sposób SafeEat zbiera, wykorzystuje i chroni Twoje dane.'
      },
      terms: {
        title: 'Warunki korzystania | SafeEat',
        description: 'Zapoznaj się z warunkami regulującymi korzystanie z aplikacji SafeEat.'
      },
      manual: {
        title: 'Instrukcja użytkownika | SafeEat',
        description: 'Poznaj zasady bezpiecznego korzystania z SafeEat do skanowania żywności.'
      },
      disclaimer: {
        title: 'Zastrzeżenie | SafeEat',
        description: 'Poznaj ograniczenia i informacyjny charakter zaleceń w aplikacji SafeEat.'
      }
    },
    nav: {
      about: 'Jak to działa',
      features: 'Funkcje',
      testimonials: 'Opinie użytkowników',
      pricing: 'Cennik',
      faq: 'FAQ',
      contacts: 'Kontakt',
      getStarted: 'Zacznij teraz'
    },
    about: {
      description: 'Skaner żywności zasilany sztuczną inteligencją, który odczytuje etykiety, kody kreskowe, dania i menu restauracyjne oraz podświetla alergeny, składniki i informacje żywieniowe na podstawie Twojego profilu zdrowotnego.'
    },
    hero: {
      badge: 'Bezpieczeństwo żywności z pomocą AI',
      title: 'Dowiedz się od razu,',
      title2: 'czy ten posiłek jest bezpieczny',
      titleHighlight: 'dla Ciebie',
      subtitle: 'Na podstawie',
      description: 'Skanuj produkty, kody kreskowe, listy składników i menu, aby w kilka sekund sprawdzić, czy są odpowiednie dla Twojego organizmu.',
      typing: ['choroby przewlekłe', 'alergie', 'nietolerancje pokarmowe'],
      stat1: '500+',
      stat1Label: 'Choroby przewlekłe',
      stat2: '250+',
      stat2Label: 'Alergie',
      stat3: '100+',
      stat3Label: 'Nietolerancje pokarmowe',
      downloadButton: 'Pobierz z App Store'
    },
    howItWorks: {
      badge: 'Jak to działa',
      mainTitle: 'Jak działa SafeEat',
      subtitle: 'Proces skanowania jest prosty i szybki – w kilku krokach otrzymasz spersonalizowany wynik.',
      setupBadge: 'Jednorazowa konfiguracja',
      setupTitle: 'Utwórz swój profil zdrowotny',
      setupDesc: 'Podczas pierwszego uruchomienia dodaj informacje o swoim stanie zdrowia, aby dopasować SafeEat do siebie. Robisz to tylko raz – późniejsze analizy zawsze uwzględniają Twój profil.',
      cards: {
        diseases: {
          title: 'Choroby przewlekłe',
          desc: 'Dodaj swoje choroby przewlekłe z bazy ponad 500 jednostek chorobowych. Od cukrzycy po problemy z sercem – uwzględniamy je w analizie.',
          statLabel: 'schorzeń'
        },
        allergies: {
          title: 'Alergie',
          desc: 'Zaznacz swoje alergie spośród ponad 250 możliwości. Od popularnych alergenów, jak orzechy czy owoce morza, po rzadsze – dla większego spokoju ducha.',
          statLabel: 'alergii'
        },
        intolerances: {
          title: 'Nietolerancje pokarmowe',
          desc: 'Określ swoje nietolerancje spośród ponad 100 opcji: laktoza, gluten, fruktoza i wiele innych – aby jeść wygodniej i bezpieczniej.',
          statLabel: 'nietolerancji'
        }
      },
      connectorText: 'A następnie przy każdym skanowaniu:',
      stepLabel: 'Krok',
      step1Title: 'Zeskanuj jedzenie',
      step1Desc: 'Zeskanuj dowolny produkt – pojedynczą żywność, gotowe danie, tabelę wartości odżywczych, kod kreskowy lub nawet menu w restauracji. Wystarczy skierować aparat, a resztą zajmie się SafeEat.',
      step2Title: 'Analiza AI',
      step2Desc: 'Aplikacja analizuje obraz i porównuje go z Twoim profilem (choroby, alergie, nietolerancje), aby ocenić, czy produkt jest dla Ciebie odpowiedni.',
      step3Title: 'Otrzymaj raport',
      step3Desc: 'SafeEat prezentuje szczegółowy raport, który pokazuje, jak dobrze zeskanowana żywność wpisuje się w Twoje potrzeby zdrowotne i dietetyczne.',
      step4Title: 'Podejmij świadomą decyzję',
      step4Desc: 'Na podstawie spersonalizowanych informacji wybierasz jedzenie, które najbardziej Ci służy. Jedz z większym spokojem, wiedząc, co jest dobre dla Twojego organizmu.',
      processTitle: 'Zobacz, jak to działa',
      processSubtitle: 'Obejrzyj, jak SafeEat analizuje Twoje jedzenie w czasie rzeczywistym'
    },
    features: {
      title: 'Mocne funkcje',
      subtitle: 'Wszystko, czego potrzebujesz, by jeść bezpieczniej',
      items: [
        {
          title: 'Skanuj dowolny produkt',
          subtitle: 'od codziennych przekąsek po egzotyczne owoce',
          description: 'Skieruj aparat na dowolny produkt – zapakowany lub na wagę – od zwykłych artykułów spożywczych po rzadkie, tropikalne owoce. Otrzymasz natychmiastową ocenę bezpieczeństwa. SafeEat rozpoznaje produkt i analizuje jego skład, aby pomóc Ci unikać ryzykownych lub niepożądanych substancji.',
          highlights: ['Produkty pakowane i na wagę', 'Natychmiastowa kontrola bezpieczeństwa', 'Analiza składników'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Skanuj etykiety i kody kreskowe',
          subtitle: 'zrozum skomplikowane etykiety w kilka sekund',
          description: 'Koniec z mrużeniem oczu do mikroskopijnego druku. SafeEat w kilka sekund skanuje tabelę wartości odżywczych, listę składników i kod kreskowy, natychmiast podświetlając alergeny, dodatki oraz potencjalnie szkodliwe substancje, abyś dokładnie wiedział, co jesz.',
          highlights: ['Skanowanie tabel wartości odżywczych', 'Wykrywanie alergenów', 'Ostrzeżenia o substancjach ryzykownych'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Skanuj dania – poznaj składniki',
          subtitle: 'zrób zdjęcie swojego talerza',
          description: 'Zrób zdjęcie posiłku, a SafeEat rozbije go na prawdopodobne składniki. Niezależnie, czy to domowy obiad, jedzenie z kantyny czy danie z restauracji – AI może wykryć potencjalne alergeny lub składniki niepasujące do Twojej diety.',
          highlights: ['Rozpoznawanie składników przez AI', 'Działa z każdym daniem', 'Sprawdzanie zgodności z dietą'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Skanuj menu w restauracjach',
          subtitle: 'znajdź bezpieczne opcje przed złożeniem zamówienia',
          description: 'Skieruj aparat na menu w kawiarni lub restauracji, a SafeEat przeanalizuje każdą pozycję i pokaże, które dania pasują do Twoich potrzeb zdrowotnych i dietetycznych. Jedz na mieście z większą pewnością, bez ciągłej obawy o ukryte alergeny lub niechciane składniki.',
          highlights: ['Analiza menu', 'Rekomendacje bezpiecznych dań', 'Wykrywanie ukrytych alergenów'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Stwórz własną listę bezpiecznych produktów',
          subtitle: 'zbuduj osobistą bibliotekę „czystej" żywności',
          description: 'Zapisuj produkty, które Ci służą, i buduj własną bibliotekę „czystej" żywności, do której możesz sięgać podczas zakupów lub jedząc na mieście. Twoja baza rośnie z każdym skanem i pomaga Ci podejmować szybsze decyzje.',
          highlights: ['Osobista baza produktów', 'Szybka kontrola w każdej chwili', 'Inteligentne rekomendacje'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Zaufany przez użytkowników',
      subtitle: 'Społeczność użytkowników na całym świecie korzysta z SafeEat.',
      items: [
        {
          name: 'Sarah M.',
          location: 'Nowy Jork, USA',
          rating: 5,
          content: 'SafeEat całkowicie zmienił mój sposób robienia zakupów! Mam celiakię, a zeskanowanie produktu zajmuje dosłownie kilka sekund. Koniec z wpatrywaniem się w drobny druk na etykietach.'
        },
        {
          name: 'Marcus L.',
          location: 'Londyn, Wielka Brytania',
          rating: 5,
          content: 'Jako osoba z kilkoma alergiami pokarmowymi zawsze stresowałem/am się jedząc na mieście. Teraz po prostu skanuję menu i od razu wiem, co jest bezpieczne. To naprawdę zmienia zasady gry!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokio, Japonia',
          rating: 5,
          content: 'Używam SafeEat codziennie, aby lepiej kontrolować cukrzycę. Skaner etykiet żywności pomaga mi natychmiast śledzić ilość węglowodanów i cukru. Gorąco polecam!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Niemcy',
          rating: 5,
          content: 'Mój syn ma ciężką alergię na orzechy. SafeEat daje mi poczucie bezpieczeństwa, gdy kupuję mu przekąski. Skaner kodów kreskowych jest niesamowicie dokładny.'
        },
        {
          name: 'Carlos R.',
          location: 'Madryt, Hiszpania',
          rating: 5,
          content: 'Mam nietolerancję laktozy i dużo podróżuję. SafeEat działa w każdym języku – za granicą po prostu skanuję menu i jem z dużo większym spokojem!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbaj, Indie',
          rating: 5,
          content: 'Ze względu na problemy z sercem u mojego partnera musimy uważać na sód. Natychmiastowa analiza w SafeEat bardzo ułatwiła planowanie posiłków.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Funkcja skanowania dań jest świetna! Zrobiłem/am zdjęcie potrawy w restauracji, a aplikacja wykryła ukryte składniki zawierające gluten. Uchroniło mnie to przed bardzo nieprzyjemną reakcją!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubaj, ZEA',
          rating: 5,
          content: 'Stosuję restrykcyjną dietę halal. SafeEat pomaga mi sprawdzać skład zapakowanych produktów, których nie znam. Niezastąpiona aplikacja!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Kanada',
          rating: 5,
          content: 'Mam trójkę dzieci, każde z inną alergią – zakupy były koszmarem. Teraz skanuję wszystko w kilka sekund. Ta aplikacja dosłownie ratuje mi życie!'
        },
        {
          name: 'Johan V.',
          location: 'Sztokholm, Szwecja',
          rating: 5,
          content: 'Mam chorobę Leśniowskiego-Crohna i muszę unikać określonych czynników wyzwalających. Spersonalizowany profil w SafeEat wychwytuje składniki, które łatwo byłoby przeoczyć.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazylia',
          rating: 5,
          content: 'Skaner menu jest idealny przy mojej alergii na owoce morza. Teraz zamawiam w nowych restauracjach z dużo większą pewnością siebie!'
        },
        {
          name: 'David C.',
          location: 'Paryż, Francja',
          rating: 5,
          content: 'Jako kucharz/kucharka z nadwrażliwościami pokarmowymi używam SafeEat, aby sprawdzać składniki przed gotowaniem. Dokładność rozpoznawania przez AI naprawdę robi wrażenie.'
        },
        {
          name: 'Fatima A.',
          location: 'Kair, Egipt',
          rating: 5,
          content: 'Cukrzyca ciążowa była dla mnie bardzo trudna do opanowania, dopóki nie odkryłam SafeEat. Skanowanie etykiet jedzenia sprawia, że kontrola jest o wiele prostsza!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Mam rzadką nietolerancję, której większość aplikacji w ogóle nie uwzględnia. W SafeEat mogłem/am dodać ją do swojego profilu i teraz jest wykrywana za każdym razem!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Holandia',
          rating: 5,
          content: 'Jestem weganinem/ką i mam alergię na soję – znalezienie bezpiecznego jedzenia było naprawdę ciężkie! SafeEat wykrywa ukryte składniki odzwierzęce i soję. Jestem zachwycony/a!'
        },
        {
          name: 'Kenji N.',
          location: 'Seul, Korea Południowa',
          rating: 5,
          content: 'Baza kodów kreskowych jest ogromna! Rozpoznawane są nawet produkty importowane. Idealne rozwiązanie do sprawdzania nieznanej żywności z innych krajów.'
        },
        {
          name: 'Elena P.',
          location: 'Rzym, Włochy',
          rating: 5,
          content: 'Moja starsza mama ma kilka problemów zdrowotnych. Założyłem/am jej profil i teraz znowu może samodzielnie robić zakupy. Dziękuję, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Prosty i przejrzysty cennik',
      subtitle: 'Wybierz plan, który najlepiej do Ciebie pasuje',
      free: 'Darmowy',
      freeDesc: '5 darmowych skanów na start',
      freeFeature1: '5 skanów z AI',
      freeFeature2: 'Podstawowa analiza',
      freeFeature3: 'Ograniczona historia',
      weekly: 'Tygodniowy',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Idealny dla regularnych użytkowników',
      weeklyFeature1: 'Nielimitowane skanowanie',
      weeklyFeature2: 'Zaawansowana analiza',
      weeklyFeature3: 'Pełna historia wyników',
      weeklyFeature4: 'Priorytetowe wsparcie',
      annual: 'Roczny',
      annualPrice: '$29.99',
      annualDesc: 'Najlepsza wartość – oszczędzasz 60%',
      annualBadge: 'Najpopularniejszy',
      annualFeature1: 'Wszystko z planu tygodniowego',
      annualFeature2: 'Oszczędność 125 $ rocznie',
      annualFeature3: 'Wsparcie premium',
      annualFeature4: 'Wcześniejszy dostęp do nowych funkcji',
      button: 'Zacznij teraz'
    },
    faq: {
      title: 'Najczęściej zadawane pytania',
      items: [
        {
          q: 'Co mogę skanować za pomocą SafeEat?',
          a: 'SafeEat może analizować: listy składników i tabele wartości odżywczych (ze zdjęcia), kody kreskowe, żywność pakowaną i na wagę, gotowe dania (ze zdjęcia) oraz menu restauracyjne.'
        },
        {
          q: 'Jak działa rozpoznawanie dań?',
          a: 'Sztuczna inteligencja rozpoznaje danie i szacuje prawdopodobne składniki. Najlepiej działa przy dobrym oświetleniu i wyraźnym kadrze; ukryte składniki lub zanieczyszczenie krzyżowe mogą nie zostać w pełni wykryte.'
        },
        {
          q: 'Czy mogę skanować menu w restauracjach?',
          a: 'Tak. Aplikacja wskaże potencjalne alergeny oraz konflikty z Twoją dietą. Menu mogą być jednak niepełne, dlatego zawsze warto zapytać obsługę o składniki, używane oleje, wspólne frytury, sosy i ryzyko zanieczyszczenia krzyżowego.'
        },
        {
          q: 'Jakie problemy zdrowotne obsługuje SafeEat?',
          a: 'Alergie pokarmowe, nietolerancje (np. laktozy, glutenu) oraz potrzeby dietetyczne, takie jak ograniczenie sodu, uważne kontrolowanie węglowodanów czy unikanie określonych składników.'
        },
        {
          q: 'Czy SafeEat działa bez dostępu do internetu?',
          a: 'Zazwyczaj skanowanie i analiza z użyciem AI wymagają połączenia z internetem. Zapisane wyniki i listy mogą być dostępne offline, w zależności od ustawień aplikacji i urządzenia.'
        },
        {
          q: 'Jak dokładna jest analiza w SafeEat?',
          a: 'Dokładność zależy od jakości zdjęcia, czytelności tekstu, języka i formatu etykiety oraz kompletności informacji w menu. SafeEat ma charakter wyłącznie informacyjny – przy wysokim ryzyku zawsze warto dodatkowo zweryfikować dane.'
        },
        {
          q: 'Co zrobić, jeśli SafeEat się pomyli?',
          a: 'Nie opieraj się na wyniku, który budzi Twoje wątpliwości; wykonaj ponownie wyraźniejsze zdjęcie; sprawdź etykietę lub stronę producenta i zapytaj obsługę; w sytuacjach wysokiego ryzyka wybierz bezpieczniejszą opcję.'
        },
        {
          q: 'Czy mogę zgłosić błędną analizę?',
          a: 'Tak. Wyślij e-mail na adres support@safeeat.app, dołącz zrzut ekranu, zdjęcie etykiety/menu/dania, podaj kraj, markę i nazwę produktu oraz opisz, co Twoim zdaniem jest nieprawidłowe.'
        },
        {
          q: 'Dlaczego rozpoznawanie dań może być mniej dokładne?',
          a: 'Na dokładność wpływają m.in. podobnie wyglądające potrawy, ukryte składniki, gęste sosy i dekoracje, słabe oświetlenie lub rozmazane zdjęcia, a także bardzo nietypowe lub lokalne dania.'
        },
        {
          q: 'Czy SafeEat jest wyrobem medycznym?',
          a: 'Nie. To narzędzie wyłącznie informacyjne. Po porady medyczne zawsze zwracaj się do profesjonalnego personelu medycznego.'
        },
        {
          q: 'Jaka jest odpowiedzialność SafeEat?',
          a: 'Informacje są przekazywane „tak jak są", bez gwarancji kompletności czy braku błędów. Ostateczne decyzje należą do Ciebie; możliwe są nieujawnione składniki oraz zanieczyszczenie krzyżowe.'
        },
        {
          q: 'Czy przy ciężkich alergiach mogę polegać wyłącznie na SafeEat?',
          a: 'Nie. Czytaj etykiety, pytaj obsługę, stosuj swój plan awaryjny i miej przy sobie przepisane leki. Jeśli masz wątpliwości, lepiej zrezygnować z danego produktu lub potrawy.'
        },
        {
          q: 'Jak chronione są moje dane zdrowotne?',
          a: 'Zbieramy wyłącznie minimalny zakres danych i chronimy je za pomocą środków bezpieczeństwa oraz ograniczonego dostępu. W sprawach prywatności skontaktuj się z nami pod adresem support@safeeat.app i zapoznaj się z Polityką prywatności.'
        },
        {
          q: 'Czy dzieci mogą korzystać z SafeEat?',
          a: 'Tak, pod nadzorem osoby dorosłej. W przypadku ciężkich alergii lub złożonych diet należy zachować szczególną ostrożność i weryfikować informacje w wiarygodnych źródłach.'
        },
        {
          q: 'Co zrobić w razie reakcji alergicznej?',
          a: 'Natychmiast skontaktuj się z pomocą medyczną i zastosuj swój plan awaryjny; przy ciężkich objawach zadzwoń po pogotowie ratunkowe. Następnie zgłoś incydent lekarzowi, restauracji lub producentowi.'
        }
      ]
    },
    cta: {
      title: 'Gotowy/a zmienić swoje podejście do jedzenia?',
      subtitle: 'Dołącz do tysięcy osób, które korzystają z SafeEat każdego dnia',
      description: 'Pobierz aplikację teraz i otrzymaj 5 darmowych skanów z AI',
      button: 'Pobierz z App Store',
      feature1: 'Bez karty kredytowej',
      feature2: '100% prywatności i bezpieczeństwa',
      feature3: 'Gotowe do użycia w kilka sekund'
    },
    newsletter: {
      title: 'Bądź na bieżąco',
      subtitle: 'Otrzymuj najnowsze informacje i wskazówki dotyczące bezpieczniejszego odżywiania',
      placeholder: 'Wpisz swój adres e-mail',
      button: 'Zapisz się',
      error: 'Podaj poprawny adres e-mail',
      success: 'Dziękujemy za zapis!'
    },
    seo: {
      title: 'Twój osobisty asystent do bezpieczeństwa żywności',
      text: 'SafeEat to aplikacja do skanowania żywności z wykorzystaniem sztucznej inteligencji dla osób z alergiami, nietolerancjami oraz ograniczeniami dietetycznymi. Błyskawicznie skanuj etykiety, tabele wartości odżywczych, kody kreskowe, menu restauracyjne i gotowe dania, aby sprawdzić, czy pasują do Twojego profilu. Nieważne, czy masz celiakię, nietolerancję laktozy, alergię na orzechy, czy stosujesz dietę bezglutenową, wegańską lub niskosodową – SafeEat pomaga podejmować bezpieczniejsze decyzje.',
      keywords: [
        'aplikacja dla alergików',
        'skaner składników',
        'wykrywanie alergenów',
        'skanowanie etykiet żywności',
        'skaner kodów kreskowych',
        'skaner menu',
        'sprawdzanie produktów bezglutenowych',
        'aplikacja dla osób z nietolerancją laktozy',
        'śledzenie diety',
        'bezpieczeństwo żywności'
      ]
    },
    cookies: {
      bannerText: 'Używamy plików cookie: niezbędnych do działania strony oraz analitycznych – tylko za Twoją zgodą.',
      accept: 'Akceptuję',
      decline: 'Odrzuć'
    },
    legal: {
      backHome: 'Powrót na stronę główną'
    },
    footer: {
      product: 'Produkt',
      features: 'Funkcje',
      howItWorks: 'Jak to działa',
      testimonials: 'Opinie użytkowników',
      pricing: 'Cennik',
      faq: 'FAQ',
      contacts: 'Kontakt',
      legal: 'Informacje prawne',
      privacy: 'Polityka prywatności',
      terms: 'Warunki świadczenia usług',
      disclaimer: 'Zastrzeżenie',
      usePolicy: 'Instrukcja użytkownika',
      company: 'FJCode OU',
      register: 'Numer rejestracyjny 16993109',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. Wszelkie prawa zastrzeżone.'
    }
  },
  pt: {
    meta: {
      home: {
        title: 'SafeEat | Scanner de alimentos com IA para alergias e intolerâncias',
        description: 'Escaneie rótulos, códigos de barras, cardápios e pratos para destacar alérgenos e riscos alimentares com base no seu perfil: alergias, intolerâncias e doenças crônicas.'
      },
      privacy: {
        title: 'Política de Privacidade | SafeEat',
        description: 'Veja como o SafeEat coleta, usa e protege seus dados.'
      },
      terms: {
        title: 'Termos de Uso | SafeEat',
        description: 'Leia os termos aplicáveis ao uso do SafeEat.'
      },
      manual: {
        title: 'Guia do Usuário | SafeEat',
        description: 'Aprenda a usar o SafeEat para escanear alimentos com segurança.'
      },
      disclaimer: {
        title: 'Aviso Legal | SafeEat',
        description: 'Entenda as informações, orientações e limitações do SafeEat.'
      }
    },
    nav: {
      about: 'Como funciona',
      features: 'Recursos',
      testimonials: 'Avaliações',
      pricing: 'Preços',
      faq: 'Perguntas frequentes',
      contacts: 'Contato',
      getStarted: 'Começar'
    },
    about: {
      description: 'Scanner de alimentos com IA que lê rótulos, códigos de barras, pratos e cardápios e destaca alérgenos, ingredientes e informações nutricionais com base no seu perfil de saúde.'
    },
    hero: {
      badge: 'Segurança alimentar com IA',
      title: 'Verifique agora',
      title2: 'a segurança dos alimentos',
      titleHighlight: 'para você',
      subtitle: 'Com base em',
      description: 'Escaneie alimentos, códigos de barras, ingredientes e cardápios para verificar na hora se é seguro para o seu corpo',
      typing: ['doenças crônicas', 'alergias', 'intolerâncias alimentares'],
      stat1: '500+',
      stat1Label: 'Doenças crônicas',
      stat2: '250+',
      stat2Label: 'Alergias',
      stat3: '100+',
      stat3Label: 'Intolerâncias alimentares',
      downloadButton: 'Baixar na App Store'
    },
    howItWorks: {
      badge: 'Como funciona',
      mainTitle: 'Como o SafeEat funciona',
      subtitle: 'O processo de escaneamento é simples e rápido — resultados personalizados em poucos passos.',
      setupBadge: 'Configuração única',
      setupTitle: 'Crie seu perfil de saúde',
      setupDesc: 'Durante o onboarding, você personaliza o SafeEat adicionando suas informações de saúde. Isso é feito uma única vez e permite uma análise personalizada de alimentos, feita para você.',
      cards: {
        diseases: {
          title: 'Doenças crônicas',
          desc: 'Adicione suas condições crônicas a partir do nosso banco de dados com mais de 500 doenças. De diabetes a doenças cardíacas — nós cobrimos.',
          statLabel: 'condições'
        },
        allergies: {
          title: 'Alergias',
          desc: 'Selecione suas alergias entre mais de 250 opções. De nozes e frutos do mar comuns a alérgenos raros — fique protegido.',
          statLabel: 'alergias'
        },
        intolerances: {
          title: 'Intolerâncias alimentares',
          desc: 'Informe suas intolerâncias entre mais de 100 opções. Lactose, glúten, frutose e mais — coma sem desconforto.',
          statLabel: 'intolerâncias'
        }
      },
      connectorText: 'E então — sempre que você escanear:',
      stepLabel: 'Etapa',
      step1Title: 'Escaneie o alimento',
      step1Desc: 'Escaneie qualquer alimento — produtos individuais, pratos prontos, rótulos nutricionais, códigos de barras ou até cardápios de restaurantes. Basta apontar a câmera e o SafeEat faz o resto.',
      step2Title: 'Análise por IA',
      step2Desc: 'O app analisa a imagem e cruza com seu perfil (doenças, alergias e intolerâncias) para avaliar a compatibilidade.',
      step3Title: 'Receba seu relatório',
      step3Desc: 'O SafeEat gera um relatório detalhado do que foi analisado e mostra o quanto aquilo se encaixa no seu perfil de saúde e nas suas necessidades alimentares.',
      step4Title: 'Tome sua decisão',
      step4Desc: 'Com informações personalizadas, você escolhe o que é melhor para você. Coma com tranquilidade — e saiba o que funciona para o seu corpo.',
      processTitle: 'Veja em ação',
      processSubtitle: 'Veja como o SafeEat analisa seus alimentos em tempo real'
    },
    features: {
      title: 'Recursos poderosos',
      subtitle: 'Tudo o que você precisa para comer com segurança',
      items: [
        {
          title: 'Escaneie qualquer produto',
          subtitle: 'de lanches do dia a dia a frutas exóticas',
          description: 'Aponte a câmera para qualquer alimento — embalado ou não — de itens comuns do supermercado a frutas tropicais raras — e receba uma avaliação imediata de segurança. O SafeEat identifica o produto e analisa sua composição para ajudar você a evitar ingredientes arriscados ou nocivos.',
          highlights: ['Alimentos embalados e não embalados', 'Verificação imediata', 'Análise da composição'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Escaneie rótulos nutricionais e códigos de barras',
          subtitle: 'entenda rótulos complicados em segundos',
          description: 'Chega de sofrer com letras pequenas ou rótulos confusos. O SafeEat escaneia informações nutricionais, listas de ingredientes e códigos de barras em poucos segundos. O app destaca imediatamente alérgenos, aditivos ou substâncias potencialmente nocivas, para você saber exatamente o que está consumindo.',
          highlights: ['Escaneamento nutricional', 'Detecção de alérgenos', 'Alertas de substâncias nocivas'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Escaneie pratos — identifique ingredientes',
          subtitle: 'a partir de uma foto da sua refeição',
          description: 'Tire uma foto da sua refeição e o SafeEat faz uma estimativa dos ingredientes. Seja comida caseira, praça de alimentação ou alta gastronomia, nossa IA identifica possíveis alérgenos ou ingredientes que não combinam com sua dieta.',
          highlights: ['Reconhecimento de ingredientes por IA', 'Funciona com qualquer prato', 'Checagem de compatibilidade'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Escaneie cardápios de restaurantes',
          subtitle: 'encontre opções seguras antes de pedir',
          description: 'Aponte a câmera para um cardápio de café ou restaurante e o SafeEat analisa cada item, mostrando o que é seguro com base no seu perfil. Ideal para comer fora sem medo de alérgenos escondidos ou ingredientes indesejados.',
          highlights: ['Análise de cardápio', 'Encontra pratos seguros', 'Detecção de alérgenos ocultos'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Crie sua lista pessoal de alimentos seguros',
          subtitle: 'monte sua biblioteca de "alimentação limpa"',
          description: 'Salve alimentos que são seguros para você, crie sua própria biblioteca de "alimentação limpa" e acesse quando quiser — no mercado ou ao comer fora. Seu banco de dados pessoal cresce a cada escaneamento.',
          highlights: ['Biblioteca pessoal de alimentos', 'Acesso rápido em qualquer lugar', 'Sugestões inteligentes'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Confiado por milhares',
      subtitle: 'Uma comunidade global de usuários confia no SafeEat.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'O SafeEat mudou completamente a forma como faço compras! Tenho doença celíaca e escanear produtos leva apenas alguns segundos. Chega de ler rótulos minúsculos no mercado.'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: 'Com várias alergias alimentares, comer fora sempre foi estressante. Agora eu escaneio o cardápio e sei exatamente o que é seguro. Mudou o jogo!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'Uso o SafeEat diariamente para controlar minha diabetes. O escâner nutricional me ajuda a acompanhar carboidratos e açúcar na hora. Recomendo muito!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: 'Meu filho tem alergia grave a nozes. O SafeEat me dá tranquilidade ao comprar lanches para ele. O escaneamento de código de barras é incrivelmente preciso.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: 'Tenho intolerância à lactose e viajo muito. O SafeEat funciona em todos os idiomas — é só escanear cardápios no exterior e comer sem preocupação!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'Gerenciar a condição cardíaca do meu marido significa ficar de olho no sódio. A análise instantânea do SafeEat torna o planejamento das refeições muito mais fácil.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'O escâner de pratos é incrível! Tirei foto do meu prato no restaurante e ele identificou ingredientes com glúten escondidos. Evitei uma reação ruim!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'Sigo uma dieta halal rigorosa. O SafeEat me ajuda a conferir ingredientes em alimentos embalados que não conheço. App indispensável!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Com três filhos e alergias diferentes, fazer compras era um pesadelo. Agora escaneio tudo em segundos. Este app é um salvador!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'Tenho doença de Crohn e preciso evitar certos gatilhos. O perfil personalizado do SafeEat detecta ingredientes que eu provavelmente deixaria passar.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'O escaneamento de cardápio é perfeito para minha alergia a frutos do mar. Sinto muito mais confiança ao pedir em restaurantes novos!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'Como chef com sensibilidades, uso o SafeEat para checar ingredientes antes de cozinhar. O reconhecimento por IA é impressionantemente preciso.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: 'Controlar diabetes gestacional foi difícil até eu encontrar o SafeEat. Escanear rótulos torna o acompanhamento muito simples!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Tenho uma intolerância alimentar rara que a maioria dos apps não cobre. O SafeEat permitiu adicionar ao meu perfil e agora detecta sempre!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: 'Sou vegano(a) com alergia a soja — era muito difícil encontrar comida segura! O SafeEat encontra produtos de origem animal escondidos E soja. Adoro!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'O banco de dados de códigos de barras é enorme! Até produtos importados são reconhecidos. Ótimo para conferir alimentos internacionais desconhecidos.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: 'Minha mãe idosa tem várias condições de saúde. Configurei o perfil dela e agora ela consegue fazer compras sozinha novamente. Obrigado, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Preços simples e transparentes',
      subtitle: 'Escolha o plano ideal para você',
      free: 'Grátis',
      freeDesc: '5 escaneamentos grátis para testar',
      freeFeature1: '5 escaneamentos com IA',
      freeFeature2: 'Análise básica',
      freeFeature3: 'Histórico limitado',
      weekly: 'Semanal',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Ideal para uso regular',
      weeklyFeature1: 'Escaneamentos ilimitados',
      weeklyFeature2: 'Análise avançada',
      weeklyFeature3: 'Histórico completo',
      weeklyFeature4: 'Suporte prioritário',
      annual: 'Anual',
      annualPrice: '$29.99',
      annualDesc: 'Melhor custo-benefício — economize 60%',
      annualBadge: 'Mais popular',
      annualFeature1: 'Tudo do Semanal',
      annualFeature2: 'Economize US$ 125/ano',
      annualFeature3: 'Suporte premium',
      annualFeature4: 'Acesso antecipado a recursos',
      button: 'Começar'
    },
    faq: {
      title: 'Perguntas frequentes',
      items: [
        {
          q: 'O que posso escanear com o SafeEat?',
          a: 'O SafeEat pode analisar: listas de ingredientes e informações nutricionais (fotos), códigos de barras, alimentos embalados e não embalados, pratos (fotos) e cardápios de restaurantes.'
        },
        {
          q: 'Como funciona o reconhecimento de pratos?',
          a: 'A IA identifica um prato e estima possíveis ingredientes. Funciona melhor com boa iluminação e ângulo adequado; ingredientes ocultos ou contaminação cruzada podem não ser detectados.'
        },
        {
          q: 'Posso escanear cardápios de restaurantes?',
          a: 'Sim. Ele destaca possíveis alérgenos e incompatibilidades com sua dieta. Cardápios podem estar incompletos; sempre confirme com a equipe sobre ingredientes, óleos, fritadeiras compartilhadas, molhos e contaminação cruzada.'
        },
        {
          q: 'Quais condições o SafeEat oferece suporte?',
          a: 'Alergias alimentares, intolerâncias (por exemplo, lactose e glúten) e necessidades alimentares (baixo sódio, controle de carboidratos, evitar ingredientes específicos).'
        },
        {
          q: 'O SafeEat funciona sem internet?',
          a: 'Normalmente é necessária conexão para escaneamentos/IA. Resultados e listas salvas podem ficar disponíveis offline, dependendo do app e do dispositivo.'
        },
        {
          q: 'Quão precisa é a análise do SafeEat?',
          a: 'Depende da qualidade da foto, do texto do rótulo, do idioma/formato e de quão completos são os cardápios. É apenas informativo — em situações de alto risco, sempre verifique novamente.'
        },
        {
          q: 'E se o SafeEat errar?',
          a: 'Não confie em um resultado duvidoso; escaneie novamente com uma imagem melhor; confirme pelo rótulo/site/equipe; em alto risco, escolha a opção mais segura.'
        },
        {
          q: 'Posso reportar resultados incorretos?',
          a: 'Sim: support@safeeat.app com capturas de tela, foto do rótulo/cardápio/prato, país/marca/produto e o que parece estar errado.'
        },
        {
          q: 'Por que o reconhecimento de pratos pode ser menos preciso?',
          a: 'Pratos parecidos, ingredientes ocultos, molhos/guarnições fortes, baixa iluminação/desfoque e variações muito personalizadas ou regionais.'
        },
        {
          q: 'O SafeEat é um dispositivo médico?',
          a: 'Não. É apenas para informação. Consulte um profissional de saúde para orientações médicas.'
        },
        {
          q: 'Qual é a responsabilidade do SafeEat?',
          a: 'As informações são fornecidas "como estão"; não é possível garantir completude. Você é responsável por suas escolhas; ingredientes não declarados e contaminação cruzada podem acontecer.'
        },
        {
          q: 'Devo confiar somente no SafeEat em casos de alergias graves?',
          a: 'Não. Leia rótulos, pergunte à equipe, siga seu plano de ação e leve os medicamentos prescritos; em caso de dúvida, evite o item.'
        },
        {
          q: 'Como vocês protegem meus dados de saúde?',
          a: 'Coletamos o mínimo de dados e protegemos com medidas de segurança e acesso restrito. Para privacidade, entre em contato via support@safeeat.app. Veja a Política de Privacidade.'
        },
        {
          q: 'Posso usar o SafeEat para meus filhos?',
          a: 'Sim, com supervisão. Tenha ainda mais cuidado em alergias graves/necessidades complexas e confirme com fontes confiáveis.'
        },
        {
          q: 'O que fazer se eu tiver uma reação?',
          a: 'Procure ajuda médica imediatamente, siga seu plano de emergência, ligue 112 para sintomas graves e depois informe o provedor/restaurante/fabricante.'
        }
      ]
    },
    cta: {
      title: 'Pronto(a) para mudar a forma como você come?',
      subtitle: 'Junte-se a milhares de pessoas que usam o SafeEat todos os dias',
      description: 'Baixe agora e ganhe 5 escaneamentos com IA grátis',
      button: 'Baixar na App Store',
      feature1: 'Sem cartão de crédito',
      feature2: '100% privado e seguro',
      feature3: 'Acesso imediato'
    },
    newsletter: {
      title: 'Fique por dentro',
      subtitle: 'Receba as últimas atualizações e dicas de saúde',
      placeholder: 'Digite seu e-mail',
      button: 'Assinar',
      success: 'Obrigado por assinar!',
      error: 'Digite um endereço de e-mail válido'
    },
    seo: {
      title: 'Seu assistente pessoal de segurança alimentar',
      text: 'O SafeEat é um app de scanner de alimentos com IA para pessoas com alergias, intolerâncias e restrições alimentares. Escaneie rótulos, informações nutricionais, códigos de barras, cardápios de restaurantes e pratos prontos para verificar a segurança com base no seu perfil. Seja doença celíaca, intolerância à lactose, alergia a nozes, ou dieta sem glúten, vegana ou com baixo sódio, o SafeEat ajuda você a escolher com segurança.',
      keywords: [
        'App de alergia alimentar',
        'Scanner de ingredientes',
        'Detector de alérgenos',
        'Leitor de rótulos nutricionais',
        'Scanner de código de barras',
        'Scanner de cardápio',
        'Checagem sem glúten',
        'App de intolerância à lactose',
        'Monitor de dieta',
        'Segurança alimentar'
      ]
    },
    cookies: {
      bannerText: 'Usamos cookies: cookies necessários para o funcionamento do site e cookies de análise somente com o seu consentimento.',
      decline: 'Recusar',
      accept: 'Aceitar'
    },
    legal: {
      backHome: 'Voltar para a página inicial'
    },
    footer: {
      product: 'Produto',
      features: 'Recursos',
      howItWorks: 'Como funciona',
      testimonials: 'Avaliações',
      pricing: 'Preços',
      faq: 'Perguntas frequentes',
      contacts: 'Contato',
      legal: 'Jurídico',
      privacy: 'Política de Privacidade',
      terms: 'Termos de Serviço',
      usePolicy: 'Guia do Usuário',
      disclaimer: 'Aviso Legal',
      company: 'FJCode OU',
      register: 'Número de registro 16993109',
      address: 'Viru väljak 2, 10111 Tallinn, Estônia',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. Todos os direitos reservados.'
    }
  },
  ro: {
    meta: {
      home: {
        title: 'SafeEat | Scanner alimentar cu inteligență artificială pentru alergii și intoleranțe',
        description: 'Scanează etichete, coduri de bare, meniuri și preparate și evidențiază alergenii și riscurile alimentare pe baza profilului tău: alergii, intoleranțe și afecțiuni cronice.'
      },
      privacy: {
        title: 'Politica de confidențialitate | SafeEat',
        description: 'Află cum colectează, folosește și protejează SafeEat datele tale.'
      },
      terms: {
        title: 'Termeni de utilizare | SafeEat',
        description: 'Consultă termenii care îți reglementează utilizarea SafeEat.'
      },
      manual: {
        title: 'Ghid de utilizare | SafeEat',
        description: 'Învață cum să folosești SafeEat în siguranță pentru a scana alimente.'
      },
      disclaimer: {
        title: 'Declinare de răspundere | SafeEat',
        description: 'Află mai multe despre limitele și caracterul informativ al recomandărilor oferite de SafeEat.'
      }
    },
    nav: {
      about: 'Cum funcționează',
      features: 'Funcționalități',
      testimonials: 'Testimoniale',
      pricing: 'Prețuri',
      faq: 'Întrebări frecvente',
      contacts: 'Contact',
      getStarted: 'Începe acum'
    },
    about: {
      description: 'Un scanner alimentar bazat pe inteligență artificială care citește etichete, coduri de bare, preparate și meniuri și evidențiază alergenii, ingredientele și informațiile nutriționale în funcție de profilul tău de sănătate.'
    },
    hero: {
      badge: 'Siguranța alimentară cu ajutorul AI',
      title: 'Află imediat',
      title2: 'cât de sigur este un aliment',
      titleHighlight: 'pentru tine',
      subtitle: 'Pe baza',
      description: 'Scanează alimente, coduri de bare, liste de ingrediente și meniuri ca să verifici în câteva secunde dacă ți se potrivesc.',
      typing: ['afecțiuni cronice', 'alergii', 'intoleranțe alimentare'],
      stat1: '500+',
      stat1Label: 'Afecțiuni cronice',
      stat2: '250+',
      stat2Label: 'Alergii',
      stat3: '100+',
      stat3Label: 'Intoleranțe alimentare',
      downloadButton: 'Descarcă din App Store'
    },
    howItWorks: {
      badge: 'Cum funcționează',
      mainTitle: 'Cum funcționează SafeEat',
      subtitle: 'Procesul de scanare este simplu și rapid – în câțiva pași primești un rezultat personalizat.',
      setupBadge: 'Configurare unică',
      setupTitle: 'Creează-ți profilul de sănătate',
      setupDesc: 'La prima pornire, adaugă informații despre starea ta de sănătate pentru ca SafeEat să îți poată personaliza analizele. Faci asta o singură dată, iar apoi fiecare analiză ține cont de profilul tău.',
      cards: {
        diseases: {
          title: 'Afecțiuni cronice',
          desc: 'Adaugă afecțiunile tale cronice din baza noastră cu peste 500 de diagnostice. De la diabet la boli cardiovasculare – le luăm în calcul în analiză.',
          statLabel: 'afecțiuni'
        },
        allergies: {
          title: 'Alergii',
          desc: 'Selectează alergiile tale din peste 250 de opțiuni. De la nuci și fructe de mare până la alergeni mai rari – pentru mai multă liniște sufletească.',
          statLabel: 'alergii'
        },
        intolerances: {
          title: 'Intoleranțe alimentare',
          desc: 'Notează intoleranțele tale dintr-o listă de peste 100 de variante: lactoză, gluten, fructoză și multe altele – ca să poți mânca mai confortabil.',
          statLabel: 'intoleranțe'
        }
      },
      connectorText: 'Apoi, la fiecare scanare:',
      stepLabel: 'Pas',
      step1Title: 'Scanează alimentul',
      step1Desc: 'Scanează orice fel de aliment: un produs ambalat, un preparat gătit, tabelul de valori nutriționale, un cod de bare sau chiar meniul dintr-un restaurant. Doar îndreaptă camera, iar SafeEat se ocupă de restul.',
      step2Title: 'Analiză cu AI',
      step2Desc: 'Aplicația analizează imaginea și o compară cu profilul tău (afecțiuni, alergii, intoleranțe) pentru a evalua dacă produsul este potrivit.',
      step3Title: 'Primește raportul',
      step3Desc: 'SafeEat îți oferă un raport detaliat care îți arată cât de bine se potrivește alimentul scanat cu nevoile tale de sănătate și de dietă.',
      step4Title: 'Ia o decizie informată',
      step4Desc: 'Pe baza acestor informații personalizate alegi alimentele care ți se potrivesc cel mai bine. Mănâncă cu încredere, știind ce este bine pentru corpul tău.',
      processTitle: 'Vezi cum funcționează',
      processSubtitle: 'Urmărește cum SafeEat îți analizează mâncarea în timp real'
    },
    features: {
      title: 'Funcționalități puternice',
      subtitle: 'Tot ce ai nevoie pentru alegeri alimentare mai sigure',
      items: [
        {
          title: 'Scanează orice produs',
          subtitle: 'de la gustări zilnice la fructe exotice',
          description: 'Îndreaptă camera spre orice aliment, ambalat sau vrac – de la produse obișnuite din supermarket până la fructe tropicale rare – și primești instant o verificare de siguranță. SafeEat recunoaște produsul și îi analizează ingredientele pentru a te ajuta să eviți substanțele riscante sau nedorite.',
          highlights: ['Produse ambalate și vrac', 'Verificare de siguranță instant', 'Analiză a ingredientelor'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Scanează etichete și coduri de bare',
          subtitle: 'înțelege etichetele complicate în câteva secunde',
          description: 'Nu mai este nevoie să te chinui cu literele foarte mici. SafeEat scanează în câteva secunde tabelul de valori nutriționale, lista de ingrediente și codul de bare, evidențiind imediat alergenii, aditivii sau substanțele potențial nocive, astfel încât să știi exact ce mănânci.',
          highlights: ['Scanarea tabelelor nutriționale', 'Detectarea alergenilor', 'Alerte pentru substanțe cu risc'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Scanează preparate – recunoaște ingredientele',
          subtitle: 'fă pur și simplu o poză farfuriei tale',
          description: 'Fotografiază masa ta, iar SafeEat o va descompune în ingrediente probabile. Fie că este gătită acasă, la cantină sau la restaurant, inteligența artificială poate identifica potențiali alergeni sau componente incompatibile cu dieta ta.',
          highlights: ['Recunoaștere a ingredientelor cu AI', 'Funcționează cu orice preparat', 'Verificare a compatibilității cu dieta'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Scanează meniuri de restaurant',
          subtitle: 'găsește opțiuni sigure înainte de a comanda',
          description: 'Îndreaptă camera spre meniul dintr-o cafenea sau un restaurant, iar SafeEat analizează fiecare preparat și îți spune care opțiuni corespund nevoilor tale de sănătate și de dietă. Poți lua masa în oraș mai relaxat, fără teamă de alergeni ascunși sau ingrediente nedorite.',
          highlights: ['Analiză de meniu', 'Recomandări de preparate sigure', 'Identificarea alergenilor ascunși'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Creează-ți propria listă de alimente sigure',
          subtitle: 'construiește-ți biblioteca personală de alimente „curate”',
          description: 'Salvează alimentele care ți se potrivesc și creează-ți o bibliotecă personală de alimente „curate”, la care poți apela oricând faci cumpărături sau mănânci în oraș. Baza ta de date personală crește cu fiecare scanare și te ajută să iei decizii mai rapid.',
          highlights: ['Bază personală de alimente', 'Verificare rapidă oriunde te afli', 'Recomandări inteligente'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'De încredere pentru utilizatori',
      subtitle: 'O comunitate globală de utilizatori se bazează pe SafeEat.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, SUA',
          rating: 5,
          content: 'SafeEat mi-a schimbat complet modul în care fac cumpărături! Am boală celiacă, iar scanarea unui produs durează doar câteva secunde. Gata cu cititul anevoios al etichetelor scrise mărunt.'
        },
        {
          name: 'Marcus L.',
          location: 'Londra, Regatul Unit',
          rating: 5,
          content: 'Ca persoană cu mai multe alergii alimentare, ieșitul la restaurant era mereu o sursă de stres. Acum pur și simplu scanez meniul și știu imediat ce este sigur pentru mine. Un adevărat game changer!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japonia',
          rating: 5,
          content: 'Folosesc SafeEat în fiecare zi pentru a-mi ține sub control diabetul. Scannerul de etichete nutriționale mă ajută să urmăresc instant carbohidrații și zahărul. Îl recomand cu căldură!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germania',
          rating: 5,
          content: 'Fiul meu are o alergie severă la nuci. SafeEat îmi oferă liniște atunci când îi cumpăr gustări. Scannerul de coduri de bare este uimitor de precis.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spania',
          rating: 5,
          content: 'Am intoleranță la lactoză și călătoresc mult. SafeEat funcționează în orice limbă – în străinătate scanez pur și simplu meniul și pot mânca mult mai relaxat!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'Din cauza problemelor cardiace ale partenerului meu trebuie să fim atenți la aportul de sodiu. Analiza instantă din SafeEat ne-a simplificat mult planificarea meselor.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Funcția de scanare a preparatelor este grozavă! Am fotografiat un fel de mâncare la restaurant, iar aplicația a detectat ingrediente ascunse care conțineau gluten. M-a scutit de o reacție foarte neplăcută!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, EAU',
          rating: 5,
          content: 'Urmez o dietă halal strictă. SafeEat mă ajută să verific ingredientele produselor ambalate pe care nu le cunosc. O aplicație de neînlocuit!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Am trei copii și fiecare are alte alergii – cumpărăturile erau un coșmar. Acum scanez totul în câteva secunde. Aplicația aceasta chiar îmi salvează viața!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Suedia',
          rating: 5,
          content: 'Sufăr de boala Crohn și trebuie să evit anumiți factori declanșatori. Profilul personalizat din SafeEat evidențiază ingrediente pe care altfel le-aș fi trecut cu vederea.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazilia',
          rating: 5,
          content: 'Scannerul de meniuri este ideal pentru alergia mea la fructe de mare. Acum comand în restaurante noi cu mult mai multă încredere!'
        },
        {
          name: 'David C.',
          location: 'Paris, Franța',
          rating: 5,
          content: 'Ca bucătar cu sensibilități alimentare folosesc SafeEat pentru a verifica ingredientele înainte de a găti. Precizia recunoașterii cu ajutorul AI este impresionantă.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egipt',
          rating: 5,
          content: 'Diabetul gestațional a fost foarte greu de gestionat până când am descoperit SafeEat. Scanarea etichetelor alimentelor face monitorizarea mult mai ușoară!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Am o intoleranță rară pe care majoritatea aplicațiilor nici nu o iau în considerare. În SafeEat am putut să o adaug în profilul meu, iar acum este detectată de fiecare dată!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Țările de Jos',
          rating: 5,
          content: 'Sunt vegan/ă și alergic/ă la soia – găsirea unor alimente sigure era extrem de dificilă! SafeEat descoperă atât ingredientele de origine animală ascunse, cât și soia. Sunt încântat/ă!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, Coreea de Sud',
          rating: 5,
          content: 'Baza de date de coduri de bare este uriașă! Sunt recunoscute chiar și produsele importate. Perfectă pentru verificarea alimentelor internaționale necunoscute.'
        },
        {
          name: 'Elena P.',
          location: 'Roma, Italia',
          rating: 5,
          content: 'Mama mea, în vârstă, are mai multe probleme de sănătate. I-am configurat un profil, iar acum poate să meargă din nou singură la cumpărături. Mulțumesc, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Prețuri simple și transparente',
      subtitle: 'Alege abonamentul potrivit pentru tine',
      free: 'Gratuit',
      freeDesc: '5 scanări gratuite pentru testare',
      freeFeature1: '5 scanări cu AI',
      freeFeature2: 'Analiză de bază',
      freeFeature3: 'Istoric limitat',
      weekly: 'Săptămânal',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Ideal pentru utilizare regulată',
      weeklyFeature1: 'Scanări nelimitate',
      weeklyFeature2: 'Analiză avansată',
      weeklyFeature3: 'Istoric complet al rezultatelor',
      weeklyFeature4: 'Asistență prioritară',
      annual: 'Anual',
      annualPrice: '$29.99',
      annualDesc: 'Cea mai bună valoare – economisești 60%',
      annualBadge: 'Cel mai popular',
      annualFeature1: 'Tot ce oferă planul săptămânal',
      annualFeature2: 'Economisești 125 $ pe an',
      annualFeature3: 'Asistență premium',
      annualFeature4: 'Acces anticipat la funcții noi',
      button: 'Începe acum'
    },
    faq: {
      title: 'Întrebări frecvente',
      items: [
        {
          q: 'Ce pot scana cu SafeEat?',
          a: 'SafeEat poate analiza: liste de ingrediente și tabele de valori nutriționale (din fotografii), coduri de bare, alimente ambalate și vrac, preparate (din fotografii) și meniuri de restaurant.'
        },
        {
          q: 'Cum funcționează recunoașterea preparatelor?',
          a: 'Inteligența artificială recunoaște preparatul și estimează ingredientele probabile. Funcționează cel mai bine cu lumină bună și un cadru clar; ingredientele ascunse sau contaminarea încrucișată pot fi uneori omise.'
        },
        {
          q: 'Pot scana meniuri în restaurante?',
          a: 'Da. Aplicația marchează potențialii alergeni și conflictele cu dieta ta. Totuși, meniurile pot fi incomplete, așa că este important să întrebi personalul despre ingrediente, tipul de ulei folosit, friteuze comune, sosuri și riscul de contaminare încrucișată.'
        },
        {
          q: 'Ce tipuri de probleme de sănătate sunt avute în vedere de SafeEat?',
          a: 'Alergii alimentare, intoleranțe (cum ar fi la lactoză sau gluten) și anumite nevoi dietetice (conținut redus de sodiu, atenție la carbohidrați, evitarea unor ingrediente specifice).'
        },
        {
          q: 'Funcționează SafeEat și fără conexiune la internet?',
          a: 'În general, scanările și analiza bazată pe AI necesită conexiune la internet. Rezultatele și listele salvate pot fi disponibile offline, în funcție de aplicație și de dispozitiv.'
        },
        {
          q: 'Cât de precisă este analiza SafeEat?',
          a: 'Precizia depinde de calitatea imaginii, lizibilitatea textului, limba/formatul etichetei și de cât de complete sunt informațiile din meniu. SafeEat are rol informativ – în situații cu risc ridicat este recomandat să verifici informațiile și din alte surse.'
        },
        {
          q: 'Ce fac dacă SafeEat greșește?',
          a: 'Nu te baza pe un rezultat care pare îndoielnic; realizează o nouă fotografie mai clară; verifică eticheta sau site-ul producătorului și întreabă personalul; în caz de risc ridicat, alege varianta cea mai sigură.'
        },
        {
          q: 'Pot raporta o analiză incorectă?',
          a: 'Da. Trimite un e-mail la support@safeeat.app cu o captură de ecran, o fotografie a etichetei/meniului/preparatului, țara, marca și numele produsului, precum și o descriere a ceea ce consideri că este greșit.'
        },
        {
          q: 'De ce recunoașterea preparatelor poate fi uneori mai puțin precisă?',
          a: 'Preparatele care arată similar, ingredientele ascunse, sosurile groase sau decorul, lumina slabă sau fotografiile neclare, precum și preparatele foarte personalizate sau regionale pot reduce precizia.'
        },
        {
          q: 'Este SafeEat un dispozitiv medical?',
          a: 'Nu. SafeEat este doar un instrument informativ. Pentru sfaturi medicale trebuie să consulți întotdeauna un profesionist din domeniul sănătății.'
        },
        {
          q: 'Care este responsabilitatea SafeEat?',
          a: 'Informațiile sunt oferite „ca atare”, fără garanția că sunt complete sau lipsite de erori. Tu ești responsabil pentru deciziile tale; pot exista ingrediente neafișate sau contaminare încrucișată.'
        },
        {
          q: 'Mă pot baza doar pe SafeEat dacă am alergii severe?',
          a: 'Nu. Citește etichetele, întreabă personalul, urmează planul tău de urgență și poartă mereu medicamentele prescrise; dacă ai dubii, mai bine eviți produsul sau preparatul.'
        },
        {
          q: 'Cum îmi protejați datele de sănătate?',
          a: 'Colectăm un volum minim de date și le protejăm prin măsuri de securitate și acces limitat. Pentru întrebări legate de confidențialitate, scrie-ne la support@safeeat.app și consultă Politica de confidențialitate.'
        },
        {
          q: 'Pot folosi copiii SafeEat?',
          a: 'Da, sub supravegherea unui adult. În cazul alergiilor severe sau al dietelor complexe este importantă o atenție sporită și verificarea informațiilor în surse de încredere.'
        },
        {
          q: 'Ce ar trebui să fac în caz de reacție alergică?',
          a: 'Caută imediat ajutor medical și urmează planul tău de urgență; în caz de simptome severe sună la serviciile de urgență. Apoi informează medicul, restaurantul sau producătorul despre incident.'
        }
      ]
    },
    cta: {
      title: 'Gata să îți schimbi felul în care mănânci?',
      subtitle: 'Alătură-te miilor de oameni care folosesc SafeEat în fiecare zi',
      description: 'Descarcă aplicația acum și primești 5 scanări gratuite cu AI',
      button: 'Descarcă din App Store',
      feature1: 'Fără card de credit',
      feature2: '100% privat și sigur',
      feature3: 'Gata de utilizare în câteva secunde'
    },
    newsletter: {
      title: 'Rămâi la curent',
      subtitle: 'Primește cele mai noi informații și sfaturi pentru alegeri alimentare mai sigure',
      placeholder: 'Introdu adresa ta de e-mail',
      button: 'Abonează-mă',
      error: 'Te rugăm să introduci o adresă de e-mail validă',
      success: 'Îți mulțumim pentru abonare!'
    },
    seo: {
      title: 'Asistentul tău personal pentru siguranța alimentară',
      text: 'SafeEat este o aplicație de scanare a alimentelor bazată pe inteligență artificială, creată pentru persoanele cu alergii, intoleranțe sau restricții alimentare. Scanează instant etichete, tabele nutriționale, coduri de bare, meniuri de restaurant și preparate gata de consum pentru a verifica dacă se potrivesc profilului tău. Indiferent dacă ai boală celiacă, intoleranță la lactoză, alergie la nuci sau urmezi o dietă fără gluten, vegană sau cu conținut redus de sodiu, SafeEat te ajută să iei decizii mai sigure.',
      keywords: [
        'aplicație pentru alergii alimentare',
        'scanner de ingrediente',
        'detectare alergeni',
        'scanare etichete alimentare',
        'scanner coduri de bare',
        'scanner de meniuri',
        'verificare produse fără gluten',
        'aplicație pentru intoleranță la lactoză',
        'monitorizare dietă',
        'siguranța alimentară'
      ]
    },
    cookies: {
      bannerText: 'Folosim cookie-uri: cookie-uri necesare pentru funcționarea site-ului și cookie-uri de analiză doar cu acordul tău.',
      accept: 'Accept',
      decline: 'Respinge'
    },
    legal: {
      backHome: 'Înapoi la pagina principală'
    },
    footer: {
      product: 'Produs',
      features: 'Funcționalități',
      howItWorks: 'Cum funcționează',
      testimonials: 'Testimoniale',
      pricing: 'Prețuri',
      faq: 'Întrebări frecvente',
      contacts: 'Contact',
      legal: 'Informații legale',
      privacy: 'Politica de confidențialitate',
      terms: 'Termenii serviciului',
      disclaimer: 'Declinare de răspundere',
      usePolicy: 'Ghid de utilizare',
      company: 'FJCode OU',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      register: 'Număr de înregistrare 16993109',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. Toate drepturile rezervate.'
    }
  },
  sk: {
    meta: {
      home: {
        title: 'SafeEat | AI skener jedla pre alergie a intolerancie',
        description: 'Skenujte etikety, čiarové kódy, jedálne lístky a jedlá, aby ste zvýraznili alergény a riziká stravy na základe vášho profilu: alergie, intolerancie a chronické ochorenia.'
      },
      privacy: {
        title: 'Zásady ochrany osobných údajov | SafeEat',
        description: 'Zistite, ako SafeEat zhromažďuje, používa a chráni vaše údaje.'
      },
      terms: {
        title: 'Podmienky používania | SafeEat',
        description: 'Prečítajte si podmienky platné pre používanie SafeEat.'
      },
      manual: {
        title: 'Používateľská príručka | SafeEat',
        description: 'Naučte sa používať SafeEat na bezpečné skenovanie jedla.'
      },
      disclaimer: {
        title: 'Vyhlásenie o vylúčení zodpovednosti | SafeEat',
        description: 'Pochopte informácie, odporúčania a obmedzenia aplikácie SafeEat.'
      }
    },
    nav: {
      about: 'Ako to funguje',
      features: 'Funkcie',
      testimonials: 'Recenzie',
      pricing: 'Cenník',
      faq: 'FAQ',
      contacts: 'Kontakt',
      getStarted: 'Začať'
    },
    about: {
      description: 'AI skener jedla, ktorý číta etikety, čiarové kódy, jedlá a jedálne lístky a zvýrazňuje alergény, ingrediencie a výživové informácie podľa vášho zdravotného profilu.'
    },
    hero: {
      badge: 'Bezpečnosť jedla s AI',
      title: 'Okamžite skontrolujte',
      title2: 'bezpečnosť jedla',
      titleHighlight: 'pre vás',
      subtitle: 'Na základe',
      description: 'Skenujte potraviny, čiarové kódy, zloženie a jedálne lístky a hneď zistite, či je jedlo bezpečné pre vaše telo',
      typing: ['chronické ochorenia', 'alergie', 'potravinové intolerancie'],
      stat1: '500+',
      stat1Label: 'Chronické ochorenia',
      stat2: '250+',
      stat2Label: 'Alergie',
      stat3: '100+',
      stat3Label: 'Potravinové intolerancie',
      downloadButton: 'Stiahnuť v App Store'
    },
    howItWorks: {
      badge: 'Ako to funguje',
      mainTitle: 'Ako funguje SafeEat',
      subtitle: 'Náš proces skenovania je jednoduchý, rýchly a prináša personalizované výsledky v pár krokoch.',
      setupBadge: 'Nastavenie iba raz',
      setupTitle: 'Vytvorte si zdravotný profil',
      setupDesc: 'Počas úvodného nastavenia prispôsobíte SafeEat pridaním svojich zdravotných informácií. Robí sa to len raz a umožní vám personalizovanú analýzu jedla presne pre vás.',
      cards: {
        diseases: {
          title: 'Chronické ochorenia',
          desc: 'Pridajte svoje chronické stavy z našej databázy s viac ako 500 ochoreniami. Od diabetu po srdcové ochorenia — sme pripravení.',
          statLabel: 'stavov'
        },
        allergies: {
          title: 'Alergie',
          desc: 'Vyberte si alergie z viac ako 250 možností. Od bežných orechov a morských plodov až po zriedkavé alergény — buďte chránení.',
          statLabel: 'alergií'
        },
        intolerances: {
          title: 'Potravinové intolerancie',
          desc: 'Uveďte intolerancie z viac ako 100 možností. Laktóza, lepok, fruktóza a ďalšie — jedzte bez nepríjemností.',
          statLabel: 'intolerancií'
        }
      },
      connectorText: 'A potom — zakaždým, keď skenujete:',
      stepLabel: 'Krok',
      step1Title: 'Skenujte jedlo',
      step1Desc: 'Skenujte akékoľvek jedlo — jednotlivé produkty, hotové jedlá, výživové štítky, čiarové kódy a dokonca aj jedálne lístky. Stačí namieriť kameru a SafeEat sa postará o zvyšok.',
      step2Title: 'AI analýza',
      step2Desc: 'Aplikácia analyzuje obrázok a porovná ho s vaším profilom (ochorenia, alergie, intolerancie), aby vyhodnotila vhodnosť.',
      step3Title: 'Získajte svoj report',
      step3Desc: 'SafeEat poskytne podrobný report o analyzovanom produkte a ukáže, ako dobre zodpovedá vášmu zdravotnému profilu a diétnym potrebám.',
      step4Title: 'Rozhodnite sa',
      step4Desc: 'S personalizovanými poznatkami si vyberiete najlepšie jedlo pre seba. Jedzte s pokojom — a presne viete, čo vášmu telu vyhovuje.',
      processTitle: 'Pozrite si to v praxi',
      processSubtitle: 'Pozrite sa, ako SafeEat analyzuje vaše jedlo v reálnom čase'
    },
    features: {
      title: 'Silné funkcie',
      subtitle: 'Všetko, čo potrebujete na bezpečné stravovanie',
      items: [
        {
          title: 'Skenujte akýkoľvek produkt',
          subtitle: 'od bežných snackov po exotické ovocie',
          description: 'Namierte kameru na balené alebo nebalené jedlo — od každodenných supermarketových produktov až po zriedkavé tropické ovocie — a získajte okamžité hodnotenie bezpečnosti. SafeEat rozpozná produkt a analyzuje zloženie, aby ste sa vyhli rizikovým alebo nevhodným ingredienciám.',
          highlights: ['Balené aj nebalené potraviny', 'Okamžitá kontrola bezpečnosti', 'Analýza zloženia'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Skenujte výživové štítky a čiarové kódy',
          subtitle: 'pochopte zložité etikety rýchlo',
          description: 'Už žiadne trápenie s drobným písmom alebo nejasnými etiketami. SafeEat dokáže naskenovať výživové údaje, zoznam ingrediencií aj čiarový kód v priebehu sekúnd. Aplikácia okamžite zvýrazní alergény, prídavné látky alebo potenciálne problematické zložky, aby ste presne vedeli, čo konzumujete.',
          highlights: ['Skenovanie výživových údajov', 'Detekcia alergénov', 'Upozornenia na rizikové látky'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Skenujte jedlá — identifikujte ingrediencie',
          subtitle: 'z fotografie vášho jedla',
          description: 'Odfotografujte jedlo a SafeEat ho rozloží na pravdepodobné ingrediencie. Či už ide o domáce jedlo, food court alebo fine dining, naša AI identifikuje možné alergény alebo zložky, ktoré sa nehodia do vašej diéty.',
          highlights: ['AI rozpoznávanie ingrediencií', 'Funguje pre všetky jedlá', 'Kontrola vhodnosti pre diétu'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Skenujte jedálne lístky',
          subtitle: 'nájdite bezpečné možnosti pred objednávkou',
          description: 'Namierte kameru na menu v kaviarni alebo reštaurácii a SafeEat analyzuje jednotlivé jedlá a ukáže, ktoré sú pre vás bezpečné. Ideálne na stravovanie mimo domu bez obáv zo skrytých alergénov alebo nechcených zložiek.',
          highlights: ['Analýza menu', 'Hľadanie bezpečných jedál', 'Detekcia skrytých alergénov'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Vytvorte si osobný zoznam bezpečných potravín',
          subtitle: 'vybudujte si svoju knižnicu „clean eating"',
          description: 'Ukladajte jedlá a potraviny, ktoré sú pre vás bezpečné, budujte si vlastnú knižnicu „clean eating" a majte ju po ruke pri nákupoch aj pri jedle vonku. Vaša osobná databáza potravín rastie s každým skenom.',
          highlights: ['Osobná knižnica potravín', 'Rýchly prístup kdekoľvek', 'Inteligentné odporúčania'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Dôverujú nám tisíce',
      subtitle: 'Globálna komunita používateľov dôveruje SafeEat.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat mi úplne zmenil spôsob nakupovania! Mám celiakiu a skenovanie produktov trvá len pár sekúnd. Už nemusím v obchode čítať drobné písmo.'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: 'S viacerými potravinovými alergiami bolo jedenie vonku vždy stresujúce. Teraz stačí naskenovať menu a hneď viem, čo je bezpečné. Obrovská zmena!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'SafeEat používam každý deň na kontrolu diabetu. Skener výživových údajov mi okamžite ukáže sacharidy a cukor. Veľmi odporúčam!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: 'Moje dieťa má silnú alergiu na orechy. SafeEat mi dáva pokoj pri výbere snackov. Skenovanie čiarových kódov je naozaj presné.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: 'Mám intoleranciu na laktózu a často cestujem. SafeEat funguje vo viacerých jazykoch — naskenujem menu v zahraničí a jem bez obáv!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'Pri srdcovom ochorení môjho partnera musíme sledovať sodík. Okamžitá analýza v SafeEat veľmi uľahčila plánovanie jedál.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Skener jedál je skvelý! Odfotil(a) som jedlo v reštaurácii a aplikácia našla skryté zdroje lepku. Vyhol(a) som sa nepríjemnej reakcii!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'Dodržiavam prísnu halal diétu. SafeEat mi pomáha kontrolovať zloženie neznámych balených potravín. Nevyhnutná aplikácia!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'S tromi deťmi a rôznymi alergiami bol nákup nočná mora. Teraz všetko skenujem za pár sekúnd. Táto aplikácia je záchrana!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'Mám Crohnovu chorobu a musím sa vyhýbať spúšťačom. Osobný profil v SafeEat zachytí zložky, ktoré by som inak prehliadol(a).'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'Funkcia skenovania menu je perfektná pri mojej alergii na morské plody. Teraz si trúfam objednať aj v nových reštauráciách!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'Ako kuchár(ka) so špecifickými citlivosťami používam SafeEat na kontrolu ingrediencií pred varením. AI rozpoznávanie je pôsobivé.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: 'Riadenie tehotenskej cukrovky bolo náročné, kým som nenašiel(a) SafeEat. Skenovanie etikiet to urobilo oveľa jednoduchšie!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Mám zriedkavú intoleranciu a väčšina aplikácií ju nepodporuje. SafeEat mi ju umožnil pridať do profilu a teraz sa vždy zohľadní!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: 'Som vegán(ka) s alergiou na sóju — nájsť bezpečné jedlo bolo ťažké! SafeEat odhalí skryté živočíšne zložky AJ sóju. Super!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'Databáza čiarových kódov je obrovská! Rozpozná aj dovážané produkty. Skvelé na kontrolu neznámych zahraničných potravín.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: 'Moja staršia mama má viac zdravotných problémov. Nastavil(a) som jej profil a teraz môže opäť nakupovať samostatne. Ďakujeme, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Jednoduché a transparentné ceny',
      subtitle: 'Vyberte si plán, ktorý vám vyhovuje',
      free: 'Zadarmo',
      freeDesc: '5 bezplatných skenov na vyskúšanie',
      freeFeature1: '5 AI skenov',
      freeFeature2: 'Základná analýza',
      freeFeature3: 'Obmedzená história',
      weekly: 'Týždenne',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Ideálne pre pravidelné používanie',
      weeklyFeature1: 'Neobmedzené skenovanie',
      weeklyFeature2: 'Rozšírená analýza',
      weeklyFeature3: 'Plná história',
      weeklyFeature4: 'Prioritná podpora',
      annual: 'Ročne',
      annualPrice: '$29.99',
      annualDesc: 'Najlepšia hodnota — ušetrite 60 %',
      annualBadge: 'Najobľúbenejšie',
      annualFeature1: 'Všetko z Týždenného',
      annualFeature2: 'Ušetrite 125 $/rok',
      annualFeature3: 'Prémiová podpora',
      annualFeature4: 'Prednostný prístup k novým funkciám',
      button: 'Začať'
    },
    faq: {
      title: 'Často kladené otázky',
      items: [
        {
          q: 'Čo môžem skenovať v SafeEat?',
          a: 'SafeEat dokáže analyzovať: zoznam ingrediencií a výživové údaje (z fotografie), čiarové kódy, balené aj nebalené potraviny, jedlá (z fotografie) a jedálne lístky v reštauráciách.'
        },
        {
          q: 'Ako funguje rozpoznávanie jedál?',
          a: 'AI rozpozná jedlo a odhadne pravdepodobné ingrediencie. Najlepšie funguje pri dobrom osvetlení a vhodnom uhle; skryté zložky alebo krížová kontaminácia nemusia byť zachytené.'
        },
        {
          q: 'Môžem skenovať jedálne lístky v reštauráciách?',
          a: 'Áno. Aplikácia označí možné alergény a konflikty s vašou diétou. Menu však môže byť neúplné; vždy si u personálu overte ingrediencie, oleje, spoločné fritézy, omáčky a riziko krížovej kontaminácie.'
        },
        {
          q: 'Aké zdravotné stavy SafeEat podporuje?',
          a: 'Potravinové alergie, intolerancie (napr. laktóza, lepok) a diétne potreby (nízky sodík, kontrola sacharidov, vyhýbanie sa konkrétnym zložkám).'
        },
        {
          q: 'Funguje SafeEat aj offline?',
          a: 'Zvyčajne je na skenovanie/AI potrebný internet. Uložené výsledky a vaše zoznamy môžu byť dostupné offline v závislosti od aplikácie a zariadenia.'
        },
        {
          q: 'Aká presná je analýza SafeEat?',
          a: 'Závisí od kvality fotografie, textu na etikete, jazyka/formátu a úplnosti menu. Ide len o informatívny nástroj — pri vyššom riziku vždy overujte.'
        },
        {
          q: 'Čo ak sa SafeEat pomýli?',
          a: 'Nespoliehajte sa na sporný výsledok; skúste znova s lepšou fotografiou; overte podľa etikety/webu/personálu; pri vysokom riziku zvoľte najbezpečnejšiu možnosť.'
        },
        {
          q: 'Môžem nahlásiť nesprávny výsledok analýzy?',
          a: 'Áno: support@safeeat.app — pošlite snímky obrazovky, foto etikety/menu/jedla, krajinu/značku/produkt a čo sa zdá nesprávne.'
        },
        {
          q: 'Prečo môže byť rozpoznávanie jedál menej presné?',
          a: 'Podobne vyzerajúce jedlá, skryté ingrediencie, výrazné omáčky/ozdoby, slabé osvetlenie/rozmazanie a veľmi upravené alebo regionálne jedlá.'
        },
        {
          q: 'Je SafeEat zdravotnícka pomôcka?',
          a: 'Nie. Slúži len na informačné účely. Pre medicínske odporúčania sa poraďte so zdravotníckym odborníkom.'
        },
        {
          q: 'Aká je zodpovednosť SafeEat?',
          a: 'Informácie sú poskytované „tak ako sú"; úplnosť sa nedá zaručiť. Za svoje rozhodnutia zodpovedáte vy; môžu sa vyskytnúť neoznačené ingrediencie a krížová kontaminácia.'
        },
        {
          q: 'Mám sa pri ťažkých alergiách spoliehať iba na SafeEat?',
          a: 'Nie. Čítajte etikety, pýtajte sa personálu, riaďte sa svojím akčným plánom a noste predpísané lieky; ak si nie ste istí, položku radšej vynechajte.'
        },
        {
          q: 'Ako chránite moje zdravotné údaje?',
          a: 'Zbierame len minimum údajov a chránime ich bezpečnostnými opatreniami a obmedzeným prístupom. Kontakt na súkromie: support@safeeat.app. Pozrite si Zásady ochrany osobných údajov.'
        },
        {
          q: 'Môžem používať SafeEat pre svoje dieťa?',
          a: 'Áno, pod dohľadom dospelého. Pri ťažkých alergiách a zložitých obmedzeniach buďte obzvlášť opatrní a informácie overujte z dôveryhodných zdrojov.'
        },
        {
          q: 'Čo mám robiť, ak dostanem reakciu?',
          a: 'Okamžite vyhľadajte lekársku pomoc, riaďte sa svojím núdzovým plánom, pri vážnych príznakoch volajte 112 a následne to nahláste poskytovateľovi/reštaurácii/výrobcovi.'
        }
      ]
    },
    cta: {
      title: 'Ste pripravení zmeniť, ako sa stravujete?',
      subtitle: 'Pridajte sa k tisícom ľudí, ktorí používajú SafeEat každý deň',
      description: 'Stiahnite si teraz a získajte 5 bezplatných AI skenov',
      button: 'Stiahnuť v App Store',
      feature1: 'Bez kreditnej karty',
      feature2: '100 % súkromné a bezpečné',
      feature3: 'Okamžitý prístup'
    },
    newsletter: {
      title: 'Zostaňte v obraze',
      subtitle: 'Získajte najnovšie novinky a zdravotné tipy',
      placeholder: 'Zadajte svoj e‑mail',
      button: 'Prihlásiť sa',
      success: 'Ďakujeme za prihlásenie!',
      error: 'Zadajte platnú e‑mailovú adresu'
    },
    seo: {
      title: 'Váš osobný asistent pre bezpečnosť jedla',
      text: 'SafeEat je AI aplikácia na skenovanie jedla pre ľudí s potravinovými alergiami, intoleranciami a diétnymi obmedzeniami. Skenujte etikety, výživové informácie, čiarové kódy, jedálne lístky a hotové jedlá a okamžite overte bezpečnosť podľa svojho profilu. Či už máte celiakiu, intoleranciu laktózy, alergiu na orechy alebo dodržiavate bezlepkovú, vegánsku či nízkosodíkovú diétu, SafeEat vám pomôže vyberať bezpečne.',
      keywords: [
        'aplikácia na potravinové alergie',
        'skener ingrediencií',
        'detektor alergénov',
        'čítačka výživových štítkov',
        'skener čiarových kódov',
        'skener menu',
        'kontrola bez lepku',
        'aplikácia na intoleranciu laktózy',
        'sledovanie diéty',
        'bezpečnosť jedla'
      ]
    },
    cookies: {
      bannerText: 'Používame cookies: nevyhnutné cookies na fungovanie stránky a analytické cookies iba s vaším súhlasom.',
      decline: 'Odmietnuť',
      accept: 'Prijať'
    },
    legal: {
      backHome: 'Späť na hlavnú stránku'
    },
    footer: {
      product: 'Produkt',
      features: 'Funkcie',
      howItWorks: 'Ako to funguje',
      testimonials: 'Recenzie',
      pricing: 'Cenník',
      faq: 'FAQ',
      contacts: 'Kontakt',
      legal: 'Právne informácie',
      privacy: 'Zásady ochrany osobných údajov',
      terms: 'Podmienky služby',
      usePolicy: 'Používateľská príručka',
      disclaimer: 'Vyhlásenie o vylúčení zodpovednosti',
      company: 'FJCode OU',
      register: 'Registračné číslo 16993109',
      address: 'Viru väljak 2, 10111 Tallinn, Estónsko',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. Všetky práva vyhradené.'
    }
  },
  sv: {
    meta: {
      home: {
        title: 'SafeEat | AI-baserad matskanner för allergier och intoleranser',
        description: 'Skanna etiketter, streckkoder, menyer och rätter för att lyfta fram allergener och kostrelaterade risker utifrån din profil: allergier, intoleranser och kroniska sjukdomar.'
      },
      privacy: {
        title: 'Integritetspolicy | SafeEat',
        description: 'Läs mer om hur SafeEat samlar in, använder och skyddar dina uppgifter.'
      },
      terms: {
        title: 'Användarvillkor | SafeEat',
        description: 'Ta del av villkoren som gäller när du använder SafeEat.'
      },
      manual: {
        title: 'Användarguide | SafeEat',
        description: 'Lär dig använda SafeEat på ett säkert sätt för att skanna livsmedel.'
      },
      disclaimer: {
        title: 'Ansvarsfriskrivning | SafeEat',
        description: 'Läs om begränsningarna och den informativa karaktären i SafeEats rekommendationer.'
      }
    },
    nav: {
      about: 'Så fungerar det',
      features: 'Funktioner',
      testimonials: 'Omdömen',
      pricing: 'Priser',
      faq: 'FAQ',
      contacts: 'Kontakt',
      getStarted: 'Kom igång'
    },
    about: {
      description: 'En AI-baserad matskanner som läser etiketter, streckkoder, rätter och restaurangmenyer och lyfter fram allergener, ingredienser och näringsinformation utifrån din hälsoprofil.'
    },
    hero: {
      badge: 'Matsäkerhet med AI',
      title: 'Se direkt',
      title2: 'om en produkt är säker',
      titleHighlight: 'för dig',
      subtitle: 'Baserat på',
      description: 'Skanna livsmedel, streckkoder, ingredienslistor och menyer för att på några sekunder se om de passar din kropp.',
      typing: ['kroniska sjukdomar', 'allergier', 'födoämnesintoleranser'],
      stat1: '500+',
      stat1Label: 'Kroniska sjukdomar',
      stat2: '250+',
      stat2Label: 'Allergier',
      stat3: '100+',
      stat3Label: 'Födoämnesintoleranser',
      downloadButton: 'Hämta i App Store'
    },
    howItWorks: {
      badge: 'Så fungerar det',
      mainTitle: 'Hur SafeEat fungerar',
      subtitle: 'Skanningsprocessen är enkel och snabb – du får personligt anpassade resultat i några få steg.',
      setupBadge: 'Engångsinställning',
      setupTitle: 'Skapa din hälsoprofil',
      setupDesc: 'Lägg till din hälsodata under första uppstarten för att anpassa SafeEat efter dig. Du gör det bara en gång – därefter är varje analys skräddarsydd efter din profil.',
      cards: {
        diseases: {
          title: 'Kroniska sjukdomar',
          desc: 'Lägg till dina kroniska sjukdomar från vår databas med över 500 tillstånd. Från diabetes till hjärtproblem – vi tar hänsyn till dem i analysen.',
          statLabel: 'tillstånd'
        },
        allergies: {
          title: 'Allergier',
          desc: 'Välj dina allergier bland mer än 250 alternativ. Från vanliga nöt- och skaldjursallergier till mer ovanliga allergener – för ett lugnare vardagsliv.',
          statLabel: 'allergier'
        },
        intolerances: {
          title: 'Födoämnesintoleranser',
          desc: 'Ange dina intoleranser bland över 100 alternativ: laktos, gluten, fruktos och mycket mer – så att du kan äta mer bekvämt.',
          statLabel: 'intoleranser'
        }
      },
      connectorText: 'Sedan, vid varje skanning:',
      stepLabel: 'Steg',
      step1Title: 'Skanna livsmedlet',
      step1Desc: 'Skanna vilket livsmedel som helst – enstaka produkt, färdig rätt, näringsdeklaration, streckkod eller till och med en restaurangmeny. Rikta bara kameran, så sköter SafeEat resten.',
      step2Title: 'AI-analys',
      step2Desc: 'Appen analyserar bilden och jämför med din profil (sjukdomar, allergier, intoleranser) för att bedöma om produkten är lämplig.',
      step3Title: 'Få en rapport',
      step3Desc: 'SafeEat ger dig en detaljerad rapport som visar hur väl det skannade livsmedlet stämmer överens med dina hälsobehov och din kost.',
      step4Title: 'Gör ett medvetet val',
      step4Desc: 'Med dessa personliga insikter kan du välja den mat som passar dig bäst. Ät med större trygghet när du vet vad som är bra för din kropp.',
      processTitle: 'Se det i praktiken',
      processSubtitle: 'Se hur SafeEat analyserar din mat i realtid'
    },
    features: {
      title: 'Kraftfulla funktioner',
      subtitle: 'Allt du behöver för säkrare matval',
      items: [
        {
          title: 'Skanna vilken produkt som helst',
          subtitle: 'från vardagssnacks till exotisk frukt',
          description: 'Rikta kameran mot vilket livsmedel som helst – förpackat eller i lösvikt – från vanliga vardagsvaror till sällsynta tropiska frukter, och få en omedelbar säkerhetskontroll. SafeEat känner igen produkten och analyserar ingredienserna för att hjälpa dig undvika riskfyllda eller oönskade ämnen.',
          highlights: ['Förpackade varor och lösvikt', 'Omedelbar säkerhetskontroll', 'Ingrediensanalys'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Skanna etiketter och streckkoder',
          subtitle: 'förstå krångliga etiketter på några sekunder',
          description: 'Slipp anstränga ögonen på pytteliten text. SafeEat skannar näringsdeklaration, ingredienslista och streckkod på bara några sekunder och markerar direkt allergener, tillsatser eller potentiellt skadliga ämnen, så att du vet exakt vad du äter.',
          highlights: ['Skanning av näringsdeklaration', 'Upptäckt av allergener', 'Varningar för riskämnen'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Skanna rätter – se ingredienserna',
          subtitle: 'ta bara ett foto av din tallrik',
          description: 'Fotografera din måltid så bryter SafeEat ner den i sannolika ingredienser. Oavsett om det är hemlagat, från skolmatsalen eller på restaurang kan AI:n upptäcka potentiella allergener eller komponenter som inte passar din kost.',
          highlights: ['AI-baserad ingrediensigenkänning', 'Fungerar med alla typer av rätter', 'Kontroll mot din kost'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Skanna restaurangmenyer',
          subtitle: 'hitta säkra alternativ innan du beställer',
          description: 'Rikta kameran mot menyn på ett café eller en restaurang så analyserar SafeEat rätterna och visar vilka som passar dina hälsobehov och din kost. Ät ute med mindre stress – utan rädsla för dolda allergener eller oönskade ingredienser.',
          highlights: ['Menyanalys', 'Rekommendationer på säkra rätter', 'Identifiering av dolda allergener'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Bygg din egen lista med säkra livsmedel',
          subtitle: 'skapa ditt eget bibliotek med "rena" produkter',
          description: 'Spara livsmedel som fungerar för dig och bygg upp ett personligt bibliotek med "rena" produkter som du kan kika i när du handlar eller äter ute. Din personliga databas växer med varje skanning och hjälper dig att fatta snabbare beslut.',
          highlights: ['Personlig livsmedelsdatabas', 'Snabb kontroll när som helst', 'Smarta rekommendationer'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Förtroende hos användare världen över',
      subtitle: 'En global community av användare litar på SafeEat.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat har helt förändrat hur jag handlar mat! Jag har celiaki och det tar bara några sekunder att skanna en produkt. Slut på kampen med oläsliga etiketter i butiken.'
        },
        {
          name: 'Marcus L.',
          location: 'London, Storbritannien',
          rating: 5,
          content: 'Som person med flera matallergier har det alltid varit stressigt att äta ute. Nu skannar jag bara menyn och ser direkt vad som är säkert. En riktig game changer!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'Jag använder SafeEat varje dag för att hantera min diabetes. Skannern för näringsdeklarationer hjälper mig att direkt hålla koll på kolhydrater och socker. Rekommenderas varmt!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Tyskland',
          rating: 5,
          content: 'Min son har svår nötallergi. SafeEat ger mig trygghet när jag köper snacks till honom. Streckkodsskannern är imponerande exakt.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spanien',
          rating: 5,
          content: 'Jag är laktosintolerant och reser mycket. SafeEat funkar oavsett språk – utomlands skannar jag bara menyn och kan äta mycket tryggare!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, Indien',
          rating: 5,
          content: 'På grund av min partners hjärtproblem måste vi hålla noga koll på natriumintaget. SafeEats snabba analys gör måltidsplaneringen mycket enklare.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australien',
          rating: 5,
          content: 'Funktionen för att skanna rätter är fantastisk! Jag tog en bild på en restaurangrätt och appen hittade dolda ingredienser med gluten. Den besparade mig en rejäl reaktion!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, Förenade Arabemiraten',
          rating: 5,
          content: 'Jag följer en strikt halal-kost. SafeEat hjälper mig att kontrollera ingredienserna i förpackade produkter jag inte känner till. En oumbärlig app!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Kanada',
          rating: 5,
          content: 'Med tre barn som alla har olika allergier var matinköp ett rent kaos. Nu skannar jag allt på några sekunder. Den här appen är verkligen livräddande!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sverige',
          rating: 5,
          content: 'Jag har Crohns sjukdom och måste undvika vissa triggers. Den personliga profilen i SafeEat fångar upp ingredienser som jag annars lätt skulle missa.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brasilien',
          rating: 5,
          content: 'Menyskannern är perfekt för min skaldjursallergi. Jag vågar beställa på nya restauranger med mycket större trygghet nu!'
        },
        {
          name: 'David C.',
          location: 'Paris, Frankrike',
          rating: 5,
          content: 'Som kock med matöverkänslighet använder jag SafeEat för att dubbelkolla ingredienser innan jag lagar mat. Noggrannheten i AI-igenkänningen är imponerande.'
        },
        {
          name: 'Fatima A.',
          location: 'Kairo, Egypten',
          rating: 5,
          content: 'Graviditetsdiabetes var väldigt svårt att hantera innan jag hittade SafeEat. Att skanna matetiketter gör uppföljningen otroligt mycket enklare!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Jag har en ovanlig intolerans som de flesta appar inte hanterar. I SafeEat kunde jag lägga till den i min profil och nu upptäcks den varje gång!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Nederländerna',
          rating: 5,
          content: 'Jag är vegan och allergisk mot soja – att hitta säker mat var nästan omöjligt! SafeEat hittar både dolda animaliska ingredienser och soja. Jag älskar den!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, Sydkorea',
          rating: 5,
          content: 'Databasen med streckkoder är enorm! Till och med importerade produkter känns igen. Perfekt när jag vill kolla okända internationella livsmedel.'
        },
        {
          name: 'Elena P.',
          location: 'Rom, Italien',
          rating: 5,
          content: 'Min äldre mamma har flera hälsoproblem. Jag skapade en profil åt henne och nu kan hon handla själv igen. Tack, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Enkla och tydliga priser',
      subtitle: 'Välj det abonnemang som passar dig',
      free: 'Gratis',
      freeDesc: '5 gratis skanningar för att prova',
      freeFeature1: '5 AI-skanningar',
      freeFeature2: 'Grundläggande analys',
      freeFeature3: 'Begränsad historik',
      weekly: 'Veckovis',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Perfekt för regelbunden användning',
      weeklyFeature1: 'Obegränsade skanningar',
      weeklyFeature2: 'Avancerad analys',
      weeklyFeature3: 'Fullständig historik',
      weeklyFeature4: 'Prioriterad support',
      annual: 'Årsvis',
      annualPrice: '$29.99',
      annualDesc: 'Bäst värde – spara 60 %',
      annualBadge: 'Mest populär',
      annualFeature1: 'Allt som ingår i veckoplanen',
      annualFeature2: 'Spara 125 $ per år',
      annualFeature3: 'Premiumsupport',
      annualFeature4: 'Tidigt tillgång till nya funktioner',
      button: 'Kom igång'
    },
    faq: {
      title: 'Vanliga frågor',
      items: [
        {
          q: 'Vad kan jag skanna med SafeEat?',
          a: 'SafeEat kan analysera: ingredienslistor och näringsdeklarationer (från foto), streckkoder, förpackade och oförpackade livsmedel, rätter (från foto) samt restaurangmenyer.'
        },
        {
          q: 'Hur fungerar igenkänning av rätter?',
          a: 'AI:n känner igen rätten och uppskattar sannolika ingredienser. Den fungerar bäst med bra belysning och en tydlig vinkel; dolda ingredienser eller korskontaminering kan ibland missas.'
        },
        {
          q: 'Kan jag skanna menyer på restauranger?',
          a: 'Ja. Appen markerar potentiella allergener och konflikter med din kost. Menyer kan dock vara ofullständiga, så fråga alltid personalen om ingredienser, oljor, delade fritöser, såser och risk för korskontaminering.'
        },
        {
          q: 'Vilka hälsotillstånd tar SafeEat hänsyn till?',
          a: 'Matallergier, intoleranser (till exempel laktos eller gluten) och vissa kostbehov (lågt natrium, fokus på kolhydrater, undvikande av specifika ingredienser).'
        },
        {
          q: 'Fungerar SafeEat utan internetuppkoppling?',
          a: 'Normalt kräver skanningar och AI-analys internetuppkoppling. Sparade resultat och listor kan vara tillgängliga offline beroende på appen och enheten.'
        },
        {
          q: 'Hur exakt är analysen i SafeEat?',
          a: 'Noggrannheten beror på bildkvalitet, läsbarhet, språk/format och hur komplett informationen i menyn är. SafeEat är ett informationsverktyg – vid hög risk bör du alltid dubbelkolla uppgifterna.'
        },
        {
          q: 'Vad gör jag om SafeEat har fel?',
          a: 'Lita inte på ett resultat som känns osäkert; ta en ny, tydligare bild; kontrollera etiketten eller tillverkarens webbplats, eller fråga personalen; vid hög risk är det bättre att välja det säkrare alternativet.'
        },
        {
          q: 'Kan jag rapportera en felaktig analys?',
          a: 'Ja. Skicka ett e‑postmeddelande till support@safeeat.app med en skärmdump, ett foto av etiketten/menyn/rätten, land, varumärke och produktnamn samt en beskrivning av vad du anser är fel.'
        },
        {
          q: 'Varför kan igenkänningen av rätter ibland vara mindre exakt?',
          a: 'Rätter som ser likadana ut, dolda ingredienser, tjocka såser eller garnering, svagt ljus eller suddiga foton samt mycket specialanpassade eller lokala rätter kan försämra noggrannheten.'
        },
        {
          q: 'Är SafeEat en medicinteknisk produkt?',
          a: 'Nej. Det är endast ett informationsverktyg. För medicinska råd ska du alltid kontakta hälso- och sjukvårdspersonal.'
        },
        {
          q: 'Vilket ansvar har SafeEat?',
          a: 'Informationen tillhandahålls "i befintligt skick" utan garanti för fullständighet eller felfrihet. Du är själv ansvarig för dina val; odeklarerade ingredienser och korskontaminering kan förekomma.'
        },
        {
          q: 'Kan jag lita enbart på SafeEat om jag har svåra allergier?',
          a: 'Nej. Läs etiketter, fråga personalen, följ din egen handlingsplan vid nödsituationer och ha alltid med dig ordinerade läkemedel. Om du är osäker bör du avstå från produkten eller rätten.'
        },
        {
          q: 'Hur skyddar ni mina hälsodata?',
          a: 'Vi samlar in minsta möjliga mängd data och skyddar dem med säkerhetsåtgärder och begränsad åtkomst. För frågor om integritet kan du kontakta support@safeeat.app och läsa vår integritetspolicy.'
        },
        {
          q: 'Kan barn använda SafeEat?',
          a: 'Ja, under uppsikt av en vuxen. Vid svåra allergier eller komplexa dieter är det extra viktigt att vara försiktig och verifiera informationen i tillförlitliga källor.'
        },
        {
          q: 'Vad ska jag göra om jag får en reaktion?',
          a: 'Sök omedelbart medicinsk hjälp och följ din handlingsplan; vid allvarliga symtom ska du kontakta akutsjukvården. Informera sedan din läkare, restaurangen eller tillverkaren om händelsen.'
        }
      ]
    },
    cta: {
      title: 'Redo att förändra hur du äter?',
      subtitle: 'Gå med i de tusentals människor som använder SafeEat varje dag',
      description: 'Ladda ner appen nu och få 5 gratis AI-skanningar',
      button: 'Hämta i App Store',
      feature1: 'Inget kreditkort krävs',
      feature2: '100 % privat och säkert',
      feature3: 'Klart att använda på några sekunder'
    },
    newsletter: {
      title: 'Håll dig uppdaterad',
      subtitle: 'Få de senaste nyheterna och tipsen för säkrare matval',
      placeholder: 'Ange din e-postadress',
      button: 'Prenumerera',
      error: 'Ange en giltig e-postadress',
      success: 'Tack för din prenumeration!'
    },
    seo: {
      title: 'Din personliga assistent för matsäkerhet',
      text: 'SafeEat är en AI-baserad matskanner för personer med allergier, intoleranser eller kostbegränsningar. Skanna omedelbart etiketter, näringsdeklarationer, streckkoder, restaurangmenyer och färdiga rätter för att kontrollera om de passar din profil. Oavsett om du har celiaki, laktosintolerans, nötallergi eller följer en glutenfri, vegansk eller natriumreducerad kost hjälper SafeEat dig att fatta säkrare beslut.',
      keywords: [
        'app för matallergi',
        'ingredienstskanner',
        'allergendetektering',
        'skanna näringsdeklarationer',
        'streckkodsläsare',
        'menyskanner',
        'kontroll av glutenfritt',
        'app för laktosintolerans',
        'kostuppföljning',
        'matsäkerhet'
      ]
    },
    cookies: {
      bannerText: 'Vi använder cookies: nödvändiga cookies för att webbplatsen ska fungera samt analyscookies endast med ditt samtycke.',
      accept: 'Godkänn',
      decline: 'Avvisa'
    },
    legal: {
      backHome: 'Tillbaka till startsidan'
    },
    footer: {
      product: 'Produkt',
      features: 'Funktioner',
      howItWorks: 'Så fungerar det',
      testimonials: 'Omdömen',
      pricing: 'Priser',
      faq: 'FAQ',
      contacts: 'Kontakt',
      legal: 'Juridisk information',
      privacy: 'Integritetspolicy',
      terms: 'Användarvillkor',
      disclaimer: 'Ansvarsfriskrivning',
      usePolicy: 'Användarguide',
      company: 'FJCode OU',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      register: 'Organisationsnummer 16993109',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. Alla rättigheter förbehålls.'
    }
  },
  th: {
    meta: {
      home: {
        title: 'SafeEat | สแกนเนอร์อาหารด้วย AI สำหรับผู้มีภูมิแพ้และภาวะไม่ทนต่ออาหาร',
        description: 'สแกนฉลาก บาร์โค้ด เมนู และจานอาหาร เพื่อไฮไลต์สารก่อภูมิแพ้และความเสี่ยงด้านโภชนาการตามโปรไฟล์ส่วนตัวของคุณ: ภูมิแพ้ ภาวะไม่ทนต่ออาหาร และโรคเรื้อรัง'
      },
      privacy: {
        title: 'นโยบายความเป็นส่วนตัว | SafeEat',
        description: 'ดูว่า SafeEat เก็บ ใช้ และปกป้องข้อมูลของคุณอย่างไร'
      },
      terms: {
        title: 'ข้อตกลงการใช้งาน | SafeEat',
        description: 'อ่านเงื่อนไขที่ใช้กับการใช้งาน SafeEat ของคุณ'
      },
      manual: {
        title: 'คู่มือการใช้งาน | SafeEat',
        description: 'เรียนรู้วิธีใช้ SafeEat เพื่อสแกนอาหารอย่างปลอดภัย'
      },
      disclaimer: {
        title: 'ข้อจำกัดความรับผิด | SafeEat',
        description: 'ทำความเข้าใจกับขอบเขตและลักษณะเชิงข้อมูลของคำแนะนำใน SafeEat'
      }
    },
    nav: {
      about: 'การทำงาน',
      features: 'ฟีเจอร์',
      testimonials: 'เสียงจากผู้ใช้',
      pricing: 'ราคา',
      faq: 'คำถามที่พบบ่อย',
      contacts: 'ติดต่อเรา',
      getStarted: 'เริ่มต้นใช้งาน'
    },
    about: {
      description: 'แอปสแกนอาหารด้วยปัญญาประดิษฐ์ที่อ่านฉลาก บาร์โค้ด จานอาหาร และเมนูร้านอาหาร แล้วไฮไลต์สารก่อภูมิแพ้ ส่วนผสม และข้อมูลโภชนาการให้ตรงกับโปรไฟล์สุขภาพของคุณ'
    },
    hero: {
      badge: 'ความปลอดภัยของอาหารด้วย AI',
      title: 'รู้ได้ทันที',
      title2: 'ว่าอาหารจานนี้ปลอดภัยหรือไม่',
      titleHighlight: 'สำหรับคุณ',
      subtitle: 'อิงตาม',
      description: 'สแกนอาหาร บาร์โค้ด รายการส่วนผสม และเมนู เพื่อเช็กในไม่กี่วินาทีว่าเหมาะกับร่างกายคุณหรือไม่',
      typing: ['โรคเรื้อรัง', 'ภูมิแพ้', 'ภาวะไม่ทนต่ออาหาร'],
      stat1: '500+',
      stat1Label: 'โรคเรื้อรัง',
      stat2: '250+',
      stat2Label: 'ภูมิแพ้',
      stat3: '100+',
      stat3Label: 'ภาวะไม่ทนต่ออาหาร',
      downloadButton: 'ดาวน์โหลดบน App Store'
    },
    howItWorks: {
      badge: 'การทำงาน',
      mainTitle: 'SafeEat ทำงานอย่างไร',
      subtitle: 'ขั้นตอนการสแกนใช้งานง่ายและรวดเร็ว – รับผลลัพธ์ที่ปรับให้เหมาะกับคุณในไม่กี่ขั้นตอน',
      setupBadge: 'ตั้งค่าเพียงครั้งเดียว',
      setupTitle: 'สร้างโปรไฟล์สุขภาพส่วนตัว',
      setupDesc: 'ระหว่างการใช้งานครั้งแรก ให้คุณเพิ่มข้อมูลสุขภาพของตัวเอง เพื่อให้ SafeEat วิเคราะห์ได้ตรงกับคุณ ตั้งค่าเพียงครั้งเดียว จากนั้นการวิเคราะห์ทุกครั้งจะอ้างอิงจากโปรไฟล์นี้',
      cards: {
        diseases: {
          title: 'โรคเรื้อรัง',
          desc: 'เพิ่มโรคเรื้อรังของคุณจากฐานข้อมูลมากกว่า 500 ภาวะ ตั้งแต่เบาหวานไปจนถึงโรคหัวใจ เรานำมาคำนวณในผลประเมินให้ทั้งหมด',
          statLabel: 'ภาวะ'
        },
        allergies: {
          title: 'ภูมิแพ้',
          desc: 'เลือกอาการภูมิแพ้ของคุณจากตัวเลือกกว่า 250 รายการ ตั้งแต่ถั่ว อาหารทะเล ไปจนถึงสารก่อภูมิแพ้ที่พบได้น้อย เพื่อความสบายใจที่มากขึ้น',
          statLabel: 'ภูมิแพ้'
        },
        intolerances: {
          title: 'ภาวะไม่ทนต่ออาหาร',
          desc: 'ระบุภาวะไม่ทนต่ออาหารของคุณจากตัวเลือกกว่า 100 รายการ เช่น ไม่ทนต่อแลคโตส กลูเตน ฟรุกโตส และอื่นๆ เพื่อให้กินได้สบายขึ้น',
          statLabel: 'ภาวะไม่ทน'
        }
      },
      connectorText: 'หลังจากนั้น ในทุกครั้งที่สแกน:',
      stepLabel: 'ขั้นตอน',
      step1Title: 'สแกนอาหาร',
      step1Desc: 'สแกนอาหารอะไรก็ได้ ทั้งผลิตภัณฑ์เดี่ยว อาหารพร้อมรับประทาน ตารางโภชนาการ บาร์โค้ด หรือแม้แต่เมนูในร้านอาหาร แค่เล็งกล้อง แอปจะจัดการส่วนที่เหลือให้',
      step2Title: 'วิเคราะห์ด้วย AI',
      step2Desc: 'แอปจะวิเคราะห์ภาพและเทียบกับโปรไฟล์ของคุณ (โรคเรื้อรัง ภูมิแพ้ ภาวะไม่ทนต่ออาหาร) เพื่อประเมินว่าอาหารนั้นเหมาะสมหรือไม่',
      step3Title: 'รับรายงาน',
      step3Desc: 'SafeEat แสดงรายงานละเอียดว่าอาหารที่สแกนสอดคล้องกับความต้องการด้านสุขภาพและโภชนาการของคุณมากน้อยเพียงใด',
      step4Title: 'ตัดสินใจอย่างมั่นใจ',
      step4Desc: 'จากข้อมูลเชิงลึกแบบเฉพาะบุคคล คุณสามารถเลือกอาหารที่เหมาะกับตัวเองที่สุด กินได้อย่างมั่นใจเมื่อรู้ว่าอะไรดีต่อร่างกาย',
      processTitle: 'ดูการทำงานจริง',
      processSubtitle: 'ชมการวิเคราะห์อาหารแบบเรียลไทม์ด้วย SafeEat'
    },
    features: {
      title: 'ฟีเจอร์ทรงพลัง',
      subtitle: 'ทุกอย่างที่คุณต้องการเพื่อเลือกกินได้ปลอดภัยขึ้น',
      items: [
        {
          title: 'สแกนผลิตภัณฑ์อาหารทุกชนิด',
          subtitle: 'ตั้งแต่ขนมกินเล่นประจำวันไปถึงผลไม้แปลกใหม่',
          description: 'เล็งกล้องไปที่อาหารใดๆ ทั้งแบบบรรจุหีบห่อหรือขายเป็นชิ้น/กิโล จากของใช้ประจำวันไปจนถึงผลไม้เขตร้อนหายาก แล้วรับการตรวจสอบความปลอดภัยทันที SafeEat จะระบุผลิตภัณฑ์และวิเคราะห์ส่วนผสมเพื่อช่วยให้คุณหลีกเลี่ยงสารที่เสี่ยงหรือไม่ต้องการ',
          highlights: ['อาหารแบบบรรจุภัณฑ์และขายปลีก', 'ตรวจสอบความปลอดภัยทันที', 'วิเคราะห์ส่วนผสม'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'สแกนฉลากโภชนาการและบาร์โค้ด',
          subtitle: 'เข้าใจฉลากซับซ้อนได้ในไม่กี่วินาที',
          description: 'ไม่ต้องเพ่งตัวหนังสือเล็กจิ๋วอีกต่อไป SafeEat สแกนตารางโภชนาการ รายการส่วนผสม และบาร์โค้ดในไม่กี่วินาที พร้อมไฮไลต์สารก่อภูมิแพ้ วัตถุเจือปน และสารที่อาจเป็นอันตรายทันที เพื่อให้คุณรู้แน่ชัดว่ากำลังกินอะไร',
          highlights: ['สแกนตารางโภชนาการ', 'ตรวจจับสารก่อภูมิแพ้', 'แจ้งเตือนสารที่มีความเสี่ยง'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'สแกนจานอาหาร – แยกส่วนผสม',
          subtitle: 'แค่ถ่ายรูปจานของคุณ',
          description: 'ถ่ายรูปมื้ออาหารของคุณ แล้ว SafeEat จะแยกออกเป็นส่วนผสมที่เป็นไปได้ ไม่ว่าจะเป็นอาหารทำเอง อาหารโรงอาหาร หรือร้านอาหาร AI สามารถช่วยชี้ให้เห็นสารก่อภูมิแพ้หรือส่วนผสมที่ไม่สอดคล้องกับรูปแบบการกินของคุณ',
          highlights: ['รู้ส่วนผสมด้วย AI', 'ใช้ได้กับทุกประเภทของจานอาหาร', 'ตรวจสอบความเข้ากันได้กับรูปแบบการกินของคุณ'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'สแกนเมนูร้านอาหาร',
          subtitle: 'หาเมนูที่ปลอดภัยก่อนสั่ง',
          description: 'เล็งกล้องไปที่เมนูในคาเฟ่หรือร้านอาหาร แล้ว SafeEat จะวิเคราะห์แต่ละจานและแสดงว่าเมนูใดเหมาะกับข้อจำกัดสุขภาพและโภชนาการของคุณ กินข้าวนอกบ้านได้สบายใจขึ้น โดยไม่ต้องกังวลกับสารก่อภูมิแพ้หรือส่วนผสมที่ซ่อนอยู่',
          highlights: ['วิเคราะห์เมนูร้านอาหาร', 'แนะนำเมนูที่ปลอดภัย', 'ค้นหาสารก่อภูมิแพ้ที่อาจซ่อนอยู่'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'สร้างลิสต์อาหารปลอดภัยของคุณเอง',
          subtitle: 'สร้างไลบรารีอาหารสะอาดแบบส่วนตัว',
          description: 'บันทึกอาหารที่เหมาะกับคุณและสร้างไลบรารีอาหารที่คุณไว้ใจได้ เพื่อเปิดดูขณะซื้อของหรือทานนอกบ้าน ฐานข้อมูลส่วนตัวของคุณจะเติบโตขึ้นทุกครั้งที่สแกน ช่วยให้ตัดสินใจได้เร็วและง่ายขึ้น',
          highlights: ['ฐานข้อมูลอาหารส่วนตัว', 'เช็กได้รวดเร็วทุกที่ทุกเวลา', 'คำแนะนำแบบฉลาด'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'ผู้ใช้ทั่วโลกให้ความไว้วางใจ',
      subtitle: 'ชุมชนผู้ใช้จากหลายประเทศเลือกใช้ SafeEat',
      items: [
        {
          name: 'Sarah M.',
          location: 'นิวยอร์ก สหรัฐอเมริกา',
          rating: 5,
          content: 'SafeEat เปลี่ยนวิธีการซื้อของเข้าบ้านของฉันไปเลย ฉันเป็นโรคเซลิแอก และใช้เวลาไม่กี่วินาทีในการสแกนผลิตภัณฑ์หนึ่งชิ้น ไม่ต้องมานั่งอ่านตัวหนังสือเล็กๆ บนฉลากอีกต่อไป'
        },
        {
          name: 'Marcus L.',
          location: 'ลอนดอน สหราชอาณาจักร',
          rating: 5,
          content: 'ในฐานะคนที่มีภูมิแพ้อาหารหลายชนิด การออกไปทานข้าวข้างนอกเคยเป็นเรื่องเครียดมาก ตอนนี้ฉันแค่สแกนเมนูแล้วรู้ทันทีว่าอะไรปลอดภัยสำหรับฉัน ถือว่าเปลี่ยนเกมจริงๆ'
        },
        {
          name: 'Yuki T.',
          location: 'โตเกียว ญี่ปุ่น',
          rating: 5,
          content: 'ฉันใช้ SafeEat ทุกวันเพื่อช่วยควบคุมเบาหวาน ฟีเจอร์สแกนฉลากโภชนาการช่วยให้ฉันดูปริมาณคาร์บและน้ำตาลได้ทันที แนะนำอย่างยิ่ง'
        },
        {
          name: 'Anna K.',
          location: 'เบอร์ลิน เยอรมนี',
          rating: 5,
          content: 'ลูกชายของฉันแพ้ถั่วอย่างรุนแรง SafeEat ทำให้ฉันสบายใจเวลาซื้อขนมให้เขา สแกนบาร์โค้ดได้แม่นยำมากๆ'
        },
        {
          name: 'Carlos R.',
          location: 'มาดริด สเปน',
          rating: 5,
          content: 'ฉันไม่ทนต่อแลคโตสและเดินทางบ่อย SafeEat ใช้ได้กับทุกภาษา เวลาอยู่ต่างประเทศฉันแค่สแกนเมนูแล้วก็ทานอาหารได้อย่างมั่นใจขึ้นเยอะ'
        },
        {
          name: 'Priya S.',
          location: 'มุมไบ อินเดีย',
          rating: 5,
          content: 'เพราะคู่ของฉันมีปัญหาเกี่ยวกับหัวใจ เราต้องคุมปริมาณโซเดียมในอาหาร การวิเคราะห์แบบทันทีของ SafeEat ทำให้วางแผนมื้ออาหารง่ายขึ้นมาก'
        },
        {
          name: 'Emma W.',
          location: 'ซิดนีย์ ออสเตรเลีย',
          rating: 5,
          content: 'ฟีเจอร์สแกนจานอาหารยอดเยี่ยมมาก ฉันถ่ายรูปจานจากร้านอาหารแล้วแอปก็เจอส่วนผสมที่มีกลูเตนซ่อนอยู่ ช่วยให้ฉันรอดจากอาการแพ้หนักๆ ได้เลย'
        },
        {
          name: 'Ahmed H.',
          location: 'ดูไบ สหรัฐอาหรับเอมิเรตส์',
          rating: 5,
          content: 'ฉันทานฮาลาลแบบเคร่งครัด SafeEat ช่วยเช็กส่วนผสมของผลิตภัณฑ์บรรจุหีบห่อที่ฉันไม่คุ้นเคย เป็นแอปที่ขาดไม่ได้จริงๆ'
        },
        {
          name: 'Lisa M.',
          location: 'โตรอนโต แคนาดา',
          rating: 5,
          content: 'ฉันมีลูกสามคนที่แต่ละคนแพ้อาหารไม่เหมือนกัน การซื้อของเคยเป็นฝันร้าย ตอนนี้ฉันสแกนทุกอย่างได้ในไม่กี่วินาที แอปนี้ช่วยชีวิตฉันจริงๆ'
        },
        {
          name: 'Johan V.',
          location: 'สตอกโฮล์ม สวีเดน',
          rating: 5,
          content: 'ฉันเป็นโรคโครห์นและต้องหลีกเลี่ยงอาหารกระตุ้นบางอย่าง โปรไฟล์ส่วนตัวใน SafeEat ช่วยไฮไลต์ส่วนผสมที่ฉันอาจมองข้ามไป'
        },
        {
          name: 'Maria G.',
          location: 'เซาเปาลู บราซิล',
          rating: 5,
          content: 'ฟีเจอร์สแกนเมนูเหมาะมากสำหรับการแพ้อาหารทะเลของฉัน ตอนนี้ฉันสั่งอาหารในร้านใหม่ๆ ได้อย่างมั่นใจมากขึ้นเยอะ'
        },
        {
          name: 'David C.',
          location: 'ปารีส ฝรั่งเศส',
          rating: 5,
          content: 'ในฐานะเชฟที่มีความไวต่ออาหารบางชนิด ฉันใช้ SafeEat ตรวจส่วนผสมก่อนปรุงอาหาร ความแม่นยำของ AI นั้นน่าประทับใจมาก'
        },
        {
          name: 'Fatima A.',
          location: 'ไคโร อียิปต์',
          rating: 5,
          content: 'เบาหวานขณะตั้งครรภ์เป็นเรื่องจัดการยากมากสำหรับฉัน จนได้มาเจอ SafeEat การสแกนฉลากอาหารทำให้การควบคุมง่ายขึ้นเยอะ'
        },
        {
          name: 'Michael O.',
          location: 'ลากอส ไนจีเรีย',
          rating: 5,
          content: 'ฉันมีภาวะไม่ทนต่ออาหารที่พบได้ยาก ซึ่งแอปส่วนใหญ่ไม่รองรับ แต่ใน SafeEat ฉันสามารถเพิ่มลงในโปรไฟล์ได้ และตอนนี้แอปก็ตรวจจับให้ทุกครั้ง'
        },
        {
          name: 'Sophie L.',
          location: 'อัมสเตอร์ดัม เนเธอร์แลนด์',
          rating: 5,
          content: 'ฉันเป็นวีแกนและแพ้ถั่วเหลือง การหาอาหารที่ปลอดภัยแทบเป็นไปไม่ได้ SafeEat ตรวจเจอทั้งส่วนผสมจากสัตว์ที่แอบซ่อนอยู่และถั่วเหลือง ฉันปลื้มมาก'
        },
        {
          name: 'Kenji N.',
          location: 'โซล เกาหลีใต้',
          rating: 5,
          content: 'ฐานข้อมูลบาร์โค้ดของแอปใหญ่มาก แม้แต่สินค้านำเข้ายังถูกตรวจพบ เหมาะสุดๆ เวลาต้องเช็กอาหารต่างชาติที่ไม่คุ้นเคย'
        },
        {
          name: 'Elena P.',
          location: 'โรม อิตาลี',
          rating: 5,
          content: 'แม่ของฉันอายุมากและมีปัญหาสุขภาพหลายอย่าง ฉันสร้างโปรไฟล์ให้เธอ และตอนนี้เธอกลับไปซื้อของเองได้อีกครั้ง ขอบคุณ SafeEat'
        }
      ]
    },
    pricing: {
      title: 'ราคาเข้าใจง่าย โปร่งใส',
      subtitle: 'เลือกแพ็กเกจที่เหมาะกับคุณ',
      free: 'ใช้ฟรี',
      freeDesc: 'ลองใช้ฟรี 5 ครั้ง',
      freeFeature1: 'สแกนด้วย AI ได้ 5 ครั้ง',
      freeFeature2: 'การวิเคราะห์พื้นฐาน',
      freeFeature3: 'ประวัติการใช้งานแบบจำกัด',
      weekly: 'รายสัปดาห์',
      weeklyPrice: '$2.99',
      weeklyDesc: 'เหมาะสำหรับการใช้งานเป็นประจำ',
      weeklyFeature1: 'สแกนได้ไม่จำกัด',
      weeklyFeature2: 'การวิเคราะห์ขั้นสูง',
      weeklyFeature3: 'ดูประวัติการสแกนทั้งหมด',
      weeklyFeature4: 'การสนับสนุนแบบเร่งด่วน',
      annual: 'รายปี',
      annualPrice: '$29.99',
      annualDesc: 'คุ้มที่สุด – ประหยัด 60%',
      annualBadge: 'ยอดนิยม',
      annualFeature1: 'มีทุกอย่างในแพ็กเกจรายสัปดาห์',
      annualFeature2: 'ประหยัด 125 ดอลลาร์ต่อปี',
      annualFeature3: 'การสนับสนุนระดับพรีเมียม',
      annualFeature4: 'ได้ใช้ฟีเจอร์ใหม่ก่อนใคร',
      button: 'เริ่มต้นใช้งาน'
    },
    faq: {
      title: 'คำถามที่พบบ่อย',
      items: [
        {
          q: 'ฉันสามารถสแกนอะไรได้บ้างด้วย SafeEat',
          a: 'SafeEat สามารถวิเคราะห์ได้ทั้ง รายการส่วนผสมและตารางโภชนาการจากรูปภาพ บาร์โค้ด อาหารแบบบรรจุภัณฑ์และขายปลีก จานอาหารจากรูปภาพ รวมถึงเมนูร้านอาหาร'
        },
        {
          q: 'ระบบรู้ได้อย่างไรว่าจานอาหารคืออะไร',
          a: 'AI จะพยายามจำแนกประเภทอาหารและคาดเดาส่วนผสมที่เป็นไปได้ ระบบจะทำงานได้ดีที่สุดเมื่อมีแสงสว่างเพียงพอและเห็นจานชัดเจน ส่วนผสมที่ซ่อนอยู่หรือการปนเปื้อนข้ามชนิดอาจตรวจจับได้ไม่ครบ'
        },
        {
          q: 'สามารถสแกนเมนูในร้านอาหารได้ไหม',
          a: 'ได้ แอปจะไฮไลต์สารก่อภูมิแพ้และสิ่งที่อาจขัดกับข้อจำกัดด้านอาหารของคุณ อย่างไรก็ตาม เมนูอาจมีข้อมูลไม่ครบถ้วน จึงควรถามพนักงานเพิ่มเติมเกี่ยวกับส่วนผสม ประเภทน้ำมัน การใช้เตาทอดร่วมกัน ซอส และความเสี่ยงจากการปนเปื้อนข้ามชนิด'
        },
        {
          q: 'SafeEat รองรับภาวะสุขภาพแบบไหนบ้าง',
          a: 'รองรับทั้งภูมิแพ้อาหาร ภาวะไม่ทนต่ออาหาร เช่น แลคโตสหรือกลูเตน และความต้องการด้านโภชนาการเฉพาะ เช่น ลดโซเดียม คุมคาร์บ หรือหลีกเลี่ยงส่วนผสมบางชนิด'
        },
        {
          q: 'SafeEat ใช้งานแบบออฟไลน์ได้หรือไม่',
          a: 'โดยทั่วไป การสแกนและการวิเคราะห์ด้วย AI ต้องใช้อินเทอร์เน็ต ผลลัพธ์และลิสต์ที่บันทึกไว้บางส่วนอาจเปิดดูได้แบบออฟไลน์ ขึ้นอยู่กับอุปกรณ์และการตั้งค่าแอป'
        },
        {
          q: 'การวิเคราะห์ของ SafeEat แม่นยำแค่ไหน',
          a: 'ความแม่นยำขึ้นอยู่กับคุณภาพของรูป ความชัดของตัวหนังสือ ภาษา/รูปแบบฉลาก และความครบถ้วนของข้อมูลในเมนู SafeEat เป็นเครื่องมือเพื่อข้อมูลเท่านั้น หากมีความเสี่ยงสูงควรตรวจสอบข้อมูลจากแหล่งอื่นเพิ่มเติม'
        },
        {
          q: 'ถ้า SafeEat ให้ผลที่ดูผิด ฉันควรทำอย่างไร',
          a: 'อย่าพึ่งเชื่อผลลัพธ์ที่คุณไม่มั่นใจ ให้ลองถ่ายรูปใหม่ให้ชัดขึ้น ตรวจสอบฉลากหรือเว็บไซต์ทางการของผู้ผลิต และสอบถามพนักงาน ในสถานการณ์ที่มีความเสี่ยงสูง ควรเลือกตัวเลือกที่ปลอดภัยกว่าไว้ก่อน'
        },
        {
          q: 'สามารถรายงานผลการวิเคราะห์ที่ผิดพลาดได้ไหม',
          a: 'ได้ คุณสามารถส่งอีเมลไปที่ support@safeeat.app พร้อมภาพหน้าจอ ภาพฉลาก/เมนู/จานอาหาร ประเทศ แบรนด์และชื่อสินค้า และอธิบายว่าคุณคิดว่าจุดใดผิดพลาด'
        },
        {
          q: 'ทำไมการระบุจานอาหารจึงอาจไม่แม่นยำเท่าการสแกนฉลาก',
          a: 'อาหารที่หน้าตาคล้ายกัน ส่วนผสมที่ซ่อนอยู่ ซอสหรือท็อปปิ้งที่กลบอาหาร แสงไม่เพียงพอ หรือภาพเบลอ รวมถึงเมนูเฉพาะท้องถิ่นมากๆ ล้วนส่งผลให้ความแม่นยำลดลงได้'
        },
        {
          q: 'SafeEat ถือเป็นอุปกรณ์ทางการแพทย์หรือไม่',
          a: 'ไม่ SafeEat เป็นเพียงเครื่องมือให้ข้อมูลเท่านั้น หากต้องการคำแนะนำด้านการแพทย์ โปรดปรึกษาผู้เชี่ยวชาญทางการแพทย์เสมอ'
        },
        {
          q: 'SafeEat มีความรับผิดชอบต่อข้อมูลอย่างไร',
          a: 'ข้อมูลในแอปถูกนำเสนอแบบตามที่เป็นจริง โดยไม่รับประกันว่าครบถ้วนหรือไม่มีข้อผิดพลาด การตัดสินใจสุดท้ายเป็นของคุณเอง และอาจมีส่วนผสมที่ไม่ได้ระบุหรือการปนเปื้อนข้ามชนิดเกิดขึ้นได้'
        },
        {
          q: 'ถ้าฉันมีภูมิแพ้รุนแรง สามารถพึ่งพา SafeEat เพียงอย่างเดียวได้ไหม',
          a: 'ไม่ได้ ควรอ่านฉลาก ถามพนักงาน ปฏิบัติตามแผนฉุกเฉินของตนเอง และพกยาตามใบสั่งแพทย์ไว้เสมอ หากไม่แน่ใจ ให้หลีกเลี่ยงอาหารหรือผลิตภัณฑ์นั้น'
        },
        {
          q: 'SafeEat ปกป้องข้อมูลสุขภาพของฉันอย่างไร',
          a: 'เราเก็บข้อมูลเท่าที่จำเป็นและใช้มาตรการด้านความปลอดภัยรวมถึงการจำกัดสิทธิ์การเข้าถึง หากมีคำถามเกี่ยวกับความเป็นส่วนตัว สามารถติดต่อเราได้ที่ support@safeeat.app และอ่านรายละเอียดในนโยบายความเป็นส่วนตัว'
        },
        {
          q: 'เด็กสามารถใช้ SafeEat ได้หรือไม่',
          a: 'ได้ แต่ควรอยู่ภายใต้การดูแลของผู้ใหญ่ หากมีภูมิแพ้รุนแรงหรือข้อจำกัดด้านอาหารที่ซับซ้อน ควรใช้ความระมัดระวังเป็นพิเศษและตรวจสอบข้อมูลจากแหล่งที่เชื่อถือได้'
        },
        {
          q: 'หากเกิดอาการแพ้ขึ้น ฉันควรทำอย่างไร',
          a: 'ให้รีบขอความช่วยเหลือทางการแพทย์ทันทีและทำตามแผนฉุกเฉินของคุณ หากมีอาการรุนแรงให้โทรขอความช่วยเหลือฉุกเฉิน หลังจากนั้นควรแจ้งแพทย์ ร้านอาหาร หรือผู้ผลิตเกี่ยวกับเหตุการณ์ที่เกิดขึ้น'
        }
      ]
    },
    cta: {
      title: 'พร้อมเปลี่ยนวิธีเลือกกินของคุณหรือยัง',
      subtitle: 'เข้าร่วมกับผู้ใช้หลายพันคนที่ใช้ SafeEat อยู่ทุกวัน',
      description: 'ดาวน์โหลดแอปตอนนี้และรับสิทธิ์สแกนด้วย AI ฟรี 5 ครั้ง',
      button: 'ดาวน์โหลดบน App Store',
      feature1: 'ไม่ต้องใช้บัตรเครดิต',
      feature2: 'เป็นส่วนตัวและปลอดภัย 100%',
      feature3: 'เริ่มใช้งานได้ในไม่กี่วินาที'
    },
    newsletter: {
      title: 'รับข่าวสารจากเรา',
      subtitle: 'รับอัปเดตล่าสุดและเคล็ดลับการกินอย่างปลอดภัยยิ่งขึ้น',
      placeholder: 'กรอกอีเมลของคุณ',
      button: 'สมัครรับข่าวสาร',
      error: 'โปรดกรอกอีเมลให้ถูกต้อง',
      success: 'ขอบคุณที่สมัครรับข่าวสาร'
    },
    seo: {
      title: 'ผู้ช่วยส่วนตัวด้านความปลอดภัยของอาหาร',
      text: 'SafeEat เป็นแอปสแกนอาหารด้วย AI สำหรับผู้ที่มีภูมิแพ้ ภาวะไม่ทนต่ออาหาร หรือข้อจำกัดด้านโภชนาการ คุณสามารถสแกนฉลาก ตารางโภชนาการ บาร์โค้ด เมนูร้านอาหาร และจานอาหารพร้อมรับประทานได้ทันที เพื่อเช็กว่าเหมาะกับโปรไฟล์ของคุณหรือไม่ ไม่ว่าคุณจะเป็นโรคเซลิแอก ไม่ทนต่อแลคโตส แพ้ถั่ว หรือทานอาหารแบบปราศจากกลูเตน วีแกน หรือควบคุมโซเดียม SafeEat ช่วยให้คุณตัดสินใจเลือกอาหารได้อย่างปลอดภัยยิ่งขึ้น',
      keywords: [
        'แอปสำหรับผู้มีภูมิแพ้อาหาร',
        'สแกนเนอร์ส่วนผสมอาหาร',
        'ตรวจจับสารก่อภูมิแพ้',
        'สแกนฉลากโภชนาการ',
        'สแกนบาร์โค้ดอาหาร',
        'สแกนเมนูร้านอาหาร',
        'ตรวจสอบอาหารปราศจากกลูเตน',
        'แอปสำหรับภาวะไม่ทนต่อแลคโตส',
        'ติดตามรูปแบบการกิน',
        'ความปลอดภัยของอาหาร'
      ]
    },
    cookies: {
      bannerText: 'เราใช้คุกกี้: คุกกี้ที่จำเป็นต่อการทำงานของเว็บไซต์ และคุกกี้ด้านวิเคราะห์ที่ใช้เมื่อคุณยินยอมเท่านั้น',
      accept: 'ยอมรับ',
      decline: 'ปฏิเสธ'
    },
    legal: {
      backHome: 'กลับไปหน้าแรก'
    },
    footer: {
      product: 'ผลิตภัณฑ์',
      features: 'ฟีเจอร์',
      howItWorks: 'การทำงาน',
      testimonials: 'เสียงจากผู้ใช้',
      pricing: 'ราคา',
      faq: 'คำถามที่พบบ่อย',
      contacts: 'ติดต่อเรา',
      legal: 'ข้อมูลทางกฎหมาย',
      privacy: 'นโยบายความเป็นส่วนตัว',
      terms: 'ข้อตกลงการให้บริการ',
      disclaimer: 'ข้อจำกัดความรับผิด',
      usePolicy: 'คู่มือการใช้งาน',
      company: 'FJCode OU',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      register: 'เลขทะเบียน 16993109',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat สงวนลิขสิทธิ์'
    }
  },
  tr: {
    meta: {
      home: {
        title: 'SafeEat | Alerjiler ve intoleranslar için AI gıda tarayıcı',
        description: 'Etiketleri, barkodları, menüleri ve yemekleri tarayarak; profilinize (alerjiler, intoleranslar, kronik hastalıklar) göre alerjenleri ve beslenme risklerini tespit edin.'
      },
      privacy: {
        title: 'Gizlilik Politikası | SafeEat',
        description: 'SafeEat\'in verilerinizi nasıl topladığını, kullandığını ve koruduğunu öğrenin.'
      },
      terms: {
        title: 'Kullanım Şartları | SafeEat',
        description: 'SafeEat kullanımına ilişkin şartları inceleyin.'
      },
      manual: {
        title: 'Kullanıcı Kılavuzu | SafeEat',
        description: 'SafeEat ile yiyecekleri güvenle nasıl tarayacağınızı öğrenin.'
      },
      disclaimer: {
        title: 'Sorumluluk Reddi | SafeEat',
        description: 'SafeEat\'in sunduğu bilgiler, yönlendirmeler ve sınırlamalar hakkında bilgi edinin.'
      }
    },
    nav: {
      about: 'Nasıl çalışır',
      features: 'Özellikler',
      testimonials: 'Yorumlar',
      pricing: 'Fiyatlandırma',
      faq: 'SSS',
      contacts: 'İletişim',
      getStarted: 'Başlayın'
    },
    about: {
      description: 'AI destekli gıda tarayıcı: etiketleri, barkodları, yemekleri ve menüleri okur; sağlık profilinize göre alerjenleri, içerikleri ve besin değerlerini vurgular.'
    },
    hero: {
      badge: 'AI ile gıda güvenliği',
      title: 'Hemen kontrol edin',
      title2: 'gıda güvenliğini',
      titleHighlight: 'size özel',
      subtitle: 'Temel alınarak',
      description: 'Ürünleri, barkodları, içerik listesini ve menüleri tarayın — yiyeceğin sizin için güvenli olup olmadığını anında öğrenin',
      typing: ['kronik hastalıklar', 'alerjiler', 'gıda intoleransları'],
      stat1: '500+',
      stat1Label: 'Kronik hastalıklar',
      stat2: '250+',
      stat2Label: 'Alerjiler',
      stat3: '100+',
      stat3Label: 'Gıda intoleransları',
      downloadButton: 'App Store\'dan indirin'
    },
    howItWorks: {
      badge: 'Nasıl çalışır',
      mainTitle: 'SafeEat nasıl çalışır',
      subtitle: 'Basit ve hızlı tarama süreci — birkaç adımda kişiselleştirilmiş sonuçlar.',
      setupBadge: 'Tek seferlik kurulum',
      setupTitle: 'Sağlık profilinizi oluşturun',
      setupDesc: 'İlk kurulumda sağlık bilgilerinizi ekleyerek SafeEat\'i size göre ayarlarsınız. Bu işlem bir kez yapılır ve size özel gıda analizini mümkün kılar.',
      cards: {
        diseases: {
          title: 'Kronik hastalıklar',
          desc: '500+ hastalık içeren veritabanımızdan kronik durumlarınızı ekleyin. Diyabetten kalp hastalıklarına kadar geniş bir kapsama sahibiz.',
          statLabel: 'durum'
        },
        allergies: {
          title: 'Alerjiler',
          desc: '250+ seçenek arasından alerjilerinizi seçin. Yaygın kuruyemiş ve deniz ürünlerinden nadir alerjenlere kadar — güvende kalın.',
          statLabel: 'alerji'
        },
        intolerances: {
          title: 'Gıda intoleransları',
          desc: '100+ seçenek arasından intoleranslarınızı belirtin. Laktoz, glüten, fruktoz ve daha fazlası — rahatsızlık olmadan yiyin.',
          statLabel: 'intolerans'
        }
      },
      connectorText: 'Ve sonra — her taramada:',
      stepLabel: 'Adım',
      step1Title: 'Yiyeceği tarayın',
      step1Desc: 'Tekil ürünleri, hazır yemekleri, besin etiketlerini, barkodları ve hatta restoran menülerini tarayın. Sadece kamerayı tutun — gerisini SafeEat halleder.',
      step2Title: 'AI analizi',
      step2Desc: 'Uygulama görüntüyü analiz eder ve hastalık/alergi/intolerans profilinizle karşılaştırarak uygunluğu değerlendirir.',
      step3Title: 'Raporunuzu alın',
      step3Desc: 'SafeEat ayrıntılı bir rapor üretir ve taranan öğenin sağlık profilinize ve beslenme gereksinimlerinize ne kadar uyduğunu gösterir.',
      step4Title: 'Karar verin',
      step4Desc: 'Kişiselleştirilmiş içgörülerle sizin için en iyi seçimi yapın. Güvenle yiyin — vücudunuza neyin uygun olduğunu net biçimde bilin.',
      processTitle: 'Canlı görün',
      processSubtitle: 'SafeEat\'in yiyecekleri gerçek zamanlı nasıl analiz ettiğini izleyin'
    },
    features: {
      title: 'Güçlü özellikler',
      subtitle: 'Güvenle yemek için ihtiyacınız olan her şey',
      items: [
        {
          title: 'Her ürünü tarayın',
          subtitle: 'günlük atıştırmalıklardan egzotik meyvelere',
          description: 'Ambalajlı ya da ambalajsız fark etmez — süpermarketteki günlük ürünlerden nadir tropik meyvelere kadar her şeyi kamerayla tarayın ve anında güvenlik değerlendirmesi alın. SafeEat ürünü tanır, içeriğini analiz eder ve riskli/kaçınmanız gereken bileşenleri işaretler.',
          highlights: ['Ambalajlı ve ambalajsız ürünler', 'Anında güvenlik kontrolü', 'İçerik analizi'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Besin etiketi ve barkod tarayın',
          subtitle: 'karmaşık etiketleri saniyeler içinde anlayın',
          description: 'Küçük yazılar ve kafa karıştıran etiketlerle uğraşmayın. SafeEat besin değerlerini, içerik listesini ve barkodları birkaç saniyede tarar. Alerjenleri, katkı maddelerini ve potansiyel olarak sorunlu bileşenleri hemen vurgular.',
          highlights: ['Besin değerlerini tarama', 'Alerjen tespiti', 'Riskli içerik uyarıları'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Yemeği tarayın — içerikleri tahmin edin',
          subtitle: 'yemeğinizin fotoğrafından',
          description: 'Yemeğinizin fotoğrafını çekin; SafeEat olası içeriklere ayırarak gösterir. Ev yemeği, food court veya restoran fark etmez — AI, olası alerjenleri ve diyetinize uymayan bileşenleri tespit eder.',
          highlights: ['AI ile içerik tahmini', 'Her türlü yemek için', 'Diyet uyumluluğu kontrolü'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Restoran menülerini tarayın',
          subtitle: 'sipariş vermeden güvenli seçeneği bulun',
          description: 'Kafe ya da restoran menüsüne kamerayı tutun; SafeEat her yemeği analiz ederek profilinize göre güvenli seçenekleri gösterir. Gizli alerjenler veya istemediğiniz içerikler için endişeyi azaltır.',
          highlights: ['Menü analizi', 'Güvenli seçenekleri bulma', 'Gizli alerjenleri tespit'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Kişisel güvenli gıdalar listenizi oluşturun',
          subtitle: 'kendi "clean eating" kütüphanenizi kurun',
          description: 'Sizin için güvenli olan yiyecekleri kaydedin, "clean eating" kütüphanenizi büyütün ve alışverişte ya da dışarıda yerken istediğiniz zaman erişin. Her taramayla kişisel gıda veritabanınız gelişir.',
          highlights: ['Kişisel gıda kütüphanesi', 'Her yerde hızlı erişim', 'Akıllı öneriler'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Binlerce kişi güveniyor',
      subtitle: 'Dünya çapında kullanıcı topluluğu SafeEat\'e güveniyor.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat alışveriş alışkanlığımı tamamen değiştirdi! Çölyak hastasıyım ve ürün taraması saniyeler sürüyor. Artık markette minik yazıları okumak zorunda değilim.'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: 'Birden fazla gıda alerjim var; dışarıda yemek her zaman stresliydi. Artık menüyü tarayıp neyin güvenli olduğunu hemen görüyorum. Gerçekten oyunu değiştirdi!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'Diyabetimi yönetmek için SafeEat\'i her gün kullanıyorum. Besin etiketi tarayıcı sayesinde karbonhidrat ve şekeri anında görebiliyorum. Kesinlikle tavsiye ederim!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: 'Oğlumun şiddetli kuruyemiş alerjisi var. SafeEat sayesinde atıştırmalık alırken içim daha rahat. Barkod taraması inanılmaz derecede doğru.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: 'Laktoz intoleransım var ve çok seyahat ediyorum. SafeEat birçok dilde çalışıyor — yurt dışında menü tarayıp endişe etmeden yiyebiliyorum!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'Eşimin kalp rahatsızlığı nedeniyle sodyuma dikkat etmemiz gerekiyor. SafeEat\'in anlık analizi yemek planlamayı çok daha kolay hale getirdi.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Yemek tarayıcı harika! Restoranda yemeğimin fotoğrafını çektim ve gizli glüten kaynaklarını gösterdi. Kötü bir reaksiyondan kurtuldum!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'Sıkı bir helal diyeti uyguluyorum. SafeEat, bilmediğim paketli ürünlerin içeriğini kontrol etmemde çok yardımcı. Olmazsa olmaz bir uygulama!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Üç çocuğum var ve hepsinin farklı alerjileri var; alışveriş eskiden kabustu. Şimdi her şeyi saniyeler içinde tarıyorum. Bu uygulama resmen kurtarıcı!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'Crohn hastalığım var ve bazı tetikleyicilerden kaçınmalıyım. SafeEat\'in kişisel profili, genelde gözümden kaçan içerikleri yakalıyor.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'Menü tarama özelliği deniz ürünü alerjim için mükemmel. Yeni restoranlarda sipariş verirken çok daha güvende hissediyorum!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'Hassasiyetleri olan bir şef olarak pişirmeden önce içerikleri SafeEat ile kontrol ediyorum. AI doğruluğu gerçekten etkileyici.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: 'Gebelik diyabetini yönetmek zordu; SafeEat\'i bulunca işler çok kolaylaştı. Etiketleri taramak takibi basitleştiriyor!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Nadir bir intoleransım var ve çoğu uygulama bunu kapsamıyor. SafeEat\'te profilime ekleyebildim ve artık her seferinde dikkate alıyor!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: 'Veganım ve soya alerjim var — güvenli yiyecek bulmak çok zordu. SafeEat hem gizli hayvansal içerikleri HEM de soyayı buluyor. Bayılıyorum!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'Barkod veritabanı çok geniş! İthal ürünler bile tanınıyor. Bilinmeyen uluslararası gıdaları kontrol etmek için harika.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: 'Yaşlı annemin birden fazla rahatsızlığı var. Profilini kurdum ve şimdi yeniden kendi başına alışveriş yapabiliyor. Teşekkürler, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Basit ve şeffaf fiyatlar',
      subtitle: 'Size uygun planı seçin',
      free: 'Ücretsiz',
      freeDesc: 'Denemek için 5 ücretsiz tarama',
      freeFeature1: '5 AI tarama',
      freeFeature2: 'Temel analiz',
      freeFeature3: 'Sınırlı geçmiş',
      weekly: 'Haftalık',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Düzenli kullanım için ideal',
      weeklyFeature1: 'Sınırsız tarama',
      weeklyFeature2: 'Gelişmiş analiz',
      weeklyFeature3: 'Tam geçmiş erişimi',
      weeklyFeature4: 'Öncelikli destek',
      annual: 'Yıllık',
      annualPrice: '$29.99',
      annualDesc: 'En iyi değer — %60 tasarruf',
      annualBadge: 'En popüler',
      annualFeature1: 'Haftalık plandaki her şey',
      annualFeature2: 'Yılda $125 tasarruf',
      annualFeature3: 'Premium destek',
      annualFeature4: 'Yeni özelliklere erken erişim',
      button: 'Başla'
    },
    faq: {
      title: 'Sık sorulan sorular',
      items: [
        {
          q: 'SafeEat ile neleri tarayabilirim?',
          a: 'SafeEat şunları analiz edebilir: içerik listeleri ve besin değerleri (fotoğraf), barkodlar, ambalajlı/ambalajsız gıdalar, yemekler (fotoğraf) ve restoran menüleri.'
        },
        {
          q: 'Yemek tanıma özelliği nasıl çalışır?',
          a: 'AI bir yemeği tanır ve içerebileceği olası malzemeleri tahmin eder. İyi ışık ve uygun açıyla daha iyi çalışır; gizli malzemeler veya çapraz bulaş (kontaminasyon) gözden kaçabilir.'
        },
        {
          q: 'Restoran menülerini tarayabilir miyim?',
          a: 'Evet. Olası alerjenleri ve diyet uyumsuzluklarını işaretler. Ancak menüler eksik olabilir; içerikler, yağlar, ortak fritözler, soslar ve çapraz bulaş riski için mutlaka personelle doğrulayın.'
        },
        {
          q: 'SafeEat hangi sağlık durumlarını destekliyor?',
          a: 'Gıda alerjileri, intoleranslar (örn. laktoz, glüten) ve beslenme gereksinimleri (düşük sodyum, karbonhidrat odaklı, belirli içeriklerden kaçınma vb.).'
        },
        {
          q: 'SafeEat çevrimdışı çalışır mı?',
          a: 'Genellikle tarama/AI için internet bağlantısı gerekir. Kaydedilen sonuçlar ve listeler, uygulama ve cihaza bağlı olarak çevrimdışı görüntülenebilir.'
        },
        {
          q: 'SafeEat analizleri ne kadar doğru?',
          a: 'Fotoğraf kalitesi, etiket metni, dil/format ve menüdeki bilgi düzeyine göre değişir. Bu bir bilgilendirme aracıdır; yüksek risk durumlarında mutlaka tekrar kontrol edin.'
        },
        {
          q: 'SafeEat yanlış yaparsa ne yapmalıyım?',
          a: 'Emin olmadığınız bir sonuca güvenmeyin; daha iyi bir fotoğrafla tekrar tarayın; etiketten/resmî siteden/personelden doğrulayın. Yüksek riskte en güvenli seçeneği tercih edin.'
        },
        {
          q: 'Yanlış analiz sonucunu bildirebilir miyim?',
          a: 'Evet. support@safeeat.app adresine ekran görüntüsü, etiket/menü/yemek fotoğrafı, ülke/marka/ürün bilgisi ve neyin yanlış göründüğünü gönderin.'
        },
        {
          q: 'Yemek tanıma neden bazen daha az doğru olur?',
          a: 'Benzer görünen yemekler, gizli malzemeler, yoğun sos/kaplamalar, düşük ışık/bulanıklık ve çok özelleştirilmiş veya bölgesel yemekler.'
        },
        {
          q: 'SafeEat bir tıbbi cihaz mı?',
          a: 'Hayır. Yalnızca bilgilendirme amaçlıdır. Tıbbi tavsiye için sağlık profesyoneline danışın.'
        },
        {
          q: 'SafeEat\'in sorumluluğu nedir?',
          a: 'Bilgiler "olduğu gibi" sunulur; tamlık garanti edilemez. Seçimlerinizden siz sorumlusunuz; beyan edilmemiş içerikler ve çapraz bulaş mümkün olabilir.'
        },
        {
          q: 'Şiddetli alerjim varsa yalnızca SafeEat\'e güvenmeli miyim?',
          a: 'Hayır. Etiketleri okuyun, personelle doğrulayın, eylem planınızı izleyin ve reçeteli ilaçlarınızı yanınızda bulundurun. Şüphedeyseniz tüketmeyin.'
        },
        {
          q: 'Sağlık verilerim nasıl korunuyor?',
          a: 'Yalnızca gerekli minimum veriyi toplarız ve güvenlik önlemleri ile erişim kısıtlamaları uygularız. Gizlilik için support@safeeat.app ile iletişime geçin. Daha fazlası için Gizlilik Politikası\'na bakın.'
        },
        {
          q: 'SafeEat\'i çocuklar için kullanabilir miyim?',
          a: 'Evet, yetişkin gözetiminde. Şiddetli alerji veya karmaşık diyet ihtiyaçlarında daha dikkatli olun ve güvenilir kaynaklarla doğrulayın.'
        },
        {
          q: 'Bir reaksiyon yaşarsam ne yapmalıyım?',
          a: 'Hemen tıbbi yardım alın ve acil durum planınızı uygulayın. Ciddi belirtilerde 112\'yi arayın; ardından sağlayıcıya/restorana/üreticiye bildirin.'
        }
      ]
    },
    cta: {
      title: 'Beslenme alışkanlığınızı değiştirmeye hazır mısınız?',
      subtitle: 'SafeEat\'i her gün kullanan binlerce kişiye katılın',
      description: 'Şimdi indirin ve 5 ücretsiz AI tarama kazanın',
      button: 'App Store\'dan indirin',
      feature1: 'Kredi kartı gerekmez',
      feature2: '%100 gizli ve güvenli',
      feature3: 'Anında erişim'
    },
    newsletter: {
      title: 'Güncel kalın',
      subtitle: 'En yeni güncellemeleri ve sağlık ipuçlarını alın',
      placeholder: 'E‑posta adresinizi girin',
      button: 'Abone ol',
      success: 'Abone olduğunuz için teşekkürler!',
      error: 'Geçerli bir e‑posta adresi girin'
    },
    seo: {
      title: 'Size özel gıda güvenliği asistanı',
      text: 'SafeEat; gıda alerjileri, intoleranslar ve beslenme kısıtları olan kişiler için AI destekli bir gıda tarayıcı uygulamasıdır. Etiketleri, besin değerlerini, barkodları, restoran menülerini ve hazır yemekleri tarayarak profilinize göre güvenliği anında kontrol edin. Çölyak, laktoz intoleransı, kuruyemiş alerjisi ya da glütensiz, vegan veya düşük sodyumlu beslenme gibi ihtiyaçlarınız varsa, SafeEat güvenli seçimler yapmanıza yardımcı olur.',
      keywords: [
        'gıda alerjisi uygulaması',
        'içindekiler tarayıcı',
        'alerjen tespiti',
        'besin etiketi okuyucu',
        'barkod tarayıcı',
        'menü tarayıcı',
        'glütensiz kontrol',
        'laktoz intoleransı uygulaması',
        'diyet takip',
        'gıda güvenliği'
      ]
    },
    cookies: {
      bannerText: 'Çerez kullanıyoruz: siteyi çalıştırmak için gerekli çerezler ve yalnızca onay verirseniz analitik çerezler.',
      decline: 'Reddet',
      accept: 'Kabul et'
    },
    legal: {
      backHome: 'Ana sayfaya dön'
    },
    footer: {
      product: 'Ürün',
      features: 'Özellikler',
      howItWorks: 'Nasıl çalışır',
      testimonials: 'Yorumlar',
      pricing: 'Fiyatlandırma',
      faq: 'SSS',
      contacts: 'İletişim',
      legal: 'Hukuki',
      privacy: 'Gizlilik Politikası',
      terms: 'Hizmet Şartları',
      usePolicy: 'Kullanıcı Kılavuzu',
      disclaimer: 'Sorumluluk Reddi',
      company: 'FJCode OU',
      address: 'Viru väljak 2, 10111 Tallinn, Estonya',
      register: 'Kayıt numarası 16993109',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. Tüm hakları saklıdır.'
    }
  },
  uk: {
    meta: {
      home: {
        title: 'SafeEat | AI‑сканер їжі для алергій та непереносимості',
        description: 'Скануйте етикетки, штрихкоди, меню та страви, щоб знаходити алергени й харчові ризики відповідно до вашого профілю: алергії, непереносимості та хронічні захворювання.'
      },
      privacy: {
        title: 'Політика конфіденційності | SafeEat',
        description: 'Дізнайтеся, як SafeEat збирає, використовує та захищає ваші дані.'
      },
      terms: {
        title: 'Умови використання | SafeEat',
        description: 'Ознайомтеся з умовами, що застосовуються до використання SafeEat.'
      },
      manual: {
        title: 'Посібник користувача | SafeEat',
        description: 'Дізнайтеся, як безпечно сканувати їжу за допомогою SafeEat.'
      },
      disclaimer: {
        title: 'Відмова від відповідальності | SafeEat',
        description: 'Зрозумійте, яку інформацію надає SafeEat та які його обмеження.'
      }
    },
    nav: {
      about: 'Як це працює',
      features: 'Функції',
      testimonials: 'Відгуки',
      pricing: 'Ціни',
      faq: 'FAQ',
      contacts: 'Контакти',
      getStarted: 'Почати'
    },
    about: {
      description: 'AI‑сканер їжі, який читає етикетки, штрихкоди, страви та меню й підсвічує алергени, склад і поживну цінність на основі вашого профілю здоров\'я.'
    },
    hero: {
      badge: 'Безпека харчування з AI',
      title: 'Перевіряйте миттєво',
      title2: 'безпеку їжі',
      titleHighlight: 'під вас',
      subtitle: 'На основі',
      description: 'Скануйте продукти, штрихкоди, склад і меню — та одразу дізнавайтеся, чи безпечна їжа для вашого організму',
      typing: ['хронічні захворювання', 'алергії', 'харчова непереносимість'],
      stat1: '500+',
      stat1Label: 'Хронічні захворювання',
      stat2: '250+',
      stat2Label: 'Алергії',
      stat3: '100+',
      stat3Label: 'Харчова непереносимість',
      downloadButton: 'Завантажити в App Store'
    },
    howItWorks: {
      badge: 'Як це працює',
      mainTitle: 'Як працює SafeEat',
      subtitle: 'Процес сканування простий і швидкий — персональні результати за кілька кроків.',
      setupBadge: 'Налаштування один раз',
      setupTitle: 'Створіть профіль здоров\'я',
      setupDesc: 'Під час онбордингу ви налаштовуєте SafeEat, додаючи інформацію про здоров\'я. Це робиться один раз і дозволяє отримувати персоналізований аналіз їжі саме для вас.',
      cards: {
        diseases: {
          title: 'Хронічні захворювання',
          desc: 'Додайте свої хронічні стани з нашої бази даних із 500+ захворюваннями. Від діабету до серцево‑судинних хвороб — ми враховуємо це.',
          statLabel: 'станів'
        },
        allergies: {
          title: 'Алергії',
          desc: 'Оберіть свої алергії з 250+ варіантів. Від поширених горіхів і морепродуктів до рідкісних алергенів — залишайтеся в безпеці.',
          statLabel: 'алергій'
        },
        intolerances: {
          title: 'Харчова непереносимість',
          desc: 'Вкажіть непереносимості з 100+ варіантів. Лактоза, глютен, фруктоза та інші — їжте без дискомфорту.',
          statLabel: 'непереносимостей'
        }
      },
      connectorText: 'Далі — щоразу, коли ви скануєте:',
      stepLabel: 'Крок',
      step1Title: 'Скануйте їжу',
      step1Desc: 'Скануйте що завгодно: окремі продукти, готові страви, етикетки з поживною цінністю, штрихкоди і навіть меню ресторанів. Просто наведіть камеру — SafeEat зробить решту.',
      step2Title: 'AI‑аналіз',
      step2Desc: 'Застосунок аналізує зображення та зіставляє його з вашим профілем (хвороби, алергії, непереносимість), оцінюючи сумісність і можливі ризики.',
      step3Title: 'Отримайте звіт',
      step3Desc: 'SafeEat формує детальний звіт і показує, наскільки продукт або страва відповідає вашому профілю здоров\'я та дієтичним вимогам.',
      step4Title: 'Приймайте рішення',
      step4Desc: 'З персональними підказками ви обираєте найкраще для себе. Їжте впевнено — і точно знайте, що підходить вашому організму.',
      processTitle: 'Подивіться в дії',
      processSubtitle: 'Побачте, як SafeEat аналізує їжу в реальному часі'
    },
    features: {
      title: 'Потужні функції',
      subtitle: 'Усе, що потрібно для безпечного харчування',
      items: [
        {
          title: 'Скануйте будь‑який продукт',
          subtitle: 'від щоденних перекусів до екзотичних фруктів',
          description: 'Наведіть камеру на будь‑який продукт — упакований чи без упаковки — від звичних товарів у супермаркеті до рідкісних тропічних фруктів — і отримайте миттєву оцінку безпеки. SafeEat розпізнає продукт та аналізує склад, допомагаючи уникати ризикових або небажаних інгредієнтів.',
          highlights: ['Упаковані й неупаковані продукти', 'Миттєва перевірка безпеки', 'Аналіз складу'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Скануйте етикетки й штрихкоди',
          subtitle: 'розумійте складні маркування за секунди',
          description: 'Більше не потрібно розбиратися з дрібним шрифтом і заплутаними етикетками. SafeEat за лічені секунди сканує поживну цінність, список інгредієнтів і штрихкод. Застосунок одразу підсвічує алергени, добавки або потенційно проблемні компоненти, щоб ви точно знали, що їсте.',
          highlights: ['Сканування поживної цінності', 'Виявлення алергенів', 'Попередження про ризикові речовини'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Скануйте страви — визначайте інгредієнти',
          subtitle: 'за фото вашої їжі',
          description: 'Зробіть фото страви — SafeEat розкладе її на ймовірні інгредієнти. Домашня їжа, фудкорт чи ресторан — наша AI‑модель знаходить можливі алергени та компоненти, які можуть не підходити вашій дієті.',
          highlights: ['AI‑розпізнавання інгредієнтів', 'Підходить для будь‑яких страв', 'Перевірка сумісності з дієтою'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Скануйте меню ресторанів',
          subtitle: 'знаходьте безпечні варіанти до замовлення',
          description: 'Наведіть камеру на меню кафе чи ресторану — SafeEat проаналізує позиції та покаже, що безпечно з урахуванням ваших обмежень. Ідеально для харчування поза домом без тривоги через приховані алергени або небажані інгредієнти.',
          highlights: ['Аналіз меню', 'Пошук безпечних страв', 'Виявлення прихованих алергенів'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Створіть особистий список безпечної їжі',
          subtitle: 'зіберіть власну бібліотеку «clean eating»',
          description: 'Зберігайте продукти й страви, які вам підходять, створюйте власну бібліотеку «clean eating» і відкривайте її в будь‑який момент — у магазині чи в ресторані. Ваша персональна база їжі зростає з кожним сканом.',
          highlights: ['Особиста бібліотека їжі', 'Швидкий доступ будь‑де', 'Розумні рекомендації'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Нам довіряють тисячі',
      subtitle: 'Глобальна спільнота користувачів довіряє SafeEat.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, USA',
          rating: 5,
          content: 'SafeEat повністю змінив мій підхід до покупок! У мене целіакія, і сканування продуктів займає секунди. Більше не потрібно читати дрібний шрифт на етикетках у магазині.'
        },
        {
          name: 'Marcus L.',
          location: 'London, UK',
          rating: 5,
          content: 'З кількома харчовими алергіями похід у кафе завжди був стресом. Тепер я просто сканую меню й одразу бачу, що мені безпечно. Це справжній game changer!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Japan',
          rating: 5,
          content: 'Я використовую SafeEat щодня, щоб контролювати діабет. Сканер поживної цінності миттєво показує вуглеводи та цукор. Дуже рекомендую!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Germany',
          rating: 5,
          content: 'У мого сина сильна алергія на горіхи. SafeEat дає спокій, коли я купую йому перекуси. Сканування штрихкодів дуже точне.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Spain',
          rating: 5,
          content: 'У мене непереносимість лактози, і я багато подорожую. SafeEat працює різними мовами — я сканую меню за кордоном і їм без хвилювання!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, India',
          rating: 5,
          content: 'Через серцеве захворювання чоловіка нам потрібно стежити за натрієм. Миттєвий аналіз SafeEat значно спростив планування харчування.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Australia',
          rating: 5,
          content: 'Сканер страв — неймовірний! Я сфотографувала страву в ресторані, і застосунок знайшов приховані джерела глютену. Це врятувало мене від поганої реакції!'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'Я суворо дотримуюся халяль‑дієти. SafeEat допомагає перевіряти склад незнайомих упакованих продуктів. Незамінний застосунок!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'З трьома дітьми та різними алергіями покупки були кошмаром. Тепер я сканую все за секунди. Цей застосунок — справжній рятівник!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Sweden',
          rating: 5,
          content: 'У мене хвороба Крона, і мені потрібно уникати певних тригерів. Персональний профіль SafeEat помічає інгредієнти, які я часто пропускаю.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'Функція сканування меню ідеальна при моїй алергії на морепродукти. Тепер я впевненіше замовляю в нових ресторанах!'
        },
        {
          name: 'David C.',
          location: 'Paris, France',
          rating: 5,
          content: 'Я кухар(ка) і маю чутливість до деяких продуктів — використовую SafeEat, щоб перевіряти інгредієнти перед приготуванням. Точність розпізнавання вражає.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Egypt',
          rating: 5,
          content: 'Контролювати гестаційний діабет було складно, доки я не знайшла SafeEat. Сканування етикеток робить відстеження набагато простішим!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'У мене рідкісна непереносимість, яку більшість застосунків не підтримує. У SafeEat я додала її в профіль — і тепер вона завжди враховується!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Netherlands',
          rating: 5,
          content: 'Я веган(ка) й маю алергію на сою — знайти безпечну їжу було дуже складно! SafeEat знаходить і приховані тваринні інгредієнти, і сою. Обожнюю!'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, South Korea',
          rating: 5,
          content: 'База штрихкодів величезна! Навіть імпортні продукти розпізнаються. Чудово підходить, щоб перевіряти незнайому міжнародну їжу.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Italy',
          rating: 5,
          content: 'У моєї літньої мами кілька захворювань. Я налаштував(ла) її профіль, і тепер вона знову може самостійно ходити за покупками. Дякую, SafeEat!'
        }
      ]
    },
    pricing: {
      title: 'Прості та прозорі ціни',
      subtitle: 'Оберіть план, що підходить вам',
      free: 'Безкоштовно',
      freeDesc: '5 безкоштовних сканів для знайомства',
      freeFeature1: '5 AI‑сканів',
      freeFeature2: 'Базовий аналіз',
      freeFeature3: 'Обмежена історія',
      weekly: 'Щотижня',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Для регулярного використання',
      weeklyFeature1: 'Скани без обмежень',
      weeklyFeature2: 'Розширений аналіз',
      weeklyFeature3: 'Повна історія',
      weeklyFeature4: 'Пріоритетна підтримка',
      annual: 'Щороку',
      annualPrice: '$29.99',
      annualDesc: 'Найкраща вигода — економія 60%',
      annualBadge: 'Найпопулярніший',
      annualFeature1: 'Усе з тижневого плану',
      annualFeature2: 'Економія $125 на рік',
      annualFeature3: 'Преміум‑підтримка',
      annualFeature4: 'Ранній доступ до нових функцій',
      button: 'Почати'
    },
    faq: {
      title: 'Поширені запитання',
      items: [
        {
          q: 'Що можна сканувати в SafeEat?',
          a: 'SafeEat може аналізувати: список інгредієнтів і поживну цінність (за фото), штрихкоди, упаковані та неупаковані продукти, страви (за фото) і меню ресторанів.'
        },
        {
          q: 'Як працює розпізнавання страв?',
          a: 'AI розпізнає страву та оцінює ймовірні інгредієнти. Найкраще працює за хорошого освітлення та правильного ракурсу; приховані інгредієнти або перехресний контакт (контамінація) можуть бути не виявлені.'
        },
        {
          q: 'Чи можна сканувати меню ресторанів?',
          a: 'Так. Застосунок підсвічує можливі алергени та конфлікти з вашою дієтою. Меню може бути неповним; завжди уточнюйте у персоналу інгредієнти, олії, спільні фритюрниці, соуси та ризик перехресного контакту.'
        },
        {
          q: 'Які стани підтримує SafeEat?',
          a: 'Харчові алергії, непереносимості (наприклад, лактози й глютену) та дієтичні вимоги (низький натрій, контроль вуглеводів, уникнення окремих інгредієнтів).'
        },
        {
          q: 'Чи працює SafeEat офлайн?',
          a: 'Зазвичай для сканування та AI‑аналізу потрібен інтернет. Збережені результати та списки можуть бути доступні офлайн залежно від застосунку та пристрою.'
        },
        {
          q: 'Наскільки точний аналіз SafeEat?',
          a: 'Точність залежить від якості фото, тексту на етикетці, мови/формату та повноти меню. Це інформаційний інструмент — за високого ризику завжди перевіряйте додатково.'
        },
        {
          q: 'Що робити, якщо SafeEat помилився?',
          a: 'Не покладайтеся на сумнівний результат; зробіть чіткіше фото та скануйте знову; перевірте за етикеткою/сайтом/у персоналу; за високого ризику обирайте найбезпечніший варіант.'
        },
        {
          q: 'Чи можна повідомити про помилковий результат аналізу?',
          a: 'Так: напишіть на support@safeeat.app і додайте скриншоти, фото етикетки/меню/страви, країну/бренд/продукт і що саме здається неправильним.'
        },
        {
          q: 'Чому розпізнавання страв може бути менш точним?',
          a: 'Схожі страви, приховані інгредієнти, густі соуси/декор, погане освітлення/розмиття, а також сильно кастомізовані або регіональні страви.'
        },
        {
          q: 'SafeEat — це медичний пристрій?',
          a: 'Ні. Застосунок призначений лише для інформації. За медичними порадами звертайтеся до фахівця.'
        },
        {
          q: 'Яка відповідальність SafeEat?',
          a: 'Інформація надається «як є»; повнота не гарантується. Відповідальність за вибір несете ви; можливі незазначені інгредієнти та перехресний контакт.'
        },
        {
          q: 'Чи можна при тяжкій алергії покладатися лише на SafeEat?',
          a: 'Ні. Читайте етикетки, уточнюйте у персоналу, дотримуйтеся свого плану дій і майте з собою призначені ліки; якщо є сумніви — уникайте продукту/страви.'
        },
        {
          q: 'Як ви захищаєте мої дані про здоров\'я?',
          a: 'Ми збираємо мінімально необхідні дані та захищаємо їх заходами безпеки й обмеженим доступом. З питань конфіденційності: support@safeeat.app. Детальніше — в Політиці конфіденційності.'
        },
        {
          q: 'Чи можна використовувати SafeEat для дітей?',
          a: 'Так, під наглядом дорослих. За тяжких алергій і складних обмежень будьте особливо уважні та перевіряйте інформацію з надійних джерел.'
        },
        {
          q: 'Що робити, якщо виникла реакція?',
          a: 'Негайно зверніться по медичну допомогу, дотримуйтеся свого екстреного плану, при тяжких симптомах телефонуйте 112, а потім повідомте про це постачальнику/ресторану/виробнику.'
        }
      ]
    },
    cta: {
      title: 'Готові змінити свій підхід до харчування?',
      subtitle: 'Приєднуйтеся до тисяч людей, які користуються SafeEat щодня',
      description: 'Завантажте зараз і отримайте 5 безкоштовних AI‑сканів',
      button: 'Завантажити в App Store',
      feature1: 'Без банківської картки',
      feature2: '100% приватно та безпечно',
      feature3: 'Миттєвий доступ'
    },
    newsletter: {
      title: 'Будьте в курсі',
      subtitle: 'Отримуйте останні оновлення та поради щодо здоров\'я',
      placeholder: 'Введіть ваш email',
      button: 'Підписатися',
      success: 'Дякуємо за підписку!',
      error: 'Введіть коректну email‑адресу'
    },
    seo: {
      title: 'Ваш персональний помічник з безпеки харчування',
      text: 'SafeEat — це AI‑застосунок‑сканер їжі для людей із харчовими алергіями, непереносимістю та дієтичними обмеженнями. Скануйте етикетки, поживну цінність, штрихкоди, меню ресторанів і готові страви, щоб миттєво перевіряти безпечність за вашим профілем. Целіакія, непереносимість лактози, алергія на горіхи або дієта без глютену, веганська чи з низьким вмістом натрію — SafeEat допомагає обирати безпечні варіанти.',
      keywords: [
        'додаток для харчової алергії',
        'сканер складу',
        'детектор алергенів',
        'зчитувач поживної цінності',
        'сканер штрихкодів',
        'сканер меню',
        'перевірка без глютену',
        'додаток непереносимість лактози',
        'трекер дієти',
        'безпека харчування'
      ]
    },
    cookies: {
      bannerText: 'Ми використовуємо cookies: необхідні для роботи сайту та аналітичні — лише за вашою згодою.',
      decline: 'Відхилити',
      accept: 'Прийняти'
    },
    legal: {
      backHome: 'Повернутися на головну'
    },
    footer: {
      product: 'Продукт',
      features: 'Функції',
      howItWorks: 'Як це працює',
      testimonials: 'Відгуки',
      pricing: 'Ціни',
      faq: 'FAQ',
      contacts: 'Контакти',
      legal: 'Юридична інформація',
      privacy: 'Політика конфіденційності',
      terms: 'Умови використання',
      usePolicy: 'Посібник користувача',
      disclaimer: 'Відмова від відповідальності',
      company: 'FJCode OU',
      address: 'Viru väljak 2, 10111 Tallinn, Естонія',
      register: 'Реєстраційний номер 16993109',
      email: 'support@safeeat.app',
      copyright: '(c) 2025 SafeEat. Усі права захищено.'
    }
  },
  vi: {
    meta: {
      home: {
        title: 'SafeEat | Trình quét thực phẩm dùng AI cho dị ứng và không dung nạp',
        description: 'Quét nhãn, mã vạch, thực đơn và món ăn để làm nổi bật dị nguyên và rủi ro dinh dưỡng dựa trên hồ sơ cá nhân của bạn: dị ứng, không dung nạp và bệnh mạn tính.'
      },
      privacy: {
        title: 'Chính sách quyền riêng tư | SafeEat',
        description: 'Tìm hiểu cách SafeEat thu thập, sử dụng và bảo vệ dữ liệu của bạn.'
      },
      terms: {
        title: 'Điều khoản sử dụng | SafeEat',
        description: 'Đọc các điều khoản áp dụng khi bạn sử dụng SafeEat.'
      },
      manual: {
        title: 'Hướng dẫn sử dụng | SafeEat',
        description: 'Tìm hiểu cách sử dụng SafeEat một cách an toàn để quét thực phẩm.'
      },
      disclaimer: {
        title: 'Tuyên bố miễn trừ trách nhiệm | SafeEat',
        description: 'Hiểu rõ các giới hạn và tính chất chỉ mang tính thông tin của khuyến nghị trong SafeEat.'
      }
    },
    nav: {
      about: 'Cách hoạt động',
      features: 'Tính năng',
      testimonials: 'Nhận xét',
      pricing: 'Giá',
      faq: 'Câu hỏi thường gặp',
      contacts: 'Liên hệ',
      getStarted: 'Bắt đầu'
    },
    about: {
      description: 'Trình quét thực phẩm dùng trí tuệ nhân tạo, đọc nhãn, mã vạch, món ăn và thực đơn nhà hàng, rồi làm nổi bật dị nguyên, thành phần và thông tin dinh dưỡng dựa trên hồ sơ sức khỏe của bạn.'
    },
    hero: {
      badge: 'An toàn thực phẩm với AI',
      title: 'Biết ngay',
      title2: 'món ăn đó có an toàn',
      titleHighlight: 'với bạn hay không',
      subtitle: 'Dựa trên',
      description: 'Quét thực phẩm, mã vạch, danh sách thành phần và thực đơn để kiểm tra trong vài giây xem chúng có phù hợp với cơ thể bạn không.',
      typing: ['bệnh mạn tính', 'dị ứng', 'không dung nạp thực phẩm'],
      stat1: '500+',
      stat1Label: 'Bệnh mạn tính',
      stat2: '250+',
      stat2Label: 'Dị ứng',
      stat3: '100+',
      stat3Label: 'Không dung nạp thực phẩm',
      downloadButton: 'Tải về trên App Store'
    },
    howItWorks: {
      badge: 'Cách hoạt động',
      mainTitle: 'SafeEat hoạt động như thế nào',
      subtitle: 'Quy trình quét rất đơn giản và nhanh chóng – chỉ vài bước là bạn có kết quả cá nhân hóa.',
      setupBadge: 'Thiết lập một lần',
      setupTitle: 'Tạo hồ sơ sức khỏe của bạn',
      setupDesc: 'Lần đầu mở ứng dụng, hãy thêm thông tin sức khỏe để SafeEat có thể phân tích theo đúng nhu cầu của bạn. Bạn chỉ cần làm một lần – các phân tích sau này đều dựa trên hồ sơ này.',
      cards: {
        diseases: {
          title: 'Bệnh mạn tính',
          desc: 'Thêm các bệnh mạn tính của bạn từ cơ sở dữ liệu hơn 500 tình trạng sức khỏe của chúng tôi. Từ tiểu đường đến bệnh tim – tất cả đều được tính đến trong phân tích.',
          statLabel: 'tình trạng'
        },
        allergies: {
          title: 'Dị ứng',
          desc: 'Chọn các loại dị ứng của bạn trong hơn 250 lựa chọn. Từ các dị nguyên phổ biến như hạt, hải sản đến những loại hiếm gặp hơn – để bạn yên tâm hơn mỗi ngày.',
          statLabel: 'dị ứng'
        },
        intolerances: {
          title: 'Không dung nạp thực phẩm',
          desc: 'Khai báo tình trạng không dung nạp trong hơn 100 lựa chọn: lactose, gluten, fructose và nhiều loại khác – giúp bạn ăn uống thoải mái hơn.',
          statLabel: 'không dung nạp'
        }
      },
      connectorText: 'Sau đó, với mỗi lần quét:',
      stepLabel: 'Bước',
      step1Title: 'Quét món ăn',
      step1Desc: 'Quét bất kỳ loại thực phẩm nào – một sản phẩm đóng gói, món ăn sẵn, bảng giá trị dinh dưỡng, mã vạch hoặc thậm chí cả thực đơn nhà hàng. Chỉ cần đưa camera lên, phần còn lại để SafeEat lo.',
      step2Title: 'Phân tích bằng AI',
      step2Desc: 'Ứng dụng phân tích hình ảnh và so sánh với hồ sơ của bạn (bệnh, dị ứng, không dung nạp) để đánh giá món ăn đó có phù hợp hay không.',
      step3Title: 'Nhận báo cáo chi tiết',
      step3Desc: 'SafeEat cung cấp báo cáo chi tiết cho thấy món ăn được quét phù hợp với nhu cầu sức khỏe và chế độ ăn của bạn đến mức nào.',
      step4Title: 'Ra quyết định chủ động',
      step4Desc: 'Dựa trên thông tin cá nhân hóa, bạn chọn được món ăn tốt nhất cho mình. Ăn uống tự tin hơn khi bạn hiểu rõ điều gì tốt cho cơ thể.',
      processTitle: 'Xem cách hoạt động thực tế',
      processSubtitle: 'Xem SafeEat phân tích món ăn của bạn theo thời gian thực'
    },
    features: {
      title: 'Tính năng mạnh mẽ',
      subtitle: 'Mọi thứ bạn cần để lựa chọn món ăn an toàn hơn',
      items: [
        {
          title: 'Quét mọi loại sản phẩm',
          subtitle: 'từ đồ ăn vặt hằng ngày đến trái cây ngoại nhập',
          description: 'Đưa camera vào bất kỳ loại thực phẩm nào – đóng gói hoặc bán lẻ – từ mặt hàng siêu thị quen thuộc đến trái cây nhiệt đới hiếm. Bạn sẽ nhận được đánh giá an toàn ngay lập tức. SafeEat nhận diện sản phẩm và phân tích thành phần để giúp bạn tránh những chất có rủi ro hoặc không mong muốn.',
          highlights: ['Thực phẩm đóng gói và bán lẻ', 'Kiểm tra an toàn tức thì', 'Phân tích thành phần'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: 'Quét nhãn dinh dưỡng và mã vạch',
          subtitle: 'hiểu nhãn phức tạp chỉ trong vài giây',
          description: 'Không còn phải căng mắt đọc chữ li ti nữa. SafeEat quét bảng giá trị dinh dưỡng, danh sách thành phần và mã vạch chỉ trong vài giây, đồng thời làm nổi bật dị nguyên, phụ gia hoặc các chất có thể gây hại để bạn biết chính xác mình đang ăn gì.',
          highlights: ['Quét bảng giá trị dinh dưỡng', 'Nhận diện dị nguyên', 'Cảnh báo chất có rủi ro'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: 'Quét món ăn – nhận diện thành phần',
          subtitle: 'chỉ cần chụp ảnh đĩa thức ăn của bạn',
          description: 'Chụp ảnh bữa ăn của bạn và SafeEat sẽ phân tách thành các thành phần có khả năng cao. Dù là món tự nấu, suất ăn căng tin hay món trong nhà hàng, AI có thể giúp phát hiện dị nguyên tiềm ẩn hoặc thành phần không phù hợp với chế độ ăn của bạn.',
          highlights: ['Nhận diện thành phần bằng AI', 'Hoạt động với mọi loại món ăn', 'Kiểm tra phù hợp với chế độ ăn'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: 'Quét thực đơn nhà hàng',
          subtitle: 'tìm món an toàn trước khi gọi',
          description: 'Đưa camera vào thực đơn quán cà phê hoặc nhà hàng, SafeEat sẽ phân tích từng món và cho biết món nào phù hợp với sức khỏe và chế độ ăn của bạn. Đi ăn ngoài thoải mái hơn, không lo dị nguyên ẩn hoặc thành phần không mong muốn.',
          highlights: ['Phân tích thực đơn', 'Đề xuất món ăn an toàn', 'Phát hiện dị nguyên ẩn'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: 'Tạo danh sách món ăn an toàn của riêng bạn',
          subtitle: 'xây thư viện thực phẩm "sạch" cá nhân',
          description: 'Lưu lại những thực phẩm phù hợp với bạn và xây dựng thư viện thực phẩm "sạch" của riêng mình để tham khảo khi đi chợ hoặc ăn ngoài. Cơ sở dữ liệu cá nhân lớn dần sau mỗi lần quét, giúp bạn quyết định nhanh và dễ dàng hơn.',
          highlights: ['Cơ sở dữ liệu thực phẩm cá nhân', 'Kiểm tra nhanh mọi lúc mọi nơi', 'Gợi ý thông minh'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: 'Được tin dùng trên toàn thế giới',
      subtitle: 'Cộng đồng người dùng toàn cầu đang tin tưởng sử dụng SafeEat.',
      items: [
        {
          name: 'Sarah M.',
          location: 'New York, Hoa Kỳ',
          rating: 5,
          content: 'SafeEat đã thay đổi hoàn toàn cách tôi mua thực phẩm! Tôi bị bệnh Celiac và chỉ mất vài giây để quét một sản phẩm. Không còn cảnh phải căng mắt đọc chữ nhỏ xíu trên nhãn nữa.'
        },
        {
          name: 'Marcus L.',
          location: 'Luân Đôn, Vương quốc Anh',
          rating: 5,
          content: 'Là người bị nhiều loại dị ứng thực phẩm, trước đây mỗi lần đi ăn ngoài đều rất căng thẳng. Giờ tôi chỉ cần quét thực đơn và biết ngay món nào an toàn. Thực sự là một công cụ thay đổi cuộc chơi!'
        },
        {
          name: 'Yuki T.',
          location: 'Tokyo, Nhật Bản',
          rating: 5,
          content: 'Tôi dùng SafeEat mỗi ngày để hỗ trợ kiểm soát bệnh tiểu đường. Trình quét nhãn dinh dưỡng giúp tôi theo dõi lượng carb và đường ngay lập tức. Rất đáng để thử!'
        },
        {
          name: 'Anna K.',
          location: 'Berlin, Đức',
          rating: 5,
          content: 'Con trai tôi bị dị ứng hạt rất nặng. SafeEat giúp tôi yên tâm hơn khi mua đồ ăn vặt cho con. Trình quét mã vạch chính xác đến bất ngờ.'
        },
        {
          name: 'Carlos R.',
          location: 'Madrid, Tây Ban Nha',
          rating: 5,
          content: 'Tôi không dung nạp lactose và thường xuyên đi du lịch. SafeEat hoạt động tốt dù ở ngôn ngữ nào – khi ở nước ngoài tôi chỉ cần quét thực đơn là có thể ăn uống an tâm hơn nhiều!'
        },
        {
          name: 'Priya S.',
          location: 'Mumbai, Ấn Độ',
          rating: 5,
          content: 'Vì chồng tôi có vấn đề về tim mạch nên chúng tôi phải kiểm soát lượng natri rất kỹ. Việc SafeEat phân tích tức thì khiến việc lên kế hoạch bữa ăn dễ dàng hơn rất nhiều.'
        },
        {
          name: 'Emma W.',
          location: 'Sydney, Úc',
          rating: 5,
          content: 'Tính năng quét món ăn thật tuyệt vời! Tôi chụp một món trong nhà hàng và ứng dụng đã phát hiện ra thành phần có chứa gluten bị ẩn. Nhờ vậy tôi tránh được một phản ứng dị ứng khó chịu.'
        },
        {
          name: 'Ahmed H.',
          location: 'Dubai, UAE',
          rating: 5,
          content: 'Tôi ăn theo chế độ halal nghiêm ngặt. SafeEat giúp tôi kiểm tra thành phần của các sản phẩm đóng gói lạ. Đây là ứng dụng không thể thiếu với tôi!'
        },
        {
          name: 'Lisa M.',
          location: 'Toronto, Canada',
          rating: 5,
          content: 'Tôi có ba đứa con và mỗi đứa lại dị ứng một kiểu – mua đồ ăn trước đây đúng là ác mộng. Giờ tôi quét mọi thứ chỉ trong vài giây. Ứng dụng này thực sự đã cứu vãn cuộc sống của tôi!'
        },
        {
          name: 'Johan V.',
          location: 'Stockholm, Thụy Điển',
          rating: 5,
          content: 'Tôi bị bệnh Crohn và phải tránh một số tác nhân nhất định. Hồ sơ cá nhân trong SafeEat làm nổi bật những thành phần mà bình thường tôi rất dễ bỏ sót.'
        },
        {
          name: 'Maria G.',
          location: 'São Paulo, Brazil',
          rating: 5,
          content: 'Trình quét thực đơn rất phù hợp với tình trạng dị ứng hải sản của tôi. Bây giờ tôi đặt món ở các nhà hàng mới với cảm giác yên tâm hơn nhiều!'
        },
        {
          name: 'David C.',
          location: 'Paris, Pháp',
          rating: 5,
          content: 'Là một đầu bếp có cơ địa nhạy cảm với một số thực phẩm, tôi dùng SafeEat để kiểm tra nguyên liệu trước khi nấu. Độ chính xác của AI khiến tôi rất ấn tượng.'
        },
        {
          name: 'Fatima A.',
          location: 'Cairo, Ai Cập',
          rating: 5,
          content: 'Tiểu đường thai kỳ từng là thử thách rất lớn với tôi, cho đến khi tôi tìm thấy SafeEat. Việc quét nhãn thực phẩm khiến việc theo dõi dễ dàng hơn hẳn!'
        },
        {
          name: 'Michael O.',
          location: 'Lagos, Nigeria',
          rating: 5,
          content: 'Tôi bị một dạng không dung nạp hiếm gặp mà hầu hết các ứng dụng khác không hỗ trợ. Với SafeEat, tôi có thể thêm nó vào hồ sơ và giờ ứng dụng đều nhận ra mỗi lần quét!'
        },
        {
          name: 'Sophie L.',
          location: 'Amsterdam, Hà Lan',
          rating: 5,
          content: 'Tôi ăn chay thuần (vegan) và dị ứng đậu nành – việc tìm đồ ăn an toàn trước đây gần như bất khả thi! SafeEat phát hiện được cả thành phần có nguồn gốc động vật ẩn lẫn đậu nành. Tôi thực sự rất thích ứng dụng này.'
        },
        {
          name: 'Kenji N.',
          location: 'Seoul, Hàn Quốc',
          rating: 5,
          content: 'Cơ sở dữ liệu mã vạch rất lớn! Ngay cả sản phẩm nhập khẩu cũng được nhận diện. Rất hữu ích khi cần kiểm tra thực phẩm quốc tế lạ.'
        },
        {
          name: 'Elena P.',
          location: 'Rome, Ý',
          rating: 5,
          content: 'Mẹ tôi đã lớn tuổi và có nhiều vấn đề sức khỏe. Tôi tạo hồ sơ cho mẹ và giờ bà có thể tự đi mua đồ ăn trở lại. Cảm ơn SafeEat rất nhiều!'
        }
      ]
    },
    pricing: {
      title: 'Giá rõ ràng, đơn giản',
      subtitle: 'Chọn gói phù hợp nhất với bạn',
      free: 'Miễn phí',
      freeDesc: '5 lượt quét miễn phí để dùng thử',
      freeFeature1: '5 lượt quét bằng AI',
      freeFeature2: 'Phân tích cơ bản',
      freeFeature3: 'Lịch sử sử dụng giới hạn',
      weekly: 'Theo tuần',
      weeklyPrice: '$2.99',
      weeklyDesc: 'Phù hợp cho việc sử dụng thường xuyên',
      weeklyFeature1: 'Quét không giới hạn',
      weeklyFeature2: 'Phân tích nâng cao',
      weeklyFeature3: 'Xem toàn bộ lịch sử quét',
      weeklyFeature4: 'Hỗ trợ ưu tiên',
      annual: 'Theo năm',
      annualPrice: '$29.99',
      annualDesc: 'Giá trị tối ưu – tiết kiệm 60%',
      annualBadge: 'Phổ biến nhất',
      annualFeature1: 'Bao gồm mọi thứ trong gói tuần',
      annualFeature2: 'Tiết kiệm 125 USD mỗi năm',
      annualFeature3: 'Hỗ trợ cao cấp',
      annualFeature4: 'Trải nghiệm sớm tính năng mới',
      button: 'Bắt đầu'
    },
    faq: {
      title: 'Câu hỏi thường gặp',
      items: [
        {
          q: 'Tôi có thể quét những gì với SafeEat?',
          a: 'SafeEat có thể phân tích: danh sách thành phần và bảng giá trị dinh dưỡng (từ ảnh), mã vạch, thực phẩm đóng gói và bán lẻ, món ăn (từ ảnh) và thực đơn nhà hàng.'
        },
        {
          q: 'SafeEat nhận diện món ăn như thế nào?',
          a: 'AI sẽ cố gắng nhận dạng món ăn và ước tính các thành phần có khả năng cao. Ứng dụng hoạt động tốt nhất khi ánh sáng đầy đủ và ảnh rõ nét; một số thành phần ẩn hoặc sự nhiễm chéo có thể không được nhận diện đầy đủ.'
        },
        {
          q: 'Tôi có thể quét thực đơn nhà hàng không?',
          a: 'Có. Ứng dụng sẽ đánh dấu các dị nguyên tiềm ẩn và những điểm không phù hợp với chế độ ăn của bạn. Tuy nhiên, thực đơn có thể không liệt kê đầy đủ, vì vậy bạn vẫn nên hỏi nhân viên về thành phần, loại dầu, việc dùng chung chảo/chiên, nước sốt và nguy cơ nhiễm chéo.'
        },
        {
          q: 'SafeEat hỗ trợ những tình trạng sức khỏe nào?',
          a: 'SafeEat hỗ trợ dị ứng thực phẩm, tình trạng không dung nạp (như lactose, gluten) và một số nhu cầu dinh dưỡng cụ thể (giảm natri, chú ý lượng carb, tránh một số thành phần nhất định).'
        },
        {
          q: 'SafeEat có hoạt động khi không có Internet không?',
          a: 'Thông thường việc quét và phân tích bằng AI cần kết nối Internet. Một số kết quả và danh sách đã lưu có thể xem được ngoại tuyến tùy theo thiết bị và cấu hình ứng dụng.'
        },
        {
          q: 'Kết quả phân tích của SafeEat chính xác đến mức nào?',
          a: 'Độ chính xác phụ thuộc vào chất lượng ảnh, độ rõ của chữ, ngôn ngữ/định dạng nhãn và mức độ đầy đủ của thông tin trên thực đơn. SafeEat chỉ là công cụ hỗ trợ thông tin – trong các tình huống rủi ro cao, bạn nên kiểm tra thêm từ các nguồn khác.'
        },
        {
          q: 'Nếu tôi nghĩ SafeEat phân tích sai thì phải làm sao?',
          a: 'Đừng dựa vào kết quả mà bạn không chắc chắn; hãy chụp lại ảnh rõ hơn, kiểm tra nhãn hoặc trang web của nhà sản xuất, và hỏi thêm nhân viên. Khi rủi ro cao, hãy chọn phương án an toàn hơn.'
        },
        {
          q: 'Tôi có thể báo cáo kết quả phân tích sai được không?',
          a: 'Có. Hãy gửi email tới support@safeeat.app kèm ảnh chụp màn hình, ảnh nhãn/ thực đơn/ món ăn, quốc gia, thương hiệu và tên sản phẩm, cũng như mô tả điều bạn cho là không chính xác.'
        },
        {
          q: 'Vì sao nhận diện món ăn có thể kém chính xác hơn so với quét nhãn?',
          a: 'Các món có hình thức giống nhau, thành phần ẩn, sốt đặc hoặc lớp trang trí che món ăn, điều kiện ánh sáng kém hoặc ảnh mờ, cũng như món ăn địa phương/biến tấu đặc biệt đều có thể làm giảm độ chính xác.'
        },
        {
          q: 'SafeEat có phải là thiết bị y tế không?',
          a: 'Không. SafeEat chỉ là công cụ cung cấp thông tin. Để được tư vấn y tế, bạn luôn cần hỏi ý kiến chuyên gia y tế.'
        },
        {
          q: 'SafeEat chịu trách nhiệm đến đâu với thông tin hiển thị?',
          a: 'Thông tin được cung cấp "nguyên trạng", không bảo đảm đầy đủ hoặc không có sai sót. Bạn tự chịu trách nhiệm với quyết định của mình; có thể tồn tại thành phần không được ghi rõ hoặc tình trạng nhiễm chéo.'
        },
        {
          q: 'Tôi có thể hoàn toàn dựa vào SafeEat nếu bị dị ứng nặng không?',
          a: 'Không. Bạn vẫn cần đọc kỹ nhãn, hỏi nhân viên, tuân thủ kế hoạch xử lý khẩn cấp của bản thân và luôn mang theo thuốc được kê đơn; nếu còn nghi ngờ, hãy tránh dùng sản phẩm hoặc món ăn đó.'
        },
        {
          q: 'SafeEat bảo vệ dữ liệu sức khỏe của tôi như thế nào?',
          a: 'Chúng tôi thu thập lượng dữ liệu tối thiểu cần thiết và bảo vệ chúng bằng các biện pháp bảo mật cùng cơ chế giới hạn truy cập. Nếu có câu hỏi về quyền riêng tư, hãy liên hệ support@safeeat.app và xem thêm trong Chính sách quyền riêng tư.'
        },
        {
          q: 'Trẻ em có thể dùng SafeEat không?',
          a: 'Có, nhưng nên sử dụng dưới sự giám sát của người lớn. Với dị ứng nặng hoặc chế độ ăn phức tạp, cần đặc biệt thận trọng và kiểm tra thông tin qua các nguồn đáng tin cậy.'
        },
        {
          q: 'Nếu tôi bị phản ứng dị ứng thì nên làm gì?',
          a: 'Hãy tìm kiếm trợ giúp y tế ngay lập tức và làm theo kế hoạch xử lý khẩn cấp của bạn; nếu có triệu chứng nghiêm trọng, hãy liên hệ dịch vụ cấp cứu. Sau đó, nên thông báo cho bác sĩ, nhà hàng hoặc nhà sản xuất về sự cố.'
        }
      ]
    },
    cta: {
      title: 'Sẵn sàng thay đổi cách bạn ăn uống chưa?',
      subtitle: 'Hãy cùng hàng nghìn người đang sử dụng SafeEat mỗi ngày',
      description: 'Tải ứng dụng ngay hôm nay để nhận 5 lượt quét AI miễn phí',
      button: 'Tải về trên App Store',
      feature1: 'Không cần thẻ tín dụng',
      feature2: 'Riêng tư và an toàn 100%',
      feature3: 'Bắt đầu sử dụng chỉ trong vài giây'
    },
    newsletter: {
      title: 'Nhận tin từ SafeEat',
      subtitle: 'Nhận cập nhật mới nhất và mẹo ăn uống an toàn hơn',
      placeholder: 'Nhập email của bạn',
      button: 'Đăng ký',
      error: 'Vui lòng nhập địa chỉ email hợp lệ',
      success: 'Cảm ơn bạn đã đăng ký!'
    },
    seo: {
      title: 'Trợ lý cá nhân cho an toàn thực phẩm của bạn',
      text: 'SafeEat là ứng dụng quét thực phẩm dùng trí tuệ nhân tạo dành cho những người có dị ứng, không dung nạp hoặc phải tuân theo chế độ ăn đặc biệt. Bạn có thể quét ngay lập tức nhãn, bảng giá trị dinh dưỡng, mã vạch, thực đơn nhà hàng và món ăn sẵn để kiểm tra xem chúng có phù hợp với hồ sơ của mình hay không. Dù bạn bị bệnh Celiac, không dung nạp lactose, dị ứng hạt hay đang ăn kiêng không gluten, thuần chay hoặc ít natri, SafeEat đều giúp bạn đưa ra lựa chọn an toàn hơn.',
      keywords: [
        'ứng dụng cho người dị ứng thực phẩm',
        'trình quét thành phần thực phẩm',
        'phát hiện dị nguyên',
        'quét nhãn dinh dưỡng',
        'trình đọc mã vạch thực phẩm',
        'quét thực đơn nhà hàng',
        'kiểm tra sản phẩm không gluten',
        'ứng dụng cho người không dung nạp lactose',
        'theo dõi chế độ ăn',
        'an toàn thực phẩm'
      ]
    },
    cookies: {
      bannerText: 'Chúng tôi sử dụng cookie: cookie cần thiết để trang web hoạt động và cookie phân tích chỉ khi bạn đồng ý.',
      accept: 'Đồng ý',
      decline: 'Từ chối'
    },
    legal: {
      backHome: 'Quay về trang chủ'
    },
    footer: {
      product: 'Sản phẩm',
      features: 'Tính năng',
      howItWorks: 'Cách hoạt động',
      testimonials: 'Nhận xét',
      pricing: 'Giá',
      faq: 'Câu hỏi thường gặp',
      contacts: 'Liên hệ',
      legal: 'Thông tin pháp lý',
      privacy: 'Chính sách quyền riêng tư',
      terms: 'Điều khoản dịch vụ',
      disclaimer: 'Tuyên bố miễn trừ trách nhiệm',
      usePolicy: 'Hướng dẫn sử dụng',
      company: 'FJCode OU',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      register: 'Số đăng ký 16993109',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat. Đã đăng ký bản quyền.'
    }
  },
  zh: {
    meta: {
      home: {
        title: 'SafeEat | 过敏与不耐受 AI 食品扫描器',
        description: '扫描标签、条形码、菜单和菜肴，标记与你的个人资料相符的过敏原和饮食风险：过敏、不耐受与慢性疾病。'
      },
      privacy: {
        title: '隐私政策 | SafeEat',
        description: '了解 SafeEat 如何收集、使用并保护你的数据。'
      },
      terms: {
        title: '使用条款 | SafeEat',
        description: '查看规范你使用 SafeEat 的条款。'
      },
      manual: {
        title: '用户手册 | SafeEat',
        description: '了解如何使用 SafeEat 安全地扫描食物。'
      },
      disclaimer: {
        title: '免责声明 | SafeEat',
        description: '了解 SafeEat 的信息提示与使用限制。'
      }
    },
    nav: {
      about: '工作原理',
      features: '功能',
      testimonials: '用户评价',
      pricing: '价格',
      faq: '常见问题',
      contacts: '联系方式',
      getStarted: '开始使用'
    },
    about: {
      description: 'AI 驱动的食品扫描器，可读取标签、条形码、菜品和菜单，并根据健康档案突出显示过敏原、成分与营养信息。'
    },
    hero: {
      badge: 'AI 驱动的食品安全',
      title: '即刻查看',
      title2: '食品安全',
      titleHighlight: '为你量身定制',
      subtitle: '基于你的',
      description: '扫描食品、条形码、配料和菜单，即刻检查是否适合你的身体',
      downloadButton: '在 App Store 下载',
      stat1: '500+',
      stat1Label: '慢性疾病',
      stat2: '250+',
      stat2Label: '过敏',
      stat3: '100+',
      stat3Label: '食物不耐受',
      typing: ['慢性疾病', '过敏', '食物不耐受']
    },
    howItWorks: {
      badge: '工作原理',
      mainTitle: 'SafeEat 如何工作',
      subtitle: '我们的食物扫描流程简单快速，只需几步即可得到个性化结果。',
      setupBadge: '一次性设置',
      setupTitle: '建立你的健康档案',
      setupDesc: '在引导流程中添加你的健康信息来个性化 SafeEat。只需设置一次，即可为你提供专属的食物分析。',
      cards: {
        diseases: {
          title: '慢性疾病',
          desc: '从我们包含 500+ 种疾病的数据库中添加你的慢性病状况。从糖尿病到心脏问题，我们都能覆盖。',
          statLabel: '病症'
        },
        allergies: {
          title: '过敏',
          desc: '从 250+ 选项中选择你的过敏源。从常见坚果和贝类到罕见过敏原——让你更安心。',
          statLabel: '过敏'
        },
        intolerances: {
          title: '食物不耐受',
          desc: '从 100+ 选项中指定你的不耐受类型。乳糖、麸质、果糖等——吃得更舒适。',
          statLabel: '不耐受'
        }
      },
      connectorText: '然后，每次扫描时：',
      stepLabel: '步骤',
      step1Title: '扫描食物',
      step1Desc: '扫描任何食物——单个商品、成品菜肴、营养标签、条形码，甚至餐厅菜单。只要对准镜头，剩下交给 SafeEat。',
      step2Title: 'AI 分析',
      step2Desc: '应用会分析图片，并与你的疾病、过敏和食物不耐受档案交叉比对，判断是否适合。',
      step3Title: '获取报告',
      step3Desc: 'SafeEat 会提供详细报告，展示分析结果与你的健康档案和饮食需求的匹配程度。',
      step4Title: '做出选择',
      step4Desc: '基于个性化洞察，你可以选择最适合自己的食物。放心进食，清楚知道什么适合你的身体。',
      processTitle: '看看它如何运作',
      processSubtitle: '观看 SafeEat 实时分析你的食物'
    },
    features: {
      title: '强大功能',
      subtitle: '安全饮食所需的一切',
      items: [
        {
          title: '扫描任意食品',
          subtitle: '从常见零食到异域水果',
          description: '将镜头对准任何包装或散装食品，从超市常备品到热带水果，都能即刻进行安全检查。SafeEat 会识别产品并分析其成分，帮助你避开风险或有害配料。',
          highlights: ['包装/散装食品', '即时安全检查', '成分分析'],
          image: '/img/screenshots/01.png?v=2',
          color: '#1E8242'
        },
        {
          title: '扫描营养标签和条形码',
          subtitle: '秒懂复杂标签',
          description: '不再为看不清小字或读不懂标签而烦恼。SafeEat 可在数秒内扫描营养成分表、配料表和条形码，并立即标出过敏原、添加剂或有毒物质，让你清楚自己在吃什么。',
          highlights: ['扫描营养成分表', '过敏原识别', '有害物质提醒'],
          image: '/img/screenshots/04.png?v=2',
          color: '#2563eb'
        },
        {
          title: '扫描菜肴 — 识别食材',
          subtitle: '只需拍下你的餐盘',
          description: '给你的餐点拍张照片，SafeEat 就能拆解可能的食材。无论是家常菜、食堂餐还是高端餐厅，AI 都能识别潜在过敏原或不符合你饮食的成分。',
          highlights: ['AI 食材识别', '适用于任何菜品', '饮食匹配检查'],
          image: '/img/screenshots/02.png?v=2',
          color: '#9333ea'
        },
        {
          title: '扫描餐厅菜单',
          subtitle: '下单前先找安全菜品',
          description: '将镜头对准咖啡馆或餐厅菜单，SafeEat 会分析每道菜，告诉你哪些符合你的健康需求。外出就餐更安心，不再担心隐藏过敏原或不想要的成分。',
          highlights: ['菜单解析', '安全菜品推荐', '隐藏过敏原检测'],
          image: '/img/screenshots/03.png?v=2',
          color: '#ea580c'
        },
        {
          title: '创建你的专属安全食物清单',
          subtitle: '打造你的清洁饮食库',
          description: '保存不适合你饮食的食物，建立自己的“清洁饮食”库，并在购物或外出就餐时随时查看。你的个性化食物数据库会随着每次扫描不断增长。',
          highlights: ['个人食物库', '随时快速查看', '智能推荐'],
          image: '/img/screenshots/05.png?v=2',
          color: '#0891b2'
        }
      ]
    },
    testimonials: {
      title: '深受用户信赖',
      subtitle: '获得全球用户社区的信任。',
      items: [
        {
          name: 'Sarah M.',
          location: '美国纽约',
          rating: 5,
          content: 'SafeEat 完全改变了我买菜的方式！我患有乳糜泻，扫描商品只需几秒。再也不用在店里盯着小字标签看了。'
        },
        {
          name: 'Marcus L.',
          location: '英国伦敦',
          rating: 5,
          content: '对我这种多种食物过敏的人来说，外出就餐一直很紧张。现在我只要扫一下菜单，就能知道哪些是安全的。太改变游戏规则了！'
        },
        {
          name: 'Yuki T.',
          location: '日本东京',
          rating: 5,
          content: '我每天都用 SafeEat 来管理糖尿病。营养标签扫描器能让我立刻看到碳水和糖含量。强烈推荐！'
        },
        {
          name: 'Anna K.',
          location: '德国柏林',
          rating: 5,
          content: '我儿子对坚果严重过敏。用 SafeEat 给他买零食时我安心多了。条形码扫描非常准确。'
        },
        {
          name: 'Carlos R.',
          location: '西班牙马德里',
          rating: 5,
          content: '我有乳糖不耐受，而且经常旅行。SafeEat 支持多语言——在国外扫餐厅菜单就能放心吃！'
        },
        {
          name: 'Priya S.',
          location: '印度孟买',
          rating: 5,
          content: '要管理我丈夫的心脏问题，就得特别注意钠含量。SafeEat 的即时分析让做饭计划轻松多了。'
        },
        {
          name: 'Emma W.',
          location: '澳大利亚悉尼',
          rating: 5,
          content: '菜肴扫描太神了！我拍了餐厅的餐点，它识别出了隐藏的麸质成分，救了我一命，避免了严重反应。'
        },
        {
          name: 'Ahmed H.',
          location: '阿联酋迪拜',
          rating: 5,
          content: '我遵循严格的清真饮食。SafeEat 帮我核对不熟悉的包装食品配料，非常必备。'
        },
        {
          name: 'Lisa M.',
          location: '加拿大多伦多',
          rating: 5,
          content: '三个孩子各自过敏源都不同，买菜简直噩梦。现在几秒钟就能全部扫完，这个应用太救命了！'
        },
        {
          name: 'Johan V.',
          location: '瑞典斯德哥尔摩',
          rating: 5,
          content: '我患有克罗恩病，需要避开一些诱因。SafeEat 的个性化档案能抓到我可能会忽略的成分。'
        },
        {
          name: 'Maria G.',
          location: '巴西圣保罗',
          rating: 5,
          content: '菜单扫描功能对我的贝类过敏太有用了。现在在新餐厅点餐也很有信心。'
        },
        {
          name: 'David C.',
          location: '法国巴黎',
          rating: 5,
          content: '作为一名对某些食物敏感的厨师，我会用 SafeEat 在烹饪前核对食材。AI 识别准确得惊人。'
        },
        {
          name: 'Fatima A.',
          location: '埃及开罗',
          rating: 5,
          content: '妊娠糖尿病让我一度很崩溃，直到遇到 SafeEat。扫描食品标签让记录变得非常简单。'
        },
        {
          name: 'Michael O.',
          location: '尼日利亚拉各斯',
          rating: 5,
          content: '我有一种比较罕见的不耐受，很多应用都不支持。SafeEat 让我把它加到档案里，现在每次都能识别出来！'
        },
        {
          name: 'Sophie L.',
          location: '荷兰阿姆斯特丹',
          rating: 5,
          content: '纯素但对大豆过敏，找安全食物太难了！SafeEat 能识别隐藏的动物成分以及大豆。爱了！'
        },
        {
          name: 'Kenji N.',
          location: '韩国首尔',
          rating: 5,
          content: '条形码数据库非常庞大！连进口商品也能识别。用来检查不熟悉的国际食品太合适了。'
        },
        {
          name: 'Elena P.',
          location: '意大利罗马',
          rating: 5,
          content: '我年迈的母亲有多种健康状况。我帮她设置了档案，现在她又能独立买菜了。谢谢 SafeEat！'
        }
      ]
    },
    pricing: {
      title: '简单透明的价格',
      subtitle: '选择最适合你的方案',
      free: '免费',
      freeDesc: '5 次免费扫描体验',
      freeFeature1: '5 次 AI 扫描',
      freeFeature2: '基础分析',
      freeFeature3: '有限历史记录',
      weekly: '每周',
      weeklyDesc: '适合经常使用者',
      weeklyPrice: '$2.99',
      weeklyFeature1: '无限扫描',
      weeklyFeature2: '高级分析',
      weeklyFeature3: '完整历史记录',
      weeklyFeature4: '优先支持',
      annual: '每年',
      annualBadge: '最受欢迎',
      annualDesc: '超值之选——立省 60%',
      annualPrice: '$29.99',
      annualFeature1: '包含周订阅的所有功能',
      annualFeature2: '每年节省 $125',
      annualFeature3: '高级支持',
      annualFeature4: '抢先体验新功能',
      button: '开始使用'
    },
    faq: {
      title: '常见问题',
      items: [
        { q: 'SafeEat 可以扫描什么？', a: 'SafeEat 可分析：配料表和营养成分表（拍照）、条形码、包装与散装食品、菜肴（拍照）以及餐厅菜单。' },
        { q: '菜肴识别功能如何工作？', a: 'AI 会识别菜品并推测可能的食材。光线和角度越好效果越佳；隐藏配料或交叉接触可能无法识别。' },
        { q: '可以扫描餐厅菜单吗？', a: '可以。它会提示潜在过敏原/饮食冲突。菜单可能不完整；请向店员确认配料、用油、共用炸锅、酱料及交叉接触等信息。' },
        { q: 'SafeEat 支持哪些健康情况？', a: '食物过敏、不耐受（如乳糖、麸质）以及饮食需求（低钠、关注碳水、避免某些成分）。' },
        { q: 'SafeEat 可以离线使用吗？', a: '通常扫描/AI 分析需要联网。已保存的结果/清单可能会因应用与设备而支持离线查看。' },
        { q: 'SafeEat 的分析有多准确？', a: '取决于照片质量、标签文字、语言/格式以及菜单信息是否完整。它仅提供参考——风险较高时请务必再核对。' },
        { q: '如果 SafeEat 判断错了怎么办？', a: '不要依赖存疑结果；重新拍得更清晰；通过标签/官网/店员核实；高风险情况下请选择更安全的选项。' },
        { q: '可以反馈错误的分析结果吗？', a: '可以：发送邮件至 support@safeeat.app，附上截图、标签/菜单/菜肴照片、国家/品牌/产品信息，以及你认为有问题的点。' },
        { q: '为什么菜肴识别可能不够准确？', a: '外观相似的食物、隐藏配料、厚重酱汁/装饰、光线不足/模糊、以及高度定制或地区特色菜都会影响准确性。' },
        { q: 'SafeEat 是医疗器械吗？', a: '不是。它仅提供信息参考。如需医疗建议，请咨询专业人士。' },
        { q: 'SafeEat 的责任范围是什么？', a: '信息按“现状”提供，无法保证完整无误。你需自行对选择负责；未标注成分和交叉接触都有可能发生。' },
        { q: '严重过敏能只依赖 SafeEat 吗？', a: '不能。请阅读标签、询问店员、遵循你的应急方案并随身携带处方药；不确定时请避免食用。' },
        { q: '你们如何保护我的健康数据？', a: '我们仅收集最少必要数据，并采取安全措施与权限控制。如有隐私问题可联系 support@safeeat.app。详见《隐私政策》。' },
        { q: '孩子可以使用 SafeEat 吗？', a: '可以，但需监护人监督。对于严重过敏或复杂饮食，请更保守，并用可靠来源进行核实。' },
        { q: '如果出现过敏反应该怎么办？', a: '立即就医并遵循你的紧急预案；出现严重症状请拨打急救电话；随后向医生/餐厅/生产商反馈。' }
      ]
    },
    cta: {
      title: '准备好改变你的饮食生活了吗？',
      subtitle: '加入每天使用 SafeEat 的数千用户',
      description: '现在下载即可获得 5 次免费 AI 扫描',
      button: '在 App Store 下载',
      feature1: '无需信用卡',
      feature2: '100% 私密安全',
      feature3: '立即使用'
    },
    newsletter: {
      title: '保持更新',
      subtitle: '获取最新动态与健康小贴士',
      placeholder: '输入你的邮箱',
      button: '订阅',
      error: '请输入有效的邮箱地址',
      success: '感谢订阅！'
    },
    seo: {
      title: '你的专属食品安全助手',
      text: 'SafeEat 是一款面向食物过敏、不耐受和饮食限制人群的 AI 食品扫描应用。可即时扫描标签、营养成分表、条形码、餐厅菜单和成品菜肴，检查是否符合你的个人档案。无论你是乳糜泻、乳糖不耐受、坚果过敏，还是遵循无麸质、纯素或低钠饮食，SafeEat 都能帮助你更安全地做出选择。',
      keywords: ['食物过敏应用', '配料扫描器', '过敏原检测', '营养标签识别', '条形码扫描', '菜单扫描', '无麸质检查', '乳糖不耐受应用', '饮食追踪', '食品安全']
    },
    cookies: {
      bannerText: '我们使用 Cookie：用于运行网站的必要 Cookie，以及仅在你同意的情况下使用的分析 Cookie。',
      accept: '接受',
      decline: '拒绝'
    },
    legal: {
      backHome: '返回首页'
    },
    footer: {
      product: '产品',
      features: '功能',
      howItWorks: '工作原理',
      testimonials: '用户评价',
      pricing: '价格',
      faq: '常见问题',
      contacts: '联系方式',
      legal: '法律',
      privacy: '隐私政策',
      terms: '服务条款',
      disclaimer: '免责声明',
      usePolicy: '用户手册',
      company: 'FJCode OU',
      address: 'Viru Valjak 2, 10111 Tallinn, Estonia',
      register: '注册号 16993109',
      email: 'support@safeeat.app',
      copyright: '© 2025 SafeEat。保留所有权利。'
    }
  }
};
